import React, { useState, useEffect, useRef , createContext, useContext } from 'react';
import { db } from "./firebase";
import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  doc,
  writeBatch
} from "firebase/firestore";
import { 
  LayoutDashboard, Calendar, CreditCard, Car, Users, Receipt, BarChart3, 
  Settings, FileText, Search, Plus, Filter, ChevronLeft, ChevronRight, 
  Edit3, Trash2, MoreVertical, DollarSign, Clock, CheckCircle, XCircle, 
  AlertCircle, Printer, Download, Mail, Phone, MapPin, Star, TrendingUp, 
  Wallet, Briefcase, Activity, X, Check, Bell, LogOut, Shield,
  Wrench, MessageSquare, Map, Gift, Package, Database,
  Moon, Sun, Globe, Tag, PieChart, AlertTriangle, Upload,
  Info, Menu, Maximize2, Minimize2, UserCog, ChevronDown, ChevronUp, Sparkles, Heart, Share2,
  Camera, Video, Grid, List, Layers, Zap, Award, Target, Coffee,
  User, Lock, ShieldAlert, KeyRound, ArrowRight, ScrollText, FileSignature,
  Eye, EyeOff, ShieldCheck
} from 'lucide-react';


const FLAG_CANDIDATES_BY_CODE = {
  sq: ['al.svg', 'al.png', 'sq.svg', 'sq.png', 'albania.svg', 'albania.png', 'kosovo.svg', 'kosovo.png'],
  en: ['gb.svg', 'gb.png', 'en.svg', 'en.png', 'uk.svg', 'uk.png', 'united-kingdom.svg', 'united-kingdom.png'],
  de: ['de.svg', 'de.png', 'germany.svg', 'germany.png'],
  fr: ['fr.svg', 'fr.png', 'france.svg', 'france.png'],
  it: ['it.svg', 'it.png', 'italy.svg', 'italy.png'],
  hr: ['hr.svg', 'hr.png', 'croatia.svg', 'croatia.png'],
};

const getFlagSrcByCode = (code) => {
  const candidates = FLAG_CANDIDATES_BY_CODE[code] || [];
  const publicBase = (process.env.PUBLIC_URL || '') + '/flags/';
  return candidates.length ? `${publicBase}${candidates[0]}` : null;
};

const getFlagFallbackEmoji = (code) => {
  const map = {
    sq: '🇦🇱',
    en: '🇬🇧',
    de: '🇩🇪',
    fr: '🇫🇷',
    it: '🇮🇹',
    hr: '🇭🇷',
  };
  return map[code] || '🌐';
};

/* ==============================
   i18n: translations + helpers
============================== */
const LOCALES = { sq: 'sq-AL', en: 'en-US', de: 'de-DE', it: 'it-IT', fr: 'fr-FR', hr: 'hr-HR' };

const TRANSLATIONS = {
  sq: {
    dashboard: 'Paneli',
    bookings: 'Rezervimet',
    cars: 'Veturat',
    clients: 'Klientët',
    client_details_title: 'Detajet e Klientit',
    client_tab_details: 'Detajet',
    client_tab_bookings: 'Rezervimet',
    client_tab_payments: 'Pagesat',
    client_bookings_empty: 'Ky klient nuk ka rezervime.',
    client_payments_empty: 'Ky klient nuk ka pagesa.',
    start_date_label: 'Data e Fillimit',
    end_date_label: 'Data e Mbarimit',
    no_data_found: 'Nuk u gjet asnjë e dhënë për këtë kërkim.',
    view_client: 'Shiko klientin',
    payments: 'Pagesat',
    expenses: 'Shpenzimet',
    invoices: 'Faturat',
    contracts: 'Kontratat',
    rental_contract_standard_template: 'Kontratë Qiraje (Template Standard)',
    print_contract: 'Printo Kontratën',
    client_usage: 'Klienti / Përdorimi',
    title_col: 'Titulli',
    total_paid: 'Totali i paguar',
    total_price: 'Çmimi total',
    name_client_vehicle: 'Emri (Klienti - Vetura)',
    days_count: 'Numri i ditëve',
    price_per_day_short: 'Çmimi ditor',
    id_number: 'ID / Numri',
    contracts_subtitle: 'Menaxho dhe printo kontratat për qiradhënien e veturave',
    insurance: 'Sigurimi',
    maintenance: 'Servisi',
    staff: 'Stafi',
    locations: 'Lokacionet',
    promotions: 'Promocionet',
    inventory: 'Inventari',
    settings: 'Konfigurimet',
    profile: 'Profili',
    reports: 'Raportet',
    reports_financial_summary: 'Përmbledhje dhe analitikë e performancës financiare',
    menu_main_section: 'Kryesore',
    menu_finance_analytics_section: 'Financa & Analitika',
    menu_operations_section: 'Operacione',
    booking_list: 'Lista',
    booking_payments_not_finished: 'Pagesat e Papërfunduara',
    booking_waitlist: 'Lista e Pritjes',
    booking_operations: 'Operacione',
    reservations_with_incomplete_payments: 'Rezervime me pagesa të papërfunduara',
    back_to_list: 'Kthehu te lista',
    total_unpaid: 'Totali i papaguar',
    payments_overdue: 'Pagesa të vonuara',
    payments_incoming: 'Pagesa në ardhje',
    remaining_amount: 'Shuma e mbetur',
    finish: 'Përfundo',
    no_waitlist_records: 'Nuk u gjet asnjë rezervim në listën e pritjes',
    no_waitlist_subtitle: "Shto rezervime me status waitlist për t'i parë këtu.",
    pickup_delivery_to_client: 'Dorëzim te klienti',
    pickup_return_from_client: 'Marrje nga klienti',
    month_view: 'Muaji',
    month_name_0: 'Janar',
    month_name_1: 'Shkurt',
    month_name_2: 'Mars',
    month_name_3: 'Prill',
    month_name_4: 'Maj',
    month_name_5: 'Qershor',
    month_name_6: 'Korrik',
    month_name_7: 'Gusht',
    month_name_8: 'Shtator',
    month_name_9: 'Tetor',
    month_name_10: 'Nëntor',
    month_name_11: 'Dhjetor',
    weekday_sun: 'Die',
    weekday_mon: 'Hën',
    weekday_tue: 'Mar',
    weekday_wed: 'Mër',
    weekday_thu: 'Enj',
    weekday_fri: 'Pre',
    weekday_sat: 'Sht',
    menu_system_section: 'Sistemi',
    recent_invoices: 'Faturat e fundit',
    recent_expenses: 'Shpenzimet e fundit',
    performance_last_6_months: 'Performanca për 6 muajt e fundit',
    status_active_signed: 'Aktive (Nënshkruar)',
    status_draft_template: 'Draft (Papërfunduar / Template)',
    content_tip_text: 'Shkruani pikat e marrëveshjes me klientin ose kushtet e përgjithshme. Pasi të ruhet, mund ta gjeneroni lehtësisht për ta printuar.',
    content_tip_title: 'Këshillë për përmbajtjen',
    contract_content_ph: 'Shënoni tekstin e plotë të kontratës tuaj këtu...',
    contract_content_label: 'Përmbajtja e Kontratës / Template',
    client_optional_hint: 'Lidhja e një klienti është opsionale. Lëre bosh nëse dëshironi ta ruani si Template të përgjithshëm.',
    choose_client_optional: 'Zgjidh Klientin (Lëre bosh nëse është Template)',
    created_date: 'Data e Krijimit',
    contract_title_ph: 'p.sh. Kontratë Qiraje Standarde',
    contract_title_label: 'Titulli i Kontratës*',
    expense_cat_other: 'Tjera',
    expense_cat_office_rent: 'Qira e Zyrës',
    expense_cat_salary: 'Paga',
    expense_cat_insurance: 'Sigurim',
    expense_cat_parts: 'Pjesë Këmbimi',
    expense_cat_service: 'Servis dhe Mirëmbajtje',
    expense_cat_fuel: 'Karburant',

    add: 'Shto',
    edit: 'Ndrysho',
    delete: 'Fshij',
    search: 'Kërko',

    filter: 'Filtro',
    select: 'Zgjidh',
    reset: 'Rivendos',
    close: 'Mbyll',
    bulk: 'Bulk',
    view: 'Shiko',
    // --- tables / filters / settings / profile / reports ---
    id: 'ID',
    client_col: 'Klienti',
    vehicle_col: 'Vetura',
    dates_col: 'Datat',
    total_col: 'Totali',
    status_col: 'Statusi',
    actions_col: 'Veprime',

    user_label: 'Përdoruesi',
    date_range: 'Intervali i Datave',
    to_word: 'deri',
    automatic: 'Automatik',
    configure_system: 'Konfiguro sistemin dhe ruaj Databazën',

    company_data: 'Të dhënat e kompanisë',
    company_name: 'Emri i Kompanisë',
    business_number: 'Numri i Biznesit',
    email: 'Email',
    phone: 'Telefoni',
    address: 'Adresa',
    currency: 'Valuta',
    euro: 'Euro (€)',
        save_changes: 'Ruaj Ndryshimet',
    settings_saved: 'Konfigurimet u ruajtën!',
    no_expenses: 'Nuk ka asnjë shpenzim.',
    no_invoices: 'Nuk ka faturë.',

    database_management: 'Menaxhimi i Databazës',
    db_help: 'Për të siguruar që asnjë e dhënë nuk humbet, shkarkoni rregullisht databazën tuaj. Skedari shkarkohet në formatin JSON.',
    download_database: 'Shkarko Databazën',
    save_as_json: 'Ruaj si skedar .json',
    upload_database: 'Ngarko Databazë',
    restore_from_pc: 'Rikthe të dhënat nga PC',
    reset_program: 'Reseto Programin',
    reset_program_desc: 'Fshij të gjitha të dhënat lokale dhe kthe programin në gjendjen fillestare.',
    reset_program_confirm: 'A je i sigurt që dëshiron ta resetosh programin? Të gjitha të dhënat e ruajtura lokalisht do të fshihen.',
    confirm: 'Konfirmo',
    notification: 'Njoftim',

    profile_title: 'Profili',
    profile_subtitle: 'Menaxhoni të dhënat personale dhe parametrat e llogarisë suaj.',
    details: 'Detajet',
    notification_prefs: 'Preferencat e Njoftimeve',
    reminders: 'Reminders',
    security: 'Siguria',
    google_calendar: 'Google Calendar',
    current_password: 'Fjalëkalimi Aktual',
    enter_current_password: 'Shkruani fjalëkalimin aktual',
    new_password: 'Fjalëkalimi i Ri',
    enter_new_password: 'Shkruani fjalëkalimin e ri',
    confirm_new_password: 'Konfirmo Fjalëkalimin e Ri',
    confirm_password: 'Konfirmoni fjalëkalimin e ri',
    update_password: 'Përditëso Fjalëkalimin',

    update: 'Përditëso',

    template_general: 'Template i Përgjithshëm',

    advanced_report: 'Raport i avancuar',
    advanced_report_sub: 'Zgjidh periudhën dhe shiko kostot fikse, shpenzimet, të ardhurat dhe fitimin neto.',
    from_date: 'Nga Data',
    to_date: 'Deri më Datë',
    income: 'Të Ardhurat',
    expenses_lbl: 'Shpenzimet',
    net_profit: 'Fitimi Neto',
    performance_6m: 'Performanca për 6 muajt e fundit',

    // --- dashboard extras ---
    fleet: 'Flota e Veturave',
    cars_free: 'Vetura të Lira',
    active_bookings: 'Rezervime Aktive',
    monthly_revenue: 'Qarkullimi Mujor',
    total_clients: 'Klientë Total',
    monthly_expenses: 'Shpenzime Mujore',

    badge_total: 'Total',
    badge_ready: 'Gati',
    badge_active: 'Aktiv',
    badge_monthly: 'Mujor',

    recent_bookings: 'Rezervimet e Fundit',
    see_all: 'Shiko të gjitha',
    late_return: 'Vonesë në kthim!',
    past_pickup: 'Marrje e kaluar',
    return_soon: 'Kthim i afërt!',
    return_soon_msg: '{client} pritet të kthejë {vehicle} për {mins} min.',
    late_return_msg: '{client} është vonuar në kthimin e {vehicle}.',
    pickup_soon: 'Marrje e afërt!',
    pickup_soon_msg: '{client} vjen të marrë {vehicle} për {mins} min.',
    past_pickup_msg: '{client} ishte planifikuar të marrë {vehicle} tani.',
    maintenance_scheduled: 'Servis i planifikuar',
    maintenance_scheduled_msg: '{vehicle} ka servis më {date} ({desc})',
    insurance_expiring_soon: 'Skadim i afërt i Sigurimit',
    insurance_expiring_soon_msg: '{vehicle} ({plate}) skadon pas {days} ditësh.',
    expiry_soon: 'Skadim i afërt',
    expiry_soon_msg: '{vehicle} skadon më {date}',

    was_planned_pickup: 'ishte planifikuar të marrë',

    alerts: 'Sinjalizimet',
    no_alerts_title: 'Asnjë alarm aktualisht.',
    no_alerts_subtitle: 'Çdo gjë është në rregull!',
    go_to_details: 'Shko tek detajet',

    apply_filters: 'Apliko',
    // statuses
    active: 'Aktive',
    pending: 'Në pritje',
    completed: 'E përfunduar',
    cancelled: 'Anuluar',
    rented: 'E dhënë',
    available: 'E lirë',
    maintenance_status: 'Në servis',
    scheduled: 'Planifikuar',
    in_progress: 'Në proces',
    choose_vehicle: 'Zgjidh Veturën...',
    defect_service_description: 'Përshkrimi i Defektit/Servisit',
    preparing: 'Duke u përgatitur...',
    paid: 'Paguar',


    welcome: 'Mirësevini, {name}! 👋',
    today_overview: 'Pasqyra e biznesit tuaj për ditën e sotme.',
    add_booking: 'Shto Rezervim',
    cancel: 'Anulo',
    status_partially_paid: 'Pjesërisht i paguar',
    status_paid: 'Paguar',
    status_cancelled: 'Anuluar',
    status_completed: 'Përfunduar',
    status_active: 'Aktiv',
    payment_status_label: 'Statusi i Pagesës',
    booking_status_label: 'Statusi i Rezervimit',
    total_label: 'Totali:',
    extras_label: 'Shtesat',
    rent_price: 'Çmimi i qirasë',
    price_summary: 'Përmbledhja e çmimit',
    extra_full_insurance: 'Full Insurance (15€/ditë)',
    extra_child_seat: 'Child Seat (10€/ditë)',
    extra_gps: 'GPS Navigation (5€/ditë)',
    extras_section: 'Shtesat',
    enable_additional_driver: 'Aktivizo shofer shtesë',
    additional_driver_section: 'Shofer shtesë',
    total_overall: 'gjithsej',
    bookings_word: 'rezervime',
    no_search_results: 'Nuk u gjetën të dhëna për këtë kërkim.',
    details_placeholder: 'Shkruani detaje shtesë këtu...',
    booked_for_dates: 'E nxënë në këto data',
    car_tab_reservations: 'Rezervimet',
    car_tab_services: 'Serviset',
    car_tab_expenses: 'Shpenzimet',
    car_tab_pricing_seasons: 'Sezonet e çmimeve',
    car_details_title: 'Detajet e veturës',
    car_images: 'Fotot e veturës',
    car_name: 'Emri i veturës',
    rental_price: 'Çmimi i qirasë',
    license_plate_label: 'Targat',
    identification_number: 'Numri i identifikimit',
    registration_date: 'Data e regjistrimit',
    number_of_seats: 'Numri i ulëseve',
    number_of_doors: 'Numri i dyerve',
    fuel_type: 'Lloji i karburantit',
    transmission_type: 'Lloji i transmisionit',
    has_air_conditioning: 'Ka ajër të kondicionuar',
    show_in_website: 'Shfaq në uebfaqe',
    optional_details: 'Detaje shtesë',
    green_card: 'Kartoni i gjelbër',
    add_green_card: 'Shto kartonin e gjelbër',
    add_tpl: 'Shto TPL',
    car_tab_details: 'Detajet',
    car_tab_tpl: 'TPL',
    car_no_reservations: 'Kjo veturë nuk ka rezervime.',
    car_no_services: 'Kjo veturë nuk ka servise.',
    car_no_expenses: 'Kjo veturë nuk ka shpenzime.',
    car_pricing_empty: 'Nuk ka sezone çmimesh për këtë veturë.',
    car_tpl_empty: 'Nuk ka të dhëna TPL për këtë veturë.',
    tpl_amount: 'Shuma',
    renewal_date: 'Data e rinovimit',
    expiry_date: 'Data e skadimit',
    select_car: 'Zgjidh veturën',
    no_car_selected: 'Nuk është zgjedhur vetura',
    view_car: 'Shiko veturën',
    add_car_images: 'Shto foto',
    registration_type_tpl: 'TPL',
    category: 'Kategoria',
    choose_client: 'Zgjidh klientin',
    new_client: 'Klient i ri',
    existing_client: 'Klient ekzistues',
    client_section: 'Klienti',
    choose_file: 'Zgjidh',
    click_to_upload: 'Kliko për të ngarkuar',
    enter_notes_here: 'Shkruani shënimet këtu...',
    blacklist_client: 'Blloko (Blacklist) këtë klient',
    id_back: 'ID (prapa)',
    id_front: 'ID (para)',
    passport_number: 'Numri i Pasaportit',
    license_number: 'Numri i Licencës (Patentë Shoferi)',
    personal_id_number: 'Numri i Identifikimit (Letërnjoftimit)',
    documents_section: 'Dokumentet',
    client_avatar: 'Foto e klientit (avatar)',
    address_label: 'Adresa',
    birthdate: 'Data e lindjes',
    optional: 'Opsionale',
    secondary_phone: 'Telefon shtesë',
    phone_number: 'Numri i telefonit',
    enter_full_name: 'Shkruani emrin e plotë',
    full_name_required: 'Emri i plotë*',
    client_profile_section: 'Profili i klientit',
    flight_number: 'Numri i fluturimit',
    pickup_fee: 'Tarifa e marrjes',
    delivery_fee: 'Tarifa e dorëzimit',
    notes: 'Shënime',
    deposit: 'Depozita',
    total_rent_price: 'Çmimi Total i Qirasë',
    daily_price: 'Çmimi Ditor',
    pickup_location: 'Vendndodhja e marrjes',
    return_location: 'Vendndodhja e dorëzimit',
    select_car: 'Zgjidh makinën...',
    car_required: 'Makina*',
    end_date_time: 'Data & Koha e mbarimit*',
    start_date_time: 'Data & Koha e fillimit*',
    booking_details_section: 'Detajet e Rezervimit',
    edit_payment: 'Ndrysho Pagesë',
    add_payment: 'Shto Pagesë',
    invoice_number_col: 'FATURA #',
    no_email: 'Pa email',
    no_phone: 'Pa telefon',
    unpaid_pending: 'E papaguar (Në pritje)',
    invoice_total: 'Shuma Totale',
    invoice_date: 'Data e Faturës',
    choose_client_required: 'Zgjidh Klientin*',
    status_failed: 'Dështoi',
    booking_code: 'Kodi i Rezervimit',
    expense_desc_ph: 'Përshkruani shpenzimin këtu...',
    expense_desc_label: 'Përshkrimi / Detajet',
    expense_general_no_car: 'E Përgjithshme / Pa veturë',
    expense_vehicle_link: 'Lidhet me Veturën (Opsionale)',
    expense_category_label: 'Kategoria e Shpenzimit*',
    edit_contract: 'Ndrysho Kontratën',
    add_contract: 'Krijo Kontratë/Template',
    edit_invoice: 'Ndrysho Faturë',
    add_invoice: 'Shto Faturë',
    edit_expense: 'Ndrysho Shpenzim',
    add_expense: 'Shto Shpenzim',
    edit_booking: 'Ndrysho Rezervim',
    edit_client: 'Ndrysho Klient',
    add_client: 'Shto Klient',

    pickups_today: 'Marrjet sot',
    returns_today: 'Kthimet sot',
    no_pickups_today: 'Nuk ka marrje sot',
    no_returns_today: 'Nuk ka kthime sot',

    banner_returns_today_title: 'Kthime sot',
    banner_returns_today_msg: "Ke {count} veturë(a) për t'u kthyer sot: {vehicles}",
    banner_ins_expired_title: 'Sigurime të skaduara',
    expired_insurances_title: 'Sigurime të skaduara',
    expired_insurances_msg: 'Keni {count} sigurim(e) të skaduar(a). Hapni "Sigurimet" për detaje.',
    banner_ins_expired_msg: 'Ke {count} sigurim(e) të skaduar(a). Hap “Sigurimet” për detaje.',
    banner_ins_week_title: 'Sigurime që skadojnë këtë javë',
    banner_ins_week_msg: 'Ke {count} sigurim(e) që skadojnë brenda 7 ditëve.',
alerts: 'Sinjalizimet',
insurance_expired: 'Sigurimi i Skaduar!',
client_vehicle: 'Klienti & Vetura',
expired_on: 'ka skaduar më',

company_data: 'Të dhënat e kompanisë',
system_config: 'Konfiguro sistemin dhe ruaj Databazën',

details: 'Detajet',
notification_preferences: 'Preferencat e Njoftimeve',

services_faults: 'Serviset dhe defektet',
vehicle: 'Vetura',
description: 'Përshkrimi',
date: 'Data',
cost: 'Kostoja',
status: 'Statusi',
expired: 'E skaduar',

edit_insurance: 'Ndrysho sigurimin',
fill_fields_below: 'Plotëso fushat më poshtë dhe ruaj ndryshimet.',

insurance_company: 'Kompania e Sigurimit',
policy_number: 'Numri i Policës',
expiry_date: 'Data e Skadimit',

client: 'Klienti',
search_contract: 'Kërko kontratë...',
revenue: 'Të ardhurat',
profit: 'Fitimi',

last_6_months: 'Performanca për 6 muajt e fundit',
last_expenses: 'Shpenzimet e fundit',
last_invoices: 'Faturat e fundit',
    manage_cars_subtitle: 'Menaxho dhe monitoro të gjitha veturat',

    manage_bookings_subtitle: 'Menaxho rezervimet aktive dhe të kaluara',
    contacts: 'Kontaktet',
    bookings_expenses: 'Rezervime / Shpenzime',
    search_booking_placeholder: 'Kërko rezervimin...',
    print_rental_contract: 'Printo Kontratën e Qirasë',
    transmission: 'Transmisioni',
    add_car: 'Shto Veturë',
    fill_fields: 'Plotëso fushat më poshtë dhe ruaj ndryshimet.',
    brand: 'Marka',
    model: 'Modeli',
    year: 'Viti',
    plate: 'Targa',
    color: 'Ngjyra',
    price_per_day: 'Çmimi/Ditë (€)',
    technical_specs: 'Specifikimet teknike',
    fuel: 'Karburanti',
    petrol: 'Benzinë',
    seats: 'Vendet',
    doors: 'Dyert',
    engine_size: 'Madhësia e motorit',
    main_photo: 'Foto kryesore',
    click_upload: 'Kliko për të ngarkuar',
    choose: 'Zgjidh',
    file_uploaded: 'Skedari u ngarkua',
    select_time: 'Zgjidh orën…',
    search_client_placeholder: 'Kërko klientin...',
    no_client_found: 'Asnjë klient nuk u gjet.',

    manual: 'Manual',
    day: 'ditë',
    week: 'javë',
    month: 'muaj',
    search_payment_placeholder: 'Kërko pagesën...',
    other: 'Tjera',
    paypal: 'PayPal',
    bank_transfer: 'Transfer Bankar',
    bank_card: 'Kartelë Bankare',
    cash: 'Kesh',
    date_col: 'Data',
    method_col: 'Metoda',
    amount_col: 'Shuma',
    booking_col: 'Rezervimi',
    payment_method_label: 'Mënyra e Pagesës',
    search_expense_placeholder: 'Kërko shpenzimin...',
    payments_subtitle: 'Historiku i transaksioneve',
    expenses_subtitle: 'Menaxho shpenzimet dhe kostot operative',
    invoices_subtitle: 'Faturat e krijuara për klientët',
    insurance_subtitle: 'Sigurimet e Veturave dhe Skadimet',
    edit_car: 'Ndrysho Veturë',
    diesel: 'Dizel',
    electric: 'Elektrik',
    hybrid: 'Hybrid',
    has_ac: 'Ka ajër të kondicionuar',
    show_on_web: 'Shfaq në uebfaqe',
    additional_details: 'Detajet shtesë',
    upload_hint: 'PNG / JPG / URL',

    blacklisted: 'I bllokuar',
  
    car_added: 'Makina u Shtua',
    car_updated: 'Makina u Përditësua',
    car_archived: 'Makina u Arkivua',
    car_deleted: 'Makina u Fshi',
    client_updated: 'Klienti u Përditësua',
    expense_added: 'Shpenzimi u Shtua',
    expense_updated: 'Shpenzimi u Përditësua',
    expense_deleted: 'Shpenzimi u Fshi',
    payment_added: 'Pagesa u Shtua',
    payment_updated: 'Pagesa u Përditësua',
    payment_deleted: 'Pagesa u Fshi',
    booking_archived: 'Rezervimi u Arkivua',
    booking_deleted: 'Rezervimi u Fshi',
    booking_added: 'Rezervimi u Shtua',
    booking_updated: 'Rezervimi u Përditësua',
    daily_summary: 'Përmbledhje Ditore',
    daily_summary_desc: 'Merrni një përmbledhje ditore të rezervimeve të ardhshme',
    time: 'Koha',
    car_return_notification: 'Njoftim për Kthimin e Makinës',
    car_return_desc: 'Merrni njoftim para se të vijë koha për kthimin e makinës',
    minutes_before: 'Minuta para',
    car_pickup_notification: 'Njoftim për Marrjen e Makinës',
    car_pickup_desc: 'Merrni njoftim para se të planifikohet marrja e makinës',
    car_registration_reminder: 'Kujtesë për Regjistrimin e Makinës',
    car_registration_desc: 'Merrni kujtesë kur regjistrimi i makinës është afër skadimit',
    days_before: 'Ditë para',
    save: 'Ruaj',
    save_preferences: 'Ruaj Preferencat',
    password_incorrect: 'Fjalëkalimi aktual është i pasaktë!',
    passwords_not_match: 'Fjalëkalimet e reja nuk përputhen!',
    password_min_length: 'Fjalëkalimi duhet të ketë të paktën 6 karaktere.',
    password_updated_success: 'Fjalëkalimi u përditësua me sukses!',
    details_saved_success: 'Detajet u ruajtën me sukses!',
    google_calendar_not_connected: 'Google Calendar nuk është i lidhur ende',
    connect_google_calendar: 'Lidhu me Google Calendar',
    google_calendar_desc: 'Kur aktivizohet, rezervimet tuaja të reja do të shtohen automatikisht në kalendarin tuaj të Google.',
},

  en: {
    dashboard: 'Dashboard',
    bookings: 'Bookings',
    cars: 'Cars',
    clients: 'Clients',
    client_details_title: 'Client Details',
    client_tab_details: 'Details',
    client_tab_bookings: 'Bookings',
    client_tab_payments: 'Payments',
    client_bookings_empty: 'This client has no bookings.',
    client_payments_empty: 'This client has no payments.',
    start_date_label: 'Start Date',
    end_date_label: 'End Date',
    no_data_found: 'No data found for this search.',
    view_client: 'View client',
    payments: 'Payments',
    expenses: 'Expenses',
    invoices: 'Invoices',
    contracts: 'Contracts',
    rental_contract_standard_template: 'Rental Contract (Standard Template)',
    print_contract: 'Print contract',
    client_usage: 'Client / Use',
    title_col: 'Title',
    id_number: 'ID / Number',
    contracts_subtitle: 'Manage and print rental contracts',
    insurance: 'Insurance',
    maintenance: 'Maintenance',
    staff: 'Staff',
    locations: 'Locations',
    promotions: 'Promotions',
    inventory: 'Inventory',
    settings: 'Settings',
    profile: 'Profile',
    reports: 'Reports',
    reports_financial_summary: 'Financial performance overview and analytics',
    menu_main_section: 'Main',
    menu_finance_analytics_section: 'Finance & Analytics',
    menu_operations_section: 'Operations',
    booking_list: 'List',
    booking_payments_not_finished: 'Payments Not Finished',
    booking_waitlist: 'Waitlist',
    booking_operations: 'Operations',
    reservations_with_incomplete_payments: 'Reservations with Incomplete Payments',
    back_to_list: 'Back to List',
    total_unpaid: 'Total Unpaid',
    payments_overdue: 'Payments Overdue',
    payments_incoming: 'Payments Incoming',
    remaining_amount: 'Remaining Amount',
    finish: 'Finish',
    no_waitlist_records: 'No reservation records found on waitlist',
    no_waitlist_subtitle: 'Add bookings with waitlist status to see them here.',
    pickup_delivery_to_client: 'Dropoff (Delivery to Client)',
    pickup_return_from_client: 'Pickup (Return From Client)',
    month_view: 'month',
    month_name_0: 'January',
    month_name_1: 'February',
    month_name_2: 'March',
    month_name_3: 'April',
    month_name_4: 'May',
    month_name_5: 'June',
    month_name_6: 'July',
    month_name_7: 'August',
    month_name_8: 'September',
    month_name_9: 'October',
    month_name_10: 'November',
    month_name_11: 'December',
    weekday_sun: 'Sun',
    weekday_mon: 'Mon',
    weekday_tue: 'Tue',
    weekday_wed: 'Wed',
    weekday_thu: 'Thu',
    weekday_fri: 'Fri',
    weekday_sat: 'Sat',
    menu_system_section: 'System',
    recent_invoices: 'Recent invoices',
    recent_expenses: 'Recent expenses',
    performance_last_6_months: 'Performance for the last 6 months',
    status_active_signed: 'Active (Signed)',
    status_draft_template: 'Draft (Unfinished / Template)',
    content_tip_text: 'Write the agreement points with the client or general terms. Once saved, you can easily generate it for printing.',
    content_tip_title: 'Content tip',
    contract_content_ph: 'Write the full contract text here...',
    contract_content_label: 'Contract content / Template',
    client_optional_hint: 'Linking a client is optional. Leave empty if you want to save it as a general template.',
    choose_client_optional: 'Select client (leave empty if template)',
    created_date: 'Created date',
    contract_title_ph: 'e.g. Standard rental contract',
    contract_title_label: 'Contract title*',
    expense_cat_other: 'Other',
    expense_cat_office_rent: 'Office rent',
    expense_cat_salary: 'Salary',
    expense_cat_insurance: 'Insurance',
    expense_cat_parts: 'Spare parts',
    expense_cat_service: 'Service & Maintenance',
    expense_cat_fuel: 'Fuel',

    add: 'Add',
    edit: 'Edit',
    delete: 'Delete',
    search: 'Search',

    filter: 'Filter',
    select: 'Select',
    reset: 'Reset',
    close: 'Close',
    bulk: 'Bulk',
    view: 'View',
    // --- tables / filters / settings / profile / reports ---
    id: 'ID',
    client_col: 'Client',
    vehicle_col: 'Vehicle',
    dates_col: 'Dates',
    total_col: 'Total',
    status_col: 'Status',
    actions_col: 'Actions',

    user_label: 'User',
    date_range: 'Date range',
    to_word: 'to',
    automatic: 'Automatic',
    configure_system: 'Configure the system and back up the database',

    company_data: 'Company data',
    company_name: 'Company name',
    business_number: 'Business number',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    currency: 'Currency',
    euro: 'Euro (€)',
        save_changes: 'Save Changes',
    settings_saved: 'Settings saved successfully!',
    no_expenses: 'No expenses found.',
    no_invoices: 'No invoices found.',

    database_management: 'Database management',
    db_help: 'To ensure no data is lost, download your database regularly. The file is downloaded in JSON format.',
    download_database: 'Download database',
    save_as_json: 'Save as .json file',
    upload_database: 'Upload database',
    restore_from_pc: 'Restore data from PC',
    reset_program: 'Reset Program',
    reset_program_desc: 'Delete all local data and return the program to its initial state.',
    reset_program_confirm: 'Are you sure you want to reset the program? All locally saved data will be deleted.',
    confirm: 'Confirm',
    notification: 'Notification',

    profile_title: 'Profile',
    profile_subtitle: 'Manage your personal data and account settings.',
    details: 'Details',
    notification_prefs: 'Notification preferences',
    reminders: 'Reminders',
    security: 'Security',
    google_calendar: 'Google Calendar',
    current_password: 'Current Password',
    enter_current_password: 'Enter current password',
    new_password: 'New Password',
    enter_new_password: 'Enter new password',
    confirm_new_password: 'Confirm New Password',
    confirm_password: 'Confirm the new password',
    update_password: 'Update Password',

    update: 'Update',

    template_general: 'General template',

    advanced_report: 'Advanced report',
    advanced_report_sub: 'Choose the period and see fixed costs, expenses, revenue and net profit.',
    from_date: 'From date',
    to_date: 'To date',
    income: 'Income',
    expenses_lbl: 'Expenses',
    net_profit: 'Net profit',
    performance_6m: 'Performance for the last 6 months',

    // --- dashboard extras ---
    fleet: 'Fleet',
    cars_free: 'Available cars',
    active_bookings: 'Active bookings',
    monthly_revenue: 'Monthly revenue',
    total_clients: 'Total clients',
    monthly_expenses: 'Monthly expenses',

    badge_total: 'Total',
    badge_ready: 'Ready',
    badge_active: 'Active',
    badge_monthly: 'Monthly',

    recent_bookings: 'Recent bookings',
    see_all: 'See all',
    late_return: 'Late return!',
    past_pickup: 'Missed pickup',
    return_soon: 'Return soon!',
    return_soon_msg: '{client} is expected to return {vehicle} in {mins} min.',
    late_return_msg: '{client} is late returning {vehicle}.',
    pickup_soon: 'Pickup soon!',
    pickup_soon_msg: '{client} will pick up {vehicle} in {mins} min.',
    past_pickup_msg: '{client} was scheduled to pick up {vehicle} now.',
    maintenance_scheduled: 'Scheduled maintenance',
    maintenance_scheduled_msg: '{vehicle} has maintenance on {date} ({desc})',
    insurance_expiring_soon: 'Insurance expiring soon',
    insurance_expiring_soon_msg: '{vehicle} ({plate}) expires in {days} days.',
    expiry_soon: 'Expiring soon',
    expiry_soon_msg: '{vehicle} expires on {date}',

    was_planned_pickup: 'was scheduled to pick up',

    alerts: 'Alerts',
    no_alerts_title: 'No alerts right now.',
    no_alerts_subtitle: 'Everything looks good!',
    go_to_details: 'Go to details',

    apply_filters: 'Apply',
    // statuses
    active: 'Active',
    pending: 'Pending',
    completed: 'Completed',
    cancelled: 'Cancelled',
    rented: 'Rented',
    available: 'Available',
    maintenance_status: 'In maintenance',
    scheduled: 'Scheduled',
    in_progress: 'In progress',
    choose_vehicle: 'Select vehicle...',
    defect_service_description: 'Defect / Service description',
    preparing: 'Preparing...',
    paid: 'Paid',


    welcome: 'Welcome, {name}! 👋',
    today_overview: 'Your business snapshot for today.',
    add_booking: 'Add booking',
    cancel: 'Cancel',
    status_partially_paid: 'Partially paid',
    status_paid: 'Paid',
    status_cancelled: 'Cancelled',
    status_completed: 'Completed',
    status_active: 'Active',
    payment_status_label: 'Payment status',
    booking_status_label: 'Booking status',
    total_label: 'Total:',
    extras_label: 'Extras',
    rent_price: 'Rental price',
    price_summary: 'Price summary',
    extra_full_insurance: 'Full Insurance (€15/day)',
    extra_child_seat: 'Child Seat (€10/day)',
    extra_gps: 'GPS Navigation (€5/day)',
    extras_section: 'Extras',
    enable_additional_driver: 'Enable additional driver',
    additional_driver_section: 'Additional driver',
    total_overall: 'total',
    bookings_word: 'bookings',
    no_search_results: 'No data found for this search.',
    details_placeholder: 'Write additional details here...',
    booked_for_dates: 'Booked for these dates',
    car_tab_reservations: 'Reservations',
    car_tab_services: 'Services',
    car_tab_expenses: 'Expenses',
    car_tab_pricing_seasons: 'Pricing Seasons',
    car_details_title: 'Car Details',
    car_images: 'Car Images',
    car_name: 'Car Name',
    rental_price: 'Rental Price',
    license_plate_label: 'License Plate',
    identification_number: 'Identification Number',
    registration_date: 'Registration Date',
    number_of_seats: 'Number of seats',
    number_of_doors: 'Number of doors',
    fuel_type: 'Fuel Type',
    transmission_type: 'Transmission type',
    has_air_conditioning: 'Has Air Conditioning',
    show_in_website: 'Show on Website',
    optional_details: 'Optional Details',
    green_card: 'Green Card',
    add_green_card: 'Add Green Card',
    add_tpl: 'Add TPL',
    car_tab_details: 'Details',
    car_tab_tpl: 'TPL',
    car_no_reservations: 'This car has no reservations.',
    car_no_services: 'This car has no services.',
    car_no_expenses: 'This car has no expenses.',
    car_pricing_empty: 'No pricing seasons for this car.',
    car_tpl_empty: 'No TPL records for this car.',
    tpl_amount: 'Amount',
    renewal_date: 'Renewal Date',
    expiry_date: 'Expiry Date',
    select_car: 'Select car',
    no_car_selected: 'No car selected',
    view_car: 'View car',
    add_car_images: 'Add images',
    registration_type_tpl: 'TPL',
    category: 'Category',
    choose_client: 'Choose client',
    new_client: 'New client',
    existing_client: 'Existing client',
    client_section: 'Client',
    choose_file: 'Choose',
    click_to_upload: 'Click to upload',
    enter_notes_here: 'Write notes here...',
    blacklist_client: 'Blacklist this client',
    id_back: 'ID (back)',
    id_front: 'ID (front)',
    passport_number: 'Passport number',
    license_number: 'Driver license number',
    personal_id_number: 'ID number (ID card)',
    documents_section: 'Documents',
    client_avatar: 'Client photo (avatar)',
    address_label: 'Address',
    birthdate: 'Date of birth',
    optional: 'Optional',
    secondary_phone: 'Secondary phone',
    phone_number: 'Phone number',
    enter_full_name: 'Enter full name',
    full_name_required: 'Full name*',
    client_profile_section: 'Client profile',
    flight_number: 'Flight number',
    pickup_fee: 'Pickup fee',
    delivery_fee: 'Delivery fee',
    notes: 'Notes',
    deposit: 'Deposit',
    total_rent_price: 'Total rental price',
    daily_price: 'Daily price',
    pickup_location: 'Pickup location',
    return_location: 'Drop-off location',
    select_car: 'Select car...',
    car_required: 'Car*',
    end_date_time: 'End date & time*',
    start_date_time: 'Start date & time*',
    booking_details_section: 'Booking details',
    edit_payment: 'Edit Payment',
    add_payment: 'Add Payment',
    invoice_number_col: 'INVOICE #',
    no_email: 'No email',
    no_phone: 'No phone',
    unpaid_pending: 'Unpaid (Pending)',
    invoice_total: 'Total amount',
    invoice_date: 'Invoice date',
    choose_client_required: 'Choose client*',
    status_failed: 'Failed',
    booking_code: 'Booking code',
    expense_desc_ph: 'Describe the expense here...',
    expense_desc_label: 'Description / Details',
    expense_general_no_car: 'General / No vehicle',
    expense_vehicle_link: 'Linked to vehicle (optional)',
    expense_category_label: 'Expense category*',
    edit_contract: 'Edit Contract',
    add_contract: 'Create Contract/Template',
    edit_invoice: 'Edit Invoice',
    add_invoice: 'Add Invoice',
    edit_expense: 'Edit Expense',
    add_expense: 'Add Expense',
    edit_booking: 'Edit Booking',
    edit_client: 'Edit Client',
    add_client: 'Add Client',

    pickups_today: 'Pickups today',
    returns_today: 'Returns today',
    no_pickups_today: 'No pickups today',
    no_returns_today: 'No returns today',

    banner_returns_today_title: 'Returns today',
    banner_returns_today_msg: 'You have {count} car(s) to return today: {vehicles}',
    banner_ins_expired_title: 'Expired insurances',
    expired_insurances_title: 'Expired insurances',
    expired_insurances_msg: 'You have {count} expired insurance(s). Open "Insurance" for details.',
    banner_ins_expired_msg: 'You have {count} expired insurance(s). Open “Insurance” for details.',
    banner_ins_week_title: 'Insurances expiring this week',
    banner_ins_week_msg: 'You have {count} insurance(s) expiring within 7 days.',
alerts: 'Alerts',
insurance_expired: 'Insurance Expired!',
client_vehicle: 'Client & Vehicle',
expired_on: 'expired on',

company_data: 'Company data',
system_config: 'Configure system and save database',

details: 'Details',
notification_preferences: 'Notification preferences',

services_faults: 'Services and faults',
vehicle: 'Vehicle',
description: 'Description',
date: 'Date',
cost: 'Cost',
status: 'Status',
expired: 'Expired',

edit_insurance: 'Edit insurance',
fill_fields_below: 'Fill the fields below and save changes.',

insurance_company: 'Insurance company',
policy_number: 'Policy number',
expiry_date: 'Expiry date',

client: 'Client',
search_contract: 'Search contract...',
revenue: 'Revenue',
profit: 'Profit',

last_6_months: 'Performance for the last 6 months',
last_expenses: 'Recent expenses',
last_invoices: 'Recent invoices',
    manage_cars_subtitle: 'Manage and monitor all cars',

    manage_bookings_subtitle: 'Manage active and past bookings',
    contacts: 'Contacts',
    bookings_expenses: 'Bookings / Expenses',
    search_booking_placeholder: 'Search booking...',
    print_rental_contract: 'Print rental contract',
    transmission: 'Transmission',
    add_car: 'Add Car',
    fill_fields: 'Fill in the fields below and save the changes.',
    brand: 'Brand',
    model: 'Model',
    year: 'Year',
    plate: 'License plate',
    color: 'Color',
    price_per_day: 'Price/Day (€)',
    technical_specs: 'Technical specifications',
    fuel: 'Fuel',
    petrol: 'Petrol',
    seats: 'Seats',
    doors: 'Doors',
    engine_size: 'Engine size',
    main_photo: 'Main photo',
    click_upload: 'Click to upload',
    choose: 'Choose',
    file_uploaded: 'File uploaded',
    select_time: 'Select time…',
    search_client_placeholder: 'Search client...',
    no_client_found: 'No client found.',

    manual: 'Manual',
    day: 'day',
    week: 'week',
    month: 'month',
    search_payment_placeholder: 'Search payment...',
    other: 'Other',
    paypal: 'PayPal',
    bank_transfer: 'Bank transfer',
    bank_card: 'Bank card',
    cash: 'Cash',
    date_col: 'Date',
    method_col: 'Method',
    amount_col: 'Amount',
    booking_col: 'Booking',
    payment_method_label: 'Payment method',
    search_expense_placeholder: 'Search expense...',
    payments_subtitle: 'Transaction history',
    expenses_subtitle: 'Manage operating expenses and costs',
    invoices_subtitle: 'Invoices created for clients',
    insurance_subtitle: 'Vehicle insurance and expiry dates',
    edit_car: 'Edit Car',
    diesel: 'Diesel',
    electric: 'Electric',
    hybrid: 'Hybrid',
    has_ac: 'Air conditioning',
    show_on_web: 'Show on website',
    additional_details: 'Additional details',
    upload_hint: 'PNG / JPG / URL',

    blacklisted: 'Blacklisted',
  
    car_added: 'Car Added',
    car_updated: 'Car Updated',
    car_archived: 'Car Archived',
    car_deleted: 'Car Deleted',
    client_updated: 'Client Updated',
    expense_added: 'Expense Added',
    expense_updated: 'Expense Updated',
    expense_deleted: 'Expense Deleted',
    payment_added: 'Payment Added',
    payment_updated: 'Payment Updated',
    payment_deleted: 'Payment Deleted',
    booking_archived: 'Booking Archived',
    booking_deleted: 'Booking Deleted',
    booking_added: 'Booking Added',
    booking_updated: 'Booking Updated',
    daily_summary: 'Daily Summary',
    daily_summary_desc: 'Receive a daily summary of upcoming bookings',
    time: 'Time',
    car_return_notification: 'Car Return Notification',
    car_return_desc: 'Receive a notification before the car return time',
    minutes_before: 'Minutes before',
    car_pickup_notification: 'Car Pickup Notification',
    car_pickup_desc: 'Receive a notification before the scheduled pickup',
    car_registration_reminder: 'Car Registration Reminder',
    car_registration_desc: 'Receive a reminder when the car registration is about to expire',
    days_before: 'Days before',
    save: 'Save',
    save_preferences: 'Save Preferences',
    password_incorrect: 'Current password is incorrect!',
    passwords_not_match: 'New passwords do not match!',
    password_min_length: 'Password must be at least 6 characters.',
    password_updated_success: 'Password updated successfully!',
    details_saved_success: 'Details saved successfully!',
    google_calendar_not_connected: 'Google Calendar is not connected yet',
    connect_google_calendar: 'Connect to Google Calendar',
    google_calendar_desc: 'When enabled, your new bookings will automatically be added to your Google Calendar.',
},

  de: {
    dashboard: 'Dashboard',
    bookings: 'Reservierungen',
    cars: 'Autos',
    clients: 'Kunden',
    client_details_title: 'Kundendetails',
    client_tab_details: 'Details',
    client_tab_bookings: 'Reservierungen',
    client_tab_payments: 'Zahlungen',
    client_bookings_empty: 'Dieser Kunde hat keine Reservierungen.',
    client_payments_empty: 'Dieser Kunde hat keine Zahlungen.',
    start_date_label: 'Startdatum',
    end_date_label: 'Enddatum',
    no_data_found: 'Für diese Suche wurden keine Daten gefunden.',
    view_client: 'Kunde ansehen',
    payments: 'Zahlungen',
    expenses: 'Ausgaben',
    invoices: 'Rechnungen',
    contracts: 'Verträge',
    rental_contract_standard_template: 'Mietvertrag (Standardvorlage)',
    print_contract: 'Vertrag drucken',
    client_usage: 'Kunde / Zweck',
    title_col: 'Titel',
    id_number: 'ID / Nummer',
    contracts_subtitle: 'Mietverträge verwalten und drucken',
    insurance: 'Versicherung',
    maintenance: 'Service',
    staff: 'Personal',
    locations: 'Standorte',
    promotions: 'Aktionen',
    inventory: 'Inventar',
    settings: 'Einstellungen',
    profile: 'Profil',
    reports: 'Berichte',
    reports_financial_summary: 'Übersicht und Analyse der finanziellen Leistung',
    menu_main_section: 'Hauptmenü',
    menu_finance_analytics_section: 'Finanzen & Analytik',
    menu_operations_section: 'Operationen',
    booking_list: 'Liste',
    booking_payments_not_finished: 'Zahlungen nicht abgeschlossen',
    booking_waitlist: 'Warteliste',
    booking_operations: 'Operationen',
    reservations_with_incomplete_payments: 'Reservierungen mit unvollständigen Zahlungen',
    back_to_list: 'Zurück zur Liste',
    total_unpaid: 'Gesamt offen',
    payments_overdue: 'Überfällige Zahlungen',
    payments_incoming: 'Anstehende Zahlungen',
    remaining_amount: 'Restbetrag',
    finish: 'Abschließen',
    no_waitlist_records: 'Keine Reservierungen in der Warteliste gefunden',
    no_waitlist_subtitle: 'Füge Reservierungen mit Wartelistenstatus hinzu, um sie hier zu sehen.',
    pickup_delivery_to_client: 'Übergabe an den Kunden',
    pickup_return_from_client: 'Abholung vom Kunden',
    month_view: 'Monat',
    month_name_0: 'Januar',
    month_name_1: 'Februar',
    month_name_2: 'März',
    month_name_3: 'April',
    month_name_4: 'Mai',
    month_name_5: 'Juni',
    month_name_6: 'Juli',
    month_name_7: 'August',
    month_name_8: 'September',
    month_name_9: 'Oktober',
    month_name_10: 'November',
    month_name_11: 'Dezember',
    weekday_sun: 'So',
    weekday_mon: 'Mo',
    weekday_tue: 'Di',
    weekday_wed: 'Mi',
    weekday_thu: 'Do',
    weekday_fri: 'Fr',
    weekday_sat: 'Sa',
    menu_system_section: 'System',
    recent_invoices: 'Letzte Rechnungen',
    recent_expenses: 'Letzte Ausgaben',
    performance_last_6_months: 'Leistung der letzten 6 Monate',
    status_active_signed: 'Aktiv (Unterschrieben)',
    status_draft_template: 'Entwurf (Unvollständig / Vorlage)',
    content_tip_text: 'Schreiben Sie die Vereinbarungspunkte oder allgemeine Bedingungen. Nach dem Speichern können Sie es leicht zum Drucken generieren.',
    content_tip_title: 'Inhaltstipp',
    contract_content_ph: 'Schreiben Sie hier den vollständigen Vertragstext...',
    contract_content_label: 'Vertragsinhalt / Vorlage',
    client_optional_hint: 'Die Zuordnung zu einem Kunden ist optional. Leer lassen, um als allgemeine Vorlage zu speichern.',
    choose_client_optional: 'Kunden wählen (leer lassen, wenn Vorlage)',
    created_date: 'Erstellungsdatum',
    contract_title_ph: 'z.B. Standard-Mietvertrag',
    contract_title_label: 'Vertragstitel*',
    expense_cat_other: 'Sonstiges',
    expense_cat_office_rent: 'Büromiete',
    expense_cat_salary: 'Gehalt',
    expense_cat_insurance: 'Versicherung',
    expense_cat_parts: 'Ersatzteile',
    expense_cat_service: 'Service & Wartung',
    expense_cat_fuel: 'Kraftstoff',

    add: 'Hinzufügen',
    edit: 'Bearbeiten',
    delete: 'Löschen',
    search: 'Suchen',

    filter: 'Filter',
    select: 'Auswählen',
    reset: 'Zurücksetzen',
    close: 'Schließen',
    bulk: 'Bulk',
    view: 'Ansehen',
    // --- tables / filters / settings / profile / reports ---
    id: 'ID',
    client_col: 'Kunde',
    vehicle_col: 'Fahrzeug',
    dates_col: 'Daten',
    total_col: 'Summe',
    status_col: 'Status',
    actions_col: 'Aktionen',

    user_label: 'Benutzer',
    date_range: 'Datumsbereich',
    to_word: 'bis',
    automatic: 'Automatisch',
    configure_system: 'System konfigurieren und Datenbank sichern',

    company_data: 'Firmendaten',
    company_name: 'Firmenname',
    business_number: 'Firmennummer',
    email: 'E-Mail',
    phone: 'Telefon',
    address: 'Adresse',
    currency: 'Währung',
    euro: 'Euro (€)',
        save_changes: 'Änderungen speichern',
    settings_saved: 'Einstellungen wurden erfolgreich gespeichert!',
    no_expenses: 'Keine Ausgaben gefunden.',
    no_invoices: 'Keine Rechnungen gefunden.',

    database_management: 'Datenbankverwaltung',
    db_help: 'Damit keine Daten verloren gehen, laden Sie Ihre Datenbank regelmäßig herunter. Die Datei wird im JSON-Format heruntergeladen.',
    download_database: 'Datenbank herunterladen',
    save_as_json: 'Als .json speichern',
    upload_database: 'Datenbank hochladen',
    restore_from_pc: 'Daten vom PC wiederherstellen',
    reset_program: 'Programm zurücksetzen',
    reset_program_desc: 'Alle lokalen Daten löschen und das Programm in den Ausgangszustand zurücksetzen.',
    reset_program_confirm: 'Sind Sie sicher, dass Sie das Programm zurücksetzen möchten? Alle lokal gespeicherten Daten werden gelöscht.',
    confirm: 'Bestätigen',
    notification: 'Benachrichtigung',

    profile_title: 'Profil',
    profile_subtitle: 'Verwalten Sie persönliche Daten und Kontoeinstellungen.',
    details: 'Details',
    notification_prefs: 'Benachrichtigungseinstellungen',
    reminders: 'Erinnerungen',
    security: 'Sicherheit',
    google_calendar: 'Google Kalender',
    current_password: 'Aktuelles Passwort',
    enter_current_password: 'Aktuelles Passwort eingeben',
    new_password: 'Neues Passwort',
    enter_new_password: 'Neues Passwort eingeben',
    confirm_new_password: 'Neues Passwort bestätigen',
    confirm_password: 'Neues Passwort bestätigen',
    update_password: 'Passwort aktualisieren',

    update: 'Aktualisieren',

    template_general: 'Allgemeine Vorlage',

    advanced_report: 'Erweiterter Bericht',
    advanced_report_sub: 'Zeitraum wählen und Fixkosten, Ausgaben, Einnahmen und Nettogewinn ansehen.',
    from_date: 'Von Datum',
    to_date: 'Bis Datum',
    income: 'Einnahmen',
    expenses_lbl: 'Ausgaben',
    net_profit: 'Nettogewinn',
    performance_6m: 'Leistung der letzten 6 Monate',

    // --- dashboard extras ---
    fleet: 'Fahrzeugflotte',
    cars_free: 'Verfügbare Fahrzeuge',
    active_bookings: 'Aktive Buchungen',
    monthly_revenue: 'Monatsumsatz',
    total_clients: 'Kunden gesamt',
    monthly_expenses: 'Monatliche Ausgaben',

    badge_total: 'Gesamt',
    badge_ready: 'Bereit',
    badge_active: 'Aktiv',
    badge_monthly: 'Monatlich',

    recent_bookings: 'Letzte Buchungen',
    see_all: 'Alle anzeigen',
    late_return: 'Verspätete Rückgabe!',
    past_pickup: 'Verpasste Abholung',
    return_soon: 'Rückgabe bald!',
    return_soon_msg: '{client} soll {vehicle} in {mins} Min. zurückbringen.',
    late_return_msg: '{client} ist verspätet mit der Rückgabe von {vehicle}.',
    pickup_soon: 'Abholung bald!',
    pickup_soon_msg: '{client} holt {vehicle} in {mins} Min. ab.',
    past_pickup_msg: '{client} sollte {vehicle} jetzt abholen.',
    maintenance_scheduled: 'Geplante Wartung',
    maintenance_scheduled_msg: '{vehicle} hat Wartung am {date} ({desc})',
    insurance_expiring_soon: 'Versicherung läuft bald ab',
    insurance_expiring_soon_msg: '{vehicle} ({plate}) läuft in {days} Tagen ab.',
    expiry_soon: 'Läuft bald ab',
    expiry_soon_msg: '{vehicle} läuft am {date} ab',

    was_planned_pickup: 'sollte abholen',

    alerts: 'Warnungen',
    no_alerts_title: 'Keine Warnungen aktuell.',
    no_alerts_subtitle: 'Alles ist in Ordnung!',
    go_to_details: 'Zu den Details',

    apply_filters: 'Anwenden',
    // statuses
    active: 'Aktiv',
    pending: 'Ausstehend',
    completed: 'Abgeschlossen',
    cancelled: 'Storniert',
    rented: 'Vermietet',
    available: 'Verfügbar',
    maintenance_status: 'In Wartung',
    scheduled: 'Geplant',
    in_progress: 'In Bearbeitung',
    choose_vehicle: 'Fahrzeug auswählen...',
    defect_service_description: 'Beschreibung des Defekts/Services',
    preparing: 'Wird vorbereitet...',
    paid: 'Bezahlt',


    welcome: 'Willkommen, {name}! 👋',
    today_overview: 'Überblick über dein Geschäft für heute.',
    add_booking: 'Buchung hinzufügen',
    cancel: 'Abbrechen',
    status_partially_paid: 'Teilweise bezahlt',
    status_paid: 'Bezahlt',
    status_cancelled: 'Storniert',
    status_completed: 'Abgeschlossen',
    status_active: 'Aktiv',
    payment_status_label: 'Zahlungsstatus',
    booking_status_label: 'Buchungsstatus',
    total_label: 'Summe:',
    extras_label: 'Extras',
    rent_price: 'Mietpreis',
    price_summary: 'Preisübersicht',
    extra_full_insurance: 'Vollkasko (15€/Tag)',
    extra_child_seat: 'Kindersitz (10€/Tag)',
    extra_gps: 'GPS Navigation (5€/Tag)',
    extras_section: 'Extras',
    enable_additional_driver: 'Zusatzfahrer aktivieren',
    additional_driver_section: 'Zusatzfahrer',
    total_overall: 'gesamt',
    bookings_word: 'Buchungen',
    no_search_results: 'Für diese Suche wurden keine Daten gefunden.',
    details_placeholder: 'Zusätzliche Details hier eingeben...',
    booked_for_dates: 'Für diese Daten gebucht',
    car_tab_reservations: 'Reservierungen',
    car_tab_services: 'Services',
    car_tab_expenses: 'Ausgaben',
    car_tab_pricing_seasons: 'Preissaisons',
    car_details_title: 'Fahrzeugdetails',
    car_images: 'Fahrzeugbilder',
    car_name: 'Fahrzeugname',
    rental_price: 'Mietpreis',
    license_plate_label: 'Kennzeichen',
    identification_number: 'Identifikationsnummer',
    registration_date: 'Zulassungsdatum',
    number_of_seats: 'Anzahl der Sitze',
    number_of_doors: 'Anzahl der Türen',
    fuel_type: 'Kraftstoffart',
    transmission_type: 'Getriebeart',
    has_air_conditioning: 'Klimaanlage vorhanden',
    show_in_website: 'Auf Website anzeigen',
    optional_details: 'Zusätzliche Details',
    green_card: 'Grüne Karte',
    add_green_card: 'Grüne Karte hinzufügen',
    add_tpl: 'Haftpflicht hinzufügen',
    car_tab_details: 'Details',
    car_tab_tpl: 'TPL',
    car_no_reservations: 'Dieses Fahrzeug hat keine Reservierungen.',
    car_no_services: 'Dieses Fahrzeug hat keine Services.',
    car_no_expenses: 'Dieses Fahrzeug hat keine Ausgaben.',
    car_pricing_empty: 'Keine Preissaisons für dieses Fahrzeug.',
    car_tpl_empty: 'Keine TPL-Daten für dieses Fahrzeug.',
    tpl_amount: 'Betrag',
    renewal_date: 'Verlängerungsdatum',
    expiry_date: 'Ablaufdatum',
    select_car: 'Fahrzeug auswählen',
    no_car_selected: 'Kein Fahrzeug ausgewählt',
    view_car: 'Fahrzeug anzeigen',
    add_car_images: 'Bilder hinzufügen',
    registration_type_tpl: 'TPL',
    category: 'Kategorie',
    choose_client: 'Kunden auswählen',
    new_client: 'Neuer Kunde',
    existing_client: 'Bestehender Kunde',
    client_section: 'Kunde',
    choose_file: 'Auswählen',
    click_to_upload: 'Klicken zum Hochladen',
    enter_notes_here: 'Notizen hier eingeben...',
    blacklist_client: 'Diesen Kunden sperren',
    id_back: 'Ausweis (Rückseite)',
    id_front: 'Ausweis (Vorderseite)',
    passport_number: 'Reisepassnummer',
    license_number: 'Führerscheinnummer',
    personal_id_number: 'Ausweisnummer (Personalausweis)',
    documents_section: 'Dokumente',
    client_avatar: 'Kundenfoto (Avatar)',
    address_label: 'Adresse',
    birthdate: 'Geburtsdatum',
    optional: 'Optional',
    secondary_phone: 'Zusätzliche Telefonnummer',
    phone_number: 'Telefonnummer',
    enter_full_name: 'Vollständigen Namen eingeben',
    full_name_required: 'Vollständiger Name*',
    client_profile_section: 'Kundenprofil',
    flight_number: 'Flugnummer',
    pickup_fee: 'Abholgebühr',
    delivery_fee: 'Liefergebühr',
    notes: 'Notizen',
    deposit: 'Kaution',
    total_rent_price: 'Gesamtmietpreis',
    daily_price: 'Tagespreis',
    pickup_location: 'Abholort',
    return_location: 'Rückgabeort',
    select_car: 'Auto auswählen...',
    car_required: 'Auto*',
    end_date_time: 'Enddatum & Uhrzeit*',
    start_date_time: 'Startdatum & Uhrzeit*',
    booking_details_section: 'Buchungsdetails',
    edit_payment: 'Zahlung bearbeiten',
    add_payment: 'Zahlung hinzufügen',
    invoice_number_col: 'RECHNUNG #',
    no_email: 'Keine E-Mail',
    no_phone: 'Keine Telefonnummer',
    unpaid_pending: 'Unbezahlt (Ausstehend)',
    invoice_total: 'Gesamtbetrag',
    invoice_date: 'Rechnungsdatum',
    choose_client_required: 'Kunden auswählen*',
    status_failed: 'Fehlgeschlagen',
    booking_code: 'Buchungscode',
    expense_desc_ph: 'Beschreiben Sie die Ausgabe hier...',
    expense_desc_label: 'Beschreibung / Details',
    expense_general_no_car: 'Allgemein / Kein Fahrzeug',
    expense_vehicle_link: 'Mit Fahrzeug verknüpft (optional)',
    expense_category_label: 'Ausgabenkategorie*',
    edit_contract: 'Vertrag bearbeiten',
    add_contract: 'Vertrag/Vorlage erstellen',
    edit_invoice: 'Rechnung bearbeiten',
    add_invoice: 'Rechnung hinzufügen',
    edit_expense: 'Ausgabe bearbeiten',
    add_expense: 'Ausgabe hinzufügen',
    edit_booking: 'Buchung bearbeiten',
    edit_client: 'Kunden bearbeiten',
    add_client: 'Kunden hinzufügen',

    pickups_today: 'Abholungen heute',
    returns_today: 'Rückgaben heute',
    no_pickups_today: 'Keine Abholungen heute',
    no_returns_today: 'Keine Rückgaben heute',

    banner_returns_today_title: 'Rückgaben heute',
    banner_returns_today_msg: 'Du hast {count} Auto(s) zur Rückgabe heute: {vehicles}',
    banner_ins_expired_title: 'Abgelaufene Versicherungen',
    expired_insurances_title: 'Abgelaufene Versicherungen',
    expired_insurances_msg: 'Sie haben {count} abgelaufene Versicherung(en). Öffnen Sie "Versicherung" für Details.',
    banner_ins_expired_msg: 'Du hast {count} abgelaufene Versicherung(en). Öffne „Versicherungen“ für Details.',
    banner_ins_week_title: 'Versicherungen, die diese Woche ablaufen',
    banner_ins_week_msg: 'Du hast {count} Versicherung(en), die innerhalb von 7 Tagen ablaufen.',
alerts: 'Warnungen',
insurance_expired: 'Versicherung abgelaufen!',
client_vehicle: 'Kunde & Fahrzeug',
expired_on: 'abgelaufen am',

company_data: 'Firmendaten',
system_config: 'System konfigurieren und Datenbank speichern',

details: 'Details',
notification_preferences: 'Benachrichtigungseinstellungen',

services_faults: 'Service und Defekte',
vehicle: 'Fahrzeug',
description: 'Beschreibung',
date: 'Datum',
cost: 'Kosten',
status: 'Status',
expired: 'Abgelaufen',

edit_insurance: 'Versicherung bearbeiten',
fill_fields_below: 'Füllen Sie die folgenden Felder aus und speichern Sie die Änderungen.',

insurance_company: 'Versicherungsgesellschaft',
policy_number: 'Policennummer',
expiry_date: 'Ablaufdatum',

client: 'Kunde',
search_contract: 'Vertrag suchen...',
revenue: 'Einnahmen',
profit: 'Gewinn',

last_6_months: 'Leistung der letzten 6 Monate',
last_expenses: 'Letzte Ausgaben',
last_invoices: 'Letzte Rechnungen',
    manage_cars_subtitle: 'Alle Fahrzeuge verwalten und überwachen',

    manage_bookings_subtitle: 'Aktive und vergangene Buchungen verwalten',
    contacts: 'Kontakte',
    bookings_expenses: 'Buchungen / Ausgaben',
    search_booking_placeholder: 'Buchung suchen...',
    print_rental_contract: 'Mietvertrag drucken',
    transmission: 'Getriebe',
    add_car: 'Auto hinzufügen',
    fill_fields: 'Füllen Sie die Felder unten aus und speichern Sie die Änderungen.',
    brand: 'Marke',
    model: 'Modell',
    year: 'Baujahr',
    plate: 'Kennzeichen',
    color: 'Farbe',
    price_per_day: 'Preis/Tag (€)',
    technical_specs: 'Technische Daten',
    fuel: 'Kraftstoff',
    petrol: 'Benzin',
    seats: 'Sitze',
    doors: 'Türen',
    engine_size: 'Motorgröße',
    main_photo: 'Hauptfoto',
    click_upload: 'Zum Hochladen klicken',
    choose: 'Auswählen',
    file_uploaded: 'Datei hochgeladen',
    select_time: 'Uhrzeit wählen…',
    search_client_placeholder: 'Kunde suchen...',
    no_client_found: 'Kein Kunde gefunden.',

    manual: 'Manuell',
    day: 'Tag',
    week: 'Woche',
    month: 'Monat',
    search_payment_placeholder: 'Zahlung suchen...',
    other: 'Sonstiges',
    paypal: 'PayPal',
    bank_transfer: 'Banküberweisung',
    bank_card: 'Bankkarte',
    cash: 'Bar',
    date_col: 'Datum',
    method_col: 'Methode',
    amount_col: 'Betrag',
    booking_col: 'Buchung',
    payment_method_label: 'Zahlungsmethode',
    search_expense_placeholder: 'Ausgabe suchen...',
    payments_subtitle: 'Transaktionsverlauf',
    expenses_subtitle: 'Betriebsausgaben verwalten',
    invoices_subtitle: 'Erstellte Rechnungen für Kunden',
    insurance_subtitle: 'Fahrzeugversicherungen und Ablaufdaten',
    edit_car: 'Auto bearbeiten',
    diesel: 'Diesel',
    electric: 'Elektrisch',
    hybrid: 'Hybrid',
    has_ac: 'Klimaanlage',
    show_on_web: 'Auf der Webseite anzeigen',
    additional_details: 'Zusätzliche Details',
    upload_hint: 'PNG / JPG / URL',

    blacklisted: 'Gesperrt',
  
    car_added: 'Auto hinzugefügt',
    car_updated: 'Auto aktualisiert',
    car_archived: 'Auto archiviert',
    car_deleted: 'Auto gelöscht',
    client_updated: 'Kunde aktualisiert',
    expense_added: 'Ausgabe hinzugefügt',
    expense_updated: 'Ausgabe aktualisiert',
    expense_deleted: 'Ausgabe gelöscht',
    payment_added: 'Zahlung hinzugefügt',
    payment_updated: 'Zahlung aktualisiert',
    payment_deleted: 'Zahlung gelöscht',
    booking_archived: 'Buchung archiviert',
    booking_deleted: 'Buchung gelöscht',
    booking_added: 'Buchung hinzugefügt',
    booking_updated: 'Buchung aktualisiert',
    daily_summary: 'Tägliche Zusammenfassung',
    daily_summary_desc: 'Erhalten Sie eine tägliche Zusammenfassung der kommenden Buchungen',
    time: 'Zeit',
    car_return_notification: 'Benachrichtigung zur Fahrzeugrückgabe',
    car_return_desc: 'Benachrichtigung vor der Rückgabezeit erhalten',
    minutes_before: 'Minuten vorher',
    car_pickup_notification: 'Benachrichtigung zur Fahrzeugabholung',
    car_pickup_desc: 'Benachrichtigung vor geplanter Abholung erhalten',
    car_registration_reminder: 'Erinnerung an Fahrzeugregistrierung',
    car_registration_desc: 'Erinnerung erhalten wenn die Registrierung bald abläuft',
    days_before: 'Tage vorher',
    save: 'Speichern',
    save_preferences: 'Einstellungen speichern',
    password_incorrect: 'Aktuelles Passwort ist falsch!',
    passwords_not_match: 'Neue Passwörter stimmen nicht überein!',
    password_min_length: 'Passwort muss mindestens 6 Zeichen haben.',
    password_updated_success: 'Passwort erfolgreich aktualisiert!',
    details_saved_success: 'Details wurden erfolgreich gespeichert!',
    google_calendar_not_connected: 'Google Kalender ist noch nicht verbunden',
    connect_google_calendar: 'Mit Google Kalender verbinden',
    google_calendar_desc: 'Wenn aktiviert, werden neue Buchungen automatisch zu Ihrem Google Kalender hinzugefügt.',
},

  it: {
    dashboard: 'Pannello',
    bookings: 'Prenotazioni',
    cars: 'Auto',
    clients: 'Clienti',
    client_details_title: 'Dettagli cliente',
    client_tab_details: 'Dettagli',
    client_tab_bookings: 'Prenotazioni',
    client_tab_payments: 'Pagamenti',
    client_bookings_empty: 'Questo cliente non ha prenotazioni.',
    client_payments_empty: 'Questo cliente non ha pagamenti.',
    start_date_label: 'Data di inizio',
    end_date_label: 'Data di fine',
    no_data_found: 'Nessun dato trovato per questa ricerca.',
    view_client: 'Visualizza cliente',
    payments: 'Pagamenti',
    expenses: 'Spese',
    invoices: 'Fatture',
    contracts: 'Contratti',
    rental_contract_standard_template: 'Contratto di noleggio (Template standard)',
    print_contract: 'Stampa contratto',
    client_usage: 'Cliente / Uso',
    title_col: 'Titolo',
    id_number: 'ID / Numero',
    contracts_subtitle: 'Gestisci e stampa i contratti di noleggio',
    insurance: 'Assicurazione',
    maintenance: 'Manutenzione',
    staff: 'Staff',
    locations: 'Sedi',
    promotions: 'Promozioni',
    inventory: 'Inventario',
    settings: 'Impostazioni',
    profile: 'Profilo',
    reports: 'Report',
    reports_financial_summary: 'Panoramica e analisi delle prestazioni finanziarie',
    menu_main_section: 'Principale',
    menu_finance_analytics_section: 'Finanza e analisi',
    menu_operations_section: 'Operazioni',
    booking_list: 'Lista',
    booking_payments_not_finished: 'Pagamenti non completati',
    booking_waitlist: "Lista d'attesa",
    booking_operations: 'Operazioni',
    reservations_with_incomplete_payments: 'Prenotazioni con pagamenti incompleti',
    back_to_list: 'Torna alla lista',
    total_unpaid: 'Totale non pagato',
    payments_overdue: 'Pagamenti scaduti',
    payments_incoming: 'Pagamenti in arrivo',
    remaining_amount: 'Importo residuo',
    finish: 'Completa',
    no_waitlist_records: "Nessuna prenotazione trovata nella lista d'attesa",
    no_waitlist_subtitle: "Aggiungi prenotazioni con stato lista d'attesa per vederle qui.",
    pickup_delivery_to_client: 'Consegna al cliente',
    pickup_return_from_client: 'Ritiro dal cliente',
    month_view: 'mese',
    month_name_0: 'Gennaio',
    month_name_1: 'Febbraio',
    month_name_2: 'Marzo',
    month_name_3: 'Aprile',
    month_name_4: 'Maggio',
    month_name_5: 'Giugno',
    month_name_6: 'Luglio',
    month_name_7: 'Agosto',
    month_name_8: 'Settembre',
    month_name_9: 'Ottobre',
    month_name_10: 'Novembre',
    month_name_11: 'Dicembre',
    weekday_sun: 'Dom',
    weekday_mon: 'Lun',
    weekday_tue: 'Mar',
    weekday_wed: 'Mer',
    weekday_thu: 'Gio',
    weekday_fri: 'Ven',
    weekday_sat: 'Sab',
    menu_system_section: 'Sistema',
    recent_invoices: 'Fatture recenti',
    recent_expenses: 'Spese recenti',
    performance_last_6_months: 'Performance degli ultimi 6 mesi',
    status_active_signed: 'Attivo (Firmato)',
    status_draft_template: 'Bozza (Non finito / Template)',
    content_tip_text: 'Scrivi i punti dell’accordo o le condizioni generali. Dopo il salvataggio, puoi generarlo facilmente per la stampa.',
    content_tip_title: 'Suggerimento contenuti',
    contract_content_ph: 'Scrivi qui il testo completo del contratto...',
    contract_content_label: 'Contenuto contratto / Template',
    client_optional_hint: 'Collegare un cliente è opzionale. Lascia vuoto per salvarlo come template generale.',
    choose_client_optional: 'Seleziona cliente (lascia vuoto se template)',
    created_date: 'Data di creazione',
    contract_title_ph: 'es. Contratto di noleggio standard',
    contract_title_label: 'Titolo del contratto*',
    expense_cat_other: 'Altro',
    expense_cat_office_rent: 'Affitto ufficio',
    expense_cat_salary: 'Stipendio',
    expense_cat_insurance: 'Assicurazione',
    expense_cat_parts: 'Ricambi',
    expense_cat_service: 'Servizio e manutenzione',
    expense_cat_fuel: 'Carburante',

    add: 'Aggiungi',
    edit: 'Modifica',
    delete: 'Elimina',
    search: 'Cerca',

    filter: 'Filtro',
    select: 'Seleziona',
    reset: 'Reimposta',
    close: 'Chiudi',
    bulk: 'Bulk',
    view: 'Vedi',
    // --- tables / filters / settings / profile / reports ---
    id: 'ID',
    client_col: 'Cliente',
    vehicle_col: 'Veicolo',
    dates_col: 'Date',
    total_col: 'Totale',
    status_col: 'Stato',
    actions_col: 'Azioni',

    user_label: 'Utente',
    date_range: 'Intervallo date',
    to_word: 'a',
    automatic: 'Automatico',
    configure_system: 'Configura il sistema e salva il database',

    company_data: 'Dati aziendali',
    company_name: 'Nome azienda',
    business_number: 'Numero aziendale',
    email: 'Email',
    phone: 'Telefono',
    address: 'Indirizzo',
    currency: 'Valuta',
    euro: 'Euro (€)',
        save_changes: 'Salva modifiche',
    settings_saved: 'Impostazioni salvate con successo!',
    no_expenses: 'Nessuna spesa trovata.',
    no_invoices: 'Nessuna fattura trovata.',

    database_management: 'Gestione database',
    db_help: 'Per evitare la perdita di dati, scarica regolarmente il database. Il file viene scaricato in formato JSON.',
    download_database: 'Scarica database',
    save_as_json: 'Salva come .json',
    upload_database: 'Carica database',
    restore_from_pc: 'Ripristina dal PC',
    reset_program: 'Reimposta Programma',
    reset_program_desc: 'Elimina tutti i dati locali e riporta il programma allo stato iniziale.',
    reset_program_confirm: 'Sei sicuro di voler reimpostare il programma? Tutti i dati salvati localmente verranno eliminati.',
    confirm: 'Conferma',
    notification: 'Notifica',

    profile_title: 'Profilo',
    profile_subtitle: "Gestisci i dati personali e le impostazioni dell\'account.",
    details: 'Dettagli',
    notification_prefs: 'Preferenze notifiche',
    reminders: 'Promemoria',
    security: 'Sicurezza',
    google_calendar: 'Google Calendar',
    current_password: 'Password attuale',
    enter_current_password: 'Inserisci la password attuale',
    new_password: 'Nuova password',
    enter_new_password: 'Inserisci la nuova password',
    confirm_new_password: 'Conferma nuova password',
    confirm_password: 'Conferma la nuova password',
    update_password: 'Aggiorna password',

    update: 'Aggiorna',

    template_general: 'Template generale',

    advanced_report: 'Report avanzato',
    advanced_report_sub: 'Scegli il periodo e visualizza costi fissi, spese, ricavi e utile netto.',
    from_date: 'Da data',
    to_date: 'A data',
    income: 'Entrate',
    expenses_lbl: 'Spese',
    net_profit: 'Utile netto',
    performance_6m: 'Performance ultimi 6 mesi',

    // --- dashboard extras ---
    fleet: 'Flotta veicoli',
    cars_free: 'Auto disponibili',
    active_bookings: 'Prenotazioni attive',
    monthly_revenue: 'Ricavi mensili',
    total_clients: 'Clienti totali',
    monthly_expenses: 'Spese mensili',

    badge_total: 'Totale',
    badge_ready: 'Pronto',
    badge_active: 'Attivo',
    badge_monthly: 'Mensile',

    recent_bookings: 'Prenotazioni recenti',
    see_all: 'Vedi tutto',
    late_return: 'Ritardo nella restituzione!',
    past_pickup: 'Ritiro mancato',
    return_soon: 'Riconsegna imminente!',
    return_soon_msg: '{client} dovrebbe riconsegnare {vehicle} tra {mins} min.',
    late_return_msg: '{client} è in ritardo nella restituzione di {vehicle}.',
    pickup_soon: 'Ritiro imminente!',
    pickup_soon_msg: '{client} ritirerà {vehicle} tra {mins} min.',
    past_pickup_msg: '{client} era programmato per ritirare {vehicle} ora.',
    maintenance_scheduled: 'Manutenzione programmata',
    maintenance_scheduled_msg: '{vehicle} ha manutenzione il {date} ({desc})',
    insurance_expiring_soon: 'Assicurazione in scadenza',
    insurance_expiring_soon_msg: '{vehicle} ({plate}) scade tra {days} giorni.',
    expiry_soon: 'In scadenza',
    expiry_soon_msg: '{vehicle} scade il {date}',

    was_planned_pickup: 'era programmato per ritirare',

    alerts: 'Avvisi',
    no_alerts_title: 'Nessun avviso al momento.',
    no_alerts_subtitle: 'Tutto ok!',
    go_to_details: 'Vai ai dettagli',

    apply_filters: 'Applica',
    // statuses
    active: 'Attivo',
    pending: 'In attesa',
    completed: 'Completato',
    cancelled: 'Annullato',
    rented: 'Noleggiato',
    available: 'Disponibile',
    maintenance_status: 'In manutenzione',
    scheduled: 'Programmato',
    in_progress: 'In corso',
    choose_vehicle: 'Seleziona veicolo...',
    defect_service_description: 'Descrizione del guasto/servizio',
    preparing: 'Preparazione in corso...',
    paid: 'Pagato',


    welcome: 'Benvenuto, {name}! 👋',
    today_overview: 'Panoramica della tua attività per oggi.',
    add_booking: 'Aggiungi prenotazione',
    cancel: 'Annulla',
    status_partially_paid: 'Parzialmente pagato',
    status_paid: 'Pagato',
    status_cancelled: 'Annullato',
    status_completed: 'Completato',
    status_active: 'Attivo',
    payment_status_label: 'Stato pagamento',
    booking_status_label: 'Stato prenotazione',
    total_label: 'Totale:',
    extras_label: 'Extra',
    rent_price: 'Prezzo noleggio',
    price_summary: 'Riepilogo prezzi',
    extra_full_insurance: 'Assicurazione completa (15€/giorno)',
    extra_child_seat: 'Seggiolino (10€/giorno)',
    extra_gps: 'Navigazione GPS (5€/giorno)',
    extras_section: 'Extra',
    enable_additional_driver: 'Abilita conducente aggiuntivo',
    additional_driver_section: 'Conducente aggiuntivo',
    total_overall: 'totale',
    bookings_word: 'prenotazioni',
    no_search_results: 'Nessun dato trovato per questa ricerca.',
    details_placeholder: 'Scrivi qui dettagli aggiuntivi...',
    booked_for_dates: 'Prenotata per queste date',
    car_tab_reservations: 'Prenotazioni',
    car_tab_services: 'Servizi',
    car_tab_expenses: 'Spese',
    car_tab_pricing_seasons: 'Stagioni tariffarie',
    car_details_title: 'Dettagli auto',
    car_images: 'Immagini auto',
    car_name: 'Nome auto',
    rental_price: 'Prezzo noleggio',
    license_plate_label: 'Targa',
    identification_number: 'Numero identificativo',
    registration_date: 'Data di immatricolazione',
    number_of_seats: 'Numero di posti',
    number_of_doors: 'Numero di porte',
    fuel_type: 'Tipo di carburante',
    transmission_type: 'Tipo di trasmissione',
    has_air_conditioning: 'Aria condizionata',
    show_in_website: 'Mostra sul sito',
    optional_details: 'Dettagli opzionali',
    green_card: 'Carta verde',
    add_green_card: 'Aggiungi carta verde',
    add_tpl: 'Aggiungi RCA',
    car_tab_details: 'Dettagli',
    car_tab_tpl: 'TPL',
    car_no_reservations: 'Questa auto non ha prenotazioni.',
    car_no_services: 'Questa auto non ha servizi.',
    car_no_expenses: 'Questa auto non ha spese.',
    car_pricing_empty: 'Nessuna stagione tariffaria per questa auto.',
    car_tpl_empty: 'Nessun record TPL per questa auto.',
    tpl_amount: 'Importo',
    renewal_date: 'Data rinnovo',
    expiry_date: 'Data scadenza',
    select_car: 'Seleziona auto',
    no_car_selected: 'Nessuna auto selezionata',
    view_car: 'Visualizza auto',
    add_car_images: 'Aggiungi immagini',
    registration_type_tpl: 'TPL',
    category: 'Categoria',
    choose_client: 'Seleziona cliente',
    new_client: 'Nuovo cliente',
    existing_client: 'Cliente esistente',
    client_section: 'Cliente',
    choose_file: 'Scegli',
    click_to_upload: 'Clicca per caricare',
    enter_notes_here: 'Scrivi note qui...',
    blacklist_client: 'Metti questo cliente in blacklist',
    id_back: 'ID (retro)',
    id_front: 'ID (fronte)',
    passport_number: 'Numero passaporto',
    license_number: 'Numero patente',
    personal_id_number: 'Numero ID (carta d\'identità)',
    documents_section: 'Documenti',
    client_avatar: 'Foto cliente (avatar)',
    address_label: 'Indirizzo',
    birthdate: 'Data di nascita',
    optional: 'Opzionale',
    secondary_phone: 'Telefono aggiuntivo',
    phone_number: 'Numero di telefono',
    enter_full_name: 'Inserisci nome completo',
    full_name_required: 'Nome completo*',
    client_profile_section: 'Profilo cliente',
    flight_number: 'Numero volo',
    pickup_fee: 'Costo ritiro',
    delivery_fee: 'Costo consegna',
    notes: 'Note',
    deposit: 'Deposito',
    total_rent_price: 'Prezzo totale noleggio',
    daily_price: 'Prezzo giornaliero',
    pickup_location: 'Luogo di ritiro',
    return_location: 'Luogo di consegna',
    select_car: 'Seleziona auto...',
    car_required: 'Auto*',
    end_date_time: 'Data e ora di fine*',
    start_date_time: 'Data e ora di inizio*',
    booking_details_section: 'Dettagli prenotazione',
    edit_payment: 'Modifica pagamento',
    add_payment: 'Aggiungi pagamento',
    invoice_number_col: 'FATTURA #',
    no_email: 'Nessuna email',
    no_phone: 'Nessun telefono',
    unpaid_pending: 'Non pagata (In attesa)',
    invoice_total: 'Importo totale',
    invoice_date: 'Data fattura',
    choose_client_required: 'Seleziona cliente*',
    status_failed: 'Fallito',
    booking_code: 'Codice prenotazione',
    expense_desc_ph: 'Descrivi la spesa qui...',
    expense_desc_label: 'Descrizione / Dettagli',
    expense_general_no_car: 'Generale / Nessun veicolo',
    expense_vehicle_link: 'Collegato al veicolo (opzionale)',
    expense_category_label: 'Categoria spesa*',
    edit_contract: 'Modifica contratto',
    add_contract: 'Crea contratto/template',
    edit_invoice: 'Modifica fattura',
    add_invoice: 'Aggiungi fattura',
    edit_expense: 'Modifica spesa',
    add_expense: 'Aggiungi spesa',
    edit_booking: 'Modifica prenotazione',
    edit_client: 'Modifica cliente',
    add_client: 'Aggiungi cliente',

    pickups_today: 'Ritiri oggi',
    returns_today: 'Riconsegne oggi',
    no_pickups_today: 'Nessun ritiro oggi',
    no_returns_today: 'Nessuna riconsegna oggi',

    banner_returns_today_title: 'Riconsegne oggi',
    banner_returns_today_msg: 'Hai {count} auto da riconsegnare oggi: {vehicles}',
    banner_ins_expired_title: 'Assicurazioni scadute',
    banner_ins_expired_msg: 'Hai {count} assicurazione(i) scaduta(e). Apri “Assicurazioni” per i dettagli.',
    banner_ins_week_title: 'Assicurazioni in scadenza questa settimana',
    banner_ins_week_msg: 'Hai {count} assicurazione(i) in scadenza entro 7 giorni.',
alerts: 'Avvisi',
insurance_expired: 'Assicurazione scaduta!',
client_vehicle: 'Cliente e Veicolo',
expired_on: 'scaduto il',

company_data: 'Dati aziendali',
system_config: 'Configura il sistema e salva il database',

details: 'Dettagli',
notification_preferences: 'Preferenze notifiche',

services_faults: 'Servizi e guasti',
vehicle: 'Veicolo',
description: 'Descrizione',
date: 'Data',
cost: 'Costo',
status: 'Stato',
expired: 'Scaduto',

edit_insurance: 'Modifica assicurazione',
fill_fields_below: 'Compila i campi sotto e salva le modifiche.',

insurance_company: 'Compagnia assicurativa',
policy_number: 'Numero polizza',
expiry_date: 'Data di scadenza',

client: 'Cliente',
search_contract: 'Cerca contratto...',
revenue: 'Entrate',
profit: 'Profitto',

last_6_months: 'Performance degli ultimi 6 mesi',
last_expenses: 'Ultime spese',
last_invoices: 'Ultime fatture',
    manage_cars_subtitle: 'Gestisci e monitora tutte le auto',

    manage_bookings_subtitle: 'Gestisci le prenotazioni attive e passate',
    contacts: 'Contatti',
    bookings_expenses: 'Prenotazioni / Spese',
    search_booking_placeholder: 'Cerca prenotazione...',
    print_rental_contract: 'Stampa contratto di noleggio',
    transmission: 'Cambio',
    add_car: 'Aggiungi auto',
    fill_fields: 'Compila i campi sotto e salva le modifiche.',
    brand: 'Marca',
    model: 'Modello',
    year: 'Anno',
    plate: 'Targa',
    color: 'Colore',
    price_per_day: 'Prezzo/Giorno (€)',
    technical_specs: 'Specifiche tecniche',
    fuel: 'Carburante',
    petrol: 'Benzina',
    seats: 'Posti',
    doors: 'Porte',
    engine_size: 'Cilindrata',
    main_photo: 'Foto principale',
    click_upload: 'Clicca per caricare',
    choose: 'Scegli',
    file_uploaded: 'File caricato',
    select_time: 'Seleziona orario…',
    search_client_placeholder: 'Cerca cliente...',
    no_client_found: 'Nessun cliente trovato.',

    manual: 'Manuale',
    day: 'giorno',
    week: 'settimana',
    month: 'mese',
    search_payment_placeholder: 'Cerca pagamento...',
    other: 'Altro',
    paypal: 'PayPal',
    bank_transfer: 'Bonifico bancario',
    bank_card: 'Carta bancaria',
    cash: 'Contanti',
    date_col: 'Data',
    method_col: 'Metodo',
    amount_col: 'Importo',
    booking_col: 'Prenotazione',
    payment_method_label: 'Metodo di pagamento',
    search_expense_placeholder: 'Cerca spesa...',
    payments_subtitle: 'Storico transazioni',
    expenses_subtitle: 'Gestisci spese e costi operativi',
    invoices_subtitle: 'Fatture create per i clienti',
    insurance_subtitle: 'Assicurazioni veicoli e scadenze',
    edit_car: 'Modifica auto',
    diesel: 'Diesel',
    electric: 'Elettrico',
    hybrid: 'Ibrido',
    has_ac: 'Aria condizionata',
    show_on_web: 'Mostra sul sito',
    additional_details: 'Dettagli aggiuntivi',
    upload_hint: 'PNG / JPG / URL',

    blacklisted: 'Bloccato',
  
    car_added: 'Auto aggiunta',
    car_updated: 'Auto aggiornata',
    car_archived: 'Auto archiviata',
    car_deleted: 'Auto eliminata',
    client_updated: 'Cliente aggiornato',
    expense_added: 'Spesa aggiunta',
    expense_updated: 'Spesa aggiornata',
    expense_deleted: 'Spesa eliminata',
    payment_added: 'Pagamento aggiunto',
    payment_updated: 'Pagamento aggiornato',
    payment_deleted: 'Pagamento eliminato',
    booking_archived: 'Prenotazione archiviata',
    booking_deleted: 'Prenotazione eliminata',
    booking_added: 'Prenotazione aggiunta',
    booking_updated: 'Prenotazione aggiornata',
    daily_summary: 'Riepilogo giornaliero',
    daily_summary_desc: 'Ricevi un riepilogo giornaliero delle prenotazioni imminenti',
    time: 'Orario',
    car_return_notification: 'Notifica restituzione auto',
    car_return_desc: 'Ricevi una notifica prima dell’orario di restituzione',
    minutes_before: 'Minuti prima',
    car_pickup_notification: 'Notifica ritiro auto',
    car_pickup_desc: 'Ricevi una notifica prima del ritiro programmato',
    car_registration_reminder: 'Promemoria registrazione auto',
    car_registration_desc: 'Ricevi promemoria quando la registrazione sta per scadere',
    days_before: 'Giorni prima',
    save: 'Salva',
    save_preferences: 'Salva preferenze',
    password_incorrect: 'La password attuale non è corretta!',
    passwords_not_match: 'Le nuove password non corrispondono!',
    password_min_length: 'La password deve avere almeno 6 caratteri.',
    password_updated_success: 'Password aggiornata con successo!',
    details_saved_success: 'Dettagli salvati con successo!',
    google_calendar_not_connected: 'Google Calendar non è ancora collegato',
    connect_google_calendar: 'Connetti a Google Calendar',
    google_calendar_desc: 'Quando attivato, le nuove prenotazioni verranno aggiunte automaticamente al tuo Google Calendar.',
},

  fr: {
    dashboard: 'Tableau de bord',
    bookings: 'Réservations',
    cars: 'Voitures',
    clients: 'Clients',
    client_details_title: 'Détails du client',
    client_tab_details: 'Détails',
    client_tab_bookings: 'Réservations',
    client_tab_payments: 'Paiements',
    client_bookings_empty: "Ce client n'a aucune réservation.",
    client_payments_empty: "Ce client n'a aucun paiement.",
    start_date_label: 'Date de début',
    end_date_label: 'Date de fin',
    no_data_found: 'Aucune donnée trouvée pour cette recherche.',
    view_client: 'Voir le client',
    payments: 'Paiements',
    expenses: 'Dépenses',
    invoices: 'Factures',
    contracts: 'Contrats',
    rental_contract_standard_template: 'Contrat de location (Modèle standard)',
    print_contract: 'Imprimer le contrat',
    client_usage: 'Client / Utilisation',
    title_col: 'Titre',
    id_number: 'ID / Numéro',
    contracts_subtitle: 'Gérer et imprimer les contrats de location',
    insurance: 'Assurance',
    maintenance: 'Maintenance',
    staff: 'Personnel',
    locations: 'Emplacements',
    promotions: 'Promotions',
    inventory: 'Inventaire',
    settings: 'Paramètres',
    profile: 'Profil',
    reports: 'Rapports',
    reports_financial_summary: 'Aperçu et analyse des performances financières',
    menu_main_section: 'Principal',
    menu_finance_analytics_section: 'Finance et analyses',
    menu_operations_section: 'Opérations',
    booking_list: 'Liste',
    booking_payments_not_finished: 'Paiements non terminés',
    booking_waitlist: "Liste d'attente",
    booking_operations: 'Opérations',
    reservations_with_incomplete_payments: 'Réservations avec paiements incomplets',
    back_to_list: 'Retour à la liste',
    total_unpaid: 'Total impayé',
    payments_overdue: 'Paiements en retard',
    payments_incoming: 'Paiements à venir',
    remaining_amount: 'Montant restant',
    finish: 'Terminer',
    no_waitlist_records: "Aucune réservation trouvée dans la liste d'attente",
    no_waitlist_subtitle: "Ajoutez des réservations avec le statut liste d'attente pour les voir ici.",
    pickup_delivery_to_client: 'Livraison au client',
    pickup_return_from_client: 'Retour du client',
    month_view: 'mois',
    month_name_0: 'Janvier',
    month_name_1: 'Février',
    month_name_2: 'Mars',
    month_name_3: 'Avril',
    month_name_4: 'Mai',
    month_name_5: 'Juin',
    month_name_6: 'Juillet',
    month_name_7: 'Août',
    month_name_8: 'Septembre',
    month_name_9: 'Octobre',
    month_name_10: 'Novembre',
    month_name_11: 'Décembre',
    weekday_sun: 'Dim',
    weekday_mon: 'Lun',
    weekday_tue: 'Mar',
    weekday_wed: 'Mer',
    weekday_thu: 'Jeu',
    weekday_fri: 'Ven',
    weekday_sat: 'Sam',
    menu_system_section: 'Système',
    recent_invoices: 'Factures récentes',
    recent_expenses: 'Dépenses récentes',
    performance_last_6_months: 'Performance des 6 derniers mois',
    status_active_signed: 'Actif (Signé)',
    status_draft_template: 'Brouillon (Inachevé / Modèle)',
    content_tip_text: 'Écrivez les points de l’accord ou les conditions générales. Une fois enregistré, vous pourrez facilement le générer pour l’imprimer.',
    content_tip_title: 'Conseil de contenu',
    contract_content_ph: 'Écrivez ici le texte complet du contrat...',
    contract_content_label: 'Contenu du contrat / Modèle',
    client_optional_hint: 'Lier un client est optionnel. Laissez vide pour enregistrer comme modèle général.',
    choose_client_optional: 'Choisir le client (laisser vide si modèle)',
    created_date: 'Date de création',
    contract_title_ph: 'ex. Contrat de location standard',
    contract_title_label: 'Titre du contrat*',
    expense_cat_other: 'Autre',
    expense_cat_office_rent: 'Loyer bureau',
    expense_cat_salary: 'Salaire',
    expense_cat_insurance: 'Assurance',
    expense_cat_parts: 'Pièces de rechange',
    expense_cat_service: 'Service & entretien',
    expense_cat_fuel: 'Carburant',

    add: 'Ajouter',
    edit: 'Modifier',
    delete: 'Supprimer',
    search: 'Chercher',

    filter: 'Filtrer',
    select: 'Sélectionner',
    reset: 'Réinitialiser',
    close: 'Fermer',
    bulk: 'Bulk',
    view: 'Voir',
    // --- tables / filters / settings / profile / reports ---
    id: 'ID',
    client_col: 'Client',
    vehicle_col: 'Véhicule',
    dates_col: 'Dates',
    total_col: 'Total',
    status_col: 'Statut',
    actions_col: 'Actions',

    user_label: 'Utilisateur',
    date_range: 'Intervalle de dates',
    to_word: 'à',
    automatic: 'Automatique',
    configure_system: 'Configurer le système et sauvegarder la base de données',

    company_data: "Données de l'entreprise",
    company_name: "Nom de l'entreprise",
    business_number: 'Numéro d’entreprise',
    email: 'Email',
    phone: 'Téléphone',
    address: 'Adresse',
    currency: 'Devise',
    euro: 'Euro (€)',
        save_changes: 'Enregistrer les modifications',
    settings_saved: 'Paramètres enregistrés avec succès !',
    no_expenses: 'Aucune dépense trouvée.',
    no_invoices: 'Aucune facture trouvée.',

    database_management: 'Gestion de la base de données',
    db_help: 'Pour éviter toute perte de données, téléchargez régulièrement votre base de données. Le fichier est téléchargé au format JSON.',
    download_database: 'Télécharger la base',
    save_as_json: 'Enregistrer en .json',
    upload_database: 'Importer la base',
    restore_from_pc: 'Restaurer depuis le PC',
    reset_program: 'Réinitialiser le programme',
    reset_program_desc: 'Supprimez toutes les données locales et remettez le programme à son état initial.',
    reset_program_confirm: 'Êtes-vous sûr de vouloir réinitialiser le programme ? Toutes les données enregistrées localement seront supprimées.',
    confirm: 'Confirmer',
    notification: 'Notification',

    profile_title: 'Profil',
    profile_subtitle: 'Gérez vos données personnelles et les paramètres du compte.',
    details: 'Détails',
    notification_prefs: 'Préférences de notifications',
    reminders: 'Rappels',
    security: 'Sécurité',
    google_calendar: 'Google Agenda',
    current_password: 'Mot de passe actuel',
    enter_current_password: 'Entrez le mot de passe actuel',
    new_password: 'Nouveau mot de passe',
    enter_new_password: 'Entrez le nouveau mot de passe',
    confirm_new_password: 'Confirmer le nouveau mot de passe',
    confirm_password: 'Confirmez le nouveau mot de passe',
    update_password: 'Mettre à jour le mot de passe',

    update: 'Mettre à jour',

    template_general: 'Modèle général',

    advanced_report: 'Rapport avancé',
    advanced_report_sub: 'Choisissez la période et consultez les coûts fixes, les dépenses, les revenus et le bénéfice net.',
    from_date: 'À partir du',
    to_date: "Jusqu'au",
    income: 'Revenus',
    expenses_lbl: 'Dépenses',
    net_profit: 'Bénéfice net',
    performance_6m: 'Performance des 6 derniers mois',

    // --- dashboard extras ---
    fleet: 'Flotte de véhicules',
    cars_free: 'Véhicules disponibles',
    active_bookings: 'Réservations actives',
    monthly_revenue: 'Chiffre mensuel',
    total_clients: 'Clients au total',
    monthly_expenses: 'Dépenses mensuelles',

    badge_total: 'Total',
    badge_ready: 'Prêt',
    badge_active: 'Actif',
    badge_monthly: 'Mensuel',

    recent_bookings: 'Réservations récentes',
    see_all: 'Voir tout',
    late_return: 'Retour en retard!',
    past_pickup: 'Prise manquée',
    return_soon: 'Retour imminent !',
    return_soon_msg: '{client} devrait rendre {vehicle} dans {mins} min.',
    late_return_msg: '{client} est en retard pour le retour de {vehicle}.',
    pickup_soon: 'Prise imminente !',
    pickup_soon_msg: '{client} viendra récupérer {vehicle} dans {mins} min.',
    past_pickup_msg: '{client} devait récupérer {vehicle} maintenant.',
    maintenance_scheduled: 'Entretien planifié',
    maintenance_scheduled_msg: '{vehicle} a un entretien le {date} ({desc})',
    insurance_expiring_soon: 'Assurance bientôt expirée',
    insurance_expiring_soon_msg: '{vehicle} ({plate}) expire dans {days} jours.',
    expiry_soon: 'Expiration proche',
    expiry_soon_msg: '{vehicle} expire le {date}',

    was_planned_pickup: 'devait récupérer',

    alerts: 'Alertes',
    no_alerts_title: "Aucune alerte pour l'instant.",
    no_alerts_subtitle: 'Tout est en ordre !',
    go_to_details: 'Aller aux détails',

    apply_filters: 'Appliquer',
    // statuses
    active: 'Actif',
    pending: 'En attente',
    completed: 'Terminé',
    cancelled: 'Annulé',
    rented: 'Loué',
    available: 'Disponible',
    maintenance_status: 'En maintenance',
    scheduled: 'Planifié',
    in_progress: 'En cours',
    choose_vehicle: 'Sélectionner le véhicule...',
    defect_service_description: 'Description de la panne/du service',
    preparing: 'Préparation en cours...',
    paid: 'Payé',


    welcome: 'Bienvenue, {name} ! 👋',
    today_overview: "Aperçu de votre activité pour aujourd’hui.",
    add_booking: 'Ajouter une réservation',
    cancel: 'Annuler',
    status_partially_paid: 'Partiellement payé',
    status_paid: 'Payé',
    status_cancelled: 'Annulé',
    status_completed: 'Terminé',
    status_active: 'Actif',
    payment_status_label: 'Statut paiement',
    booking_status_label: 'Statut réservation',
    total_label: 'Total :',
    extras_label: 'Options',
    rent_price: 'Prix de location',
    price_summary: 'Récapitulatif des prix',
    extra_full_insurance: 'Assurance complète (15€/jour)',
    extra_child_seat: 'Siège enfant (10€/jour)',
    extra_gps: 'GPS (5€/jour)',
    extras_section: 'Options',
    enable_additional_driver: 'Activer le conducteur supplémentaire',
    additional_driver_section: 'Conducteur supplémentaire',
    total_overall: 'total',
    bookings_word: 'réservations',
    no_search_results: 'Aucune donnée trouvée pour cette recherche.',
    details_placeholder: 'Écrivez ici des détails supplémentaires...',
    booked_for_dates: 'Réservée pour ces dates',
    car_tab_reservations: 'Réservations',
    car_tab_services: 'Services',
    car_tab_expenses: 'Dépenses',
    car_tab_pricing_seasons: 'Saisons tarifaires',
    car_details_title: 'Détails du véhicule',
    car_images: 'Images du véhicule',
    car_name: 'Nom du véhicule',
    rental_price: 'Prix de location',
    license_plate_label: 'Immatriculation',
    identification_number: 'Numéro d’identification',
    registration_date: 'Date d’immatriculation',
    number_of_seats: 'Nombre de sièges',
    number_of_doors: 'Nombre de portes',
    fuel_type: 'Type de carburant',
    transmission_type: 'Type de transmission',
    has_air_conditioning: 'Climatisation',
    show_in_website: 'Afficher sur le site',
    optional_details: 'Détails optionnels',
    green_card: 'Carte verte',
    add_green_card: 'Ajouter la carte verte',
    add_tpl: 'Ajouter la responsabilité civile',
    car_tab_details: 'Détails',
    car_tab_tpl: 'TPL',
    car_no_reservations: "Ce véhicule n'a aucune réservation.",
    car_no_services: "Ce véhicule n'a aucun service.",
    car_no_expenses: "Ce véhicule n'a aucune dépense.",
    car_pricing_empty: 'Aucune saison tarifaire pour ce véhicule.',
    car_tpl_empty: 'Aucun enregistrement TPL pour ce véhicule.',
    tpl_amount: 'Montant',
    renewal_date: 'Date de renouvellement',
    expiry_date: "Date d'expiration",
    select_car: 'Sélectionner un véhicule',
    no_car_selected: 'Aucun véhicule sélectionné',
    view_car: 'Voir le véhicule',
    add_car_images: 'Ajouter des images',
    registration_type_tpl: 'TPL',
    category: 'Catégorie',
    choose_client: 'Choisir le client',
    new_client: 'Nouveau client',
    existing_client: 'Client existant',
    client_section: 'Client',
    choose_file: 'Choisir',
    click_to_upload: 'Cliquer pour téléverser',
    enter_notes_here: 'Écrivez des notes ici...',
    blacklist_client: 'Mettre ce client sur liste noire',
    id_back: 'ID (verso)',
    id_front: 'ID (recto)',
    passport_number: 'Numéro de passeport',
    license_number: 'Numéro de permis',
    personal_id_number: 'Numéro d\'identité (carte d\'identité)',
    documents_section: 'Documents',
    client_avatar: 'Photo du client (avatar)',
    address_label: 'Adresse',
    birthdate: 'Date de naissance',
    optional: 'Optionnel',
    secondary_phone: 'Téléphone secondaire',
    phone_number: 'Numéro de téléphone',
    enter_full_name: 'Saisir le nom complet',
    full_name_required: 'Nom complet*',
    client_profile_section: 'Profil client',
    flight_number: 'Numéro de vol',
    pickup_fee: 'Frais de prise en charge',
    delivery_fee: 'Frais de livraison',
    notes: 'Notes',
    deposit: 'Caution',
    total_rent_price: 'Prix total de location',
    daily_price: 'Prix journalier',
    pickup_location: 'Lieu de prise en charge',
    return_location: 'Lieu de retour',
    select_car: 'Choisir un véhicule...',
    car_required: 'Véhicule*',
    end_date_time: 'Date et heure de fin*',
    start_date_time: 'Date et heure de début*',
    booking_details_section: 'Détails de réservation',
    edit_payment: 'Modifier le paiement',
    add_payment: 'Ajouter un paiement',
    invoice_number_col: 'FACTURE #',
    no_email: 'Pas d\'email',
    no_phone: 'Pas de téléphone',
    unpaid_pending: 'Impayée (En attente)',
    invoice_total: 'Montant total',
    invoice_date: 'Date de facture',
    choose_client_required: 'Choisir le client*',
    status_failed: 'Échoué',
    booking_code: 'Code de réservation',
    expense_desc_ph: 'Décrivez la dépense ici...',
    expense_desc_label: 'Description / Détails',
    expense_general_no_car: 'Général / Sans véhicule',
    expense_vehicle_link: 'Lié au véhicule (optionnel)',
    expense_category_label: 'Catégorie de dépense*',
    edit_contract: 'Modifier le contrat',
    add_contract: 'Créer un contrat/modèle',
    edit_invoice: 'Modifier la facture',
    add_invoice: 'Ajouter une facture',
    edit_expense: 'Modifier la dépense',
    add_expense: 'Ajouter une dépense',
    edit_booking: 'Modifier la réservation',
    edit_client: 'Modifier le client',
    add_client: 'Ajouter un client',

    pickups_today: "Prises aujourd’hui",
    returns_today: "Retours aujourd’hui",
    no_pickups_today: "Aucune prise aujourd’hui",
    no_returns_today: "Aucun retour aujourd’hui",

    banner_returns_today_title: "Retours aujourd’hui",
    banner_returns_today_msg: "Vous avez {count} voiture(s) à retourner aujourd’hui : {vehicles}",
    banner_ins_expired_title: 'Assurances expirées',
    banner_ins_expired_msg: "Vous avez {count} assurance(s) expirée(s). Ouvrez « Assurances » pour les détails.",
    banner_ins_week_title: 'Assurances expirant cette semaine',
    banner_ins_week_msg: 'Vous avez {count} assurance(s) expirant dans 7 jours.',
alerts: 'Alertes',
insurance_expired: 'Assurance expirée!',
client_vehicle: 'Client & Véhicule',
expired_on: 'expiré le',

company_data: 'Données de l’entreprise',
system_config: 'Configurer le système et sauvegarder la base de données',

details: 'Détails',
notification_preferences: 'Préférences de notification',

services_faults: 'Services et défauts',
vehicle: 'Véhicule',
description: 'Description',
date: 'Date',
cost: 'Coût',
status: 'Statut',
expired: 'Expiré',

edit_insurance: 'Modifier l’assurance',
fill_fields_below: 'Remplissez les champs ci-dessous et enregistrez les modifications.',

insurance_company: 'Compagnie d’assurance',
policy_number: 'Numéro de police',
expiry_date: 'Date d’expiration',

client: 'Client',
search_contract: 'Rechercher un contrat...',
revenue: 'Revenus',
profit: 'Profit',

last_6_months: 'Performance des 6 derniers mois',
last_expenses: 'Dernières dépenses',
last_invoices: 'Dernières factures',
    manage_cars_subtitle: 'Gérez et surveillez toutes les voitures',

    manage_bookings_subtitle: 'Gérez les réservations actives et passées',
    contacts: 'Contacts',
    bookings_expenses: 'Réservations / Dépenses',
    search_booking_placeholder: 'Rechercher une réservation...',
    print_rental_contract: 'Imprimer le contrat de location',
    transmission: 'Transmission',
    add_car: 'Ajouter une voiture',
    fill_fields: 'Remplissez les champs ci-dessous et enregistrez les modifications.',
    brand: 'Marque',
    model: 'Modèle',
    year: 'Année',
    plate: 'Plaque',
    color: 'Couleur',
    price_per_day: 'Prix/Jour (€)',
    technical_specs: 'Spécifications techniques',
    fuel: 'Carburant',
    petrol: 'Essence',
    seats: 'Sièges',
    doors: 'Portes',
    engine_size: 'Taille du moteur',
    main_photo: 'Photo principale',
    click_upload: 'Cliquez pour télécharger',
    choose: 'Choisir',
    file_uploaded: 'Fichier téléchargé',
    select_time: 'Sélectionner l’heure…',
    search_client_placeholder: 'Rechercher un client...',
    no_client_found: 'Aucun client trouvé.',

    manual: 'Manuelle',
    day: 'jour',
    week: 'semaine',
    month: 'mois',
    search_payment_placeholder: 'Rechercher un paiement...',
    other: 'Autre',
    paypal: 'PayPal',
    bank_transfer: 'Virement bancaire',
    bank_card: 'Carte bancaire',
    cash: 'Espèces',
    date_col: 'Date',
    method_col: 'Méthode',
    amount_col: 'Montant',
    booking_col: 'Réservation',
    payment_method_label: 'Mode de paiement',
    search_expense_placeholder: 'Rechercher une dépense...',
    payments_subtitle: 'Historique des transactions',
    expenses_subtitle: 'Gérez les dépenses et coûts opérationnels',
    invoices_subtitle: 'Factures créées pour les clients',
    insurance_subtitle: 'Assurances des véhicules et échéances',
    edit_car: 'Modifier la voiture',
    diesel: 'Diesel',
    electric: 'Électrique',
    hybrid: 'Hybride',
    has_ac: 'Climatisation',
    show_on_web: 'Afficher sur le site',
    additional_details: 'Détails supplémentaires',
    upload_hint: 'PNG / JPG / URL',

    blacklisted: 'Bloqué',
  
    car_added: 'Voiture ajoutée',
    car_updated: 'Voiture mise à jour',
    car_archived: 'Voiture archivée',
    car_deleted: 'Voiture supprimée',
    client_updated: 'Client mis à jour',
    expense_added: 'Dépense ajoutée',
    expense_updated: 'Dépense mise à jour',
    expense_deleted: 'Dépense supprimée',
    payment_added: 'Paiement ajouté',
    payment_updated: 'Paiement mis à jour',
    payment_deleted: 'Paiement supprimé',
    booking_archived: 'Réservation archivée',
    booking_deleted: 'Réservation supprimée',
    booking_added: 'Réservation ajoutée',
    booking_updated: 'Réservation mise à jour',
    daily_summary: 'Résumé quotidien',
    daily_summary_desc: 'Recevez un résumé quotidien des réservations à venir',
    time: 'Heure',
    car_return_notification: 'Notification de retour du véhicule',
    car_return_desc: 'Recevez une notification avant l’heure de retour',
    minutes_before: 'Minutes avant',
    car_pickup_notification: 'Notification de prise du véhicule',
    car_pickup_desc: 'Recevez une notification avant la prise prévue',
    car_registration_reminder: "Rappel d'immatriculation du véhicule",
    car_registration_desc: 'Recevez un rappel lorsque l’immatriculation expire bientôt',
    days_before: 'Jours avant',
    save: 'Enregistrer',
    save_preferences: 'Enregistrer les préférences',
    password_incorrect: 'Le mot de passe actuel est incorrect !',
    passwords_not_match: 'Les nouveaux mots de passe ne correspondent pas !',
    password_min_length: 'Le mot de passe doit contenir au moins 6 caractères.',
    password_updated_success: 'Mot de passe mis à jour avec succès !',
    details_saved_success: 'Détails enregistrés avec succès !',
    google_calendar_not_connected: 'Google Agenda n’est pas encore connecté',
    connect_google_calendar: 'Se connecter à Google Agenda',
    google_calendar_desc: 'Lorsqu’il est activé, vos nouvelles réservations seront ajoutées automatiquement à Google Agenda.',
},

  hr: {
    dashboard: 'Kontrolna ploča',
    bookings: 'Rezervacije',
    cars: 'Vozila',
    clients: 'Klijenti',
    client_details_title: 'Detalji klijenta',
    client_tab_details: 'Detalji',
    client_tab_bookings: 'Rezervacije',
    client_tab_payments: 'Plaćanja',
    client_bookings_empty: 'Ovaj klijent nema rezervacija.',
    client_payments_empty: 'Ovaj klijent nema plaćanja.',
    start_date_label: 'Datum početka',
    end_date_label: 'Datum završetka',
    no_data_found: 'Nema podataka za ovu pretragu.',
    view_client: 'Prikaži klijenta',
    payments: 'Plaćanja',
    expenses: 'Troškovi',
    invoices: 'Fakture',
    contracts: 'Ugovori',
    rental_contract_standard_template: 'Ugovor o najmu (Standardni predložak)',
    print_contract: 'Ispiši ugovor',
    client_usage: 'Klijent / Namjena',
    title_col: 'Naslov',
    id_number: 'ID / Broj',
    contracts_subtitle: 'Upravljaj i ispiši ugovore o najmu',
    insurance: 'Osiguranje',
    maintenance: 'Servis',
    staff: 'Osoblje',
    locations: 'Lokacije',
    promotions: 'Promocije',
    inventory: 'Inventar',
    settings: 'Postavke',
    profile: 'Profil',
    reports: 'Izvještaji',
    reports_financial_summary: 'Pregled i analiza financijskih performansi',
    menu_main_section: 'Glavno',
    menu_finance_analytics_section: 'Financije i analitika',
    menu_operations_section: 'Operacije',
    booking_list: 'Lista',
    booking_payments_not_finished: 'Nezavršena plaćanja',
    booking_waitlist: 'Lista čekanja',
    booking_operations: 'Operacije',
    reservations_with_incomplete_payments: 'Rezervacije s nepotpunim plaćanjima',
    back_to_list: 'Nazad na listu',
    total_unpaid: 'Ukupno neplaćeno',
    payments_overdue: 'Zakašnjela plaćanja',
    payments_incoming: 'Dolazeća plaćanja',
    remaining_amount: 'Preostali iznos',
    finish: 'Završi',
    no_waitlist_records: 'Nema rezervacija na listi čekanja',
    no_waitlist_subtitle: 'Dodajte rezervacije sa statusom lista čekanja da ih vidite ovdje.',
    pickup_delivery_to_client: 'Dostava klijentu',
    pickup_return_from_client: 'Povrat od klijenta',
    month_view: 'mjesec',
    month_name_0: 'Januar',
    month_name_1: 'Februar',
    month_name_2: 'Mart',
    month_name_3: 'April',
    month_name_4: 'Maj',
    month_name_5: 'Juni',
    month_name_6: 'Juli',
    month_name_7: 'Avgust',
    month_name_8: 'Septembar',
    month_name_9: 'Oktobar',
    month_name_10: 'Novembar',
    month_name_11: 'Decembar',
    weekday_sun: 'Ned',
    weekday_mon: 'Pon',
    weekday_tue: 'Uto',
    weekday_wed: 'Sri',
    weekday_thu: 'Čet',
    weekday_fri: 'Pet',
    weekday_sat: 'Sub',
    menu_system_section: 'Sistem',
    recent_invoices: 'Nedavni računi',
    recent_expenses: 'Nedavni troškovi',
    performance_last_6_months: 'Učinak zadnjih 6 mjeseci',
    status_active_signed: 'Aktivno (Potpisano)',
    status_draft_template: 'Skica (Nedovršeno / Predložak)',
    content_tip_text: 'Upišite točke dogovora ili opće uvjete. Nakon spremanja možete ga lako generirati za ispis.',
    content_tip_title: 'Savjet za sadržaj',
    contract_content_ph: 'Ovdje upišite puni tekst ugovora...',
    contract_content_label: 'Sadržaj ugovora / Predložak',
    client_optional_hint: 'Povezivanje klijenta je opcionalno. Ostavite prazno za opći predložak.',
    choose_client_optional: 'Odaberi klijenta (prazno ako je predložak)',
    created_date: 'Datum izrade',
    contract_title_ph: 'npr. Standardni ugovor o najmu',
    contract_title_label: 'Naziv ugovora*',
    expense_cat_other: 'Ostalo',
    expense_cat_office_rent: 'Najam ureda',
    expense_cat_salary: 'Plaća',
    expense_cat_insurance: 'Osiguranje',
    expense_cat_parts: 'Rezervni dijelovi',
    expense_cat_service: 'Servis i održavanje',
    expense_cat_fuel: 'Gorivo',

    add: 'Dodaj',
    edit: 'Uredi',
    delete: 'Obriši',
    search: 'Pretraži',

    filter: 'Filtriraj',
    select: 'Odaberi',
    reset: 'Resetiraj',
    close: 'Zatvori',
    bulk: 'Bulk',
    view: 'Pogledaj',
    // --- tables / filters / settings / profile / reports ---
    id: 'ID',
    client_col: 'Klijent',
    vehicle_col: 'Vozilo',
    dates_col: 'Datumi',
    total_col: 'Ukupno',
    status_col: 'Status',
    actions_col: 'Radnje',

    user_label: 'Korisnik',
    date_range: 'Raspon datuma',
    to_word: 'do',
    automatic: 'Automatski',
    configure_system: 'Konfiguriraj sustav i spremi bazu podataka',

    company_data: 'Podaci tvrtke',
    company_name: 'Naziv tvrtke',
    business_number: 'Broj tvrtke',
    email: 'Email',
    phone: 'Telefon',
    address: 'Adresa',
    currency: 'Valuta',
    euro: 'Euro (€)',
        save_changes: 'Spremi promjene',
    settings_saved: 'Postavke su uspješno spremljene!',
    no_expenses: 'Nema pronađenih troškova.',
    no_invoices: 'Nema pronađenih računa.',

    database_management: 'Upravljanje bazom podataka',
    db_help: 'Kako se podaci ne bi izgubili, redovito preuzimajte bazu podataka. Datoteka se preuzima u JSON formatu.',
    download_database: 'Preuzmi bazu',
    save_as_json: 'Spremi kao .json',
    upload_database: 'Učitaj bazu',
    restore_from_pc: 'Vrati podatke s računala',
    reset_program: 'Resetiraj program',
    reset_program_desc: 'Izbriši sve lokalne podatke i vrati program u početno stanje.',
    reset_program_confirm: 'Jeste li sigurni da želite resetirati program? Svi lokalno spremljeni podaci bit će izbrisani.',
    confirm: 'Potvrdi',
    notification: 'Obavijest',

    profile_title: 'Profil',
    profile_subtitle: 'Upravljajte osobnim podacima i postavkama računa.',
    details: 'Detalji',
    notification_prefs: 'Postavke obavijesti',
    reminders: 'Podsjetnici',
    security: 'Sigurnost',
    google_calendar: 'Google kalendar',
    current_password: 'Trenutna lozinka',
    enter_current_password: 'Unesite trenutnu lozinku',
    new_password: 'Nova lozinka',
    enter_new_password: 'Unesite novu lozinku',
    confirm_new_password: 'Potvrdite novu lozinku',
    confirm_password: 'Potvrdite novu lozinku',
    update_password: 'Ažuriraj lozinku',

    update: 'Ažuriraj',

    template_general: 'Opći predložak',

    advanced_report: 'Napredni izvještaj',
    advanced_report_sub: 'Odaberite razdoblje i pogledajte fiksne troškove, troškove, prihode i neto dobit.',
    from_date: 'Od datuma',
    to_date: 'Do datuma',
    income: 'Prihodi',
    expenses_lbl: 'Troškovi',
    net_profit: 'Neto dobit',
    performance_6m: 'Učinak zadnjih 6 mjeseci',

    // --- dashboard extras ---
    fleet: 'Vozni park',
    cars_free: 'Dostupna vozila',
    active_bookings: 'Aktivne rezervacije',
    monthly_revenue: 'Mjesečni prihodi',
    total_clients: 'Ukupno klijenata',
    monthly_expenses: 'Mjesečni troškovi',

    badge_total: 'Ukupno',
    badge_ready: 'Spremno',
    badge_active: 'Aktivno',
    badge_monthly: 'Mjesečno',

    recent_bookings: 'Nedavne rezervacije',
    see_all: 'Prikaži sve',
    late_return: 'Kašnjenje pri povratu!',
    past_pickup: 'Propušteno preuzimanje',
    return_soon: 'Skori povrat!',
    return_soon_msg: '{client} treba vratiti {vehicle} za {mins} min.',
    late_return_msg: '{client} kasni s povratom {vehicle}.',
    pickup_soon: 'Skoro preuzimanje!',
    pickup_soon_msg: '{client} preuzima {vehicle} za {mins} min.',
    past_pickup_msg: '{client} je trebao preuzeti {vehicle} sada.',
    maintenance_scheduled: 'Planirani servis',
    maintenance_scheduled_msg: '{vehicle} ima servis {date} ({desc})',
    insurance_expiring_soon: 'Osiguranje uskoro istječe',
    insurance_expiring_soon_msg: '{vehicle} ({plate}) istječe za {days} dana.',
    expiry_soon: 'Uskoro istječe',
    expiry_soon_msg: '{vehicle} istječe {date}',

    was_planned_pickup: 'trebao je preuzeti',

    alerts: 'Upozorenja',
    no_alerts_title: 'Trenutno nema upozorenja.',
    no_alerts_subtitle: 'Sve je u redu!',
    go_to_details: 'Idi na detalje',

    apply_filters: 'Primijeni',
    // statuses
    active: 'Aktivno',
    pending: 'Na čekanju',
    completed: 'Završeno',
    cancelled: 'Otkazano',
    rented: 'Iznajmljeno',
    available: 'Dostupno',
    maintenance_status: 'Na servisu',
    scheduled: 'Planirano',
    in_progress: 'U tijeku',
    choose_vehicle: 'Odaberi vozilo...',
    defect_service_description: 'Opis kvara/servisa',
    preparing: 'Priprema...',
    paid: 'Plaćeno',


    welcome: 'Dobrodošli, {name}! 👋',
    today_overview: 'Pregled poslovanja za danas.',
    add_booking: 'Dodaj rezervaciju',
    cancel: 'Odustani',
    status_partially_paid: 'Djelomično plaćeno',
    status_paid: 'Plaćeno',
    status_cancelled: 'Otkazano',
    status_completed: 'Završeno',
    status_active: 'Aktivno',
    payment_status_label: 'Status plaćanja',
    booking_status_label: 'Status rezervacije',
    total_label: 'Ukupno:',
    extras_label: 'Dodaci',
    rent_price: 'Cijena najma',
    price_summary: 'Sažetak cijene',
    extra_full_insurance: 'Puno osiguranje (15€/dan)',
    extra_child_seat: 'Dječja sjedalica (10€/dan)',
    extra_gps: 'GPS navigacija (5€/dan)',
    extras_section: 'Dodaci',
    enable_additional_driver: 'Omogući dodatnog vozača',
    additional_driver_section: 'Dodatni vozač',
    total_overall: 'ukupno',
    bookings_word: 'rezervacije',
    no_search_results: 'Nema podataka za ovu pretragu.',
    details_placeholder: 'Ovdje unesite dodatne detalje...',
    booked_for_dates: 'Rezervisano za ove datume',
    car_tab_reservations: 'Rezervacije',
    car_tab_services: 'Servisi',
    car_tab_expenses: 'Troškovi',
    car_tab_pricing_seasons: 'Sezone cijena',
    car_details_title: 'Detalji vozila',
    car_images: 'Slike vozila',
    car_name: 'Naziv vozila',
    rental_price: 'Cijena najma',
    license_plate_label: 'Registarske tablice',
    identification_number: 'Identifikacijski broj',
    registration_date: 'Datum registracije',
    number_of_seats: 'Broj sjedišta',
    number_of_doors: 'Broj vrata',
    fuel_type: 'Vrsta goriva',
    transmission_type: 'Tip mjenjača',
    has_air_conditioning: 'Ima klimu',
    show_in_website: 'Prikaži na web stranici',
    optional_details: 'Dodatni detalji',
    green_card: 'Zeleni karton',
    add_green_card: 'Dodaj zeleni karton',
    add_tpl: 'Dodaj TPL',
    car_tab_details: 'Detalji',
    car_tab_tpl: 'TPL',
    car_no_reservations: 'Ovo vozilo nema rezervacija.',
    car_no_services: 'Ovo vozilo nema servisa.',
    car_no_expenses: 'Ovo vozilo nema troškova.',
    car_pricing_empty: 'Nema sezona cijena za ovo vozilo.',
    car_tpl_empty: 'Nema TPL zapisa za ovo vozilo.',
    tpl_amount: 'Iznos',
    renewal_date: 'Datum obnove',
    expiry_date: 'Datum isteka',
    select_car: 'Odaberi vozilo',
    no_car_selected: 'Nije odabrano vozilo',
    view_car: 'Prikaži vozilo',
    add_car_images: 'Dodaj slike',
    registration_type_tpl: 'TPL',
    category: 'Kategorija',
    choose_client: 'Odaberi klijenta',
    new_client: 'Novi klijent',
    existing_client: 'Postojeći klijent',
    client_section: 'Klijent',
    choose_file: 'Odaberi',
    click_to_upload: 'Klikni za učitavanje',
    enter_notes_here: 'Upišite bilješke ovdje...',
    blacklist_client: 'Stavi klijenta na crnu listu',
    id_back: 'ID (stražnja strana)',
    id_front: 'ID (prednja strana)',
    passport_number: 'Broj putovnice',
    license_number: 'Broj vozačke dozvole',
    personal_id_number: 'Broj osobne iskaznice',
    documents_section: 'Dokumenti',
    client_avatar: 'Fotografija klijenta (avatar)',
    address_label: 'Adresa',
    birthdate: 'Datum rođenja',
    optional: 'Opcionalno',
    secondary_phone: 'Dodatni telefon',
    phone_number: 'Broj telefona',
    enter_full_name: 'Unesite puno ime',
    full_name_required: 'Puno ime*',
    client_profile_section: 'Profil klijenta',
    flight_number: 'Broj leta',
    pickup_fee: 'Naknada preuzimanja',
    delivery_fee: 'Naknada dostave',
    notes: 'Bilješke',
    deposit: 'Polog',
    total_rent_price: 'Ukupna cijena najma',
    daily_price: 'Dnevna cijena',
    pickup_location: 'Lokacija preuzimanja',
    return_location: 'Lokacija povrata',
    select_car: 'Odaberi vozilo...',
    car_required: 'Vozilo*',
    end_date_time: 'Datum i vrijeme završetka*',
    start_date_time: 'Datum i vrijeme početka*',
    booking_details_section: 'Detalji rezervacije',
    edit_payment: 'Uredi plaćanje',
    add_payment: 'Dodaj plaćanje',
    invoice_number_col: 'RAČUN #',
    no_email: 'Nema e-pošte',
    no_phone: 'Nema telefona',
    unpaid_pending: 'Neplaćeno (Na čekanju)',
    invoice_total: 'Ukupan iznos',
    invoice_date: 'Datum računa',
    choose_client_required: 'Odaberi klijenta*',
    status_failed: 'Neuspjelo',
    booking_code: 'Kod rezervacije',
    expense_desc_ph: 'Opišite trošak ovdje...',
    expense_desc_label: 'Opis / Detalji',
    expense_general_no_car: 'Općenito / Bez vozila',
    expense_vehicle_link: 'Povezano s vozilom (opcionalno)',
    expense_category_label: 'Kategorija troška*',
    edit_contract: 'Uredi ugovor',
    add_contract: 'Kreiraj ugovor/predložak',
    edit_invoice: 'Uredi račun',
    add_invoice: 'Dodaj račun',
    edit_expense: 'Uredi trošak',
    add_expense: 'Dodaj trošak',
    edit_booking: 'Uredi rezervaciju',
    edit_client: 'Uredi klijenta',
    add_client: 'Dodaj klijenta',

    pickups_today: 'Preuzimanja danas',
    returns_today: 'Vraćanja danas',
    no_pickups_today: 'Nema preuzimanja danas',
    no_returns_today: 'Nema vraćanja danas',

    banner_returns_today_title: 'Vraćanja danas',
    banner_returns_today_msg: 'Imate {count} vozilo(a) za vratiti danas: {vehicles}',
    banner_ins_expired_title: 'Istekla osiguranja',
    banner_ins_expired_msg: 'Imate {count} isteklih osiguranja. Otvorite “Osiguranje” za detalje.',
    banner_ins_week_title: 'Osiguranja koja ističu ovaj tjedan',
    banner_ins_week_msg: 'Imate {count} osiguranja koja ističu unutar 7 dana.',
alerts: 'Upozorenja',
insurance_expired: 'Osiguranje je isteklo!',
client_vehicle: 'Klijent i Vozilo',
expired_on: 'isteklo',

company_data: 'Podaci o kompaniji',
system_config: 'Konfiguriraj sistem i spremi bazu podataka',

details: 'Detalji',
notification_preferences: 'Postavke obavijesti',

services_faults: 'Servisi i kvarovi',
vehicle: 'Vozilo',
description: 'Opis',
date: 'Datum',
cost: 'Trošak',
status: 'Status',
expired: 'Isteklo',

edit_insurance: 'Uredi osiguranje',
fill_fields_below: 'Popunite polja ispod i spremite promjene.',

insurance_company: 'Osiguravajuća kompanija',
policy_number: 'Broj police',
expiry_date: 'Datum isteka',

client: 'Klijent',
search_contract: 'Pretraži ugovor...',
revenue: 'Prihodi',
profit: 'Profit',

last_6_months: 'Performanse za zadnjih 6 mjeseci',
last_expenses: 'Zadnji troškovi',
last_invoices: 'Zadnje fakture',
    manage_cars_subtitle: 'Upravljaj i prati sva vozila',

    manage_bookings_subtitle: 'Upravljaj aktivnim i prošlim rezervacijama',
    contacts: 'Kontakti',
    bookings_expenses: 'Rezervacije / Troškovi',
    search_booking_placeholder: 'Pretraži rezervaciju...',
    print_rental_contract: 'Ispiši ugovor o najmu',
    transmission: 'Mjenjač',
    add_car: 'Dodaj vozilo',
    fill_fields: 'Popunite polja ispod i spremite promjene.',
    brand: 'Marka',
    model: 'Model',
    year: 'Godina',
    plate: 'Tablica',
    color: 'Boja',
    price_per_day: 'Cijena/Dan (€)',
    technical_specs: 'Tehničke specifikacije',
    fuel: 'Gorivo',
    petrol: 'Benzin',
    seats: 'Sjedala',
    doors: 'Vrata',
    engine_size: 'Zapremina motora',
    main_photo: 'Glavna fotografija',
    click_upload: 'Kliknite za prijenos',
    choose: 'Odaberi',
    file_uploaded: 'Datoteka prenesena',
    select_time: 'Odaberite vrijeme…',
    search_client_placeholder: 'Pretraži klijenta...',
    no_client_found: 'Nije pronađen nijedan klijent.',

    manual: 'Ručni',
    day: 'dan',
    week: 'tjedan',
    month: 'mjesec',
    search_payment_placeholder: 'Pretraži plaćanje...',
    other: 'Ostalo',
    paypal: 'PayPal',
    bank_transfer: 'Bankovni prijenos',
    bank_card: 'Bankovna kartica',
    cash: 'Gotovina',
    date_col: 'Datum',
    method_col: 'Metoda',
    amount_col: 'Iznos',
    booking_col: 'Rezervacija',
    payment_method_label: 'Način plaćanja',
    search_expense_placeholder: 'Pretraži trošak...',
    payments_subtitle: 'Povijest transakcija',
    expenses_subtitle: 'Upravljaj operativnim troškovima',
    invoices_subtitle: 'Fakture kreirane za klijente',
    insurance_subtitle: 'Osiguranja vozila i isteci',
    edit_car: 'Uredi vozilo',
    diesel: 'Dizel',
    electric: 'Električno',
    hybrid: 'Hibrid',
    has_ac: 'Klima uređaj',
    show_on_web: 'Prikaži na webu',
    additional_details: 'Dodatni detalji',
    upload_hint: 'PNG / JPG / URL',

    blacklisted: 'Blokiran',
  
    car_added: 'Vozilo dodano',
    car_updated: 'Vozilo ažurirano',
    car_archived: 'Vozilo arhivirano',
    car_deleted: 'Vozilo obrisano',
    client_updated: 'Klijent ažuriran',
    expense_added: 'Trošak dodan',
    expense_updated: 'Trošak ažuriran',
    expense_deleted: 'Trošak obrisan',
    payment_added: 'Plaćanje dodano',
    payment_updated: 'Plaćanje ažurirano',
    payment_deleted: 'Plaćanje obrisano',
    booking_archived: 'Rezervacija arhivirana',
    booking_deleted: 'Rezervacija obrisana',
    booking_added: 'Rezervacija dodana',
    booking_updated: 'Rezervacija ažurirana',
    daily_summary: 'Dnevni sažetak',
    daily_summary_desc: 'Primite dnevni sažetak nadolazećih rezervacija',
    time: 'Vrijeme',
    car_return_notification: 'Obavijest o povratu vozila',
    car_return_desc: 'Primite obavijest prije vremena povrata',
    minutes_before: 'Minute prije',
    car_pickup_notification: 'Obavijest o preuzimanju vozila',
    car_pickup_desc: 'Primite obavijest prije planiranog preuzimanja',
    car_registration_reminder: 'Podsjetnik za registraciju vozila',
    car_registration_desc: 'Primite podsjetnik kada registracija uskoro ističe',
    days_before: 'Dana prije',
    save: 'Spremi',
    save_preferences: 'Spremi postavke',
    password_incorrect: 'Trenutna lozinka nije točna!',
    passwords_not_match: 'Nove lozinke se ne podudaraju!',
    password_min_length: 'Lozinka mora imati najmanje 6 znakova.',
    password_updated_success: 'Lozinka je uspješno ažurirana!',
    details_saved_success: 'Detalji su uspješno spremljeni!',
    google_calendar_not_connected: 'Google kalendar još nije povezan',
    connect_google_calendar: 'Poveži s Google kalendarom',
    google_calendar_desc: 'Kada je aktivirano, nove rezervacije će se automatski dodati u vaš Google kalendar.',
}
};


const CONTRACT_I18N = {
  sq: {
    companyTagline: 'Shërbime Rent a Car',
    addressLabel: 'Adresa',
    phoneLabel: 'Tel / Viber',
    businessNoLabel: 'NUI',
    rentalAgreementTitle: 'Kontratë Qiraje',
    rentalAgreementSubtitle: 'Mbi dhënien e automjetit me qira',
    referenceNo: 'Nr. Referencës',
    renterDetails: 'Të Dhënat e Klientit',
    vehicleDetails: 'Të Dhënat e Automjetit',
    rentalPeriodCharges: 'Periudha e Qirasë & Pagesa',
    fullName: 'Emri & Mbiemri',
    birthDate: 'Datëlindja',
    idPassport: 'Nr. Personal / Pasaportë',
    licenseNo: 'Nr. Patentë Shoferi',
    residenceAddress: 'Adresa e Banimit',
    phoneMobile: 'Tel / Mobil',
    makeModel: 'Marka & Modeli',
    plateNo: 'Targat',
    productionYear: 'Viti i Prodhimit',
    color: 'Ngjyra',
    fuelLevel: 'Niveli i Karburantit',
    odometer: 'Gjendja e Km',
    writeHere: '(Shëno këtu)',
    pickupDate: 'Data e Marrjes',
    pickupTime: 'Ora e Marrjes',
    pickupPlace: 'Vendi i Marrjes',
    returnDate: 'Data e Kthimit',
    returnTime: 'Ora e Kthimit',
    returnPlace: 'Vendi i Kthimit',
    dailyPrice: 'Çmimi Ditor',
    depositCash: 'Depozita',
    totalPrice: 'Çmimi Total',
    termsTitle: 'Kushtet e Përgjithshme',
    bookingTerms: [
      '<strong>Përgjegjësia:</strong> Qiramarrësi mban përgjegjësi të plotë për automjetin nga momenti i marrjes deri në kthim dhe është i detyruar të kompensojë çdo dëm të shkaktuar nga pakujdesia ose përdorimi i parregullt.',
      '<strong>Sigurimi:</strong> Mjeti mund të jetë i mbuluar nga sigurimi sipas policës aktive, por klienti mbetet përgjegjës për pjesëmarrjen në dëm, dëmet nga neglizhenca, përdorimi nga persona të paautorizuar dhe rastet kur nuk respektohen procedurat ligjore.',
      '<strong>Policia dhe raportimi:</strong> Në rast aksidenti, dëmtimi ose vjedhjeje, klienti duhet të njoftojë menjëherë kompaninë dhe policinë. Raporti i policisë është i detyrueshëm për çdo kërkesë sigurimi.',
      '<strong>Kufizimi i përdorimit:</strong> Automjeti nuk lejohet të përdoret jashtë zonës së autorizuar, për gara, transport të paligjshëm, nën ndikimin e alkoolit / narkotikëve ose nga persona të pacaktuar në kontratë.',
      '<strong>Kthimi i automjetit:</strong> Automjeti duhet të kthehet në datën, orën dhe gjendjen e përcaktuar, me dokumentet, çelësat dhe pajisjet përkatëse. Vonesat, mungesat apo dëmtimet faturohen shtesë.'
    ],
    acceptanceNote: 'Me nënshkrimin e kësaj kontrate, klienti deklaron se i ka lexuar, kuptuar dhe pranuar të gjitha kushtet e sipërshënuara.',
    companyRepresentative: 'Përfaqësuesi i Kompanisë',
    clientRenter: 'Klienti / Qiramarrësi',
    signPlaceholder: 'Emri dhe Nënshkrimi',
    generatedFooter: 'Ky dokument përbën marrëveshje ligjore dhe është gjeneruar elektronikisht nga sistemi.',
    contractDate: 'Data e Kontratës',
    partyClient: 'Pala / Klienti',
    manualContractTitle: 'Kontratë / Marrëveshje Qiraje',
    manualSections: [
      { title: '1. Palët kontraktuese', body: 'Kjo marrëveshje lidhet ndërmjet kompanisë {company} si qiradhënës dhe palës tjetër / klientit {client} si qiramarrës.' },
      { title: '2. Objekti i kontratës', body: 'Qiradhënësi i jep me qira qiramarrësit automjetin sipas të dhënave të përshkruara në kontratë ose në dokumentet përcjellëse, për periudhën dhe kundrejt çmimit të dakorduar.' },
      { title: '3. Detyrimet e qiramarrësit', body: 'Qiramarrësi detyrohet ta përdorë automjetin me kujdes, në përputhje me ligjin, të mos ia japë personave të paautorizuar dhe ta kthejë në kohë, në gjendje të rregullt dhe me dokumentacionin përkatës.' },
      { title: '4. Pagesa dhe depozita', body: 'Pagesa e qirasë, depozita dhe çdo tarifë shtesë bëhen sipas marrëveshjes ndërmjet palëve. Çdo vonesë, dëm, gjobë ose shpenzim shtesë i shkaktuar gjatë përdorimit mbulohet nga qiramarrësi.' },
      { title: '5. Aksidentet dhe dëmet', body: 'Në rast aksidenti, dëmtimi ose vjedhjeje, qiramarrësi është i detyruar të njoftojë menjëherë kompaninë dhe organet kompetente dhe të sigurojë raportin përkatës. Në të kundërt, mban përgjegjësi të plotë për dëmin.' },
      { title: '6. Dispozitat përfundimtare', body: 'Palët deklarojnë se e kanë lexuar dhe kuptuar këtë kontratë dhe e pranojnë atë vullnetarisht. Për çdo çështje të paparashikuar zbatohen ligjet në fuqi dhe kushtet e kompanisë.' }
    ],
    clientSignature: 'Nënshkrimi i Klientit'
  },
  en: {
    companyTagline: 'Rent a Car Services',
    addressLabel: 'Address',
    phoneLabel: 'Phone / Viber',
    businessNoLabel: 'Business No.',
    rentalAgreementTitle: 'Rental Agreement',
    rentalAgreementSubtitle: 'Vehicle rental authorization',
    referenceNo: 'Reference No.',
    renterDetails: 'Renter Details',
    vehicleDetails: 'Vehicle Details',
    rentalPeriodCharges: 'Rental Period & Charges',
    fullName: 'Full Name',
    birthDate: 'Date of Birth',
    idPassport: 'ID / Passport No.',
    licenseNo: 'Driver License No.',
    residenceAddress: 'Residential Address',
    phoneMobile: 'Phone / Mobile',
    makeModel: 'Make & Model',
    plateNo: 'License Plate',
    productionYear: 'Production Year',
    color: 'Color',
    fuelLevel: 'Fuel Level',
    odometer: 'Odometer',
    writeHere: '(Write here)',
    pickupDate: 'Pickup Date',
    pickupTime: 'Pickup Time',
    pickupPlace: 'Pickup Place',
    returnDate: 'Return Date',
    returnTime: 'Return Time',
    returnPlace: 'Return Place',
    dailyPrice: 'Daily Price',
    depositCash: 'Deposit',
    totalPrice: 'Total Price',
    termsTitle: 'Terms and Conditions',
    bookingTerms: [
      '<strong>Liability:</strong> The renter assumes full responsibility for the vehicle from pickup until return and shall compensate any damage caused by negligence, misuse or breach of this agreement.',
      '<strong>Insurance:</strong> The vehicle may be covered by insurance under the active policy, however the renter remains responsible for deductibles, negligence, unauthorized drivers and cases where legal procedures are not followed.',
      '<strong>Police and reporting:</strong> In the event of accident, damage or theft, the renter must immediately notify the company and the police. A police report is mandatory for every insurance claim.',
      '<strong>Use restrictions:</strong> The vehicle may not be used outside the authorized territory, for racing, illegal transport, under the influence of alcohol / drugs or by persons not authorized in this agreement.',
      '<strong>Vehicle return:</strong> The vehicle must be returned on the agreed date, time and condition, together with all documents, keys and equipment. Delays, missing items or damages will be charged additionally.'
    ],
    acceptanceNote: 'By signing this agreement, the renter confirms that all terms have been read, understood and accepted.',
    companyRepresentative: 'Company Representative',
    clientRenter: 'Client / Renter',
    signPlaceholder: 'Name and Signature',
    generatedFooter: 'This document forms a legal agreement and has been generated electronically by the system.',
    contractDate: 'Contract Date',
    partyClient: 'Party / Client',
    manualContractTitle: 'Rental Contract / Agreement',
    manualSections: [
      { title: '1. Contracting parties', body: 'This agreement is entered into between {company} as the lessor and {client} as the renter / other party.' },
      { title: '2. Subject of the agreement', body: 'The lessor rents to the renter the vehicle described in this contract or in its accompanying documents, for the agreed period and against the agreed price.' },
      { title: '3. Renter obligations', body: 'The renter shall use the vehicle carefully and lawfully, shall not allow unauthorized persons to use it and shall return it on time, in proper condition and with all related documents.' },
      { title: '4. Payment and deposit', body: 'Rental payment, deposit and any additional fees shall be paid according to the agreement between the parties. Any delay, damage, fine or extra cost incurred during use is borne by the renter.' },
      { title: '5. Accidents and damages', body: 'In the event of an accident, damage or theft, the renter must immediately notify the company and the competent authorities and obtain the relevant report. Otherwise, the renter bears full responsibility for the loss.' },
      { title: '6. Final provisions', body: 'The parties declare that they have read and understood this agreement and accept it voluntarily. For all matters not expressly regulated, the applicable law and company rules shall apply.' }
    ],
    clientSignature: 'Client Signature'
  },
  de: {
    companyTagline: 'Autovermietung',
    addressLabel: 'Adresse',
    phoneLabel: 'Telefon / Viber',
    businessNoLabel: 'Firmennummer',
    rentalAgreementTitle: 'Mietvertrag',
    rentalAgreementSubtitle: 'Fahrzeugvermietung',
    referenceNo: 'Referenz-Nr.',
    renterDetails: 'Mieterdaten',
    vehicleDetails: 'Fahrzeugdaten',
    rentalPeriodCharges: 'Mietdauer & Kosten',
    fullName: 'Vor- und Nachname',
    birthDate: 'Geburtsdatum',
    idPassport: 'Ausweis / Pass-Nr.',
    licenseNo: 'Führerschein-Nr.',
    residenceAddress: 'Wohnadresse',
    phoneMobile: 'Telefon / Mobil',
    makeModel: 'Marke & Modell',
    plateNo: 'Kennzeichen',
    productionYear: 'Baujahr',
    color: 'Farbe',
    fuelLevel: 'Kraftstoffstand',
    odometer: 'Kilometerstand',
    writeHere: '(Hier eintragen)',
    pickupDate: 'Abholdatum',
    pickupTime: 'Abholzeit',
    pickupPlace: 'Abholort',
    returnDate: 'Rückgabedatum',
    returnTime: 'Rückgabezeit',
    returnPlace: 'Rückgabeort',
    dailyPrice: 'Tagespreis',
    depositCash: 'Kaution',
    totalPrice: 'Gesamtpreis',
    termsTitle: 'Allgemeine Bedingungen',
    bookingTerms: [
      '<strong>Haftung:</strong> Der Mieter trägt die volle Verantwortung für das Fahrzeug von der Übernahme bis zur Rückgabe und ersetzt alle Schäden, die durch Fahrlässigkeit, Missbrauch oder Vertragsverletzung entstehen.',
      '<strong>Versicherung:</strong> Das Fahrzeug kann gemäß der aktiven Police versichert sein, der Mieter bleibt jedoch für Selbstbeteiligungen, Fahrlässigkeit, unbefugte Fahrer und Verstöße gegen gesetzliche Verfahren verantwortlich.',
      '<strong>Polizei und Meldung:</strong> Im Falle eines Unfalls, Schadens oder Diebstahls muss der Mieter unverzüglich das Unternehmen und die Polizei informieren. Ein Polizeibericht ist für jeden Versicherungsfall verpflichtend.',
      '<strong>Nutzungsbeschränkungen:</strong> Das Fahrzeug darf nicht außerhalb des genehmigten Gebiets, für Rennen, illegale Transporte, unter Alkohol- / Drogeneinfluss oder von nicht im Vertrag autorisierten Personen benutzt werden.',
      '<strong>Rückgabe des Fahrzeugs:</strong> Das Fahrzeug ist zum vereinbarten Datum, zur vereinbarten Uhrzeit und im vereinbarten Zustand samt Dokumenten, Schlüsseln und Ausstattung zurückzugeben. Verspätungen, fehlende Gegenstände oder Schäden werden zusätzlich berechnet.'
    ],
    acceptanceNote: 'Mit seiner Unterschrift bestätigt der Mieter, dass alle Bedingungen gelesen, verstanden und akzeptiert wurden.',
    companyRepresentative: 'Firmenvertreter',
    clientRenter: 'Kunde / Mieter',
    signPlaceholder: 'Name und Unterschrift',
    generatedFooter: 'Dieses Dokument stellt eine rechtsverbindliche Vereinbarung dar und wurde elektronisch vom System erstellt.',
    contractDate: 'Vertragsdatum',
    partyClient: 'Partei / Kunde',
    manualContractTitle: 'Mietvertrag / Vereinbarung',
    manualSections: [
      { title: '1. Vertragsparteien', body: 'Dieser Vertrag wird zwischen {company} als Vermieter und {client} als Mieter / anderer Vertragspartei geschlossen.' },
      { title: '2. Vertragsgegenstand', body: 'Der Vermieter überlässt dem Mieter das in diesem Vertrag oder den Begleitunterlagen beschriebene Fahrzeug für den vereinbarten Zeitraum und gegen den vereinbarten Preis.' },
      { title: '3. Pflichten des Mieters', body: 'Der Mieter verpflichtet sich, das Fahrzeug sorgfältig und gesetzeskonform zu nutzen, es keinen unbefugten Personen zu überlassen und es rechtzeitig, in ordnungsgemäßem Zustand und mit allen Unterlagen zurückzugeben.' },
      { title: '4. Zahlung und Kaution', body: 'Mietzahlung, Kaution und eventuelle Zusatzgebühren erfolgen gemäß der Vereinbarung der Parteien. Jede Verzögerung, jeder Schaden, jede Strafe oder jeder Zusatzaufwand während der Nutzung geht zu Lasten des Mieters.' },
      { title: '5. Unfälle und Schäden', body: 'Bei Unfall, Schaden oder Diebstahl hat der Mieter unverzüglich das Unternehmen und die zuständigen Behörden zu informieren und den entsprechenden Bericht zu beschaffen. Andernfalls trägt der Mieter die volle Verantwortung für den Schaden.' },
      { title: '6. Schlussbestimmungen', body: 'Die Parteien erklären, dass sie diesen Vertrag gelesen und verstanden haben und ihn freiwillig akzeptieren. Für nicht ausdrücklich geregelte Fragen gelten das anwendbare Recht und die Unternehmensregeln.' }
    ],
    clientSignature: 'Unterschrift des Kunden'
  },
  it: {
    companyTagline: 'Servizi Rent a Car',
    addressLabel: 'Indirizzo',
    phoneLabel: 'Telefono / Viber',
    businessNoLabel: 'Numero aziendale',
    rentalAgreementTitle: 'Contratto di noleggio',
    rentalAgreementSubtitle: 'Autorizzazione al noleggio del veicolo',
    referenceNo: 'N. Riferimento',
    renterDetails: 'Dati del cliente',
    vehicleDetails: 'Dati del veicolo',
    rentalPeriodCharges: 'Periodo di noleggio e costi',
    fullName: 'Nome e Cognome',
    birthDate: 'Data di nascita',
    idPassport: 'N. Documento / Passaporto',
    licenseNo: 'N. Patente',
    residenceAddress: 'Indirizzo di residenza',
    phoneMobile: 'Telefono / Cellulare',
    makeModel: 'Marca e Modello',
    plateNo: 'Targa',
    productionYear: 'Anno di produzione',
    color: 'Colore',
    fuelLevel: 'Livello carburante',
    odometer: 'Chilometraggio',
    writeHere: '(Scrivere qui)',
    pickupDate: 'Data ritiro',
    pickupTime: 'Ora ritiro',
    pickupPlace: 'Luogo ritiro',
    returnDate: 'Data restituzione',
    returnTime: 'Ora restituzione',
    returnPlace: 'Luogo restituzione',
    dailyPrice: 'Prezzo giornaliero',
    depositCash: 'Deposito',
    totalPrice: 'Prezzo totale',
    termsTitle: 'Termini e condizioni',
    bookingTerms: [
      '<strong>Responsabilità:</strong> Il cliente si assume piena responsabilità del veicolo dal momento del ritiro fino alla restituzione e risponde di ogni danno causato da negligenza, uso improprio o violazione del contratto.',
      '<strong>Assicurazione:</strong> Il veicolo può essere coperto dalla polizza attiva, tuttavia il cliente resta responsabile per franchigie, negligenza, conducenti non autorizzati e casi in cui non vengano seguite le procedure legali.',
      '<strong>Polizia e segnalazione:</strong> In caso di incidente, danno o furto, il cliente deve avvisare immediatamente la società e la polizia. Il verbale della polizia è obbligatorio per ogni richiesta assicurativa.',
      '<strong>Limitazioni d\'uso:</strong> Il veicolo non può essere utilizzato fuori dal territorio autorizzato, per gare, trasporti illegali, sotto l\'effetto di alcol / droghe o da persone non autorizzate dal presente accordo.',
      '<strong>Restituzione del veicolo:</strong> Il veicolo deve essere restituito alla data, ora e nelle condizioni concordate, con documenti, chiavi e accessori. Ritardi, elementi mancanti o danni comportano costi aggiuntivi.'
    ],
    acceptanceNote: 'Con la firma del presente contratto, il cliente conferma di aver letto, compreso e accettato tutte le condizioni.',
    companyRepresentative: 'Rappresentante della società',
    clientRenter: 'Cliente / Noleggiatore',
    signPlaceholder: 'Nome e Firma',
    generatedFooter: 'Questo documento costituisce un accordo legale ed è stato generato elettronicamente dal sistema.',
    contractDate: 'Data del contratto',
    partyClient: 'Parte / Cliente',
    manualContractTitle: 'Contratto / Accordo di noleggio',
    manualSections: [
      { title: '1. Parti contrattuali', body: 'Il presente accordo è stipulato tra {company} in qualità di locatore e {client} in qualità di noleggiatore / altra parte.' },
      { title: '2. Oggetto del contratto', body: 'Il locatore concede al noleggiatore il veicolo descritto nel presente contratto o nei documenti allegati, per il periodo e il corrispettivo concordati.' },
      { title: '3. Obblighi del noleggiatore', body: 'Il noleggiatore si impegna a utilizzare il veicolo con cura e nel rispetto della legge, a non affidarlo a persone non autorizzate e a restituirlo puntualmente, in buone condizioni e con tutta la documentazione.' },
      { title: '4. Pagamento e deposito', body: 'Il pagamento del noleggio, il deposito ed eventuali costi aggiuntivi sono dovuti secondo l\'accordo tra le parti. Ogni ritardo, danno, multa o costo extra sostenuto durante l\'uso è a carico del noleggiatore.' },
      { title: '5. Incidenti e danni', body: 'In caso di incidente, danno o furto, il noleggiatore deve informare immediatamente la società e le autorità competenti e ottenere il relativo verbale. In caso contrario, il noleggiatore risponde integralmente del danno.' },
      { title: '6. Disposizioni finali', body: 'Le parti dichiarano di aver letto e compreso il presente accordo e di accettarlo volontariamente. Per tutto quanto non espressamente disciplinato si applicano la legge vigente e le regole della società.' }
    ],
    clientSignature: 'Firma del Cliente'
  },
  fr: {
    companyTagline: 'Services de location de voitures',
    addressLabel: 'Adresse',
    phoneLabel: 'Téléphone / Viber',
    businessNoLabel: 'Numéro d\'entreprise',
    rentalAgreementTitle: 'Contrat de location',
    rentalAgreementSubtitle: 'Autorisation de location du véhicule',
    referenceNo: 'N° de Référence',
    renterDetails: 'Informations du client',
    vehicleDetails: 'Informations du véhicule',
    rentalPeriodCharges: 'Période de location et frais',
    fullName: 'Nom et prénom',
    birthDate: 'Date de naissance',
    idPassport: 'N° Carte / Passeport',
    licenseNo: 'N° Permis de conduire',
    residenceAddress: 'Adresse de résidence',
    phoneMobile: 'Téléphone / Mobile',
    makeModel: 'Marque et modèle',
    plateNo: 'Immatriculation',
    productionYear: 'Année de production',
    color: 'Couleur',
    fuelLevel: 'Niveau de carburant',
    odometer: 'Kilométrage',
    writeHere: '(Écrire ici)',
    pickupDate: 'Date de prise',
    pickupTime: 'Heure de prise',
    pickupPlace: 'Lieu de prise',
    returnDate: 'Date de retour',
    returnTime: 'Heure de retour',
    returnPlace: 'Lieu de retour',
    dailyPrice: 'Prix journalier',
    depositCash: 'Dépôt',
    totalPrice: 'Prix total',
    termsTitle: 'Conditions générales',
    bookingTerms: [
      '<strong>Responsabilité :</strong> Le locataire assume l\'entière responsabilité du véhicule de la prise en charge jusqu\'au retour et indemnise tout dommage causé par négligence, mauvaise utilisation ou violation du contrat.',
      '<strong>Assurance :</strong> Le véhicule peut être couvert par la police active, toutefois le locataire reste responsable des franchises, de la négligence, des conducteurs non autorisés et des cas où les procédures légales ne sont pas respectées.',
      '<strong>Police et déclaration :</strong> En cas d\'accident, de dommage ou de vol, le locataire doit immédiatement informer la société et la police. Un rapport de police est obligatoire pour toute demande d\'assurance.',
      '<strong>Restrictions d\'utilisation :</strong> Le véhicule ne peut pas être utilisé hors du territoire autorisé, pour des courses, des transports illégaux, sous l\'influence de l\'alcool / drogues ou par des personnes non autorisées par ce contrat.',
      '<strong>Restitution du véhicule :</strong> Le véhicule doit être restitué à la date, à l\'heure et dans l\'état convenus, avec tous les documents, clés et équipements. Les retards, éléments manquants ou dommages seront facturés en supplément.'
    ],
    acceptanceNote: 'En signant ce contrat, le client confirme avoir lu, compris et accepté l\'ensemble des conditions.',
    companyRepresentative: 'Représentant de la société',
    clientRenter: 'Client / Locataire',
    signPlaceholder: 'Nom et Signature',
    generatedFooter: 'Ce document constitue un accord juridique et a été généré électroniquement par le système.',
    contractDate: 'Date du contrat',
    partyClient: 'Partie / Client',
    manualContractTitle: 'Contrat / Accord de location',
    manualSections: [
      { title: '1. Parties contractantes', body: 'Le présent accord est conclu entre {company} en qualité de bailleur et {client} en qualité de locataire / autre partie.' },
      { title: '2. Objet du contrat', body: 'Le bailleur loue au locataire le véhicule décrit dans le présent contrat ou dans ses documents annexes, pour la période convenue et contre le prix convenu.' },
      { title: '3. Obligations du locataire', body: 'Le locataire s\'engage à utiliser le véhicule avec soin et conformément à la loi, à ne pas le confier à des personnes non autorisées et à le restituer à temps, en bon état et avec tous les documents nécessaires.' },
      { title: '4. Paiement et dépôt', body: 'Le paiement de la location, le dépôt et tous frais supplémentaires sont dus conformément à l\'accord entre les parties. Tout retard, dommage, amende ou coût supplémentaire supporté pendant l\'utilisation est à la charge du locataire.' },
      { title: '5. Accidents et dommages', body: 'En cas d\'accident, de dommage ou de vol, le locataire doit immédiatement informer la société et les autorités compétentes et obtenir le rapport correspondant. À défaut, il supporte l\'entière responsabilité du préjudice.' },
      { title: '6. Dispositions finales', body: 'Les parties déclarent avoir lu et compris le présent accord et l\'accepter volontairement. Pour toute question non expressément réglée, la loi applicable et les règles de la société s\'appliquent.' }
    ],
    clientSignature: 'Signature du Client'
  },
  hr: {
    companyTagline: 'Rent a Car usluge',
    addressLabel: 'Adresa',
    phoneLabel: 'Telefon / Viber',
    businessNoLabel: 'Poslovni broj',
    rentalAgreementTitle: 'Ugovor o najmu',
    rentalAgreementSubtitle: 'Odobrenje za najam vozila',
    referenceNo: 'Referentni broj',
    renterDetails: 'Podaci klijenta',
    vehicleDetails: 'Podaci o vozilu',
    rentalPeriodCharges: 'Razdoblje najma i troškovi',
    fullName: 'Ime i prezime',
    birthDate: 'Datum rođenja',
    idPassport: 'Br. osobne iskaznice / putovnice',
    licenseNo: 'Br. vozačke dozvole',
    residenceAddress: 'Adresa stanovanja',
    phoneMobile: 'Telefon / Mobitel',
    makeModel: 'Marka i model',
    plateNo: 'Registarske tablice',
    productionYear: 'Godina proizvodnje',
    color: 'Boja',
    fuelLevel: 'Razina goriva',
    odometer: 'Kilometraža',
    writeHere: '(Upišite ovdje)',
    pickupDate: 'Datum preuzimanja',
    pickupTime: 'Vrijeme preuzimanja',
    pickupPlace: 'Mjesto preuzimanja',
    returnDate: 'Datum povrata',
    returnTime: 'Vrijeme povrata',
    returnPlace: 'Mjesto povrata',
    dailyPrice: 'Dnevna cijena',
    depositCash: 'Polog',
    totalPrice: 'Ukupna cijena',
    termsTitle: 'Uvjeti i odredbe',
    bookingTerms: [
      '<strong>Odgovornost:</strong> Najmoprimac preuzima punu odgovornost za vozilo od trenutka preuzimanja do povrata i dužan je nadoknaditi svu štetu uzrokovanu nepažnjom, zlouporabom ili kršenjem ugovora.',
      '<strong>Osiguranje:</strong> Vozilo može biti pokriveno aktivnom policom osiguranja, ali najmoprimac ostaje odgovoran za franšizu, nemar, neovlaštene vozače i slučajeve kada se ne poštuju zakonske procedure.',
      '<strong>Policija i prijava:</strong> U slučaju nesreće, oštećenja ili krađe, najmoprimac mora odmah obavijestiti tvrtku i policiju. Policijski zapisnik obvezan je za svaki zahtjev prema osiguranju.',
      '<strong>Ograničenja korištenja:</strong> Vozilo se ne smije koristiti izvan odobrenog područja, za utrke, ilegalni prijevoz, pod utjecajem alkohola / droga ili od strane osoba koje nisu ovlaštene ovim ugovorom.',
      '<strong>Povrat vozila:</strong> Vozilo se mora vratiti na dogovoreni datum, vrijeme i u dogovorenom stanju, zajedno sa svim dokumentima, ključevima i opremom. Kašnjenja, nedostaci ili štete dodatno se naplaćuju.'
    ],
    acceptanceNote: 'Potpisom ovog ugovora klijent potvrđuje da je pročitao, razumio i prihvatio sve uvjete.',
    companyRepresentative: 'Predstavnik tvrtke',
    clientRenter: 'Klijent / Najmoprimac',
    signPlaceholder: 'Ime i potpis',
    generatedFooter: 'Ovaj dokument predstavlja pravni sporazum i elektronički je generiran sustavom.',
    contractDate: 'Datum ugovora',
    partyClient: 'Strana / Klijent',
    manualContractTitle: 'Ugovor / Sporazum o najmu',
    manualSections: [
      { title: '1. Ugovorne strane', body: 'Ovaj sporazum sklapaju {company} kao najmodavac i {client} kao najmoprimac / druga ugovorna strana.' },
      { title: '2. Predmet ugovora', body: 'Najmodavac daje najmoprimcu u najam vozilo opisano u ovom ugovoru ili pratećoj dokumentaciji, za dogovoreno razdoblje i uz dogovorenu naknadu.' },
      { title: '3. Obveze najmoprimca', body: 'Najmoprimac se obvezuje koristiti vozilo pažljivo i zakonito, ne davati ga neovlaštenim osobama te ga vratiti na vrijeme, u ispravnom stanju i sa svom pripadajućom dokumentacijom.' },
      { title: '4. Plaćanje i polog', body: 'Najamnina, polog i sve dodatne naknade plaćaju se prema dogovoru stranaka. Svako kašnjenje, šteta, kazna ili dodatni trošak nastao tijekom korištenja snosi najmoprimac.' },
      { title: '5. Nesreće i štete', body: 'U slučaju nesreće, oštećenja ili krađe, najmoprimac mora odmah obavijestiti tvrtku i nadležna tijela te pribaviti odgovarajuće izvješće. U protivnom snosi punu odgovornost za štetu.' },
      { title: '6. Završne odredbe', body: 'Strane izjavljuju da su pročitale i razumjele ovaj sporazum te ga dobrovoljno prihvaćaju. Na sva pitanja koja nisu izričito uređena primjenjuju se važeći propisi i pravila tvrtke.' }
    ],
    clientSignature: 'Potpis klijenta'
  }
};

const EXTRA_UI_TRANSLATIONS = {
  sq: {
    reserved: 'E rezervuar',
    reservation_details: 'Detajet e Rezervimit',
    client_info: 'Info Klienti',
    drivers_tab: 'Shoferët',
    comments_tab: 'Komentet',
    pickup_dropoff_location: 'Lokacioni i Marrjes/Dorëzimit',
    dropoff_location: 'Lokacioni i Dorëzimit',
    documents: 'Dokumentet',
    no_documents_available: 'Nuk ka dokumente të disponueshme',
    car_label: 'Vetura',
    name_label: 'Emri',
    total_label_short: 'Totali',
    paid_label: 'Paguar',
    remaining_label: 'Mbetja',
    no_payments_available: 'Nuk ka pagesa të disponueshme',
    main_driver: 'Shoferi kryesor',
    additional_driver: 'Shofer shtesë',
    no_additional_driver: 'Nuk ka shofer shtesë',
    notes_label: 'Shënime',
    yes_word: 'Po',
    no_word: 'Jo',
    client_word: 'Klienti',
    car_word: 'Vetura',
    total_days: 'Numri i Ditëve',
    deposit_amount: 'Shuma e Depozitës',
    flight_number: 'Numri i Fluturimit',
    daily_price_label: 'Çmimi Ditor',
    rental_price_label: 'Çmimi i Qirasë',
    total_payment_label: 'Pagesa Totale',
    personal_id: 'ID Personale',
    license_number: 'Numri i Patentës',
    no_comments_available: 'Nuk ka komente.',
  },
  en: {
    reserved: 'Reserved',
    reservation_details: 'Reservation Details',
    client_info: 'Client Info',
    drivers_tab: 'Drivers',
    comments_tab: 'Comments',
    pickup_dropoff_location: 'Pickup/Dropoff Location',
    dropoff_location: 'Dropoff Location',
    documents: 'Documents',
    no_documents_available: 'No documents available',
    car_label: 'Car',
    name_label: 'Name',
    total_label_short: 'Total',
    paid_label: 'Paid',
    remaining_label: 'Remaining',
    no_payments_available: 'No payments available',
    main_driver: 'Main Driver',
    additional_driver: 'Additional Driver',
    no_additional_driver: 'No additional driver',
    notes_label: 'Notes',
    yes_word: 'Yes',
    no_word: 'No',
    client_word: 'Client',
    car_word: 'Car',
    total_days: 'Total Days',
    deposit_amount: 'Deposit Amount',
    flight_number: 'Flight Number',
    daily_price_label: 'Daily Price',
    rental_price_label: 'Rental Price',
    total_payment_label: 'Total Payment',
    personal_id: 'Personal ID',
    license_number: 'License Number',
    no_comments_available: 'No comments available.',
  },
  de: {
    reserved: 'Reserviert',
    reservation_details: 'Reservierungsdetails',
    client_info: 'Kundeninfo',
    drivers_tab: 'Fahrer',
    comments_tab: 'Kommentare',
    pickup_dropoff_location: 'Abhol-/Rückgabeort',
    dropoff_location: 'Rückgabeort',
    documents: 'Dokumente',
    no_documents_available: 'Keine Dokumente verfügbar',
    car_label: 'Fahrzeug',
    name_label: 'Name',
    total_label_short: 'Gesamt',
    paid_label: 'Bezahlt',
    remaining_label: 'Offen',
    no_payments_available: 'Keine Zahlungen verfügbar',
    main_driver: 'Hauptfahrer',
    additional_driver: 'Zusatzfahrer',
    no_additional_driver: 'Kein Zusatzfahrer',
    notes_label: 'Notizen',
    yes_word: 'Ja',
    no_word: 'Nein',
    client_word: 'Kunde',
    car_word: 'Fahrzeug',
    total_days: 'Gesamttage',
    deposit_amount: 'Kautionsbetrag',
    flight_number: 'Flugnummer',
    daily_price_label: 'Tagespreis',
    rental_price_label: 'Mietpreis',
    total_payment_label: 'Gesamtzahlung',
    personal_id: 'Persönliche ID',
    license_number: 'Führerscheinnummer',
    no_comments_available: 'Keine Kommentare verfügbar.',
  },
  it: {
    reserved: 'Prenotato',
    reservation_details: 'Dettagli Prenotazione',
    client_info: 'Info Cliente',
    drivers_tab: 'Conducenti',
    comments_tab: 'Commenti',
    pickup_dropoff_location: 'Luogo Ritiro/Consegna',
    dropoff_location: 'Luogo di consegna',
    documents: 'Documenti',
    no_documents_available: 'Nessun documento disponibile',
    car_label: 'Auto',
    name_label: 'Nome',
    total_label_short: 'Totale',
    paid_label: 'Pagato',
    remaining_label: 'Residuo',
    no_payments_available: 'Nessun pagamento disponibile',
    main_driver: 'Conducente principale',
    additional_driver: 'Conducente aggiuntivo',
    no_additional_driver: 'Nessun conducente aggiuntivo',
    notes_label: 'Note',
    yes_word: 'Sì',
    no_word: 'No',
    client_word: 'Cliente',
    car_word: 'Auto',
    total_days: 'Giorni totali',
    deposit_amount: 'Importo deposito',
    flight_number: 'Numero volo',
    daily_price_label: 'Prezzo giornaliero',
    rental_price_label: 'Prezzo noleggio',
    total_payment_label: 'Pagamento totale',
    personal_id: 'ID personale',
    license_number: 'Numero patente',
    no_comments_available: 'Nessun commento disponibile.',
  },
  fr: {
    reserved: 'Réservé',
    reservation_details: 'Détails de la réservation',
    client_info: 'Infos client',
    drivers_tab: 'Conducteurs',
    comments_tab: 'Commentaires',
    pickup_dropoff_location: 'Lieu de prise/restitution',
    dropoff_location: 'Lieu de restitution',
    documents: 'Documents',
    no_documents_available: 'Aucun document disponible',
    car_label: 'Voiture',
    name_label: 'Nom',
    total_label_short: 'Total',
    paid_label: 'Payé',
    remaining_label: 'Restant',
    no_payments_available: 'Aucun paiement disponible',
    main_driver: 'Conducteur principal',
    additional_driver: 'Conducteur supplémentaire',
    no_additional_driver: 'Aucun conducteur supplémentaire',
    notes_label: 'Notes',
    yes_word: 'Oui',
    no_word: 'Non',
    client_word: 'Client',
    car_word: 'Voiture',
    total_days: 'Nombre de jours',
    deposit_amount: 'Montant de la caution',
    flight_number: 'Numéro de vol',
    daily_price_label: 'Prix journalier',
    rental_price_label: 'Prix de location',
    total_payment_label: 'Paiement total',
    personal_id: 'ID personnel',
    license_number: 'Numéro de permis',
    no_comments_available: 'Aucun commentaire disponible.',
  },
  hr: {
    reserved: 'Rezervirano',
    reservation_details: 'Detalji rezervacije',
    client_info: 'Podaci klijenta',
    drivers_tab: 'Vozači',
    comments_tab: 'Komentari',
    pickup_dropoff_location: 'Lokacija preuzimanja/povrata',
    dropoff_location: 'Lokacija povrata',
    documents: 'Dokumenti',
    no_documents_available: 'Nema dostupnih dokumenata',
    car_label: 'Vozilo',
    name_label: 'Ime',
    total_label_short: 'Ukupno',
    paid_label: 'Plaćeno',
    remaining_label: 'Preostalo',
    no_payments_available: 'Nema dostupnih uplata',
    main_driver: 'Glavni vozač',
    additional_driver: 'Dodatni vozač',
    no_additional_driver: 'Nema dodatnog vozača',
    notes_label: 'Napomene',
    yes_word: 'Da',
    no_word: 'Ne',
    client_word: 'Klijent',
    car_word: 'Vozilo',
    total_days: 'Ukupno dana',
    deposit_amount: 'Iznos pologa',
    flight_number: 'Broj leta',
    daily_price_label: 'Dnevna cijena',
    rental_price_label: 'Cijena najma',
    total_payment_label: 'Ukupno plaćanje',
    personal_id: 'Osobni ID',
    license_number: 'Broj vozačke dozvole',
    no_comments_available: 'Nema dostupnih komentara.',
  }
};

Object.keys(EXTRA_UI_TRANSLATIONS).forEach((lang) => {
  TRANSLATIONS[lang] = { ...(TRANSLATIONS[lang] || {}), ...EXTRA_UI_TRANSLATIONS[lang] };
});

const I18nContext = createContext({ language: 'sq', locale: 'sq-AL', t: (k) => k });
const useI18n = () => useContext(I18nContext);

const translate = (language, key, params = {}) => {
  const str = TRANSLATIONS?.[language]?.[key] ?? TRANSLATIONS?.sq?.[key] ?? TRANSLATIONS?.en?.[key] ?? key;
  return String(str).replace(/\{(\w+)\}/g, (_, k) => (params[k] ?? `{${k}}`));
};

// --- helper: fuel label (used in cars) ---
const trFuelLabel = (t, v) => {
  if (!v) return '';
  const vv = String(v).toLowerCase().trim();
  if (vv === 'benzine' || vv === 'benzinë' || vv === 'benzin' || vv === 'petrol' || vv === 'gasoline') return t('petrol');
  if (vv === 'dizel' || vv === 'diesel') return t('diesel');
  if (vv === 'elektrik' || vv === 'electric' || vv === 'ev') return t('electric');
  if (vv === 'hybrid' || vv === 'hibрид' || vv === 'ibrid' || vv === 'hibrid' || vv === 'hybride') return t('hybrid');
  return v;
};

// --- helper: transmission label (used in cars + alerts) ---
const trTransmissionLabel = (t, v) => {
  if (!v) return '';
  const vv = String(v).toLowerCase();
  if (vv === 'automatik' || vv === 'automatic') return t('automatic');
  if (vv === 'manuel' || vv === 'manual') return t('manual');
  return v;
};




// --- STORAGE HOOKS (optimized for Firestore collections, low reads) ---
function useBrowserStorage(key, initialValue) {
  const resolveInitialValue = () => (initialValue instanceof Function ? initialValue() : initialValue);
  const [storedValue, setStoredValue] = useState(resolveInitialValue);
  const hydratedRef = useRef(false);

  useEffect(() => {
    try {
      const localValue = window.localStorage.getItem(key);
      if (localValue) {
        setStoredValue(JSON.parse(localValue));
      } else {
        setStoredValue(resolveInitialValue());
      }
    } catch (error) {
      console.error(`Browser storage read failed for ${key}:`, error);
      setStoredValue(resolveInitialValue());
    } finally {
      hydratedRef.current = true;
    }
  }, [key]);

  useEffect(() => {
    if (!hydratedRef.current) return;
    try {
      window.localStorage.setItem(key, JSON.stringify(storedValue));
    } catch (error) {
      console.error(`Browser storage write failed for ${key}:`, error);
    }
  }, [key, storedValue]);

  const setValue = (value) => {
    setStoredValue((prev) => (value instanceof Function ? value(prev) : value));
  };

  return [storedValue, setValue];
}

function buildStableId(prefix = 'item') {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeCollectionItem(item, prefix) {
  if (!item || typeof item !== 'object') return { id: buildStableId(prefix), value: item };
  return item.id ? item : { ...item, id: buildStableId(prefix) };
}

function sanitizeFirestoreItem(item) {
  if (!item || typeof item !== 'object') return { value: item };
  const { id, ...rest } = item;
  return rest;
}

function useFirestoreDocument(key, docPath, initialValue) {
  const resolveInitialValue = () => (initialValue instanceof Function ? initialValue() : initialValue);
  const [storedValue, setStoredValue] = useState(resolveInitialValue);
  const hydratedRef = useRef(false);
  const lastSavedRef = useRef(JSON.stringify(resolveInitialValue()));
  const docRef = doc(db, ...docPath);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      const fallbackValue = resolveInitialValue();

      try {
        const localValue = window.localStorage.getItem(key);
        if (localValue && isMounted) {
          const parsed = JSON.parse(localValue);
          setStoredValue(parsed);
          lastSavedRef.current = JSON.stringify(parsed);
        }
      } catch (error) {
        console.error(`Local cache read failed for ${key}:`, error);
      }

      try {
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const remoteValue = snap.data()?.value;
          const normalized = remoteValue === undefined ? fallbackValue : remoteValue;
          if (isMounted) setStoredValue(normalized);
          window.localStorage.setItem(key, JSON.stringify(normalized));
          lastSavedRef.current = JSON.stringify(normalized);
        } else {
          const localValue = window.localStorage.getItem(key);
          const initialDocValue = localValue ? JSON.parse(localValue) : fallbackValue;
          if (isMounted) setStoredValue(initialDocValue);
          await setDoc(docRef, {
            key,
            value: initialDocValue,
            updatedAt: new Date().toISOString(),
          }, { merge: true });
          lastSavedRef.current = JSON.stringify(initialDocValue);
        }
      } catch (error) {
        console.error(`Firestore document load failed for ${key}:`, error);
        if (isMounted) setStoredValue(fallbackValue);
      } finally {
        hydratedRef.current = true;
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [key]);

  useEffect(() => {
    if (!hydratedRef.current) return;

    const timer = setTimeout(async () => {
      try {
        const serialized = JSON.stringify(storedValue);
        window.localStorage.setItem(key, serialized);
        if (serialized === lastSavedRef.current) return;
        await setDoc(docRef, {
          key,
          value: storedValue,
          updatedAt: new Date().toISOString(),
        }, { merge: true });
        lastSavedRef.current = serialized;
      } catch (error) {
        console.error(`Firestore document save failed for ${key}:`, error);
      }
    }, 700);

    return () => clearTimeout(timer);
  }, [key, storedValue]);

  const setValue = (value) => {
    setStoredValue((prev) => (value instanceof Function ? value(prev) : value));
  };

  return [storedValue, setValue];
}

function useFirestoreCollection(key, collectionName, initialValue = []) {
  const resolveInitialValue = () => (initialValue instanceof Function ? initialValue() : initialValue);
  const [storedValue, setStoredValue] = useState(resolveInitialValue);
  const hydratedRef = useRef(false);
  const collectionRef = collection(db, collectionName);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      const fallbackValue = Array.isArray(resolveInitialValue()) ? resolveInitialValue() : [];
      let normalizedLocal = fallbackValue;

      try {
        const localValue = window.localStorage.getItem(key);
        if (localValue) {
          const parsed = JSON.parse(localValue);
          normalizedLocal = Array.isArray(parsed)
            ? parsed.map((item) => normalizeCollectionItem(item, collectionName))
            : fallbackValue;

          if (isMounted) {
            setStoredValue(normalizedLocal);
          }
        }
      } catch (error) {
        console.error(`Local cache read failed for ${key}:`, error);
      }

      try {
        const snap = await getDocs(collectionRef);
        if (!isMounted) return;

        if (!snap.empty) {
          const remoteValue = snap.docs.map((d) => normalizeCollectionItem({ id: d.id, ...d.data() }, collectionName));
          setStoredValue(remoteValue);
          window.localStorage.setItem(key, JSON.stringify(remoteValue));
        } else {
          setStoredValue(normalizedLocal);
          window.localStorage.setItem(key, JSON.stringify(normalizedLocal));
        }
      } catch (error) {
        console.error(`Firestore collection load failed for ${key}:`, error);
        if (isMounted) setStoredValue(normalizedLocal);
      } finally {
        hydratedRef.current = true;
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [key, collectionName]);

  const setValue = (value) => {
    setStoredValue((prev) => {
      const resolved = value instanceof Function ? value(prev) : value;
      const normalized = Array.isArray(resolved)
        ? resolved.map((item) => normalizeCollectionItem(item, collectionName))
        : [];

      if (hydratedRef.current) {
        try {
          window.localStorage.setItem(key, JSON.stringify(normalized));
        } catch (error) {
          console.error(`Local cache save failed for ${key}:`, error);
        }
      }

      return normalized;
    });
  };

  return [storedValue, setValue];
}

const useLocalStorage = useBrowserStorage;

// =========================================================================
// --- KOMPONENTËT E FORMËS TË NXJERRË JASHTË (PËR TË RUAJTUR FOKUSIN) ---
// =========================================================================

const Input = ({ label, name, type = 'text', required = true, placeholder = '', icon: Icon, formData, setFormData, list, t: tProp }) => {
  // Allow passing t from parent, otherwise use i18n hook
  const { t: tHook } = useI18n();
  const tt = tProp || tHook || ((k) => k);
  const resolvedPlaceholder = (() => {
  // Translate placeholder key if possible; if missing, avoid showing raw keys like "*_placeholder".
  if (!placeholder) return '';
  const translated = tt(placeholder);
  if (translated !== placeholder) return translated;
  if (/_placeholder$/.test(placeholder)) return '';
  return placeholder;
})();

  return (
    <div className="flex flex-col w-full mb-4">
      {label && <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-1.5">{label}</label>}
      <div className="relative">
        {Icon && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400">
            <Icon size={18} />
          </div>
        )}
        <input 
          type={type} 
          required={required} 
          list={list}
          value={formData[name] !== undefined && formData[name] !== null ? formData[name] : ''} 
          placeholder={resolvedPlaceholder}
          onChange={(e) => setFormData({...formData, [name]: e.target.value})} 
          className={`w-full ${Icon ? 'pl-10' : 'px-4'} pr-10 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-slate-800 dark:text-white hover:border-indigo-300 dark:hover:border-indigo-700`} 
        />
      </div>
    </div>
  );
};

const Select = ({ label, name, options, icon: Icon, required = false, formData, setFormData }) => (
  <div className="flex flex-col w-full mb-4">
    {label && <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-1.5">{label}</label>}
    <div className="relative">
      {Icon && (
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 z-10">
          <Icon size={18} />
        </div>
      )}
      <select 
        required={required}
        value={formData[name] !== undefined && formData[name] !== null ? formData[name] : ''} 
        onChange={(e) => setFormData({...formData, [name]: e.target.value})}
        className={`w-full ${Icon ? 'pl-10' : 'px-4'} pr-10 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-slate-800 dark:text-white appearance-none cursor-pointer hover:border-indigo-300 dark:hover:border-indigo-700 disabled:opacity-50`}
      >
        {options.map(opt => (
          typeof opt === 'string' ? 
            <option key={opt} value={opt}>{opt}</option> : 
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>{opt.label}</option>
        ))}
      </select>
      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 pointer-events-none">
        <ChevronDown size={18} />
      </div>
    </div>
  </div>
);


// --- TIME OPTIONS (00:00 - 23:30) ---
// Përdoret për të zëvendësuar <input type="time"> (që varet nga sistemi / AM-PM)
const TIME_OPTIONS = (() => {
  const out = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      out.push(`${hh}:${mm}`);
    }
  }
  return out;
})();

const TimeSelect = ({ label, value, onChange, required = false, className = '', hint }) => {
  const { t } = useI18n();
  return (
    <div className={`flex flex-col w-full ${label ? 'mb-4' : ''}`}>
      {label && (
        <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-1.5">
          {label}
        </label>
      )}
      <div className="relative">
        <select
          required={required}
          value={value || ''}
          onChange={(e) => onChange?.(e.target.value)}
          className={`w-full px-4 py-2.5 pr-10 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-slate-800 dark:text-white appearance-none cursor-pointer hover:border-indigo-300 dark:hover:border-indigo-700 font-semibold tabular-nums ${className}`}
        >
          <option value="" disabled>{t('select_time')}</option>
          {TIME_OPTIONS.map((timeOpt) => (
            <option key={timeOpt} value={timeOpt}>{timeOpt}</option>
          ))}
        </select>
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 pointer-events-none">
          <ChevronDown size={18} />
        </div>
        {hint && <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{hint}</p>}
      </div>
    </div>
  );
};

const Textarea = ({ label, name, rows = 3, placeholder = '', formData, setFormData, placeholderKey }) => {
  const { t } = useI18n();
  const ph = (() => {
    const raw = placeholder || (placeholderKey ? t(placeholderKey) : '');
    if (placeholderKey && raw === placeholderKey) return '';
    return raw;
  })();
  return (
    <div className="flex flex-col w-full mb-4">
      {label && (
        <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-1.5">
          {label}
        </label>
      )}
      <textarea
        value={formData[name] !== undefined && formData[name] !== null ? formData[name] : ''}
        onChange={(e) => setFormData({ ...formData, [name]: e.target.value })}
        rows={rows}
        placeholder={ph}
        className="w-full px-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-slate-800 dark:text-white resize-none hover:border-indigo-300 dark:hover:border-indigo-700"
      />
    </div>
  );
};

const Checkbox = ({ label, name, inline = false, formData, setFormData }) => (
  <div className={`flex items-center space-x-2 w-full ${inline ? '' : 'p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl mb-4'}`}>
    <input 
      type="checkbox" 
      id={name} 
      checked={formData[name] || false} 
      onChange={(e) => setFormData({...formData, [name]: e.target.checked})} 
      className="w-5 h-5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer" 
    />
    <label htmlFor={name} className="text-base font-bold text-slate-700 dark:text-slate-300 cursor-pointer select-none">{label}</label>
  </div>
);

const FileUpload = ({ label, name, accept = "image/*", hint = "PNG / JPG", formData, setFormData, showToast }) => {
  const { t } = useI18n();
  const [fileMeta, setFileMeta] = useState({ name: '', size: '' });
  const fileValue = formData[name] || '';
  const hasFile = typeof fileValue === 'string' && fileValue.length > 0;
  const isImage = hasFile && fileValue.startsWith('data:image');

  const formatFileSize = (size) => {
    if (!size || Number.isNaN(size)) return '';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const removeFile = () => {
    setFormData({ ...formData, [name]: '' });
    setFileMeta({ name: '', size: '' });
    showToast('File removed', 'info');
  };

  return (
    <div className="flex flex-col w-full mb-4">
      {label && <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-1.5">{label}</label>}

      <label className="flex items-center justify-between gap-3 p-3 border border-slate-200 dark:border-slate-700 rounded-xl cursor-pointer bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
        <div className="flex items-center space-x-3 overflow-hidden min-w-0">
          <div className={`w-11 h-11 flex-shrink-0 rounded-full flex items-center justify-center border ${hasFile ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/30 dark:border-emerald-800' : 'bg-slate-50 border-slate-100 dark:bg-slate-800 dark:border-slate-700'}`}>
            {hasFile ? <CheckCircle className="text-emerald-500" size={18} /> : <Upload className="text-slate-400" size={20} />}
          </div>

          <div className="truncate min-w-0">
            <p className="text-base font-bold text-slate-800 dark:text-white truncate">{hasFile ? t('file_uploaded') : t('click_upload')}</p>
            <p className="text-xs text-slate-500 truncate">
              {fileMeta.name ? `${fileMeta.name}${fileMeta.size ? ` • ${fileMeta.size}` : ''}` : hint}
            </p>
          </div>
        </div>

        <div className="px-3 py-1.5 flex-shrink-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-base font-bold text-slate-600 dark:text-slate-300 shadow-sm ml-2">
          {t('choose')}
        </div>

        <input
          type="file"
          className="hidden"
          accept={accept}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              const reader = new FileReader();
              reader.onload = (ev) => {
                setFormData({ ...formData, [name]: ev.target.result });
                setFileMeta({ name: file.name, size: formatFileSize(file.size) });
                showToast(t('file_uploaded'), 'info');
              };
              reader.readAsDataURL(file);
            }
          }}
        />
      </label>

      {hasFile && (
        <div className="mt-3 p-3 border border-emerald-200 dark:border-emerald-800/70 rounded-xl bg-emerald-50/70 dark:bg-emerald-900/10">
          <div className="flex items-start gap-3">
            {isImage ? (
              <img
                src={fileValue}
                alt="Preview"
                className="w-24 h-24 object-cover rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm"
              />
            ) : (
              <div className="w-24 h-24 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center justify-center text-slate-400">
                <FileText size={26} />
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-xs font-bold mb-2">
                <CheckCircle size={14} /> Uploaded successfully
              </div>
              <p className="text-sm font-bold text-slate-800 dark:text-white truncate">{fileMeta.name || 'Uploaded file'}</p>
              <p className="text-xs text-slate-500 mt-1">{fileMeta.size || hint}</p>

              <div className="mt-3 flex gap-2 flex-wrap">
                {isImage && (
                  <a
                    href={fileValue}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-1.5 rounded-lg text-sm font-semibold border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                  >
                    View photo
                  </a>
                )}
                <button
                  type="button"
                  onClick={removeFile}
                  className="px-3 py-1.5 rounded-lg text-sm font-semibold border border-rose-200 dark:border-rose-800 bg-white dark:bg-slate-800 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FormSection = ({ title, icon: Icon, children, defaultOpen = true }) => (
  <details open={defaultOpen} className="border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 mb-4 group relative">
    <summary className="font-bold text-slate-800 dark:text-white p-4 bg-slate-50 dark:bg-slate-700/50 cursor-pointer flex justify-between items-center select-none rounded-xl group-open:rounded-b-none">
      <div className="flex items-center space-x-2">{Icon && <Icon size={18} className="text-indigo-500" />}<span>{title}</span></div>
      <ChevronDown size={18} />
    </summary>
    <div className="p-5 relative">{children}</div>
  </details>
);

const SearchableClientSelect = ({ label, name, placeholder = undefined, clients, formData, setFormData }) => {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const clientOptions = clients.map(c => ({ value: c.id, label: c.name, phone: c.phone, email: c.email, avatar: c.avatar, address: c.address, rentals: c.rentals, totalSpent: c.totalSpent }));
  const selectedOption = clientOptions.find(opt => String(opt.value) === String(formData[name]));

  useEffect(() => {
    if (!isOpen && selectedOption) setSearch(selectedOption.label);
    else if (!isOpen && !selectedOption) setSearch('');
  }, [isOpen, selectedOption]);

  const filteredOptions = clientOptions.filter(opt => opt.label.toLowerCase().includes(search.toLowerCase()) || (opt.phone && opt.phone.toLowerCase().includes(search.toLowerCase())) || (opt.email && opt.email.toLowerCase().includes(search.toLowerCase())));

  return (
    <div className="flex flex-col w-full relative z-[60] mb-4">
      {label && <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-1.5">{label}</label>}
      <div className="relative">
        <input type="text" placeholder={placeholder ? (t(placeholder) !== placeholder ? t(placeholder) : placeholder) : t('search_client_placeholder')} value={search} onChange={(e) => { setSearch(e.target.value); setIsOpen(true); if (formData[name]) setFormData({...formData, [name]: ''}); }} onFocus={() => setIsOpen(true)} className="w-full px-4 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-slate-800 dark:text-white" />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2"><ChevronDown size={18} className="text-slate-400" /></div>
      </div>
      {isOpen && (
        <>
          <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-[0_10px_40px_-15px_rgba(0,0,0,0.3)] z-[100] max-h-64 overflow-y-auto p-2 animate-in slide-in-from-top-2 fade-in duration-200">
            {filteredOptions.length > 0 ? (
              filteredOptions.map(opt => (
                <div key={opt.value} onClick={() => { setFormData({...formData, [name]: opt.value}); setSearch(opt.label); setIsOpen(false); }} className={`p-3 rounded-xl cursor-pointer transition-all mb-1 ${String(formData[name]) === String(opt.value) ? 'bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50 border border-transparent'}`}>
                  <div className="flex items-center space-x-3">
                    {opt.avatar ? <img src={opt.avatar} alt={opt.label} className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center"><User size={18} className="text-indigo-600 dark:text-indigo-400" /></div>}
                    <div className="flex-1">
                      <p className="font-bold text-slate-800 dark:text-white">{opt.label}</p>
                      <div className="flex items-center space-x-2 text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        {opt.phone && <span>{opt.phone}</span>}
                        {opt.email && <><span>•</span><span>{opt.email}</span></>}
                      </div>
                    </div>
                    {String(formData[name]) === String(opt.value) && <Check size={16} className="text-indigo-600" />}
                  </div>
                </div>
              ))
            ) : (<div className="p-4 text-center"><p className="text-sm text-slate-500">{t('no_client_found')}</p></div>)}
          </div>
          <div className="fixed inset-0 z-[50]" onClick={() => setIsOpen(false)} />
        </>
      )}
    </div>
  );
};

// --- KOMPONENTI SWITCH PËR PROFILIN ---
const SwitchToggle = ({ checked, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2 ${
      checked ? 'bg-indigo-600' : 'bg-slate-300 dark:bg-slate-600'
    }`}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </button>
);

// =========================================================================
// --- GLOBAL BANNERS (NJOFTIME NË KRYE TË FAQES) ---
// =========================================================================
const GlobalBannerBar = ({ alerts = [], onDismiss }) => {
  const { t } = useI18n();
  if (!alerts || alerts.length === 0) return null;

  const styleByType = {
    info:   { wrap: 'bg-blue-50 border-blue-200 dark:bg-blue-900/25 dark:border-blue-800', text: 'text-blue-800 dark:text-blue-200' },
    warning:{ wrap: 'bg-amber-50 border-amber-200 dark:bg-amber-900/25 dark:border-amber-800', text: 'text-amber-900 dark:text-amber-200' },
    danger: { wrap: 'bg-red-50 border-red-200 dark:bg-red-900/25 dark:border-red-800', text: 'text-red-900 dark:text-red-200' },
    success:{ wrap: 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/25 dark:border-emerald-800', text: 'text-emerald-900 dark:text-emerald-200' },
  };

  return (
    <div className="mt-3 space-y-2">
      {alerts.slice(0, 4).map(a => {
        const Icon = a.icon || Info;
        const s = styleByType[a.type] || styleByType.info;
        return (
          <div key={a.id} className={`w-full border rounded-2xl px-4 py-3 flex items-start gap-3 shadow-sm ${s.wrap}`}>
            <div className="mt-0.5">
              <Icon size={18} className={s.text} />
            </div>
            <div className="flex-1">
              <p className={`font-extrabold ${s.text}`}>{a.title}</p>
              <p className={`text-sm font-medium ${s.text} opacity-90`}>{a.message}</p>
            </div>
            <button
              type="button"
              onClick={() => onDismiss?.(a.id)}
              className="p-1.5 rounded-xl hover:bg-black/5 dark:hover:bg-white/5 transition"
              title={t("close")}
            >
              <X size={18} className={s.text} />
            </button>
          </div>
        );
      })}
    </div>
  );
};



const SectionHeader = ({ title, subtitle, onAdd, searchTerm, setSearchTerm, viewMode, setViewMode, actions, t }) => (
  <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
    <div>
      <h2 className="text-3xl font-extrabold text-slate-900 text-slate-800 dark:text-white tracking-tight">{title}</h2>
      <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mt-1">{subtitle}</p>
    </div>
    <div className="flex items-center space-x-3">
      {setSearchTerm !== undefined && (
        <div className="relative group">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={20} />
          <input 
            type="text" 
            placeholder={t('search')} 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 w-full md:w-72 shadow-sm transition-all text-slate-800 dark:text-white hover:border-indigo-300 dark:hover:border-indigo-700" 
          />
        </div>
      )}
      {setViewMode !== undefined && (
        <button 
          onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
          className="p-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
        >
          {viewMode === 'grid' ? <List size={20} className="text-slate-600 dark:text-slate-400" /> : <Grid size={20} className="text-slate-600 dark:text-slate-400" />}
        </button>
      )}
      {actions}
      {onAdd && (
        <button 
          onClick={onAdd} 
          className="group relative bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-5 py-2.5 rounded-xl flex items-center space-x-2 hover:shadow-lg hover:shadow-indigo-500/30 transition-all font-medium overflow-hidden"
        >
          <span className="absolute inset-0 bg-white/20 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"></span>
          <Plus size={20} className="relative z-10" />
          <span className="relative z-10 hidden sm:inline">{t('add')}</span>
        </button>
      )}
    </div>
  </div>
);

const MenuItem = ({ icon: Icon, label, id, badge, rightIcon: RightIcon, activeMenu, setActiveMenu, setSearchTerm, setAdvancedFilters, isSidebarCollapsed = false }) => {
  const isActive = activeMenu === id;

  return (
    <button
      onClick={() => {
        setActiveMenu(id);
        setSearchTerm('');
        if (typeof setAdvancedFilters === 'function') {
          setAdvancedFilters({ client: '', method: '', status: '', category: '', startDate: '', endDate: '', search: '' });
        }
      }}
      className={`group relative w-full flex items-center ${isSidebarCollapsed ? 'justify-center px-2.5' : 'justify-start px-3.5 lg:px-4'} py-3 lg:py-3.5 rounded-2xl transition-all duration-200 border ${
        isActive
          ? 'bg-white/10 text-white border-white/10 shadow-[0_10px_25px_rgba(15,23,42,0.18)]'
          : 'text-slate-300 border-transparent hover:text-white hover:bg-white/5 hover:border-white/10'
      }`}
      title={isSidebarCollapsed ? label : ''}
    >
      {isActive && !isSidebarCollapsed && (
        <span className="absolute left-0 top-2.5 bottom-2.5 w-1 rounded-r-full bg-gradient-to-b from-indigo-400 to-blue-500 shadow-[0_0_14px_rgba(99,102,241,0.45)]" />
      )}

      <div className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-3.5'} w-full min-w-0`}>
        <Icon
          size={20}
          strokeWidth={2.1}
          className={`${isActive ? 'text-white' : 'text-slate-400 group-hover:text-white'} transition-colors flex-shrink-0 drop-shadow-sm`}
        />

        {!isSidebarCollapsed && (
          <>
            <span className={`text-[14px] lg:text-[15.5px] font-semibold tracking-[0.01em] transition-colors truncate ${isActive ? 'text-white' : 'text-slate-200 group-hover:text-white'}`}>
              {label}
            </span>

            <div className="ml-auto flex items-center gap-2">
              {badge ? (
                <span className={`min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-bold flex items-center justify-center ${
                  isActive ? 'bg-indigo-500 text-white shadow-sm' : 'bg-white/8 text-slate-200'
                }`}>
                  {badge}
                </span>
              ) : null}
              {RightIcon ? (
                <span className={`${isActive ? 'text-white' : 'text-slate-400 group-hover:text-white'} transition-colors`}>
                  <RightIcon size={16} strokeWidth={2.2} />
                </span>
              ) : null}
            </div>
          </>
        )}
      </div>

      {isActive && isSidebarCollapsed && (
        <span className="absolute right-0 top-2.5 bottom-2.5 w-1 rounded-l-full bg-gradient-to-b from-indigo-400 to-blue-500 shadow-[0_0_14px_rgba(99,102,241,0.45)]" />
      )}
    </button>
  );
};

// =========================================================================
// --- FILTER BAR COMPONENT ---
// =========================================================================

const FilterBar = ({ config, filters, setFilters, t }) => {
  const [isOpen, setIsOpen] = useState(true);

  const handleReset = () => {
    setFilters({ client: '', method: '', status: '', category: '', startDate: '', endDate: '', search: '' });
  };

  return (
    <div className="mb-6 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
      <div className="flex items-center p-3 border-b border-slate-100 dark:border-slate-700 gap-3">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-lg text-base font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-50 transition"
        >
          <Filter size={16} />
          <span>{t('filter')}</span>
          {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        <button className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-lg text-base font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-50 transition">
          <LayoutDashboard size={16} />
          <span>{t('bulk')}</span>
          <ChevronDown size={16} />
        </button>
      </div>

      {isOpen && (
        <div className="p-5 animate-in slide-in-from-top-2 duration-200">
          <div className="flex flex-wrap md:flex-nowrap gap-4 mb-5">
            {config.map(field => (
              <div key={field.name} className={`flex flex-col ${field.type === 'dateRange' ? 'w-full md:w-[35%]' : 'w-full md:w-[20%]'}`}>
                <label className="text-base font-bold text-slate-700 dark:text-slate-300 mb-2">{field.label}</label>
                
                {field.type === 'select' && (
                  <div className="relative">
                    <select
                      value={filters[field.name] || ''}
                      onChange={e => setFilters({...filters, [field.name]: e.target.value})}
                      className="w-full px-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-lg text-sm font-medium outline-none focus:ring-2 focus:ring-indigo-500 appearance-none text-slate-600 dark:text-slate-300 cursor-pointer"
                    >
                      <option value="">{t("select")}</option>
                      {field.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                )}

                {field.type === 'text' && (
                  <input
                    type="text"
                    placeholder={field.placeholder || t('search')}
                    value={filters[field.name] || ''}
                    onChange={e => setFilters({...filters, [field.name]: e.target.value})}
                    className="w-full px-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-lg text-sm font-medium outline-none focus:ring-2 focus:ring-indigo-500 text-slate-600 dark:text-slate-300"
                  />
                )}

                {field.type === 'dateRange' && (
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <input 
                        type="date" 
                        value={filters.startDate || ''} 
                        onChange={e => setFilters({...filters, startDate: e.target.value})} 
                        className="w-full px-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-lg text-sm font-medium outline-none focus:ring-2 focus:ring-indigo-500 text-slate-600 dark:text-slate-300 [&::-webkit-calendar-picker-indicator]:opacity-50" 
                      />
                    </div>
                    <div className="relative flex-1">
                      <input 
                        type="date" 
                        value={filters.endDate || ''} 
                        onChange={e => setFilters({...filters, endDate: e.target.value})} 
                        className="w-full px-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-lg text-sm font-medium outline-none focus:ring-2 focus:ring-indigo-500 text-slate-600 dark:text-slate-300 [&::-webkit-calendar-picker-indicator]:opacity-50" 
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex gap-3">
            <button className="px-6 py-2 bg-black dark:bg-slate-700 text-white text-base font-bold rounded-full hover:bg-slate-800 transition shadow-sm">{t('search')}</button>
            <button
              onClick={handleReset}
              className="px-6 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-200 text-base font-bold rounded-full hover:bg-slate-50 transition shadow-sm"
            >{t('reset')}</button>
          </div>
        </div>
      )}
    </div>
  );
};


// =========================================================================
// --- APP KRYESOR ---
// =========================================================================

const App = () => {
  // --- GOOGLE CALENDAR SETUP (Desktop OAuth) ---
  useEffect(() => {
    let mounted = true;

    const syncGoogleCalendarStatus = async () => {
      try {
        if (!window.api?.googleCalendarStatus) return;
        const status = await window.api.googleCalendarStatus();
        if (!mounted || !status) return;

        if (status.connected && !profileNotificationPrefs.googleSync) {
          setProfileNotificationPrefs((prev) => ({ ...prev, googleSync: true }));
        } else if (!status.connected && profileNotificationPrefs.googleSync) {
          setProfileNotificationPrefs((prev) => ({ ...prev, googleSync: false }));
        }
      } catch (error) {
        console.error('Failed to load Google Calendar status:', error);
      }
    };

    syncGoogleCalendarStatus();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    document.title = 'AutoRent - Rent A Car';

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
        <defs>
          <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stop-color="#2563eb"/>
            <stop offset="100%" stop-color="#0f172a"/>
          </linearGradient>
        </defs>
        <rect width="64" height="64" rx="16" fill="url(#g)"/>
        <path d="M15 38l4-10c1-2 3-4 6-4h14c3 0 5 2 6 4l4 10" fill="none" stroke="#fff" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M14 39h36v6a4 4 0 0 1-4 4h-4v-4H22v4h-4a4 4 0 0 1-4-4z" fill="#fff" opacity="0.95"/>
        <circle cx="22" cy="43" r="3" fill="#2563eb"/>
        <circle cx="42" cy="43" r="3" fill="#2563eb"/>
        <text x="32" y="18" text-anchor="middle" font-family="Arial, sans-serif" font-size="12" font-weight="700" fill="#fff">AR</text>
      </svg>`;

    const faviconHref = `data:image/svg+xml,${encodeURIComponent(svg)}`;
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'icon');
      document.head.appendChild(link);
    }
    link.setAttribute('type', 'image/svg+xml');
    link.setAttribute('href', faviconHref);
  }, []);

  const handleGoogleLogin = async () => {
    try {
      if (!window.api?.googleCalendarConnect) {
        showToast('Google Calendar nuk është i disponueshëm në këtë version të aplikacionit.', 'error');
        return;
      }

      const result = await window.api.googleCalendarConnect();
      if (result?.success) {
        setProfileNotificationPrefs((prev) => ({ ...prev, googleSync: true }));
        showToast(result.message || 'U lidh me Google Calendar me sukses!', 'success');
      } else {
        showToast(result?.message || 'Lidhja me Google Calendar dështoi.', 'error');
      }
    } catch (error) {
      console.error('Google Calendar connect failed:', error);
      showToast(error?.message || 'Gabim gjatë lidhjes me Google Calendar.', 'error');
    }
  };

  const addBookingToCalendar = async (bookingData) => {
    try {
      if (!window.api?.googleCalendarAddEvent) return;
      const result = await window.api.googleCalendarAddEvent({
        summary: `Rezervim: ${bookingData.vehicle || bookingData.car || ''} - ${bookingData.client || ''}`.trim(),
        description: `Klienti: ${bookingData.client || ''}\nTel: ${bookingData.phone || bookingData.newClientPhone || ''}\nÇmimi Total: ${bookingData.total || bookingData.totalPrice || 0}€\nShënime: ${bookingData.notes || "S'ka"}`,
        startDate: bookingData.startDate,
        startTime: bookingData.startTime || '09:00',
        endDate: bookingData.endDate,
        endTime: bookingData.endTime || '09:00',
        timeZone: 'Europe/Belgrade'
      });

      if (result?.success) {
        showToast('Rezervimi u ruajt edhe në Google Calendar!', 'success');
      } else if (result?.message) {
        showToast(result.message, 'error');
      }
    } catch (error) {
      console.error('Gabim gjatë shtimit në kalendar:', error);
    }
  };

  useEffect(() => {
    if (!window.api?.onAppUpdate) return undefined;

    const unsubscribe = window.api.onAppUpdate((payload) => {
      if (!payload?.type) return;

      if (payload.type === 'checking-for-update') {
        setUpdateState((prev) => ({
          ...prev,
          visible: true,
          stage: 'checking',
          message: 'Po kontrollohen përditësimet...'
        }));
        return;
      }

      if (payload.type === 'update-available') {
        setUpdateState({
          visible: true,
          stage: 'downloading',
          version: payload.version || '',
          percent: 0,
          transferred: '',
          total: '',
          bytesPerSecond: 0,
          message: `Versioni i ri ${payload.version || ''} po shkarkohet në sfond.`
        });
        showToast(`Update ${payload.version || ''} u gjet dhe po shkarkohet.`, 'success', 3200);
        return;
      }

      if (payload.type === 'download-progress') {
        setUpdateState((prev) => ({
          ...prev,
          visible: true,
          stage: 'downloading',
          percent: Number(payload.percent || 0),
          transferred: payload.transferred || '',
          total: payload.total || '',
          bytesPerSecond: Number(payload.bytesPerSecond || 0),
          message: 'Po shkarkohet versioni i ri...'
        }));
        return;
      }

      if (payload.type === 'update-downloaded') {
        setUpdateState((prev) => ({
          ...prev,
          visible: true,
          stage: 'ready',
          version: payload.version || prev.version || '',
          percent: 100,
          message: `Versioni i ri ${payload.version || prev.version || ''} u shkarkua me sukses.`
        }));
        showToast(`Update ${payload.version || ''} është gati për instalim.`, 'success', 3500);
        return;
      }

      if (payload.type === 'update-not-available') {
        setUpdateState((prev) => ({
          ...prev,
          visible: false,
          stage: 'idle',
          message: ''
        }));
        return;
      }

      if (payload.type === 'error') {
        setUpdateState((prev) => ({
          ...prev,
          visible: false,
          stage: 'idle',
          message: ''
        }));
        showToast(payload.message || 'Kontrolli i update dështoi.', 'error', 4500);
      }
    });

    return unsubscribe;
  }, []);

  const formatUpdateBytes = (bytes) => {
    const value = Number(bytes || 0);
    if (!value) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const power = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
    const size = value / (1024 ** power);
    return `${size >= 100 ? Math.round(size) : size.toFixed(size >= 10 ? 1 : 2)} ${units[power]}`;
  };

  const handleInstallUpdateNow = async () => {
    try {
      if (!window.api?.installAppUpdate) return;
      await window.api.installAppUpdate();
    } catch (error) {
      showToast(error?.message || 'Instalimi i update dështoi.', 'error');
    }
  };

  const handleLaterUpdate = () => {
    setUpdateState((prev) => ({ ...prev, visible: false }));
  };

  // --- AUTH STATE (Tani ruan loginin edhe pas refresh) ---
  const [isAuthenticated, setIsAuthenticated] = useBrowserStorage('rac_is_authenticated', false);
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [adminPassword, setAdminPassword] = useBrowserStorage('rac_admin_password', 'admin123'); 
  
  const [user, setUser] = useBrowserStorage('rac_user_profile', { 
    id: 1, name: 'Administrator', username: 'admin_autorent', role: 'Admin', access: 'everything', email: 'admin@autorent.com', phone: '+383 44 000 000',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100'
  });
  
  // --- THEME & UI STATES ---
  const [isDarkMode, setIsDarkMode] = useBrowserStorage('rac_dark_mode', false);
  const [language, setLanguage] = useBrowserStorage('rac_language', 'sq');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [bookingOpsDate, setBookingOpsDate] = useState(() => new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [updateState, setUpdateState] = useState({
    visible: false,
    stage: 'idle',
    version: '',
    percent: 0,
    transferred: '',
    total: '',
    bytesPerSecond: 0,
    message: ''
  });
  const [viewMode, setViewMode] = useState('grid');
  const [showNotificationsPanel, setShowNotificationsPanel] = useState(false); 
  
  // Advanced Filter State
  const defaultFilters = { client: '', method: '', status: '', category: '', startDate: '', endDate: '', search: '' };
  const [advancedFilters, setAdvancedFilters] = useState(defaultFilters);

  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [activeMenu]);

  // --- ADVANCED REPORT RANGE (Nga - Deri) ---
  const getTodayISO = () => new Date().toISOString().slice(0, 10);
  const getMonthStartISO = () => {
    const d = new Date();
    const first = new Date(d.getFullYear(), d.getMonth(), 1);
    return first.toISOString().slice(0, 10);
  };
  const [reportRange, setReportRange] = useState({ from: getMonthStartISO(), to: getTodayISO() });



  // --- PROFILE SPECIFIC STATES ---
  const [activeProfileTab, setActiveProfileTab] = useState('detajet');
  const [activeClientTab, setActiveClientTab] = useState('details');
  const [activeCarTab, setActiveCarTab] = useState('details');
  const [activeBookingTab, setActiveBookingTab] = useState('details');
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editProfileForm, setEditProfileForm] = useState({});
  
  const [profileNotificationPrefs, setProfileNotificationPrefs] = useBrowserStorage('rac_notif_prefs', {
    makinaShtua: true, makinaPerditesua: false, makinaArkivua: true, makinaFshi: true,
    klientiPerditesua: true, shpenzimiShtua: true, shpenzimiPerditesua: true, shpenzimiFshi: true,
    pagesaShtua: true, pagesaPerditesua: true, pagesaFshi: true,
    rezervimiArkivua: true, rezervimiFshi: true, rezervimiShtua: false, rezervimiPerditesua: false,
    googleSync: false
  });
  const [profileReminders, setProfileReminders] = useBrowserStorage('rac_reminder_prefs', {
    permDitore: true, permDitoreKoha: '09:00',
    kthimMakine: true, kthimMakineMin: 60,
    marrjeMakine: true, marrjeMakineMin: 60,
    kujteseRegjistrim: true, kujteseRegjistrimDite: 7
  });
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [showPassword, setShowPassword] = useState({ current: false, new: false, confirm: false });

  // --- DATABASE (LOCAL STORAGE) ---
  const [settings, setSettings] = useFirestoreDocument('rac_settings', ['settings', 'main'], {
    companyName: 'AutoRent', businessNumber: '810000123', email: 'contact@autorent.com',
    phone: '+383 44 123 456', address: 'Brigada 123, Suharekë', currency: '€', tax: 18,
    dateFormat: 'dd/mm/yyyy', timezone: 'Europe/Belgrade', emailNotifications: true
  });

  const [cars, setCars] = useFirestoreCollection('rac_cars', 'cars', []);

  const [clients, setClients] = useFirestoreCollection('rac_clients', 'clients', []);

  const [bookings, setBookings] = useFirestoreCollection('rac_bookings', 'bookings', []);

  const [payments, setPayments] = useFirestoreCollection('rac_payments', 'payments', []);

  const [expenses, setExpenses] = useFirestoreCollection('rac_expenses', 'expenses', []);

  const [invoices, setInvoices] = useFirestoreCollection('rac_invoices', 'invoices', []);

  const [insurances, setInsurances] = useFirestoreCollection('rac_insurances', 'insurances', []);

  const [maintenance, setMaintenance] = useFirestoreCollection('rac_maintenance', 'maintenance', []);

  const [tplRecords, setTplRecords] = useFirestoreCollection('rac_tpl_records', 'tplRecords', []);

  const [staff, setStaff] = useFirestoreCollection('rac_staff', 'staff', []);

  const [locations, setLocations] = useFirestoreCollection('rac_locations', 'locations', []);

  const [promotions, setPromotions] = useFirestoreCollection('rac_promotions', 'promotions', []);
  const [inventory, setInventory] = useFirestoreCollection('rac_inventory', 'inventory', []);
  
  const [contracts, setContracts] = useFirestoreCollection('rac_contracts', 'contracts', []);

  const [systemUsers, setSystemUsers] = useFirestoreCollection('rac_system_users', 'systemUsers', []);
  const [loginIdentifier, setLoginIdentifier] = useState('');

  const LEGACY_DEFAULT_SYSTEM_USER_KEYS = new Set([
    'admin|admin@autorent.com',
    'staff|desk@autorent.com',
    'manager|manager@autorent.com'
  ]);

  const getDefaultAccessForRole = (role) => {
    switch (String(role || '')) {
      case 'Admin':
        return 'everything';
      case 'Manager':
        return 'reports';
      case 'Viewer':
        return 'viewer';
      case 'Staff':
      default:
        return 'bookings';
    }
  };

  const isViewerAccess = (currentUser = user) => {
    const normalizedRole = String(currentUser?.role || '').toLowerCase();
    const normalizedAccess = String(currentUser?.access || '').toLowerCase();
    return normalizedRole === 'viewer' || normalizedAccess === 'viewer';
  };

  const getSystemUserAccessLabel = (access) => {
    switch (String(access || '')) {
      case 'everything':
        return uiText({sq:'Gjithçka',en:'Everything',de:'Alles',it:'Tutto',fr:'Tout',hr:'Sve'});
      case 'bookings':
        return uiText({sq:'Rezervime',en:'Bookings',de:'Buchungen',it:'Prenotazioni',fr:'Réservations',hr:'Rezervacije'});
      case 'reports':
        return uiText({sq:'Raporte',en:'Reports',de:'Berichte',it:'Report',fr:'Rapports',hr:'Izvještaji'});
      case 'viewer':
        return uiText({sq:'Vetëm shikim',en:'View only',de:'Nur ansehen',it:'Solo visualizzazione',fr:'Lecture seule',hr:'Samo pregled'});
      default:
        return access || '-';
    }
  };

  useEffect(() => {
    const legacyUsers = systemUsers.filter((entry) => {
      const key = `${String(entry?.username || '').toLowerCase()}|${String(entry?.email || '').toLowerCase()}`;
      return LEGACY_DEFAULT_SYSTEM_USER_KEYS.has(key);
    });

    if (!legacyUsers.length) return;

    setSystemUsers((prev) => prev.filter((entry) => {
      const key = `${String(entry?.username || '').toLowerCase()}|${String(entry?.email || '').toLowerCase()}`;
      return !LEGACY_DEFAULT_SYSTEM_USER_KEYS.has(key);
    }));

    if (typeof window !== 'undefined') {
      try {
        const filteredUsers = systemUsers.filter((entry) => {
          const key = `${String(entry?.username || '').toLowerCase()}|${String(entry?.email || '').toLowerCase()}`;
          return !LEGACY_DEFAULT_SYSTEM_USER_KEYS.has(key);
        });
        window.localStorage.setItem('rac_system_users', JSON.stringify(filteredUsers));
      } catch (error) {
        console.error('Failed to clean local system users cache:', error);
      }
    }

    legacyUsers.forEach((entry) => {
      deleteDoc(doc(db, 'systemUsers', String(entry.id))).catch((error) => {
        console.error('Failed to delete legacy default system user:', error);
      });
    });
  }, [systemUsers, setSystemUsers]);

  const [systemUserForm, setSystemUserForm] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    role: 'Staff',
    access: 'bookings'
  });


  const PAGINATION_LIMITS = {
    booking: 12,
    bookings_not_finished: 12,
    bookings_waitlist: 12,
    payment: 12,
    booking_calendar: 12,
    deposits: 12,
    invoice_auto: 12,
    car: 12,
    vehicle_status: 12,
    tpl: 12,
    insurance: 12,
    maintenance: 12,
    client: 12,
    expense: 12,
    invoice: 12,
    analytics_plus: 12,
    smart_notifications: 12,
    contract: 12,
    contracts_auto: 10
  };

  const [paginationState, setPaginationState] = useState({});

  const setPageFor = (key, page) => {
    setPaginationState(prev => ({ ...prev, [key]: page }));
  };

  const paginateCollection = (key, items) => {
    const limit = PAGINATION_LIMITS[key] || 12;
    const totalPages = Math.max(1, Math.ceil(items.length / limit));
    const currentPage = Math.min(paginationState[key] || 1, totalPages);
    const startIndex = (currentPage - 1) * limit;
    return {
      pageItems: items.slice(startIndex, startIndex + limit),
      totalPages,
      currentPage,
      shouldPaginate: items.length > limit
    };
  };

  const renderPaginationControls = (key, pagination) => {
    if (!pagination.shouldPaginate || pagination.totalPages <= 1) return null;
    return (
      <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
        <button
          onClick={() => setPageFor(key, Math.max(1, pagination.currentPage - 1))}
          disabled={pagination.currentPage === 1}
          className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-700 text-slate-800 dark:text-slate-200 font-bold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uiText({ sq: 'Mbrapa', en: 'Previous', de: 'Zurück', it: 'Indietro', fr: 'Précédent', hr: 'Prethodna' })}
        </button>
        {Array.from({ length: pagination.totalPages }, (_, index) => {
          const page = index + 1;
          return (
            <button
              key={page}
              onClick={() => setPageFor(key, page)}
              className={`px-4 py-2 rounded-xl font-bold ${pagination.currentPage === page ? 'bg-indigo-600 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-700 text-slate-800 dark:text-slate-200'}`}
            >
              {uiText({ sq: 'Faqe', en: 'Page', de: 'Seite', it: 'Pagina', fr: 'Page', hr: 'Stranica' })} {page}
            </button>
          );
        })}
        <button
          onClick={() => setPageFor(key, Math.min(pagination.totalPages, pagination.currentPage + 1))}
          disabled={pagination.currentPage === pagination.totalPages}
          className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-700 text-slate-800 dark:text-slate-200 font-bold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uiText({ sq: 'Tjetra', en: 'Next', de: 'Weiter', it: 'Avanti', fr: 'Suivant', hr: 'Sljedeća' })}
        </button>
      </div>
    );
  };

  useEffect(() => {
    setPaginationState({});
  }, [activeMenu, searchTerm, JSON.stringify(advancedFilters)]);

  // --- MODAL & DIALOG STATES ---
  const toastTimerRef = useRef(null);
  const [toast, setToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', confirmText: '', cancelText: '', variant: 'danger', onConfirm: null });
  const [modalMode, setModalMode] = useState('add');
  const [modalType, setModalType] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const fileInputRef = useRef(null);

  // Apply Dark Mode Class
  useEffect(() => {
    if (isDarkMode) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  }, [isDarkMode]);

  // =========================================================================
  // --- UTILS & HELPERS (TË ZHVENDOSURA KËTU PËR TË PARANDALUAR GABIMET TDZ) ---
  // =========================================================================

  const closeToast = () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(null);
  };

  const showToast = (message, type = 'success', duration = 3200) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    const nextToast = { message, type, id: Date.now(), duration };
    setToast(nextToast);
    toastTimerRef.current = setTimeout(() => setToast(null), duration);
  };

  const showConfirmDialog = ({ title, message, onConfirm, confirmText, cancelText, variant = 'danger' }) => {
    setConfirmDialog({
      isOpen: true,
      title: title || t('notification'),
      message,
      confirmText: confirmText || t('confirm'),
      cancelText: cancelText || t('cancel'),
      variant,
      onConfirm: () => {
        try {
          onConfirm && onConfirm();
        } finally {
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);
  const t = (key, params = {}) => translate(language, key, params);
  const uiText = (map) => map?.[language] || map?.en || map?.sq || '';

  const getTodayDateOnly = () => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  const parseDateOnly = (value) => {
    if (!value) return null;
    const raw = String(value).trim();
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
      return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }

    const normalized = raw.replace(/\./g, '-').replace(/\//g, '-');
    const parts = normalized.split('-');
    if (parts.length === 3) {
      let yyyy, mm, dd;
      const [a, b, c] = parts;
      if (a.length === 4) {
        yyyy = a; mm = b; dd = c;
      } else if (c.length === 4) {
        dd = a; mm = b; yyyy = c;
      }
      if (yyyy && mm && dd) {
        return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
      }
    }
    return null;
  };

  const getBookingDate = (booking, field) => parseDateOnly(booking?.[field]);

  const isBookingActiveOnDate = (booking, date) => {
    if (!booking || !date) return false;

    const start = getBookingDate(booking, 'startDate');
    const end = getBookingDate(booking, 'endDate');

    if (!start || !end) return false;

    const checkDate = new Date(date);
    checkDate.setHours(12, 0, 0, 0);

    const startDate = new Date(start);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(end);
    endDate.setHours(23, 59, 59, 999);

    return checkDate >= startDate && checkDate <= endDate;
  };

  const getBookingsForCar = (carData) => {
    const fullName = [carData?.brand, carData?.model].filter(Boolean).join(' ').trim();
    return bookings.filter((booking) =>
      booking?.vehicle === fullName ||
      booking?.vehicle === carData?.plate ||
      String(booking?.carId || '') === String(carData?.id || '')
    );
  };

  const formatLocalDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const shiftBookingDays = (bookingId, days) => {
  setBookings((prev) =>
    prev.map((booking) => {
      if (booking.id !== bookingId) return booking;

      const start = parseDateOnly(booking.startDate);
      const end = parseDateOnly(booking.endDate);
      if (!start || !end) return booking;

      const newStart = new Date(start);
      const newEnd = new Date(end);

      newStart.setDate(newStart.getDate() + days);
      newEnd.setDate(newEnd.getDate() + days);

      return {
        ...booking,
        startDate: formatLocalDate(newStart),
        endDate: formatLocalDate(newEnd),
      };
    })
  );

    showToast(uiText({
      sq: 'Rezervimi u zhvendos',
      en: 'Booking moved',
      de: 'Buchung verschoben',
      it: 'Prenotazione spostata',
      fr: 'Réservation déplacée',
      hr: 'Rezervacija je pomaknuta',
    }), 'success');
  };

  const createContractFromBooking = (booking) => {
    const contractId = `KONT-${Date.now()}`;
    const content = `
${uiText({sq: 'Klienti', en: 'Client', de: 'Kunde', it: 'Cliente', fr: 'Client', hr: 'Klijent'})}: ${booking.client}
${uiText({sq: 'Vetura', en: 'Vehicle', de: 'Fahrzeug', it: 'Veicolo', fr: 'Véhicule', hr: 'Vozilo'})}: ${booking.vehicle}
${uiText({sq: 'Data e fillimit', en: 'Start date', de: 'Startdatum', it: 'Data inizio', fr: 'Date de début', hr: 'Datum početka'})}: ${booking.startDate}
${uiText({sq: 'Data e mbarimit', en: 'End date', de: 'Enddatum', it: 'Data fine', fr: 'Date de fin', hr: 'Datum završetka'})}: ${booking.endDate}
${uiText({sq: 'Çmimi', en: 'Price', de: 'Preis', it: 'Prezzo', fr: 'Prix', hr: 'Cijena'})}: ${formatCurrency(booking.total || booking.totalPrice || 0)}
${uiText({sq: 'Depozita', en: 'Deposit', de: 'Kaution', it: 'Deposito', fr: 'Dépôt', hr: 'Depozit'})}: ${formatCurrency(booking.deposit || 0)}

${uiText({sq: 'Nënshkrimi', en: 'Signature', de: 'Unterschrift', it: 'Firma', fr: 'Signature', hr: 'Potpis'})}: ____________________
`.trim();

    const newContract = {
      id: contractId,
      title: `${uiText({sq: 'Kontratë për', en: 'Contract for', de: 'Vertrag für', it: 'Contratto per', fr: 'Contrat pour', hr: 'Ugovor za'})} ${booking.client}`,
      client: booking.client,
      clientId: booking.clientId || null,
      date: new Date().toISOString().slice(0, 10),
      status: 'active',
      bookingId: booking.id,
      vehicle: booking.vehicle,
      startDate: booking.startDate,
      endDate: booking.endDate,
      total: booking.total || booking.totalPrice || 0,
      totalPrice: booking.total || booking.totalPrice || 0,
      deposit: booking.deposit || 0,
      content,
    };
    setContracts((prev) => [newContract, ...prev]);
    showToast(uiText({
      sq: 'Kontrata u krijua me sukses',
      en: 'Contract created successfully',
      de: 'Vertrag erfolgreich erstellt',
      it: 'Contratto creato con successo',
      fr: 'Contrat créé avec succès',
      hr: 'Ugovor je uspješno kreiran',
    }), 'success');
    printBookingContract(booking);
    return newContract;
  };

  const toggleDepositReturned = (bookingId) => {
    setBookings((prev) =>
      prev.map((booking) =>
        booking.id === bookingId
          ? { ...booking, depositReturned: !booking.depositReturned }
          : booking
      )
    );
    showToast(uiText({
      sq: 'Statusi i depozitës u përditësua',
      en: 'Deposit status updated',
      de: 'Kautionsstatus aktualisiert',
      it: 'Stato del deposito aggiornato',
      fr: 'Statut du dépôt mis à jour',
      hr: 'Status depozita je ažuriran',
    }), 'success');
  };

  const createInvoiceFromBooking = (booking) => {
    const existing = invoices.find((inv) => inv.bookingId === booking.id);
    if (existing) {
      showToast(uiText({
        sq: 'Fatura ekziston tashmë për këtë rezervim',
        en: 'Invoice already exists for this booking',
        de: 'Rechnung für diese Buchung existiert bereits',
        it: 'Esiste già una fattura per questa prenotazione',
        fr: 'Une facture existe déjà pour cette réservation',
        hr: 'Faktura već postoji za ovu rezervaciju',
      }), 'info');
      return existing;
    }

    const newInvoice = {
      id: `INV-${new Date().getFullYear()}-${Date.now().toString().slice(-4)}`,
      client: booking.client,
      clientId: booking.clientId || null,
      bookingId: booking.id,
      date: new Date().toISOString().slice(0, 10),
      amount: Number(booking.total || booking.totalPrice || 0),
      status: booking.paymentStatus === 'paid' ? 'paid' : 'pending',
    };
    setInvoices((prev) => [newInvoice, ...prev]);
    showToast(uiText({
      sq: 'Fatura u krijua me sukses',
      en: 'Invoice created successfully',
      de: 'Rechnung erfolgreich erstellt',
      it: 'Fattura creata con successo',
      fr: 'Facture créée avec succès',
      hr: 'Faktura je uspješno kreirana',
    }), 'success');
    return newInvoice;
  };

  const getVehicleStatusComputed = (car) => {
    const today = getTodayDateOnly();
    if ((car?.status || '').toLowerCase() === 'maintenance') return 'maintenance';

    const relatedBookings = getBookingsForCar(car);
    const hasCurrentBooking = relatedBookings.some((b) =>
      isBookingActiveOnDate(b, today) &&
      ['active', 'confirmed', 'pending'].includes(String(b?.status || '').toLowerCase())
    );

    const hasUpcomingBooking = relatedBookings.some((b) => {
      const start = getBookingDate(b, 'startDate');
      return start && start > today &&
        ['active', 'confirmed', 'pending'].includes(String(b?.status || '').toLowerCase());
    });

    if (hasCurrentBooking) return 'rented';
    if (hasUpcomingBooking) return 'reserved';
    return 'available';
  };

  const vehicleStatusLabel = (status) => ({
    available: uiText({ sq: 'E lirë', en: 'Available', de: 'Verfügbar', it: 'Disponibile', fr: 'Disponible', hr: 'Dostupno' }),
    rented: uiText({ sq: 'E dhënë', en: 'Rented', de: 'Vermietet', it: 'Noleggiato', fr: 'Loué', hr: 'Iznajmljeno' }),
    maintenance: uiText({ sq: 'Në servis', en: 'Maintenance', de: 'Wartung', it: 'Manutenzione', fr: 'Maintenance', hr: 'Servis' }),
    reserved: uiText({ sq: 'E rezervuar', en: 'Reserved', de: 'Reserviert', it: 'Prenotato', fr: 'Réservé', hr: 'Rezervirano' }),
  }[status] || status);

  const getDepositStatus = (booking) => {
    if (booking?.depositReturned) return 'returned';
    if (Number(booking?.deposit || 0) > 0) return 'held';
    return 'none';
  };

  const depositStatusLabel = (status) => ({
    none: uiText({ sq: 'Pa depozitë', en: 'No deposit', de: 'Keine Kaution', it: 'Nessun deposito', fr: 'Aucun dépôt', hr: 'Bez depozita' }),
    held: uiText({ sq: 'E mbajtur', en: 'Held', de: 'Einbehalten', it: 'Trattenuto', fr: 'Conservé', hr: 'Zadržan' }),
    returned: uiText({ sq: 'E kthyer', en: 'Returned', de: 'Zurückgegeben', it: 'Restituito', fr: 'Retourné', hr: 'Vraćen' }),
  }[status] || status);


  const LANGUAGE_OPTIONS = [
    { code: 'sq', flag: '🇦🇱', flagSrc: getFlagSrcByCode('sq'), label: 'Shqip' },
    { code: 'en', flag: '🇬🇧', flagSrc: getFlagSrcByCode('en'), label: 'English' },
    { code: 'de', flag: '🇩🇪', flagSrc: getFlagSrcByCode('de'), label: 'Deutsch' },
    { code: 'fr', flag: '🇫🇷', flagSrc: getFlagSrcByCode('fr'), label: 'Français' },
    { code: 'it', flag: '🇮🇹', flagSrc: getFlagSrcByCode('it'), label: 'Italiano' },
    { code: 'hr', flag: '🇭🇷', flagSrc: getFlagSrcByCode('hr'), label: 'Hrvatski' },
  ];
  const currentLanguageOption = LANGUAGE_OPTIONS.find(item => item.code === language) || LANGUAGE_OPTIONS[0];
  const changeLanguage = (code) => {
    setLanguage(code);
    setShowLanguageMenu(false);
  };

  const locale = LOCALES[language] || 'en-US';
  const formatCurrency = (amount) => `${settings.currency}${Number(amount || 0).toLocaleString(locale, { minimumFractionDigits: 2 })}`;
  const formatDate = (date) => date ? new Date(date).toLocaleDateString(locale) : '';
  const generateId = (prefix) => `${prefix}-${Math.floor(Math.random() * 9000) + 1000}`;

  const getStatusColor = (status) => {
    const s = String(status).toLowerCase();
    if (['available', 'active', 'paid', 'completed', 'vip', 'e lirë', 'aktive'].includes(s)) return 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400';
    if (['rented', 'pending', 'scheduled', 'e dhënë', 'në pritje'].includes(s)) return 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400';
    if (['maintenance', 'maintenance_status', 'low', 'warning', 'në servis', 'inactive'].includes(s)) return 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400';
    if (['blacklist', 'i bllokuar', 'cancelled', 'anuluar'].includes(s)) return 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400';
    if (['draft'].includes(s)) return 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300';
    return 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300';
  };

  const getStatusIcon = (status) => {
    const s = String(status).toLowerCase();
    if (['available', 'active', 'paid', 'completed', 'e lirë', 'aktive'].includes(s)) return <CheckCircle size={16} className="text-emerald-500" />;
    if (['rented', 'pending', 'scheduled', 'e dhënë', 'në pritje'].includes(s)) return <Clock size={16} className="text-blue-500" />;
    if (['maintenance', 'low', 'warning', 'në servis'].includes(s)) return <AlertTriangle size={16} className="text-amber-500" />;
    if (['blacklist', 'i bllokuar', 'cancelled', 'anuluar'].includes(s)) return <XCircle size={16} className="text-red-500" />;
    return <Info size={16} className="text-slate-400" />;
  };

  // =========================================================================
  // --- LLOGARITJA E NJOFTIMEVE (ALERTS) GLOBALE ---
  // =========================================================================

  const getGlobalAlerts = () => {
    const alerts = [];
    const today = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() + 30);

    // 1. Sigurimet / Regjistrimi
    insurances.forEach(ins => {
      const expDate = new Date(ins.endDate);
      const diffTime = expDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays < 0) {
        alerts.push({ id: `ins-exp-${ins.id}`, type: 'danger', icon: ShieldAlert, title: t('insurance_expired'), message: `${ins.vehicle} (${ins.plate}) ${t('expired_on')} ${formatDate(ins.endDate)}` });
      } else if (profileReminders.kujteseRegjistrim && diffDays <= Number(profileReminders.kujteseRegjistrimDite)) {
        alerts.push({ id: `ins-warn-${ins.id}`, type: 'warning', icon: AlertTriangle, title: t('insurance_expiring_soon'), message: t('insurance_expiring_soon_msg', { vehicle: ins.vehicle, plate: ins.plate, days: diffDays }) });
      } else if (diffDays <= 30 && !profileReminders.kujteseRegjistrim) {
        alerts.push({ id: `ins-warn-${ins.id}`, type: 'warning', icon: AlertTriangle, title: t('expiry_soon'), message: t('expiry_soon_msg', { vehicle: ins.vehicle, date: formatDate(ins.endDate) }) });
      }
    });

    // 2. Kthimi i Veturave (Bazuar në profileReminders.kthimMakine)
    if (profileReminders.kthimMakine) {
      bookings.filter(b => b.status === 'active').forEach(b => {
        const returnDate = new Date(`${b.endDate}T${b.endTime || '09:00'}:00`);
        const diffMins = Math.floor((returnDate - today) / 60000); 

        if (diffMins <= Number(profileReminders.kthimMakineMin) && diffMins > 0) {
          alerts.push({ id: `ret-warn-${b.id}`, type: 'warning', icon: Clock, title: t('return_soon'), message: t('return_soon_msg', { client: b.client, vehicle: b.vehicle, mins: diffMins }) });
        } else if (diffMins <= 0) {
          alerts.push({ id: `ret-dang-${b.id}`, type: 'danger', icon: AlertCircle, title: t('late_return'), message: t('late_return_msg', { client: b.client, vehicle: b.vehicle }) });
        }
      });
    }

    // 3. Marrja e Veturave (Bazuar në profileReminders.marrjeMakine)
    if (profileReminders.marrjeMakine) {
      bookings.filter(b => b.status === 'pending').forEach(b => {
        const pickupDate = new Date(`${b.startDate}T${b.startTime || '09:00'}:00`);
        const diffMins = Math.floor((pickupDate - today) / 60000);

        if (diffMins <= Number(profileReminders.marrjeMakineMin) && diffMins > 0) {
          alerts.push({ id: `pick-info-${b.id}`, type: 'info', icon: KeyRound, title: t('pickup_soon'), message: t('pickup_soon_msg', { client: b.client, vehicle: b.vehicle, mins: diffMins }) });
        } else if (diffMins <= 0) {
          alerts.push({ id: `pick-warn-${b.id}`, type: 'warning', icon: AlertCircle, title: t('past_pickup'), message: t('past_pickup_msg', { client: b.client, vehicle: b.vehicle }) });
        }
      });
    }

    // 4. Servisi (Maintenance)
    maintenance.filter(m => m.status === 'scheduled').forEach(m => {
      const maintDate = new Date(m.date);
      if (maintDate <= thirtyDaysFromNow && maintDate >= today) {
        alerts.push({ id: `maint-${m.id}`, type: 'info', icon: Wrench, title: t('maintenance_scheduled'), message: t('maintenance_scheduled_msg', { vehicle: m.vehicle, date: formatDate(m.date), desc: m.description }) });
      }
    });

    return alerts;
  };

  const globalAlerts = getGlobalAlerts();

  // --- BANNER ALERTS (vetëm disa njoftime kryesore, gjithmonë të dukshme në krye) ---
  const [dismissedBannerIds, setDismissedBannerIds] = useBrowserStorage('rac_dismissed_banner_ids', []);

  const getBannerAlerts = () => {
    const alerts = [];
    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);
    const weekAhead = new Date(now);
    weekAhead.setDate(now.getDate() + 7);

    // 1) Makina për të marrë mbrapsht sot (rezervime aktive që mbarojnë sot)
    const dueToday = bookings.filter(b => b.status === 'active' && b.endDate === todayStr);
    if (dueToday.length > 0) {
      alerts.push({
        id: 'banner-returns-today',
        type: 'warning',
        icon: Clock,
        title: t('banner_returns_today_title'),
        message: t('banner_returns_today_msg', { count: dueToday.length, vehicles: `${dueToday.slice(0, 3).map(x => x.vehicle).join(', ')}${dueToday.length > 3 ? '…' : ''}` })
      });
    }

    // 2) Sigurime të skaduara / skadojnë këtë javë
    const expOrWeek = insurances
      .map(ins => {
        const exp = new Date(ins.endDate);
        return { ins, exp, diffDays: Math.ceil((exp - now) / (1000*60*60*24)) };
      })
      .filter(x => x.diffDays < 0 || x.exp <= weekAhead);

    const expiredCount = expOrWeek.filter(x => x.diffDays < 0).length;
    const weekCount = expOrWeek.filter(x => x.diffDays >= 0).length;

    if (expiredCount > 0) {
      alerts.push({
        id: 'banner-ins-expired',
        type: 'danger',
        icon: ShieldAlert,
        title: t('banner_ins_expired_title'),
        message: t('banner_ins_expired_msg', { count: expiredCount })
      });
    }
    if (weekCount > 0) {
      alerts.push({
        id: 'banner-ins-week',
        type: 'info',
        icon: Shield,
        title: t('banner_ins_week_title'),
        message: t('banner_ins_week_msg', { count: weekCount })
      });
    }

    return alerts;
  };

  const bannerAlerts = getBannerAlerts().filter(a => !dismissedBannerIds.includes(a.id));

  const dismissBanner = (id) => {
    setDismissedBannerIds(prev => (prev.includes(id) ? prev : [...prev, id]));
  };



  // --- AUTO-CALCULATE BOOKING TOTALS, CHECK AVAILABILITY & AUTO-FILL PLATE ---
  useEffect(() => {
    if (isModalOpen) {
      let updated = false;
      let newFormData = { ...formData };

      if (['insurance', 'maintenance'].includes(modalType)) {
        if (newFormData.vehicle && newFormData.vehicle !== newFormData._lastVehicle) {
          const selectedCar = cars.find(c => `${c.brand} ${c.model}` === newFormData.vehicle);
          if (selectedCar) {
            newFormData.plate = selectedCar.plate;
            newFormData._lastVehicle = newFormData.vehicle;
            updated = true;
          }
        }
      }

      if (modalType === 'booking') {
        if (newFormData.vehicle && newFormData.startDate && newFormData.endDate) {
          const formStart = new Date(newFormData.startDate);
          const formEnd = new Date(newFormData.endDate);
          formStart.setHours(0,0,0,0);
          formEnd.setHours(23,59,59,999);

          const isBooked = bookings.some(b => {
            if (newFormData.id && b.id === newFormData.id) return false;
            if (b.status === 'cancelled' || b.status === 'completed') return false;
            if (b.vehicle !== newFormData.vehicle) return false;

            const bStart = new Date(b.startDate);
            const bEnd = new Date(b.endDate);
            bStart.setHours(0,0,0,0);
            bEnd.setHours(23,59,59,999);

            return formStart <= bEnd && formEnd >= bStart;
          });

          if (isBooked) {
            newFormData.vehicle = '';
            newFormData._lastVehicle = '';
            newFormData.dailyPrice = 0;
            newFormData.totalPrice = 0;
            updated = true;
          }
        }

        const getFullCarName = (c) => [c.brand, c.model].filter(Boolean).join(' ');

        if (newFormData.vehicle && newFormData.vehicle !== newFormData._lastVehicle) {
          const selectedCar = cars.find(c => getFullCarName(c) === newFormData.vehicle);
          if (selectedCar) {
            newFormData.dailyPrice = selectedCar.price;
            newFormData._lastVehicle = newFormData.vehicle;
            updated = true;
          }
        }

        if (newFormData.startDate && newFormData.endDate) {
          const start = new Date(newFormData.startDate);
          const end = new Date(newFormData.endDate);
          let days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
          if (days <= 0) days = 1;

          const daily = parseFloat(newFormData.dailyPrice) || 0;
          const calculatedTotal = daily * days;

          if (calculatedTotal !== parseFloat(newFormData.totalPrice || 0)) {
            newFormData.totalPrice = calculatedTotal;
            updated = true;
          }
        }
      }

      if (updated) {
        setFormData(newFormData);
      }
    }
  }, [formData.vehicle, formData.startDate, formData.endDate, formData.dailyPrice, isModalOpen, modalType, cars, bookings]);

  // --- LOGIN HANDLER ---
  const handleLogin = (e) => {
    e.preventDefault();

    const typedIdentifier = String(loginIdentifier || '').trim().toLowerCase();
    const matchedUser = systemUsers.find((u) =>
      u.status !== 'disabled' &&
      (
        String(u.username || '').toLowerCase() === typedIdentifier ||
        String(u.email || '').toLowerCase() === typedIdentifier
      ) &&
      String(u.password || '') === String(loginPassword || '')
    );

    if (matchedUser) {
      setUser((prev) => ({
        ...prev,
        id: matchedUser.id,
        name: matchedUser.name,
        username: matchedUser.username,
        role: matchedUser.role,
        access: matchedUser.access || getDefaultAccessForRole(matchedUser.role),
        email: matchedUser.email
      }));
      setIsAuthenticated(true);
      setLoginError('');
      showToast(uiText({sq:'Keni hyrë me sukses!', en:'Login successful!', de:'Erfolgreich angemeldet!', it:'Accesso effettuato con successo!', fr:'Connexion réussie !', hr:'Prijava uspješna!'}), 'success');
      return;
    }

    if (!typedIdentifier && loginPassword === adminPassword) {
      setIsAuthenticated(true);
      setLoginError('');
      showToast(uiText({sq:'Keni hyrë me sukses!', en:'Login successful!', de:'Erfolgreich angemeldet!', it:'Accesso effettuato con successo!', fr:'Connexion réussie !', hr:'Prijava uspješna!'}), 'success');
      return;
    }

    setLoginError(uiText({
      sq:'Të dhënat e hyrjes janë të pasakta!',
      en:'Invalid login credentials!',
      de:'Ungültige Zugangsdaten!',
      it:'Credenziali di accesso non valide!',
      fr:'Identifiants invalides !',
      hr:'Neispravni podaci za prijavu!'
    }));
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setLoginPassword('');
    setLoginIdentifier('');
    setActiveMenu('dashboard');
  };

  const handleResetProgram = () => {
    showConfirmDialog({
      title: t('reset_program'),
      message: t('reset_program_confirm'),
      confirmText: t('confirm'),
      cancelText: t('cancel'),
      variant: 'danger',
      onConfirm: async () => {
        const keysToRemove = [
          'rac_cars',
          'rac_clients',
          'rac_bookings',
          'rac_payments',
          'rac_expenses',
          'rac_invoices',
          'rac_insurances',
          'rac_maintenance',
          'rac_tpl_records',
          'rac_staff',
          'rac_locations',
          'rac_promotions',
          'rac_inventory',
          'rac_contracts',
          'rac_notif_prefs',
          'rac_reminder_prefs',
          'rac_dismissed_banner_ids',
          'rac_settings',
          'rac_users',
          'rac_system_users',
          'rac_activity_logs',
          'rac_backup_settings',
          'rac_current_user',
          'rac_google_calendar_auth'
        ];

        try {
          keysToRemove.forEach((key) => window.localStorage.removeItem(key));

          if (window.api?.factoryReset) {
            await window.api.factoryReset();
            return;
          }

          if (window.api?.resetData) {
            await window.api.resetData();
          } else if (window.api?.storageDelete) {
            for (const key of keysToRemove) {
              await window.api.storageDelete(key);
            }
          }

          window.location.reload();
        } catch (error) {
          console.error('Reset failed:', error);
          showToast('Reset dështoi', 'error');
        }
      }
    });
  };

  const handleCreateSystemUser = async () => {
    const payload = {
      name: String(systemUserForm.name || '').trim(),
      username: String(systemUserForm.username || '').trim(),
      email: String(systemUserForm.email || '').trim(),
      password: String(systemUserForm.password || ''),
      role: systemUserForm.role || 'Staff',
      access: systemUserForm.access || getDefaultAccessForRole(systemUserForm.role || 'Staff')
    };

    if (!payload.name || !payload.username || !payload.email || !payload.password) {
      showToast(uiText({
        sq:'Plotëso emrin, username, email dhe password.',
        en:'Fill in name, username, email and password.',
        de:'Name, Benutzername, E-Mail und Passwort ausfüllen.',
        it:'Compila nome, username, email e password.',
        fr:'Remplissez le nom, le nom d’utilisateur, l’e-mail et le mot de passe.',
        hr:'Unesite ime, korisničko ime, e-mail i lozinku.'
      }), 'error');
      return;
    }

    const exists = systemUsers.some((u) =>
      String(u.username || '').toLowerCase() === payload.username.toLowerCase() ||
      String(u.email || '').toLowerCase() === payload.email.toLowerCase()
    );

    if (exists) {
      showToast(uiText({
        sq:'Ky username ose email ekziston tashmë.',
        en:'This username or email already exists.',
        de:'Dieser Benutzername oder diese E-Mail existiert bereits.',
        it:'Questo username o e-mail esiste già.',
        fr:'Ce nom d’utilisateur ou e-mail existe déjà.',
        hr:'Ovo korisničko ime ili e-mail već postoji.'
      }), 'error');
      return;
    }

    const newUser = {
      id: `system_user_${Date.now()}`,
      ...payload,
      status: 'active'
    };

    try {
      await persistItemToFirestore('systemUser', newUser);
      upsertCollectionState('systemUser', newUser, 'add');
      setSystemUserForm({ name: '', username: '', email: '', password: '', role: 'Staff', access: 'bookings' });
      showToast(uiText({
        sq:'Përdoruesi u krijua me sukses.',
        en:'User created successfully.',
        de:'Benutzer erfolgreich erstellt.',
        it:'Utente creato con successo.',
        fr:'Utilisateur créé avec succès.',
        hr:'Korisnik je uspješno kreiran.'
      }), 'success');
    } catch (error) {
      console.error('Failed to create system user:', error);
      showToast(uiText({
        sq:'Krijimi i përdoruesit dështoi.',
        en:'Failed to create user.',
        de:'Benutzer konnte nicht erstellt werden.',
        it:'Impossibile creare l’utente.',
        fr:'Impossible de créer l’utilisateur.',
        hr:'Nije moguće kreirati korisnika.'
      }), 'error');
    }
  };

  // --- FUNKSIONI I RI PËR PRINTIM NË IFRAME ---
  const printContentToIframe = (htmlContent) => {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(htmlContent);
    doc.close();

    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      }, 2000);
    }, 500);
  };

  // --- FUNKSIONET PËR PRINTIMIN E KONTRATAVE ---
  
  const getContractI18n = () => CONTRACT_I18N[language] || CONTRACT_I18N.en;
  const replaceContractVars = (text, vars = {}) => String(text).replace(/\{(\w+)\}/g, (_, key) => vars[key] ?? `{${key}}`);
  const getLocalizedContractClientName = (name) => name && name !== 'Template i Përgjithshëm' ? name : '________________________';
  const buildManualContractSectionsHtml = (contract, ci) => {
    const vars = { company: settings.companyName || 'Rent A Car', client: getLocalizedContractClientName(contract?.client) };
    return ci.manualSections.map((section) => `
      <div class="manual-section">
        <h4>${section.title}</h4>
        <p>${replaceContractVars(section.body, vars)}</p>
      </div>
    `).join('');
  };
  const getLocalizedManualContractBody = (contract, ci) => {
    if (contract?.content && contract.content.trim()) {
      return `<div class="content">${contract.content.replace(/\r?\n/g, '<br/>')}</div>`;
    }
    return `<div class="content">${buildManualContractSectionsHtml(contract, ci)}</div>`;
  };
  const getLocalizedManualContractText = (contract, ci) => {
    const vars = { company: settings.companyName || 'Rent A Car', client: getLocalizedContractClientName(contract?.client) };
    return ci.manualSections
      .map((section) => `${section.title}\n${replaceContractVars(section.body, vars)}`)
      .join('\n\n');
  };


const printInvoice = (invoice) => {
    const client = clients.find(c => String(c.id) === String(invoice?.clientId) || c.name === invoice?.client) || {};
    const linkedBooking = bookings.find(b => (invoice?.clientId && String(b.clientId) === String(invoice.clientId)) || b.client === invoice?.client) || {};
    const businessLabel = (CONTRACT_I18N[language] || CONTRACT_I18N.en)?.businessNoLabel || 'Business No.';
    const clientDetailsLabel = language === 'sq' ? 'Detajet e klientit' :
      language === 'de' ? 'Kundendaten' :
      language === 'it' ? 'Dettagli cliente' :
      language === 'fr' ? 'Détails du client' :
      language === 'hr' ? 'Podaci o klijentu' : 'Client details';
    const invoiceTitle = language === 'sq' ? 'FATURË' :
      language === 'de' ? 'RECHNUNG' :
      language === 'it' ? 'FATTURA' :
      language === 'fr' ? 'FACTURE' :
      language === 'hr' ? 'RAČUN' : 'INVOICE';
    const bookingLabel = language === 'sq' ? 'Rezervimi' :
      language === 'de' ? 'Reservierung' :
      language === 'it' ? 'Prenotazione' :
      language === 'fr' ? 'Réservation' :
      language === 'hr' ? 'Rezervacija' : 'Booking';
    const vehicleLabel = language === 'sq' ? 'Vetura' :
      language === 'de' ? 'Fahrzeug' :
      language === 'it' ? 'Veicolo' :
      language === 'fr' ? 'Véhicule' :
      language === 'hr' ? 'Vozilo' : 'Vehicle';
    const statusLabel = t('status');
    const amountLabel = t('amount_col');
    const dateLabel = t('date_col');
    const paymentStatus = invoice?.status === 'paid' ? t('paid') : t(invoice?.status || 'pending');
    const printContent = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${invoiceTitle} ${invoice?.id || ''}</title>
          <style>
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; box-sizing: border-box; }
            body { font-family: Arial, Helvetica, sans-serif; margin: 0; color: #0f172a; background: #fff; }
            .page { padding: 32px; }
            .header { display:flex; justify-content:space-between; gap:24px; margin-bottom:24px; align-items:flex-start; }
            .brand h1 { margin:0; font-size:28px; color:#1d4ed8; }
            .brand p { margin:6px 0 0; color:#475569; font-size:13px; }
            .card { border:1px solid #e2e8f0; border-radius:16px; padding:18px; }
            .grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-bottom:18px; }
            .muted { color:#64748b; font-size:12px; text-transform:uppercase; letter-spacing:.04em; font-weight:700; }
            .value { margin-top:6px; font-size:15px; font-weight:700; color:#0f172a; word-break:break-word; }
            table { width:100%; border-collapse:collapse; margin-top:16px; }
            th, td { border:1px solid #e2e8f0; padding:12px; text-align:left; }
            th { background:#f8fafc; font-size:12px; text-transform:uppercase; letter-spacing:.04em; color:#475569; }
            .total { margin-top:20px; display:flex; justify-content:flex-end; }
            .total-box { min-width:260px; border-radius:16px; background:#eff6ff; border:1px solid #bfdbfe; padding:18px; }
            .total-box .amount { font-size:30px; font-weight:900; color:#1d4ed8; }
            .footer { margin-top:30px; color:#64748b; font-size:12px; text-align:center; }
            @media print { body { background:#fff; } .page { padding: 12px; } }
          </style>
        </head>
        <body>
          <div class="page">
            <div class="header">
              <div class="brand">
                <h1>${settings.companyName || 'AutoRent'}</h1>
                <p>${settings.email || ''}${settings.phone ? ' • ' + settings.phone : ''}</p>
                <p>${settings.address || ''}</p>
                <p><strong>${businessLabel}:</strong> ${settings.businessNumber || '-'}</p>
              </div>
              <div class="card" style="min-width:280px;">
                <div class="muted">${invoiceTitle}</div>
                <div class="value">${invoice?.id || '-'}</div>
                <div class="muted" style="margin-top:14px;">${dateLabel}</div>
                <div class="value">${formatDate(invoice?.date) || '-'}</div>
                <div class="muted" style="margin-top:14px;">${statusLabel}</div>
                <div class="value">${paymentStatus}</div>
              </div>
            </div>

            <div class="grid">
              <div class="card">
                <div class="muted">${clientDetailsLabel}</div>
                <div class="value">${invoice?.client || '-'}</div>
                <div style="margin-top:8px; color:#475569; font-size:14px;">
                  <div>${client?.phone || client?.secondaryPhone || ''}</div>
                  <div>${client?.email || ''}</div>
                  <div>${client?.address || ''}</div>
                </div>
              </div>
              <div class="card">
                <div class="muted">${bookingLabel}</div>
                <div class="value">${linkedBooking?.id || '-'}</div>
                <div style="margin-top:8px; color:#475569; font-size:14px;">
                  <div><strong>${vehicleLabel}:</strong> ${linkedBooking?.vehicle || '-'}</div>
                  <div><strong>${t('start_date_label')}:</strong> ${formatDate(linkedBooking?.startDate) || '-'}</div>
                  <div><strong>${t('end_date_label')}:</strong> ${formatDate(linkedBooking?.endDate) || '-'}</div>
                </div>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>${t('description')}</th>
                  <th>${dateLabel}</th>
                  <th>${amountLabel}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>${bookingLabel} ${linkedBooking?.id ? '- ' + linkedBooking.id : ''}</td>
                  <td>${formatDate(invoice?.date) || '-'}</td>
                  <td>${formatCurrency(invoice?.amount || 0)}</td>
                </tr>
              </tbody>
            </table>

            <div class="total">
              <div class="total-box">
                <div class="muted">${t('invoice_total')}</div>
                <div class="amount">${formatCurrency(invoice?.amount || 0)}</div>
              </div>
            </div>

            <div class="footer">
              ${settings.companyName || 'AutoRent'}
            </div>
          </div>
        </body>
      </html>
    `;
    printContentToIframe(printContent);
  };

const printBookingContract = (booking) => {
    const ci = getContractI18n();
    const client = clients.find(c => String(c.id) === String(booking.clientId)) || {};
    const car = cars.find(c => {
       const carName = [c.brand, c.model].filter(Boolean).join(' ');
       return carName === booking.vehicle;
    }) || {};

    const city = settings.address.includes(',') ? settings.address.split(',').pop().trim() : settings.address;

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${ci.rentalAgreementTitle} - ${booking.id}</title>
        <style>
          @page { size: A4; margin: 8mm 10mm; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; padding: 0; color: #1e293b; font-size: 11px; margin: 0; line-height: 1.38; background-color: #ffffff; }
          .header { background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%); border-radius: 12px; padding: 16px 18px; color: white; display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; box-shadow: 0 4px 15px rgba(59, 130, 246, 0.2); }
          .logo-text h1 { margin: 0; font-size: 24px; font-weight: 900; letter-spacing: -0.5px; text-transform: uppercase; }
          .logo-text p { margin: 3px 0 0 0; font-size: 9px; font-weight: 700; color: #bfdbfe; text-transform: uppercase; letter-spacing: 1.2px; }
          .company-info { text-align: right; font-size: 9px; color: #e0e7ff; line-height: 1.4; }
          .company-info strong { color: #ffffff; font-weight: 800; }
          .doc-title { text-align: center; margin-bottom: 12px; position: relative; }
          .doc-title h2 { margin: 0; font-size: 17px; font-weight: 900; color: #0f172a; text-transform: uppercase; letter-spacing: 0.8px; }
          .doc-title p { margin: 4px 0 0 0; font-size: 9px; color: #64748b; text-transform: uppercase; font-weight: 700; letter-spacing: 0.8px; }
          .doc-number { display: inline-block; background: #fef2f2; color: #e11d48; padding: 5px 12px; border-radius: 20px; font-weight: 800; font-size: 11px; margin-top: 8px; border: 1px solid #fecdd3; }
          .grid-container { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 10px; }
          .box { flex: 1; min-width: 45%; background: white; border-radius: 10px; border: 1px solid #e2e8f0; overflow: hidden; }
          .box-full { width: 100%; background: white; border-radius: 10px; border: 1px solid #e2e8f0; overflow: hidden; margin-bottom: 15px; }
          .box-title { background: #f8fafc; color: #2563eb; padding: 8px 12px; font-weight: 800; font-size: 9px; text-transform: uppercase; border-bottom: 1px solid #e2e8f0; letter-spacing: 0.4px; }
          .box-content { padding: 10px 12px; }
          .data-row { display: flex; margin-bottom: 5px; align-items: baseline; }
          .data-label { width: 118px; font-size: 8px; color: #64748b; font-weight: 700; text-transform: uppercase; }
          .data-value { flex: 1; font-size: 10px; font-weight: 700; color: #0f172a; border-bottom: 1px solid #f1f5f9; padding-bottom: 2px; }
          .highlight-value { color: #e11d48; font-size: 11px; font-weight: 900; }
          .totals-area { background: #eff6ff; padding: 10px; border-radius: 8px; border: 1px dashed #bfdbfe; margin-left: 6px;}
          .totals-area .data-label { color: #1e40af; }
          .totals-area .data-value { border-bottom-color: #dbeafe; }
          .grand-total { font-size: 14px !important; color: #1d4ed8 !important; font-weight: 900 !important; border-bottom: 2px solid #1d4ed8 !important; }
          .terms { background: #f8fafc; border-left: 4px solid #3b82f6; padding: 10px 14px; border-radius: 0 8px 8px 0; margin-top: 8px; font-size: 8.6px; color: #475569; page-break-inside: avoid; break-inside: avoid; }
          .terms h4 { margin: 0 0 6px 0; color: #1e3a8a; font-size: 9px; text-transform: uppercase; font-weight: 800; }
          .terms ul { margin: 0; padding-left: 12px; }
          .terms li { margin-bottom: 3px; }
          .terms strong { color: #0f172a; }
          .signatures { display: flex; justify-content: space-between; margin-top: 16px; padding: 0 8px; page-break-inside: avoid; break-inside: avoid; }
          .sig-block { width: 30%; text-align: center; }
          .sig-line { border-bottom: 2px dashed #cbd5e1; height: 22px; margin-bottom: 6px; }
          .sig-title { font-weight: 800; font-size: 9px; color: #0f172a; text-transform: uppercase; }
          .sig-desc { font-size: 8px; color: #64748b; margin-top: 2px;}
          .footer { margin-top: 10px; border-top: 1px solid #e2e8f0; padding-top: 8px; text-align: center; font-size: 7.8px; color: #94a3b8; font-weight: 600; page-break-inside: avoid; break-inside: avoid; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo-text">
            <h1>${settings.companyName}</h1>
            <p>${ci.companyTagline}</p>
          </div>
          <div class="company-info">
            <strong>${ci.addressLabel}:</strong> ${settings.address}<br>
            <strong>${ci.phoneLabel}:</strong> ${settings.phone}<br>
            <strong>Email:</strong> ${settings.email}<br>
            <strong>${ci.businessNoLabel}:</strong> ${settings.businessNumber}
          </div>
        </div>

        <div class="doc-title">
          <h2>${ci.rentalAgreementTitle}</h2>
          <p>${ci.rentalAgreementSubtitle}</p>
          <div class="doc-number">${ci.referenceNo} ${booking.id}</div>
        </div>

        <div class="grid-container">
          <div class="box">
            <div class="box-title">1. ${ci.renterDetails}</div>
            <div class="box-content">
              <div class="data-row"><div class="data-label">${ci.fullName}:</div><div class="data-value">${client.name || ''}</div></div>
              <div class="data-row"><div class="data-label">${ci.birthDate}:</div><div class="data-value">${client.birthdate ? formatDate(client.birthdate) : ''}</div></div>
              <div class="data-row"><div class="data-label">${ci.idPassport}:</div><div class="data-value">${client.personalId || client.passportNumber || ''}</div></div>
              <div class="data-row"><div class="data-label">${ci.licenseNo}:</div><div class="data-value">${client.licenseNumber || ''}</div></div>
              <div class="data-row"><div class="data-label">${ci.residenceAddress}:</div><div class="data-value">${client.address || ''}</div></div>
              <div class="data-row"><div class="data-label">${ci.phoneMobile}:</div><div class="data-value">${client.phone || ''}</div></div>
            </div>
          </div>

          <div class="box">
            <div class="box-title">2. ${ci.vehicleDetails}</div>
            <div class="box-content">
              <div class="data-row"><div class="data-label">${ci.makeModel}:</div><div class="data-value">${booking.vehicle || ''}</div></div>
              <div class="data-row"><div class="data-label">${ci.plateNo}:</div><div class="data-value highlight-value">${car.plate || ''}</div></div>
              <div class="data-row"><div class="data-label">${ci.productionYear}:</div><div class="data-value">${car.year || ''}</div></div>
              <div class="data-row"><div class="data-label">${ci.color}:</div><div class="data-value">${car.color || ''}</div></div>
              <div class="data-row"><div class="data-label">${ci.fuelLevel}:</div><div class="data-value" style="color: #94a3b8; font-weight: normal;">${ci.writeHere}</div></div>
              <div class="data-row"><div class="data-label">${ci.odometer}:</div><div class="data-value" style="color: #94a3b8; font-weight: normal;">${ci.writeHere}</div></div>
            </div>
          </div>
        </div>

        <div class="box-full">
          <div class="box-title">3. ${ci.rentalPeriodCharges}</div>
          <div class="box-content" style="display: flex; gap: 12px;">
            <div style="flex: 1;">
              <div class="data-row"><div class="data-label">${ci.pickupDate}:</div><div class="data-value">${formatDate(booking.startDate)}</div></div>
              <div class="data-row"><div class="data-label">${ci.pickupTime}:</div><div class="data-value">${booking.startTime || '09:00'}</div></div>
              <div class="data-row"><div class="data-label">${ci.pickupPlace}:</div><div class="data-value">${booking.pickupLocation || city}</div></div>
            </div>
            <div style="flex: 1;">
              <div class="data-row"><div class="data-label">${ci.returnDate}:</div><div class="data-value">${formatDate(booking.endDate)}</div></div>
              <div class="data-row"><div class="data-label">${ci.returnTime}:</div><div class="data-value">${booking.endTime || '09:00'}</div></div>
              <div class="data-row"><div class="data-label">${ci.returnPlace}:</div><div class="data-value">${booking.returnLocation || city}</div></div>
            </div>
            <div class="totals-area" style="flex: 1.1;">
              <div class="data-row"><div class="data-label">${ci.dailyPrice}:</div><div class="data-value">${formatCurrency(booking.dailyPrice || 0)}</div></div>
              <div class="data-row"><div class="data-label">${ci.depositCash}:</div><div class="data-value">${formatCurrency(booking.deposit || 0)}</div></div>
              <div class="data-row" style="margin-top: 8px;"><div class="data-label" style="color:#1d4ed8; font-size:11px;">${ci.totalPrice}:</div><div class="data-value grand-total">${formatCurrency(booking.total || 0)}</div></div>
            </div>
          </div>
        </div>

        <div class="terms">
          <h4>4. ${ci.termsTitle}</h4>
          <ul>${ci.bookingTerms.map((item) => `<li>${item}</li>`).join('')}</ul>
          <p style="margin-top: 10px; font-weight: 800; font-size: 10px; text-align: center; color: #0f172a;">${ci.acceptanceNote}</p>
        </div>

        <div class="signatures">
          <div class="sig-block">
            <div class="sig-line"></div>
            <div class="sig-title">${settings.companyName}</div>
            <div class="sig-desc">${ci.companyRepresentative}</div>
          </div>

          <div class="sig-block" style="width: 20%;">
            <div style="font-weight: 800; border-bottom: 2px solid #e2e8f0; padding-bottom: 4px; color: #0f172a;">${ci.contractDate}: ${formatDate(new Date())}</div>
            <div class="sig-desc">${city}</div>
          </div>

          <div class="sig-block">
            <div class="sig-line"></div>
            <div class="sig-title">${ci.clientRenter}</div>
            <div class="sig-desc">${client.name || ci.signPlaceholder}</div>
          </div>
        </div>

        <div class="footer">
          ${ci.generatedFooter}
        </div>
      </body>
      </html>
    `;

    printContentToIframe(printContent);
  };

const printManualContract = (contract) => {
    const linkedBooking = contract?.bookingId ? bookings.find((b) => b.id === contract.bookingId) : null;
    if (linkedBooking) {
      printBookingContract(linkedBooking);
      return;
    }

    const ci = getContractI18n();
    const clientName = getLocalizedContractClientName(contract?.client);
    const displayTitle = contract?.title && contract.title.trim() ? contract.title : ci.manualContractTitle;
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${displayTitle}</title>
        <style>
          @page { size: A4; margin: 14mm; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; padding: 0; color: #1e293b; font-size: 12px; margin: 0; line-height: 1.55; }
          .header { background: linear-gradient(135deg, #0f172a 0%, #334155 100%); border-radius: 12px; padding: 24px; text-align: center; color: white; margin-bottom: 24px; box-shadow: 0 4px 15px rgba(0,0,0,0.1); }
          .header h1 { margin: 0; font-size: 28px; font-weight: 900; letter-spacing: 1px; text-transform: uppercase; color: #fff; }
          .header p { margin: 8px 0 0 0; color: #cbd5e1; font-size: 11px; font-weight: 500; letter-spacing: 0.8px;}
          .title { text-align: center; font-size: 20px; font-weight: 900; color: #1e40af; text-transform: uppercase; margin-bottom: 18px; position: relative; padding-bottom: 12px; }
          .title::after { content: ''; position: absolute; bottom: 0; left: 50%; transform: translateX(-50%); width: 60px; height: 4px; background: #3b82f6; border-radius: 2px; }
          .meta-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 18px; display: flex; justify-content: space-between; margin-bottom: 18px; gap: 16px; }
          .meta-item { display: flex; flex-direction: column; }
          .meta-label { font-size: 10px; color: #64748b; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
          .meta-value { font-size: 13px; font-weight: 700; color: #0f172a; }
          .content { background: white; padding: 2px 4px; font-size: 12px; color: #334155; line-height: 1.6; }
          .manual-section { margin-bottom: 14px; }
          .manual-section h4 { margin: 0 0 6px 0; color: #0f172a; font-size: 13px; text-transform: uppercase; }
          .manual-section p { margin: 0; text-align: justify; white-space: pre-wrap; }
          .signatures { display: flex; justify-content: space-between; margin-top: 28px; gap: 16px; }
          .sig-block { width: 35%; text-align: center; background: #f8fafc; border-radius: 10px; padding: 16px; border: 1px solid #e2e8f0; }
          .sig-label { font-weight: 800; color: #0f172a; margin-bottom: 32px; text-transform: uppercase; font-size: 10px; letter-spacing: 0.8px;}
          .sig-line { border-bottom: 2px dashed #94a3b8; margin-bottom: 10px; height: 24px; }
          .sig-name { font-weight: 700; color: #3b82f6; font-size: 12px; }
          .footer { margin-top: 14px; text-align: center; font-size: 9px; color: #94a3b8; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${settings.companyName}</h1>
          <p>${settings.address} • ${ci.phoneLabel}: ${settings.phone} • Email: ${settings.email}</p>
        </div>

        <div class="title">${displayTitle}</div>

        <div class="meta-box">
          <div class="meta-item">
            <span class="meta-label">${ci.contractDate}</span>
            <span class="meta-value">${formatDate(contract.date || new Date())}</span>
          </div>
          <div class="meta-item" style="text-align: right;">
            <span class="meta-label">${ci.partyClient}</span>
            <span class="meta-value" style="color: #1e40af;">${clientName}</span>
          </div>
        </div>

        ${getLocalizedManualContractBody(contract, ci)}

        <div class="signatures">
          <div class="sig-block">
            <div class="sig-label">${ci.companyRepresentative}</div>
            <div class="sig-line"></div>
            <div class="sig-name">${settings.companyName}</div>
          </div>
          <div class="sig-block">
            <div class="sig-label">${ci.clientRenter}</div>
            <div class="sig-line"></div>
            <div class="sig-name">${contract?.client && contract.client !== 'Template i Përgjithshëm' ? contract.client : ci.clientSignature}</div>
          </div>
        </div>
        <div class="footer">${ci.generatedFooter}</div>
      </body>
      </html>
    `;

    printContentToIframe(printContent);
  };

  // --- CRUD ACTIONS ---
  const openModal = (type, mode, data = null) => {
    if (mode !== 'view' && isViewerAccess()) {
      showToast(uiText({sq:'Ky përdorues ka vetëm qasje për shikim.', en:'This user has view-only access.', de:'Dieser Benutzer hat nur Lesezugriff.', it:'Questo utente ha accesso in sola lettura.', fr:'Cet utilisateur a un accès en lecture seule.', hr:'Ovaj korisnik ima samo pristup za pregled.'}), 'error');
      return;
    }

    setModalType(type);
    setModalMode(mode);
    if (type === 'client') setActiveClientTab('details');
    if (type === 'car') setActiveCarTab('details');
    if (type === 'booking') setActiveBookingTab('details');
    const today = new Date().toISOString().split('T')[0];
    
    if ((mode === 'edit' || mode === 'view') && data) {
      setFormData({ 
        ...data,
        clientType: data.clientId ? 'existing' : 'new' 
      });
    } else {
      const defaults = {
        car: { brand: '', model: '', year: new Date().getFullYear(), color: '', plate: '', price: '', status: 'available' },
        client: { name: '', phone: '', email: '', status: 'active' },
        booking: { startDate: today, startTime: '09:00', endDate: today, endTime: '09:00', clientType: 'existing', status: 'pending', paymentStatus: 'pending' },
        payment: { bookingId: '', amount: '', method: 'Kesh', date: today, status: 'completed' },
        expense: { category: 'Karburant', description: '', amount: '', date: today, vehicle: '' },
        invoice: { clientId: '', client: '', date: today, amount: '', status: 'pending' },
        contract: (() => {
          const contractI18n = getContractI18n();
          const emptyContract = { title: (contractI18n.manualContractTitle || ''), client: 'Template i Përgjithshëm', clientId: '', date: today, status: 'draft' };
          return { ...emptyContract, content: getLocalizedManualContractText(emptyContract, contractI18n) };
        })(),
        insurance: { vehicle: '', plate: '', provider: '', policyNumber: '', endDate: today, cost: '', status: 'active' },
        maintenance: { vehicle: '', plate: '', description: '', date: today, cost: '', status: 'scheduled' },
        tpl: { carId: data?.id || '', vehicle: data ? [data.brand, data.model].filter(Boolean).join(' ') : '', plate: data?.plate || '', amount: '', renewalDate: today, expiryDate: today },
        staff: { name: '', position: '', email: '', phone: '', status: 'active' },
        location: { name: '', address: '', phone: '', status: 'active' },
        promotion: { code: '', discount: '', type: 'percentage', validTo: today, status: 'active' },
        inventory: { name: '', category: '', quantity: '', status: 'available' }
      };
      setFormData(defaults[type] || {});
    }

    setIsModalOpen(true);
  };

  useEffect(() => {
    if (!isModalOpen || modalType !== 'contract' || modalMode !== 'add') return;
    setFormData((prev) => {
      if (!prev || Object.keys(prev).length === 0) return prev;
      const contractI18n = getContractI18n();
      const nextTitle = contractI18n.manualContractTitle || prev.title || '';
      const nextBase = { ...prev, title: nextTitle, client: prev.client || 'Template i Përgjithshëm' };
      if (prev.content && prev.content.trim()) {
        return prev.title === nextTitle ? prev : { ...prev, title: nextTitle };
      }
      return { ...nextBase, content: getLocalizedManualContractText(nextBase, contractI18n) };
    });
  }, [language, isModalOpen, modalMode, modalType]);

  const FIRESTORE_COLLECTIONS = {
    car: 'cars',
    client: 'clients',
    booking: 'bookings',
    payment: 'payments',
    expense: 'expenses',
    invoice: 'invoices',
    contract: 'contracts',
    insurance: 'insurances',
    maintenance: 'maintenance',
    tpl: 'tplRecords',
    staff: 'staff',
    location: 'locations',
    promotion: 'promotions',
    inventory: 'inventory',
    systemUser: 'systemUsers'
  };

  const upsertCollectionState = (type, item, mode = 'add') => {
    const id = String(item.id);
    const byMode = (prev) => {
      const filtered = prev.filter((entry) => String(entry.id) !== id);
      if (mode === 'edit') {
        return prev.map((entry) => String(entry.id) === id ? item : entry);
      }
      return [item, ...filtered];
    };

    switch (type) {
      case 'car':
        setCars(byMode);
        break;
      case 'client':
        setClients(byMode);
        break;
      case 'booking':
        setBookings(byMode);
        break;
      case 'payment':
        setPayments(byMode);
        break;
      case 'expense':
        setExpenses(byMode);
        break;
      case 'invoice':
        setInvoices(byMode);
        break;
      case 'contract':
        setContracts(byMode);
        break;
      case 'insurance':
        setInsurances(byMode);
        break;
      case 'maintenance':
        setMaintenance(byMode);
        break;
      case 'tpl':
        setTplRecords(byMode);
        break;
      case 'staff':
        setStaff(byMode);
        break;
      case 'location':
        setLocations(byMode);
        break;
      case 'promotion':
        setPromotions(byMode);
        break;
      case 'inventory':
        setInventory(byMode);
        break;
      case 'systemUser':
        setSystemUsers(byMode);
        break;
      default:
        break;
    }
  };

  const removeCollectionState = (type, id) => {
    const removeById = (prev) => prev.filter((entry) => String(entry.id) !== String(id));

    switch (type) {
      case 'car':
        setCars(removeById);
        break;
      case 'client':
        setClients(removeById);
        break;
      case 'booking':
        setBookings(removeById);
        break;
      case 'payment':
        setPayments(removeById);
        break;
      case 'expense':
        setExpenses(removeById);
        break;
      case 'invoice':
        setInvoices(removeById);
        break;
      case 'contract':
        setContracts(removeById);
        break;
      case 'insurance':
        setInsurances(removeById);
        break;
      case 'maintenance':
        setMaintenance(removeById);
        break;
      case 'tpl':
        setTplRecords(removeById);
        break;
      case 'staff':
        setStaff(removeById);
        break;
      case 'location':
        setLocations(removeById);
        break;
      case 'promotion':
        setPromotions(removeById);
        break;
      case 'inventory':
        setInventory(removeById);
        break;
      case 'systemUser':
        setSystemUsers(removeById);
        break;
      default:
        break;
    }
  };

  const persistItemToFirestore = async (type, item) => {
    const collectionName = FIRESTORE_COLLECTIONS[type];
    if (!collectionName) return;

    await setDoc(
      doc(db, collectionName, String(item.id)),
      {
        ...sanitizeFirestoreItem(item),
        updatedAt: new Date().toISOString(),
      },
      { merge: true }
    );
  };

  const deleteItemFromFirestore = async (type, id) => {
    const collectionName = FIRESTORE_COLLECTIONS[type];
    if (!collectionName) return;
    await deleteDoc(doc(db, collectionName, String(id)));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (isViewerAccess()) {
      showToast(uiText({sq:'Ky përdorues ka vetëm qasje për shikim.', en:'This user has view-only access.', de:'Dieser Benutzer hat nur Lesezugriff.', it:'Questo utente ha accesso in sola lettura.', fr:'Cet utilisateur a un accès en lecture seule.', hr:'Ovaj korisnik ima samo pristup za pregled.'}), 'error');
      return;
    }

    try {
      let newItem = { ...formData };

      if (modalType === 'booking') {
        let bookingClientName = '';

        const rentTotal = parseFloat(newItem.totalPrice) || 0;
        const deliveryTotal = parseFloat(newItem.deliveryCharge) || 0;
        const pickupTotal = parseFloat(newItem.pickupCharge) || 0;
        const extrasTotal = (newItem.extraGps ? 5 : 0) + (newItem.extraChildSeat ? 10 : 0) + (newItem.extraInsurance ? 15 : 0);
        newItem.deposit = parseFloat(newItem.deposit) || 0;
        newItem.depositReturned = Boolean(newItem.depositReturned) && (parseFloat(newItem.deposit) || 0) > 0;
        newItem.total = rentTotal + deliveryTotal + pickupTotal + extrasTotal;

        if (newItem.clientType === 'new' && newItem.newClientName) {
          const newClient = {
            id: `client_${Date.now()}`,
            name: newItem.newClientName || '',
            phone: newItem.newClientPhone || '',
            email: newItem.newClientEmail || '',
            address: newItem.newClientAddress || '',
            birthdate: newItem.newClientBirthdate || '',
            secondaryPhone: newItem.newClientSecondaryPhone || '',
            personalId: newItem.newClientPersonalId || '',
            licenseNumber: newItem.newClientLicenseNumber || '',
            passportNumber: newItem.newClientPassportNumber || '',
            idFront: newItem.newClientIdFront || '',
            idBack: newItem.newClientIdBack || '',
            status: 'active',
            rentals: 1,
            totalSpent: Number(newItem.total || 0),
            joinDate: new Date().toISOString().split('T')[0]
          };

          await persistItemToFirestore('client', newClient);
          upsertCollectionState('client', newClient, 'add');

          bookingClientName = newClient.name;
          newItem.clientId = newClient.id;
        } else if (newItem.clientId) {
          const existing = clients.find((c) => String(c.id) === String(newItem.clientId));
          bookingClientName = existing ? existing.name : 'Klient i panjohur';

          if (existing) {
            const updatedClient = {
              ...existing,
              rentals: Number(existing.rentals || 0) + 1,
              totalSpent: Number(existing.totalSpent || 0) + Number(newItem.total || 0)
            };
            await persistItemToFirestore('client', updatedClient);
            upsertCollectionState('client', updatedClient, 'edit');
          }
        }

        newItem.client = bookingClientName;
      }

      if (modalType === 'invoice' && newItem.clientId) {
        const existing = clients.find((c) => String(c.id) === String(newItem.clientId));
        newItem.client = existing ? existing.name : (newItem.client || 'Klient i panjohur');
      }

      if (modalType === 'tpl') {
        const selectedCar = cars.find((c) => String(c.id) === String(newItem.carId));
        if (selectedCar) {
          newItem.carId = selectedCar.id;
          newItem.vehicle = [selectedCar.brand, selectedCar.model].filter(Boolean).join(' ');
          newItem.plate = selectedCar.plate || '';
        }
      }

      if (modalType === 'contract') {
        if (newItem.clientId) {
          const existing = clients.find((c) => String(c.id) === String(newItem.clientId));
          newItem.client = existing ? existing.name : (newItem.client || 'I panjohur');
        } else {
          newItem.client = 'Template i Përgjithshëm';
        }
      }

      if (!newItem.id) {
        newItem.id = ['booking', 'payment', 'invoice', 'contract'].includes(modalType)
          ? generateId(modalType.substring(0, 3).toUpperCase())
          : `${modalType}_${Date.now()}`;
      }

      const saveMode = modalMode === 'add' ? 'add' : 'edit';
      await persistItemToFirestore(modalType, newItem);
      upsertCollectionState(modalType, newItem, saveMode);

      if (modalType === 'booking' && profileNotificationPrefs.googleSync) {
        addBookingToCalendar(newItem);
      }

      showToast(language === 'sq' ? 'Të dhënat u ruajtën!' : 'Data saved!');
      setIsModalOpen(false);
    } catch (error) {
      console.error(`Failed to save ${modalType}:`, error);
      showToast(language === 'sq' ? 'Gabim gjatë ruajtjes!' : 'Save failed!', 'error');
    }
  };

  const handleDelete = (type, id, name) => {
    if (isViewerAccess()) {
      showToast(uiText({sq:'Ky përdorues ka vetëm qasje për shikim.', en:'This user has view-only access.', de:'Dieser Benutzer hat nur Lesezugriff.', it:'Questo utente ha accesso in sola lettura.', fr:'Cet utilisateur a un accès en lecture seule.', hr:'Ovaj korisnik ima samo pristup za pregled.'}), 'error');
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: language === 'sq' ? 'Konfirmo Fshirjen' : 'Confirm Delete',
      message: language === 'sq' ? `A jeni i sigurt që dëshironi ta fshini: "${name}"?` : `Are you sure you want to delete: "${name}"?`,
      onConfirm: async () => {
        try {
          await deleteItemFromFirestore(type, id);
          removeCollectionState(type, id);
          showToast(language === 'sq' ? 'U fshi me sukses!' : 'Deleted successfully!', 'error');
        } catch (error) {
          console.error(`Failed to delete ${type}:`, error);
          showToast(language === 'sq' ? 'Gabim gjatë fshirjes!' : 'Delete failed!', 'error');
        } finally {
          setConfirmDialog({ isOpen: false });
        }
      }
    });
  };

  const exportDatabase = () => {
    const allData = { settings, cars, clients, bookings, payments, expenses, invoices, contracts, insurances, maintenance, tplRecords, staff, locations, promotions, inventory, systemUsers };
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(allData, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", `AutoRent_Database_${new Date().toISOString().split('T')[0]}.json`);
    document.body.appendChild(downloadAnchorNode); 
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
    showToast('Databaza u shkarkua!');
  };

  const importDatabase = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const data = JSON.parse(event.target.result);
        if (!data.cars || !data.settings) {
          showToast('Skedari nuk është valid!', 'error');
          return;
        }

        const collectionsToImport = [
          ['cars', data.cars || []],
          ['clients', data.clients || []],
          ['bookings', data.bookings || []],
          ['payments', data.payments || []],
          ['expenses', data.expenses || []],
          ['invoices', data.invoices || []],
          ['contracts', data.contracts || []],
          ['insurances', data.insurances || []],
          ['maintenance', data.maintenance || []],
          ['tplRecords', data.tplRecords || []],
          ['staff', data.staff || []],
          ['locations', data.locations || []],
          ['promotions', data.promotions || []],
          ['inventory', data.inventory || []],
          ['systemUsers', data.systemUsers || systemUsers || []]
        ];

        const batch = writeBatch(db);
        collectionsToImport.forEach(([collectionName, items]) => {
          (Array.isArray(items) ? items : []).forEach((item) => {
            const normalizedItem = normalizeCollectionItem(item, collectionName);
            batch.set(
              doc(db, collectionName, String(normalizedItem.id)),
              {
                ...sanitizeFirestoreItem(normalizedItem),
                updatedAt: new Date().toISOString(),
              },
              { merge: true }
            );
          });
        });

        batch.set(doc(db, 'settings', 'main'), {
          key: 'rac_settings',
          value: data.settings,
          updatedAt: new Date().toISOString(),
        }, { merge: true });

        await batch.commit();

        setSettings(data.settings);
        setCars(data.cars || []);
        setClients(data.clients || []);
        setBookings(data.bookings || []);
        setPayments(data.payments || []);
        setExpenses(data.expenses || []);
        setInvoices(data.invoices || []);
        setContracts(data.contracts || []);
        setInsurances(data.insurances || []);
        setMaintenance(data.maintenance || []);
        setTplRecords(data.tplRecords || []);
        setStaff(data.staff || []);
        setLocations(data.locations || []);
        setPromotions(data.promotions || []);
        setInventory(data.inventory || []);
        if (data.systemUsers) setSystemUsers(data.systemUsers);

        showToast('Databaza u ngarkua!');
      } catch (err) {
        console.error('Import failed:', err);
        showToast('Gabim gjatë leximit.', 'error');
      }
    };
    reader.readAsText(file);
    e.target.value = null;
  };

  const getModalHeaderInfo = () => {
    switch(modalType) {
      case 'car': return { icon: Car, title: modalMode === 'add' ? t('add_car') : modalMode === 'view' ? `${t('car_details_title')}: ${[formData?.brand, formData?.model].filter(Boolean).join(' ')}` : t('edit_car'), color: 'bg-blue-600' };
      case 'client': return { icon: Users, title: modalMode === 'add' ? t('add_client') : modalMode === 'view' ? `${t('client_details_title')}: ${formData?.name || ''}` : t('edit_client'), color: 'bg-indigo-600' };
      case 'booking': return { icon: Calendar, title: modalMode === 'add' ? t('add_booking') : modalMode === 'view' ? t('reservation_details') : t('edit_booking'), color: 'bg-purple-600' };
      case 'payment': return { icon: CreditCard, title: modalMode === 'add' ? t('add_payment') : t('edit_payment'), color: 'bg-emerald-600' };
      case 'expense': return { icon: Receipt, title: modalMode === 'add' ? t('add_expense') : t('edit_expense'), color: 'bg-amber-600' };
      case 'invoice': return { icon: FileText, title: modalMode === 'add' ? t('add_invoice') : t('edit_invoice'), color: 'bg-rose-600' };
      case 'tpl': return { icon: Shield, title: modalMode === 'add' ? t('add_tpl') : `TPL - ${formData?.vehicle || ''}`, color: 'bg-cyan-600' };
      case 'contract': return { icon: ScrollText, title: modalMode === 'add' ? t('add_contract') : t('edit_contract'), color: 'bg-slate-700' };
      default: return { icon: Edit3, title: `${modalMode === 'add' ? t('add') : t('edit')} ${t(modalType) || modalType}`, color: 'bg-blue-600' };
    }
  };

  // --- DASHBOARD RENDERING ---
  const renderDashboard = () => {
    const activeBookingsCount = bookings.filter(b => b.status === 'active').length;
    const availableCarsCount = cars.filter(c => c.status === 'available').length;
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const monthlyRevenue = payments
      .filter(p => p.status === 'completed')
      .filter(p => {
        const d = new Date(p.date);
        return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
      })
      .reduce((acc, curr) => acc + Number(curr.amount), 0);

    const totalClients = clients.length;
    const monthlyExpenses = expenses
      .filter(e => {
        const d = new Date(e.date);
        return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
      })
      .reduce((acc, curr) => acc + Number(curr.amount), 0);

    // --- Pickups/Returns today (për kartat "Marrjet Sot" / "Kthimet Sot") ---
    const todayStr = new Date().toISOString().slice(0, 10);
    const pickupsToday = bookings
      .filter(b => b.startDate === todayStr)
      .filter(b => b.status !== 'cancelled');
    const returnsToday = bookings
      .filter(b => b.endDate === todayStr)
      .filter(b => b.status !== 'cancelled');

    return (
      <div className="space-y-6 sm:space-y-8 animate-in fade-in duration-500">
        <div className="flex flex-col gap-5 md:flex-row md:justify-between md:items-end bg-gradient-to-r from-indigo-900 to-slate-800 rounded-[28px] p-5 sm:p-8 text-white relative overflow-hidden shadow-2xl">
          <div className="absolute top-0 left-0 w-full h-full opacity-20 pointer-events-none" 
               style={{ backgroundImage: 'radial-gradient(circle at 20% 150%, rgba(99, 102, 241, 0.5) 0%, transparent 50%), radial-gradient(circle at 80% -50%, rgba(56, 189, 248, 0.5) 0%, transparent 50%)' }}>
          </div>
          <div className="relative z-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-2 tracking-tight leading-tight">{t('welcome', { name: user.name })}</h2>
            <p className="text-indigo-200 text-sm sm:text-base lg:text-lg">{t('today_overview')}</p>
          </div>
          <div className="relative z-10 w-full md:w-auto">
            <button onClick={() => openModal('booking', 'add')} className="w-full md:w-auto justify-center bg-white text-indigo-900 px-5 sm:px-6 py-3 rounded-2xl font-bold flex items-center space-x-2 hover:bg-indigo-50 hover:scale-[1.02] transition-all shadow-lg">
              <Plus size={20} />
              <span>{t('add_booking')}</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5 lg:gap-6">
          <div className="group relative bg-white dark:bg-slate-800 rounded-[22px] p-4 sm:p-6 shadow-lg sm:shadow-xl shadow-slate-200/50 dark:shadow-none border border-slate-100 dark:border-slate-700 overflow-hidden hover:-translate-y-1 transition-transform min-h-[150px] sm:min-h-[176px]">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl group-hover:bg-blue-500/20 transition-colors"></div>
            <div className="flex justify-between items-start mb-4">
              <div className="w-11 h-11 sm:w-14 sm:h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30 text-white">
                <Car size={28} />
              </div>
              <span className="px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-[10px] sm:text-xs font-bold rounded-lg">{t('badge_total')}</span>
            </div>
            <h3 className="text-slate-500 dark:text-slate-400 font-medium text-[11px] sm:text-sm leading-snug">{t('fleet')}</h3>
            <p className="text-2xl sm:text-4xl font-black text-slate-800 dark:text-white mt-1 leading-none">{cars.length}</p>
          </div>

          <div className="group relative bg-white dark:bg-slate-800 rounded-[22px] p-4 sm:p-6 shadow-lg sm:shadow-xl shadow-slate-200/50 dark:shadow-none border border-slate-100 dark:border-slate-700 overflow-hidden hover:-translate-y-1 transition-transform min-h-[150px] sm:min-h-[176px]">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-emerald-500/10 rounded-full blur-2xl group-hover:bg-emerald-500/20 transition-colors"></div>
            <div className="flex justify-between items-start mb-4">
              <div className="w-11 h-11 sm:w-14 sm:h-14 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/30 text-white">
                <CheckCircle size={28} />
              </div>
              <span className="px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-[10px] sm:text-xs font-bold rounded-lg">{t('badge_ready')}</span>
            </div>
            <h3 className="text-slate-500 dark:text-slate-400 font-medium text-[11px] sm:text-sm leading-snug">{t('cars_free')}</h3>
            <p className="text-2xl sm:text-4xl font-black text-slate-800 dark:text-white mt-1 leading-none">{availableCarsCount}</p>
          </div>

          <div className="group relative bg-white dark:bg-slate-800 rounded-[22px] p-4 sm:p-6 shadow-lg sm:shadow-xl shadow-slate-200/50 dark:shadow-none border border-slate-100 dark:border-slate-700 overflow-hidden hover:-translate-y-1 transition-transform min-h-[150px] sm:min-h-[176px]">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-amber-500/10 rounded-full blur-2xl group-hover:bg-amber-500/20 transition-colors"></div>
            <div className="flex justify-between items-start mb-4">
              <div className="w-11 h-11 sm:w-14 sm:h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/30 text-white">
                <Calendar size={28} />
              </div>
              <span className="px-2 py-1 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-[10px] sm:text-xs font-bold rounded-lg flex items-center"><Clock size={12} className="mr-1"/> {t('badge_active')}</span>
            </div>
            <h3 className="text-slate-500 dark:text-slate-400 font-medium text-[11px] sm:text-sm leading-snug">{t('active_bookings')}</h3>
            <p className="text-2xl sm:text-4xl font-black text-slate-800 dark:text-white mt-1 leading-none">{activeBookingsCount}</p>
          </div>

          <div className="group relative bg-white dark:bg-slate-800 rounded-[22px] p-4 sm:p-6 shadow-lg sm:shadow-xl shadow-slate-200/50 dark:shadow-none border border-slate-100 dark:border-slate-700 overflow-hidden hover:-translate-y-1 transition-transform min-h-[150px] sm:min-h-[176px]">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-purple-500/10 rounded-full blur-2xl group-hover:bg-purple-500/20 transition-colors"></div>
            <div className="flex justify-between items-start mb-4">
              <div className="w-11 h-11 sm:w-14 sm:h-14 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/30 text-white">
                <Wallet size={28} />
              </div>
              <span className="px-2 py-1 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 text-[10px] sm:text-xs font-bold rounded-lg flex items-center">+12%</span>
            </div>
            <h3 className="text-slate-500 dark:text-slate-400 font-medium text-[11px] sm:text-sm leading-snug">{t('monthly_revenue')}</h3>
            <p className="text-lg sm:text-3xl font-black text-slate-800 dark:text-white mt-1 leading-tight break-words">{formatCurrency(monthlyRevenue)}</p>
          </div>
          <div className="group relative bg-white dark:bg-slate-800 rounded-[22px] p-4 sm:p-6 shadow-lg sm:shadow-xl shadow-slate-200/50 dark:shadow-none border border-slate-100 dark:border-slate-700 overflow-hidden hover:-translate-y-1 transition-transform min-h-[150px] sm:min-h-[176px]">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-cyan-500/10 rounded-full blur-2xl group-hover:bg-cyan-500/20 transition-colors"></div>
            <div className="flex justify-between items-start mb-4">
              <div className="w-11 h-11 sm:w-14 sm:h-14 bg-gradient-to-br from-cyan-500 to-sky-600 rounded-2xl flex items-center justify-center shadow-lg shadow-cyan-500/30 text-white">
                <Users size={28} />
              </div>
              <span className="px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-[10px] sm:text-xs font-bold rounded-lg">{t('badge_total')}</span>
            </div>
            <h3 className="text-slate-500 dark:text-slate-400 font-medium text-[11px] sm:text-sm leading-snug">{t('total_clients')}</h3>
            <p className="text-2xl sm:text-4xl font-black text-slate-800 dark:text-white mt-1 leading-none">{totalClients}</p>
          </div>

          <div className="group relative bg-white dark:bg-slate-800 rounded-[22px] p-4 sm:p-6 shadow-lg sm:shadow-xl shadow-slate-200/50 dark:shadow-none border border-slate-100 dark:border-slate-700 overflow-hidden hover:-translate-y-1 transition-transform min-h-[150px] sm:min-h-[176px]">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-rose-500/10 rounded-full blur-2xl group-hover:bg-rose-500/20 transition-colors"></div>
            <div className="flex justify-between items-start mb-4">
              <div className="w-11 h-11 sm:w-14 sm:h-14 bg-gradient-to-br from-rose-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg shadow-rose-500/30 text-white">
                <Receipt size={28} />
              </div>
              <span className="px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-[10px] sm:text-xs font-bold rounded-lg">{t('badge_monthly')}</span>
            </div>
            <h3 className="text-slate-500 dark:text-slate-400 font-medium text-[11px] sm:text-sm leading-snug">{t('monthly_expenses')}</h3>
            <p className="text-lg sm:text-3xl font-black text-slate-800 dark:text-white mt-1 leading-tight break-words">{formatCurrency(monthlyExpenses)}</p>
          </div>
          </div>

        {/* Pickups & Returns (Today) */}
<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
{/* Pickups */}
          <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-100 dark:border-slate-700 shadow-xl shadow-slate-200/40 dark:shadow-none overflow-hidden">
            <div className="px-6 py-5 flex items-center justify-between bg-gradient-to-r from-sky-600 to-indigo-600 text-white">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-white/15 flex items-center justify-center ring-1 ring-white/25">
                  <KeyRound size={20} />
                </div>
                <div>
                  <h3 className="font-extrabold text-lg leading-tight">{t('pickups_today')}</h3>
                  <p className="text-white/80 text-sm font-medium">{formatDate(todayStr)}</p>
                </div>
              </div>
              <div className="px-3 py-1.5 rounded-2xl bg-white/15 ring-1 ring-white/25 font-black tabular-nums">
                {pickupsToday.length}
              </div>
            </div>

            <div className="p-4 lg:p-6">
              {pickupsToday.length === 0 ? (
                <div className="rounded-2xl border border-sky-100 dark:border-sky-900/40 bg-sky-50 dark:bg-sky-900/25 p-5 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center">
                    <CheckCircle size={18} className="text-sky-700 dark:text-sky-300" />
                  </div>
                  <div>
                    <p className="font-extrabold text-slate-800 dark:text-white">{t('no_pickups_today')}</p>
                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400">—</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {pickupsToday.slice(0, 5).map(b => (
                    <div key={b.id} className="rounded-2xl border border-slate-100 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/30 p-4 flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="font-extrabold text-slate-900 text-slate-800 dark:text-white truncate">{b.client}</p>
                        <p className="text-sm font-semibold text-slate-600 dark:text-slate-300 truncate">{b.vehicle}</p>
                        <p className="text-xs font-bold text-slate-500 dark:text-slate-400 mt-1 tabular-nums">
                          {(b.startTime || '09:00')} • {formatDate(b.startDate)}
                        </p>
                      </div>

                      <button
                        onClick={() => openModal('booking', 'edit', b)}
                        className="shrink-0 px-4 py-2 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 hover:shadow-md transition font-bold text-slate-700 text-slate-800 dark:text-slate-200 flex items-center gap-2"
                        title={t('view')}
                      >
                        <Eye size={16} />
                        <span className="hidden sm:inline">{t('view')}</span>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Returns */}
          <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-100 dark:border-slate-700 shadow-xl shadow-slate-200/40 dark:shadow-none overflow-hidden">
            <div className="px-6 py-5 flex items-center justify-between bg-gradient-to-r from-amber-600 to-rose-600 text-white">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-white/15 flex items-center justify-center ring-1 ring-white/25">
                  <Clock size={20} />
                </div>
                <div>
                  <h3 className="font-extrabold text-lg leading-tight">{t('returns_today')}</h3>
                  <p className="text-white/80 text-sm font-medium">{formatDate(todayStr)}</p>
                </div>
              </div>
              <div className="px-3 py-1.5 rounded-2xl bg-white/15 ring-1 ring-white/25 font-black tabular-nums">
                {returnsToday.length}
              </div>
            </div>

            <div className="p-4 sm:p-5 lg:p-6">
              {returnsToday.length === 0 ? (
                <div className="rounded-2xl border border-amber-100 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/20 p-5 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                    <CheckCircle size={18} className="text-amber-700 dark:text-amber-300" />
                  </div>
                  <div>
                    <p className="font-extrabold text-slate-800 dark:text-white">{t('no_returns_today')}</p>
                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400">—</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {returnsToday.slice(0, 5).map(b => (
                    <div key={b.id} className="rounded-2xl border border-slate-100 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/30 p-4 flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="font-extrabold text-slate-900 text-slate-800 dark:text-white truncate">{b.client}</p>
                        <p className="text-sm font-semibold text-slate-600 dark:text-slate-300 truncate">{b.vehicle}</p>
                        <p className="text-xs font-bold text-slate-500 dark:text-slate-400 mt-1 tabular-nums">
                          {(b.endTime || '09:00')} • {formatDate(b.endDate)}
                        </p>
                      </div>

                      <button
                        onClick={() => openModal('booking', 'edit', b)}
                        className="shrink-0 px-4 py-2 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 hover:shadow-md transition font-bold text-slate-700 text-slate-800 dark:text-slate-200 flex items-center gap-2"
                        title={t('view')}
                      >
                        <Eye size={16} />
                        <span className="hidden sm:inline">{t('view')}</span>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
</div>

<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-[28px] shadow-xl shadow-slate-200/40 dark:shadow-none border border-slate-100 dark:border-slate-700 p-5 sm:p-6 lg:p-7">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
              <h3 className="font-extrabold text-slate-800 dark:text-white text-xl flex items-center"><Clock size={20} className="mr-2 text-indigo-500"/> {t('recent_bookings')}</h3>
              <button onClick={() => setActiveMenu('bookings')} className="inline-flex items-center justify-center text-sm bg-slate-50 dark:bg-slate-700/80 text-slate-600 dark:text-slate-300 px-4 py-2.5 rounded-2xl font-bold hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors border border-slate-200 dark:border-slate-600">
                {t('see_all')}
              </button>
            </div>

            <div className="space-y-4">
              {bookings.slice(0, 4).map((b) => {
                const depositStatus = getDepositStatus(b);
                const depositTone = depositStatus === 'returned'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800'
                  : depositStatus === 'held'
                    ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800'
                    : 'bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-900/30 dark:text-slate-300 dark:border-slate-700';

                return (
                  <button
                    key={b.id}
                    onClick={() => openModal('booking', 'edit', b)}
                    className="group w-full text-left rounded-[24px] border border-slate-200/80 dark:border-slate-700 bg-gradient-to-r from-slate-50 via-white to-slate-50 dark:from-slate-900/40 dark:via-slate-900/20 dark:to-slate-900/40 p-4 sm:p-5 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200"
                  >
                    <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 text-xs font-black border border-indigo-100 dark:border-indigo-800/60">
                            <Car size={14} />
                            {b.vehicle}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-xs font-black border inline-flex items-center gap-1.5 ${getStatusColor(b.status)}`}>
                            {getStatusIcon(b.status)}<span>{t(b.status)}</span>
                          </span>
                          <span className={`px-3 py-1 rounded-full text-xs font-black border ${depositTone}`}>
                            {depositStatusLabel(depositStatus)}
                          </span>
                        </div>

                        <div className="min-w-0">
                          <div className="text-lg sm:text-[1.08rem] font-extrabold text-slate-800 dark:text-white truncate">{b.client}</div>
                          <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 mt-1 truncate">{b.bookingCode || `#${b.id}`}</div>
                        </div>

                        <div className="hidden sm:flex h-11 w-11 items-center justify-center rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-300 group-hover:text-indigo-500 transition-colors">
                          <ArrowRight size={18} />
                        </div>
                      </div>

                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                          <div className="rounded-xl bg-white/80 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 px-3 py-2">
                            <div className="text-[11px] uppercase tracking-wide font-black text-slate-400">{t('start_date_label')}</div>
                            <div className="font-bold text-slate-700 dark:text-slate-200">{formatDate(b.startDate)}</div>
                          </div>
                          <div className="rounded-xl bg-white/80 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 px-3 py-2">
                            <div className="text-[11px] uppercase tracking-wide font-black text-slate-400">{t('end_date_label')}</div>
                            <div className="font-bold text-slate-700 dark:text-slate-200">{formatDate(b.endDate)}</div>
                          </div>
                          <div className="rounded-xl bg-white/80 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 px-3 py-2">
                            <div className="text-[11px] uppercase tracking-wide font-black text-slate-400">{t('total_col')}</div>
                            <div className="font-extrabold text-slate-900 dark:text-white">{formatCurrency(b.total || b.totalPrice || 0)}</div>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-row xl:flex-col items-start xl:items-end justify-between gap-3 xl:min-w-[150px]">
                        <div className="text-sm text-slate-500 dark:text-slate-400">
                          <div className="font-bold">{uiText({ sq: 'Depozita', en: 'Deposit', de: 'Kaution', it: 'Deposito', fr: 'Dépôt', hr: 'Depozit' })}</div>
                          <div className="text-base font-extrabold text-slate-800 dark:text-white">{formatCurrency(Number(b.deposit || 0))}</div>
                        </div>
                        <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 font-bold group-hover:border-indigo-200 dark:group-hover:border-indigo-700 group-hover:text-indigo-600 dark:group-hover:text-indigo-300 transition-colors">
                          <Eye size={16} />
                          <span>{t('view')}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}

              {bookings.length === 0 && (
                <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 p-8 text-center text-slate-500 dark:text-slate-400 font-semibold">
                  {t('no_search_results')}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl shadow-slate-200/40 dark:shadow-none border border-slate-100 dark:border-slate-700 p-7 flex flex-col">
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-slate-100 dark:border-slate-700">
              <h3 className="font-extrabold text-slate-800 dark:text-white text-xl flex items-center">
                <Bell size={20} className="mr-2 text-indigo-500"/>
                {t('alerts')}
              </h3>
              {globalAlerts.length > 0 && (
                <span className="bg-red-100 text-red-600 text-xs font-black px-2 py-1 rounded-lg">{globalAlerts.length}</span>
              )}
            </div>
            
            <div className="space-y-4 flex-1 overflow-y-auto pr-2 custom-scrollbar">
              {globalAlerts.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-slate-400 opacity-70 mt-10">
                  <CheckCircle size={48} className="mb-4 text-emerald-400" />
                  <p className="font-medium text-center">{t('no_alerts_title')}<br/>{t('no_alerts_subtitle')}</p>
                </div>
              ) : (
                globalAlerts.map(alert => {
                  const AlertIcon = alert.icon;
                  return (
                    <div key={alert.id} className={`p-4 rounded-2xl border flex items-start space-x-4 transition-all hover:scale-[1.02] ${
                      alert.type === 'danger' ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/50' :
                      alert.type === 'warning' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/50' :
                      'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800/50'
                    }`}>
                      <div className={`p-2 rounded-xl mt-0.5 flex-shrink-0 ${
                        alert.type === 'danger' ? 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400' :
                        alert.type === 'warning' ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400' :
                        'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400'
                      }`}>
                        <AlertIcon size={20} />
                      </div>
                      <div>
                        <p className={`font-bold text-sm mb-1 ${
                          alert.type === 'danger' ? 'text-red-900 dark:text-red-200' :
                          alert.type === 'warning' ? 'text-amber-900 dark:text-amber-200' :
                          'text-blue-900 dark:text-blue-200'
                        }`}>{alert.title}</p>
                        <p className="text-xs text-slate-600 dark:text-slate-400 font-medium leading-relaxed">{alert.message}</p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            
            {globalAlerts.length > 0 && (
              <button onClick={() => setActiveMenu('insurance')} className="w-full mt-4 py-3 bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl text-base font-bold text-slate-600 dark:text-slate-300 transition-colors">
                {t('go_to_details')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const getClientBookings = (clientData) => {
    if (!clientData) return [];
    return bookings.filter(b => String(b.clientId) === String(clientData.id) || String(b.client).trim().toLowerCase() === String(clientData.name || '').trim().toLowerCase());
  };

  const getClientPayments = (clientData) => {
    const clientBookings = getClientBookings(clientData);
    const bookingIds = clientBookings.map(b => String(b.id));
    return payments.filter(p => bookingIds.includes(String(p.bookingId)) || String(p.clientId || '') === String(clientData?.id || ''));
  };

  const renderClientDetailsView = (clientData) => {
    const detailRows = [
      [t('full_name_required').replace('*', ''), clientData?.name || '-'],
      [t('phone_number'), clientData?.phone || '-'],
      [t('birthdate'), clientData?.birthdate || '-'],
      [t('address_label'), clientData?.address || '-'],
      [t('secondary_phone'), clientData?.secondaryPhone || '-'],
      [t('email'), clientData?.email || '-'],
      [t('personal_id_number'), clientData?.personalId || '-'],
      [t('passport_number'), clientData?.passportNumber || '-'],
      [t('license_number'), clientData?.licenseNumber || '-'],
      [t('id_front'), clientData?.idFront ? (<a href={clientData.idFront} target="_blank" rel="noreferrer" className="text-indigo-600 font-medium hover:underline">{t('view')}</a>) : <span className="italic text-slate-400">-</span>],
      [t('id_back'), clientData?.idBack ? (<a href={clientData.idBack} target="_blank" rel="noreferrer" className="text-indigo-600 font-medium hover:underline">{t('view')}</a>) : <span className="italic text-slate-400">-</span>],
    ];

    return (
      <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <table className="w-full text-sm">
          <tbody>
            {detailRows.map(([label, value]) => (
              <tr key={String(label)} className="border-b border-slate-200 dark:border-slate-700 last:border-b-0">
                <td className="w-[45%] px-4 py-3 font-medium text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-900/40">{label}:</td>
                <td className="px-4 py-3 text-slate-900 text-slate-800 dark:text-slate-100">{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderClientBookingsTab = (clientData) => {
    const clientBookings = getClientBookings(clientData);
    if (!clientBookings.length) {
      return <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-8 text-center text-slate-500 dark:text-slate-400">{t('client_bookings_empty')}</div>;
    }
    return (
      <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('id')}</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('client_col')} - {t('vehicle_col')}</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('start_date_label')}</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('end_date_label')}</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('amount_col')}</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('daily_price')}</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('actions_col')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {clientBookings.map(booking => (
                <tr key={booking.id}>
                  <td className="px-4 py-4 text-slate-700 dark:text-slate-300">{booking.id}</td>
                  <td className="px-4 py-4 font-semibold text-slate-900 text-slate-800 dark:text-slate-100">{booking.client} - {booking.vehicle}</td>
                  <td className="px-4 py-4 text-slate-700 dark:text-slate-300">{booking.startDate} {booking.startTime || ''}</td>
                  <td className="px-4 py-4 text-slate-700 dark:text-slate-300">{booking.endDate} {booking.endTime || ''}</td>
                  <td className="px-4 py-4 text-slate-700 dark:text-slate-300">{formatCurrency(booking.total)}</td>
                  <td className="px-4 py-4 text-slate-700 dark:text-slate-300">{booking.dailyPrice ? formatCurrency(booking.dailyPrice) : '-'}</td>
                  <td className="px-4 py-4 text-slate-700 dark:text-slate-200">
                    <button type="button" onClick={() => openModal('booking', 'edit', booking)} className="p-2 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors">
                      <Eye size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderClientPaymentsTab = (clientData) => {
    const clientPayments = getClientPayments(clientData);
    if (!clientPayments.length) {
      return <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-8 text-center text-slate-500 dark:text-slate-400">{t('client_payments_empty')}</div>;
    }
    return (
      <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('booking_col')}</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('user_label')}</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('payment_method_label')}</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('amount_col')}</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('date_col')}</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('notes')}</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('actions_col')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {clientPayments.map(payment => {
                const booking = bookings.find(b => String(b.id) === String(payment.bookingId));
                return (
                  <tr key={payment.id}>
                    <td className="px-4 py-4 text-slate-700 dark:text-slate-300">{booking ? `${booking.client} - ${booking.vehicle}` : payment.bookingId}</td>
                    <td className="px-4 py-4 text-slate-700 dark:text-slate-300">{booking?.client || clientData?.name || '-'}</td>
                    <td className="px-4 py-4 text-slate-700 dark:text-slate-300">{payment.method ? t(payment.method === 'Kesh' ? 'cash' : payment.method === 'Kartelë Bankare' ? 'bank_card' : payment.method === 'Transfer Bankar' ? 'bank_transfer' : payment.method === 'PayPal' ? 'paypal' : 'other') : '-'}</td>
                    <td className="px-4 py-4 text-slate-700 dark:text-slate-300">{formatCurrency(payment.amount)}</td>
                    <td className="px-4 py-4 text-slate-700 dark:text-slate-300">{payment.date}</td>
                    <td className="px-4 py-4 text-slate-700 dark:text-slate-300">{payment.notes || '-'}</td>
                    <td className="px-4 py-4 text-slate-700 dark:text-slate-200">
                      <div className="flex gap-2">
                        <button type="button" onClick={() => openModal('payment', 'edit', payment)} className="p-2 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors">
                          <Edit3 size={16} />
                        </button>
                        <button type="button" onClick={() => handleDelete('payment', payment.id, payment.id)} className="p-2 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };


  const getCarDisplayName = (carData) => [carData?.brand, carData?.model].filter(Boolean).join(' ').trim();

  const getCarBookings = (carData) => {
    const fullName = getCarDisplayName(carData);
    return bookings.filter(b => String(b.vehicle || '').trim() === fullName || String(b.vehicle || '').includes(carData?.plate || ''));
  };

  const getCarServices = (carData) => {
    const fullName = getCarDisplayName(carData);
    return maintenance.filter(m => String(m.vehicle || '').trim() === fullName || String(m.plate || '').trim() === String(carData?.plate || ''));
  };

  const getCarExpenses = (carData) => {
    const fullName = getCarDisplayName(carData);
    return expenses.filter(e => String(e.vehicle || '').trim() === String(carData?.plate || '') || String(e.vehicle || '').trim() === fullName || String(e.description || '').includes(fullName));
  };

  const getCarTplRecords = (carData) => tplRecords.filter(r => String(r.carId) === String(carData?.id) || String(r.plate || '').trim() === String(carData?.plate || ''));

  const renderCarDetailsView = (carData) => {
    const rows = [
      [t('car_name'), getCarDisplayName(carData) || '-'],
      [t('year'), carData?.year || '-'],
      [t('rental_price'), formatCurrency(carData?.price || 0)],
      [t('color'), carData?.color || '-'],
      [t('license_plate_label'), carData?.plate || '-'],
      [t('identification_number'), carData?.vin || carData?.identificationNumber || '-'],
      [t('registration_date'), carData?.registrationDate || '-'],
      [t('number_of_seats'), carData?.seats || '-'],
      [t('number_of_doors'), carData?.doors || '-'],
      [t('fuel_type'), carData?.fuelType ? trFuelLabel(t, carData?.fuelType) : '-'],
      [t('transmission_type'), carData?.transmission ? trTransmissionLabel(t, carData?.transmission) : '-'],
      [t('has_air_conditioning'), carData?.hasAc ? t('yes') : t('no')],
      [t('show_in_website'), carData?.showOnWeb ? t('yes') : t('no')],
      [t('engine_size'), carData?.engineSize || '-'],
      [t('optional_details'), carData?.details || '-'],
    ];
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/40 font-bold text-slate-800 dark:text-white">{t('car_details_title')}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {rows.map(([label, value]) => (
                  <tr key={label} className="border-b border-slate-100 dark:border-slate-700 last:border-b-0">
                    <td className="px-4 py-3 font-semibold text-slate-700 text-slate-800 dark:text-slate-200 w-1/2">{label}</td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="space-y-4">
          <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
            <div className="aspect-[4/3] bg-slate-100 dark:bg-slate-700 overflow-hidden">
              <img src={carData?.image || 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=500'} alt={getCarDisplayName(carData)} className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
            <div className="font-bold text-slate-800 dark:text-white mb-3">{t('car_images')}</div>
            <button type="button" onClick={() => openModal('car','edit',carData)} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-2 font-semibold text-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors">
              <Camera size={16} /> {t('add_car_images')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCarSimpleEmpty = (message) => <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-8 text-center text-slate-500 dark:text-slate-400">{message}</div>;

  const renderCarBookingsTab = (carData) => {
    const carBookings = getCarBookings(carData);
    if (!carBookings.length) return renderCarSimpleEmpty(t('car_no_reservations'));
    return (
      <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead><tr className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">ID</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('client_col')}</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('start_date_label')}</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('end_date_label')}</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('status')}</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {carBookings.map(item => <tr key={item.id}><td className="px-4 py-4 text-slate-700 dark:text-slate-200">{item.id}</td><td className="px-4 py-4 text-slate-700 dark:text-slate-200">{item.client}</td><td className="px-4 py-4 text-slate-700 dark:text-slate-200">{item.startDate}</td><td className="px-4 py-4 text-slate-700 dark:text-slate-200">{item.endDate}</td><td className="px-4 py-4 text-slate-700 dark:text-slate-200"><span className={`px-2.5 py-1 rounded-lg text-xs font-bold border ${getStatusColor(item.status)}`}>{t(item.status)}</span></td></tr>)}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCarServicesTab = (carData) => {
    const items = getCarServices(carData);
    if (!items.length) return renderCarSimpleEmpty(t('car_no_services'));
    return (
      <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead><tr className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('defect_service_description')}</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('date')}</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('cost')}</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('status')}</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {items.map(item => <tr key={item.id}><td className="px-4 py-4 text-slate-700 dark:text-slate-200">{item.description}</td><td className="px-4 py-4 text-slate-700 dark:text-slate-200">{item.date}</td><td className="px-4 py-4 text-slate-700 dark:text-slate-200">{formatCurrency(item.cost || 0)}</td><td className="px-4 py-4 text-slate-700 dark:text-slate-200"><span className={`px-2.5 py-1 rounded-lg text-xs font-bold border ${getStatusColor(item.status)}`}>{t(item.status)}</span></td></tr>)}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCarExpensesTab = (carData) => {
    const items = getCarExpenses(carData);
    if (!items.length) return renderCarSimpleEmpty(t('car_no_expenses'));
    return (
      <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead><tr className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('category')}</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('expense_desc_label')}</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('amount_col')}</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('date')}</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {items.map(item => <tr key={item.id}><td className="px-4 py-4 text-slate-700 dark:text-slate-200">{item.category}</td><td className="px-4 py-4 text-slate-700 dark:text-slate-200">{item.description}</td><td className="px-4 py-4 text-slate-700 dark:text-slate-200">{formatCurrency(item.amount || 0)}</td><td className="px-4 py-4 text-slate-700 dark:text-slate-200">{item.date}</td></tr>)}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCarTplTab = (carData) => {
    const items = getCarTplRecords(carData);
    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <button type="button" onClick={() => openModal('tpl','add',carData)} className="inline-flex items-center gap-2 rounded-xl bg-cyan-600 px-4 py-2 font-semibold text-white hover:bg-cyan-700 transition-colors"><Plus size={16} /> {t('add_tpl')}</button>
        </div>
        {!items.length ? renderCarSimpleEmpty(t('car_tpl_empty')) : (
          <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead><tr className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('tpl_amount')}</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('renewal_date')}</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('expiry_date')}</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('actions_col')}</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {items.map(item => <tr key={item.id}><td className="px-4 py-4 text-slate-700 dark:text-slate-200">{formatCurrency(item.amount || 0)}</td><td className="px-4 py-4 text-slate-700 dark:text-slate-200">{item.renewalDate}</td><td className="px-4 py-4 text-slate-700 dark:text-slate-200">{item.expiryDate}</td><td className="px-4 py-4 text-slate-700 dark:text-slate-200"><div className="flex gap-2"><button type="button" onClick={() => openModal('tpl','edit',item)} className="p-2 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"><Edit3 size={16} /></button><button type="button" onClick={() => handleDelete('tpl', item.id, item.vehicle || item.plate)} className="p-2 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"><Trash2 size={16} /></button></div></td></tr>)}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderCarPricingTab = () => renderCarSimpleEmpty(t('car_pricing_empty'));

  const renderModalContent = () => {
    switch(modalType) {
      case 'client':
        const clientTabButtonClass = (tab) => `px-4 py-2.5 rounded-xl font-bold text-sm transition-colors ${activeClientTab === tab ? 'bg-green-100 text-slate-900 dark:bg-green-900/30 text-slate-800 dark:text-white border border-green-200 dark:border-green-800' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 border border-transparent'}`;
        return (
          <div className="space-y-5">
            {modalMode !== 'add' && (
              <div>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-extrabold text-slate-900 text-slate-800 dark:text-white mb-4">{t('client_details_title')}: {formData.name}</h3>
                <div className="flex flex-wrap gap-2 border-b border-slate-200 dark:border-slate-700 pb-4">
                  <button type="button" onClick={() => setActiveClientTab('details')} className={clientTabButtonClass('details')}>{t('client_tab_details')}</button>
                  <button type="button" onClick={() => setActiveClientTab('bookings')} className={clientTabButtonClass('bookings')}>{t('client_tab_bookings')}</button>
                  <button type="button" onClick={() => setActiveClientTab('payments')} className={clientTabButtonClass('payments')}>{t('client_tab_payments')}</button>
                </div>
              </div>
            )}

            {(modalMode === 'add' || activeClientTab === 'details') && (
              modalMode === 'view' ? renderClientDetailsView(formData) : (
                <div className="space-y-4">
                  <FormSection title={t('client_profile_section')} icon={User}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <Input formData={formData} setFormData={setFormData} label={t('full_name_required')} name="name" required={true} placeholder={t('enter_full_name')} icon={User} />
                      </div>
                      <Input formData={formData} setFormData={setFormData} label={t('phone_number')} name="phone" required={true} placeholder="p.sh. 044123456" icon={Phone} />
                      <Input formData={formData} setFormData={setFormData} label={t('email')} name="email" type="email" required={false} placeholder="test@gmail.com" icon={Mail} />
                      <Input formData={formData} setFormData={setFormData} label={t('secondary_phone')} name="secondaryPhone" required={false} placeholder={t('optional')} icon={Phone} />
                      <Input formData={formData} setFormData={setFormData} label={t('birthdate')} name="birthdate" type="date" required={false} icon={Calendar} />
                      <div className="md:col-span-2">
                        <Input formData={formData} setFormData={setFormData} label={t('address_label')} name="address" required={false} placeholder="p.sh. Rr. Skenderbeu 12" icon={MapPin} />
                      </div>
                      <div className="md:col-span-2">
                        <FileUpload formData={formData} setFormData={setFormData} showToast={showToast} label={t('client_avatar')} name="avatar" hint={t('optional')} />
                      </div>
                    </div>
                  </FormSection>

                  <FormSection title={t('documents_section')} icon={Shield}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input formData={formData} setFormData={setFormData} label={t('personal_id_number')} name="personalId" required={false} placeholder="p.sh. 123456789" />
                      <Input formData={formData} setFormData={setFormData} label={t('license_number')} name="licenseNumber" required={false} placeholder={t('optional')} />
                      <div className="md:col-span-2">
                        <Input formData={formData} setFormData={setFormData} label={t('passport_number')} name="passportNumber" required={false} placeholder={t('optional')} />
                      </div>
                      <FileUpload formData={formData} setFormData={setFormData} showToast={showToast} label={t('id_front')} name="idFront" hint="PNG / JPG" />
                      <FileUpload formData={formData} setFormData={setFormData} showToast={showToast} label={t('id_back')} name="idBack" hint="PNG / JPG" />
                      <div className="md:col-span-2">
                        <Checkbox formData={formData} setFormData={setFormData} label={t('blacklist_client')} name="isBlacklisted" />
                      </div>
                    </div>
                  </FormSection>

                  <FormSection title={t('notes')} icon={MessageSquare}>
                    <Textarea formData={formData} setFormData={setFormData} label={t('notes')} name="notes" placeholder={t('enter_notes_here')} rows={4} />
                  </FormSection>
                </div>
              )
            )}

            {modalMode !== 'add' && activeClientTab === 'bookings' && renderClientBookingsTab(formData)}
            {modalMode !== 'add' && activeClientTab === 'payments' && renderClientPaymentsTab(formData)}
          </div>
        );

      case 'booking':
        const rentTotal = parseFloat(formData.totalPrice) || 0;
        const deliveryTotal = parseFloat(formData.deliveryCharge) || 0;
        const pickupTotal = parseFloat(formData.pickupCharge) || 0;
        const extrasTotal = (formData.extraGps ? 5 : 0) + (formData.extraChildSeat ? 10 : 0) + (formData.extraInsurance ? 15 : 0);
        const grandTotal = rentTotal + deliveryTotal + pickupTotal + extrasTotal;

        if (modalMode === 'view') {
          return renderBookingViewContent(formData);
        }

        const checkCarBooked = (carName) => {
          if (!formData.startDate || !formData.endDate) return false;
          
          const formStart = new Date(formData.startDate);
          const formEnd = new Date(formData.endDate);
          formStart.setHours(0,0,0,0);
          formEnd.setHours(23,59,59,999);

          return bookings.some(b => {
            if (formData.id && b.id === formData.id) return false; 
            if (b.status === 'cancelled' || b.status === 'completed') return false; 
            if (b.vehicle !== carName) return false;

            const bStart = new Date(b.startDate);
            const bEnd = new Date(b.endDate);
            bStart.setHours(0,0,0,0);
            bEnd.setHours(23,59,59,999);

            return formStart <= bEnd && formEnd >= bStart;
          });
        };

        return (
          <div className="space-y-4">
            <FormSection title={t('booking_details_section')} icon={Calendar}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-base font-bold text-slate-700 dark:text-slate-300 mb-1.5">{t('start_date_time')}</label>
                  <div className="flex space-x-2">
                    <input 
                      type="date" 
                      required 
                      value={formData.startDate || ''} 
                      onChange={e => setFormData({...formData, startDate: e.target.value})} 
                      className="w-2/3 px-3 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-slate-800 dark:text-white transition-all" 
                    />
                    <TimeSelect required value={formData.startTime || ""} onChange={(v) => setFormData({ ...formData, startTime: v })} className="w-1/3 min-w-[110px]" />
                  </div>
                </div>
                <div className="flex flex-col">
                  <label className="text-base font-bold text-slate-700 dark:text-slate-300 mb-1.5">{t('end_date_time')}</label>
                  <div className="flex space-x-2">
                    <input 
                      type="date" 
                      required 
                      value={formData.endDate || ''} 
                      onChange={e => setFormData({...formData, endDate: e.target.value})} 
                      className="w-2/3 px-3 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-slate-800 dark:text-white transition-all" 
                    />
                    <TimeSelect required value={formData.endTime || ""} onChange={(v) => setFormData({ ...formData, endTime: v })} className="w-1/3 min-w-[110px]" />
                  </div>
                </div>
                
                <div className="md:col-span-2">
                  <Select 
                    formData={formData} setFormData={setFormData}
                    label={t('car_required')} 
                    name="vehicle" 
                    icon={Car}
                    required={true}
                    options={[
                      {value: '', label: t('select_car')},
                      ...cars.map(c => {
                        const carName = [c.brand, c.model].filter(Boolean).join(' ');
                        const isBooked = checkCarBooked(carName);
                        return {
                          value: carName, 
                          label: `${carName} (${c.plate}) - ${settings.currency}${c.price}/${t('day')}${isBooked ? ` ⚠️ (${t('booked_for_dates')})` : ''}`,
                          disabled: isBooked
                        }
                      })
                    ]} 
                  />
                </div>

                <Input formData={formData} setFormData={setFormData} label={t('return_location')} name="returnLocation" required={false} icon={MapPin} />
                <Input formData={formData} setFormData={setFormData} label={t('pickup_location')} name="pickupLocation" required={false} icon={MapPin} />
                
                <Input formData={formData} setFormData={setFormData} label={t('daily_price')} name="dailyPrice" type="number" icon={DollarSign} />
                <Input formData={formData} setFormData={setFormData} label={t('total_rent_price')} name="totalPrice" type="number" icon={DollarSign} />
                
                <Input formData={formData} setFormData={setFormData} label={t('deposit')} name="deposit" type="number" required={false} placeholder="0" icon={DollarSign} />
                <Input formData={formData} setFormData={setFormData} label={t('notes')} name="notes" required={false} icon={MessageSquare} />
                
                <Input formData={formData} setFormData={setFormData} label={t('delivery_fee')} name="deliveryCharge" type="number" required={false} icon={DollarSign} />
                <Input formData={formData} setFormData={setFormData} label={t('pickup_fee')} name="pickupCharge" type="number" required={false} icon={DollarSign} />
                
                <div className="md:col-span-2">
                  <Input formData={formData} setFormData={setFormData} label={t('flight_number')} name="flightNumber" required={false} icon={Zap} />
                </div>
              </div>
            </FormSection>

            <FormSection title={t('client_section')} icon={Users}>
              <div className="flex items-center space-x-6 mb-6 pb-4 border-b border-slate-100 dark:border-slate-700">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="clientType" 
                    value="existing" 
                    checked={formData.clientType === 'existing' || !formData.clientType} 
                    onChange={() => setFormData({...formData, clientType: 'existing'})} 
                    className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 border-slate-300" 
                  />
                  <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{t('existing_client')}</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="clientType" 
                    value="new" 
                    checked={formData.clientType === 'new'} 
                    onChange={() => setFormData({...formData, clientType: 'new'})} 
                    className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 border-slate-300" 
                  />
                  <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{t('new_client')}</span>
                </label>
              </div>

              {formData.clientType === 'existing' ? (
                <div className="space-y-4 relative">
                  <SearchableClientSelect 
                    formData={formData} setFormData={setFormData} clients={clients}
                    label={t('choose_client')} 
                    name="clientId" 
                    placeholder={t('search_client_placeholder')}
                  />
                  
                  {formData.clientId && (
                    <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-700/30 rounded-xl border border-slate-200 dark:border-slate-700 relative z-10">
                      {(() => {
                        const selectedClient = clients.find(c => String(c.id) === String(formData.clientId));
                        if (!selectedClient) return null;
                        
                        return (
                          <div className="flex items-start space-x-4">
                            {selectedClient.avatar ? (
                              <img src={selectedClient.avatar} alt={selectedClient.name} className="w-16 h-16 rounded-xl object-cover" />
                            ) : (
                              <div className="w-16 h-16 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                                <User size={24} className="text-indigo-600 dark:text-indigo-400" />
                              </div>
                            )}
                            <div className="flex-1">
                              <h4 className="font-bold text-lg text-slate-800 dark:text-white">{selectedClient.name}</h4>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-2 text-sm">
                                {selectedClient.phone && (
                                  <div className="flex items-center space-x-2 text-slate-600 dark:text-slate-400">
                                    <Phone size={14} />
                                    <span>{selectedClient.phone}</span>
                                  </div>
                                )}
                                {selectedClient.email && (
                                  <div className="flex items-center space-x-2 text-slate-600 dark:text-slate-400">
                                    <Mail size={14} />
                                    <span>{selectedClient.email}</span>
                                  </div>
                                )}
                                {selectedClient.address && (
                                  <div className="flex items-center space-x-2 text-slate-600 dark:text-slate-400 col-span-2">
                                    <MapPin size={14} />
                                    <span>{selectedClient.address}</span>
                                  </div>
                                )}
                              </div>
                              <div className="mt-3 flex items-center space-x-3 text-xs">
                                <span className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-full">
                                  {selectedClient.rentals || 0} {t('bookings_word')}
                                </span>
                                <span className="px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full">
                                  {formatCurrency(selectedClient.totalSpent || 0)} {t('total_overall')}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Input formData={formData} setFormData={setFormData} label="Emri i Klientit*" name="newClientName" placeholder="Emri i Klientit" icon={User} />
                  </div>
                  <Input formData={formData} setFormData={setFormData} label="Numri i Telefonit" name="newClientPhone" placeholder="Numri i Telefonit" icon={Phone} required={false} />
                  <Input formData={formData} setFormData={setFormData} label={t('email')} name="newClientEmail" type="email" placeholder="Email i Klientit" icon={Mail} required={false} />
                  <Input formData={formData} setFormData={setFormData} label={t('address_label')} name="newClientAddress" placeholder="Adresa e Klientit" icon={MapPin} required={false} />
                  <Input formData={formData} setFormData={setFormData} label={t('birthdate')} name="newClientBirthdate" type="date" icon={Calendar} required={false} />
                  <Input formData={formData} setFormData={setFormData} label={t('secondary_phone')} name="newClientSecondaryPhone" placeholder="Telefon shtesë" icon={Phone} required={false} />
                  <Input formData={formData} setFormData={setFormData} label="Numri i Identifikimit" name="newClientPersonalId" placeholder="Numri personal" required={false} />
                  <Input formData={formData} setFormData={setFormData} label="Numri i Licencës" name="newClientLicenseNumber" placeholder="Numri i licencës" required={false} />
                  <Input formData={formData} setFormData={setFormData} label={t('passport_number')} name="newClientPassportNumber" placeholder="Numri i pasaportit" required={false} />
                  <FileUpload formData={formData} setFormData={setFormData} showToast={showToast} label={t('id_front')} name="newClientIdFront" hint="PNG / JPG" />
                  <FileUpload formData={formData} setFormData={setFormData} showToast={showToast} label={t('id_back')} name="newClientIdBack" hint="PNG / JPG" />
                </div>
              )}
            </FormSection>

            <FormSection title={t('additional_driver_section')} icon={Users}>
              <div className="space-y-4">
                <Checkbox formData={formData} setFormData={setFormData} label={t('enable_additional_driver')} name="additionalDriver" inline={true} />
                {formData.additionalDriver && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
                    <Input formData={formData} setFormData={setFormData} label={t('full_name_required').replace('*', '')} name="additionalDriverName" placeholder={t('enter_full_name')} icon={User} />
                    <Input formData={formData} setFormData={setFormData} label={t('phone_number')} name="additionalDriverPhone" placeholder={t('phone_number')} icon={Phone} />
                    <Input formData={formData} setFormData={setFormData} label={t('license_number')} name="additionalDriverLicense" placeholder={t('license_number')} />
                    <Input formData={formData} setFormData={setFormData} label={t('birthdate')} name="additionalDriverBirthdate" type="date" />
                  </div>
                )}
              </div>
            </FormSection>

            <FormSection title={t('extras_section')} icon={Package}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Checkbox formData={formData} setFormData={setFormData} label={t('extra_gps')} name="extraGps" inline={true} />
                <Checkbox formData={formData} setFormData={setFormData} label={t('extra_child_seat')} name="extraChildSeat" inline={true} />
                <Checkbox formData={formData} setFormData={setFormData} label={t('extra_full_insurance')} name="extraInsurance" inline={true} />
              </div>
            </FormSection>

            <div className="p-5 bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-xl border border-indigo-100 dark:border-indigo-800 mt-4">
              <h3 className="font-extrabold text-lg mb-4 text-indigo-900 dark:text-indigo-300">{t('price_summary')}</h3>
              <div className="space-y-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                <div className="flex justify-between"><span>{t('rent_price')}</span> <span>{formatCurrency(rentTotal)}</span></div>
                <div className="flex justify-between"><span>{t('delivery_fee')}</span> <span>{formatCurrency(deliveryTotal)}</span></div>
                <div className="flex justify-between"><span>{t('pickup_fee')}</span> <span>{formatCurrency(pickupTotal)}</span></div>
                <div className="flex justify-between"><span>{t('extras_label')}</span> <span>{formatCurrency(extrasTotal)}</span></div>
              </div>
              <div className="mt-4 pt-3 border-t border-indigo-200 dark:border-indigo-700 flex justify-between items-center">
                <span className="font-black text-xl text-indigo-900 dark:text-indigo-300">{t('total_label')}</span>
                <span className="font-black text-2xl text-indigo-600 dark:text-indigo-400">{formatCurrency(grandTotal)}</span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Select formData={formData} setFormData={setFormData}
                label={t('booking_status_label')} 
                name="status" 
                options={[
                  {value: 'pending', label: t('pending')}, 
                  {value: 'active', label: t('status_active')}, 
                  {value: 'completed', label: t('status_completed')},
                  {value: 'cancelled', label: t('status_cancelled')}
                ]} 
              />
              <Select formData={formData} setFormData={setFormData}
                label={t('payment_status_label')} 
                name="paymentStatus" 
                options={[
                  {value: 'pending', label: t('pending')}, 
                  {value: 'paid', label: t('status_paid')},
                  {value: 'partially_paid', label: t('status_partially_paid')}
                ]} 
              />
            </div>
          </div>
        );

      case 'car':
        if (modalMode === 'view') {
          const carTabButtonClass = (tab) => `px-4 py-2.5 rounded-xl font-bold text-sm transition-colors ${activeCarTab === tab ? 'bg-green-100 text-slate-900 dark:bg-green-900/30 text-slate-800 dark:text-white border border-green-200 dark:border-green-800' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 border border-transparent'}`;
          return (
            <div className="space-y-5">
              <div>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-extrabold text-slate-900 text-slate-800 dark:text-white mb-4">{t('car_details_title')}: {getCarDisplayName(formData)}</h3>
                <div className="flex flex-wrap gap-2 border-b border-slate-200 dark:border-slate-700 pb-4">
                  <button type="button" onClick={() => setActiveCarTab('details')} className={carTabButtonClass('details')}>{t('car_tab_details')}</button>
                  <button type="button" onClick={() => setActiveCarTab('reservations')} className={carTabButtonClass('reservations')}>{t('car_tab_reservations')}</button>
                  <button type="button" onClick={() => setActiveCarTab('services')} className={carTabButtonClass('services')}>{t('car_tab_services')}</button>
                  <button type="button" onClick={() => setActiveCarTab('expenses')} className={carTabButtonClass('expenses')}>{t('car_tab_expenses')}</button>
                  <button type="button" onClick={() => setActiveCarTab('pricing')} className={carTabButtonClass('pricing')}>{t('car_tab_pricing_seasons')}</button>
                  <button type="button" onClick={() => setActiveCarTab('tpl')} className={carTabButtonClass('tpl')}>{t('car_tab_tpl')}</button>
                </div>
              </div>
              {activeCarTab === 'details' && renderCarDetailsView(formData)}
              {activeCarTab === 'reservations' && renderCarBookingsTab(formData)}
              {activeCarTab === 'services' && renderCarServicesTab(formData)}
              {activeCarTab === 'expenses' && renderCarExpensesTab(formData)}
              {activeCarTab === 'pricing' && renderCarPricingTab(formData)}
              {activeCarTab === 'tpl' && renderCarTplTab(formData)}
            </div>
          );
        }
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Input formData={formData} setFormData={setFormData} label={t("brand")} name="brand" required={true} icon={Tag} />
            </div>
            <Input formData={formData} setFormData={setFormData} label={t("model")} name="model" icon={Tag} />
            <Input formData={formData} setFormData={setFormData} label={t("year")} name="year" type="number" />
            <Input formData={formData} setFormData={setFormData} label={t("plate")} name="plate" />
            <Input formData={formData} setFormData={setFormData} label={t("color")} name="color" required={false} />
            <Input formData={formData} setFormData={setFormData} label={`${t("price_per_day")} (${settings.currency})`} name="price" type="number" icon={DollarSign} />
            
            <div className="md:col-span-2">
              <Select formData={formData} setFormData={setFormData}
                label={t("status")}
                name="status"
                options={[
                  { value: 'available', label: t('available') },
                  { value: 'rented', label: t('rented') },
                  { value: 'maintenance', label: t('maintenance_status') },
                ]}
              />
            </div>

            <div className="md:col-span-2 border-t border-slate-200 dark:border-slate-700 pt-4 mt-2">
              <h4 className="font-bold text-slate-800 dark:text-white mb-3">{t('technical_specs')}</h4>
            </div>
            
            <Select formData={formData} setFormData={setFormData}
              label={t("fuel")}
              name="fuelType"
              options={[{value:"petrol",label:t("petrol")},{value:"diesel",label:t("diesel")},{value:"electric",label:t("electric")},{value:"hybrid",label:t("hybrid")}]}
            />
            <Select formData={formData} setFormData={setFormData}
              label={t('transmission')}
              name="transmission"
              options={[{value:'manual', label: t('manual')},{value:'automatic', label: t('automatic')}]}
            />
            <Input formData={formData} setFormData={setFormData} label={t("seats")} name="seats" type="number" />
            <Input formData={formData} setFormData={setFormData} label={t("doors")} name="doors" type="number" />
            <Input formData={formData} setFormData={setFormData} label={t("engine_size")} name="engineSize" required={false} />
            
            <div className="md:col-span-2">
              <Checkbox formData={formData} setFormData={setFormData} label={t("has_ac")} name="hasAc" inline={true} />
            </div>
            <div className="md:col-span-2">
              <Checkbox formData={formData} setFormData={setFormData} label={t("show_on_web")} name="showOnWeb" inline={true} />
            </div>
            
            <div className="md:col-span-2">
              <Textarea formData={formData} setFormData={setFormData} label={t("additional_details")} name="details" rows={3} placeholder={t("details_placeholder")} />
            </div>
            
            <div className="md:col-span-2">
              <FileUpload formData={formData} setFormData={setFormData} showToast={showToast} label={t("main_photo")} name="image" hint={t("upload_hint")} t={t} />
            </div>
          </div>
        );

      case 'payment':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Input formData={formData} setFormData={setFormData} label={t('booking_code')} name="bookingId" icon={Calendar} />
            </div>
            <Input formData={formData} setFormData={setFormData} label={`${t('amount_col')} (${settings.currency})`} name="amount" type="number" icon={DollarSign} />
            <Input formData={formData} setFormData={setFormData} label={t('date')} name="date" type="date" icon={Calendar} />
            <div className="md:col-span-2">
              <Select formData={formData} setFormData={setFormData}
                label={t('payment_method_label')} 
                name="method" 
                options={[
                  { value: 'Kesh', label: t('cash') },
                  { value: 'Kartelë Bankare', label: t('bank_card') },
                  { value: 'Transfer Bankar', label: t('bank_transfer') },
                  { value: 'PayPal', label: t('paypal') },
                  { value: 'Tjera', label: t('other') },
                ]} 
                icon={CreditCard}
              />
            </div>
            <div className="md:col-span-2">
              <Select formData={formData} setFormData={setFormData}
                label={t("status")} 
                name="status" 
                options={[
                  {value: 'completed', label: t('status_completed')},
                  {value: 'pending', label: t('pending')},
                  {value: 'failed', label: t('status_failed')}
                ]} 
              />
            </div>
          </div>
        );

      case 'expense':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Select formData={formData} setFormData={setFormData} label={t('expense_category_label')} name="category" required={true} options={[
                { value: 'Karburant', label: t('expense_cat_fuel') },
                { value: 'Servis dhe Mirëmbajtje', label: t('expense_cat_service') },
                { value: 'Pjesë Këmbimi', label: t('expense_cat_parts') },
                { value: 'Sigurim', label: t('expense_cat_insurance') },
                { value: 'Paga', label: t('expense_cat_salary') },
                { value: 'Qira e Zyrës', label: t('expense_cat_office_rent') },
                { value: 'Tjera', label: t('expense_cat_other') },
              ]} icon={Tag} />
            </div>
            <Input formData={formData} setFormData={setFormData} label={`${t('amount_col')} (${settings.currency})`} name="amount" type="number" icon={DollarSign} required={true} />
            <Input formData={formData} setFormData={setFormData} label={t('date')} name="date" type="date" icon={Calendar} required={true} />
            <div className="md:col-span-2">
              <Select formData={formData} setFormData={setFormData} label={t('expense_vehicle_link')} name="vehicle" options={[{value: '', label: t('expense_general_no_car')}, ...cars.map(c => ({value: c.plate, label: `${c.brand} ${c.model} (${c.plate})`}))]} icon={Car} />
            </div>
            <div className="md:col-span-2">
              <Textarea formData={formData} setFormData={setFormData} label={t('expense_desc_label')} name="description" rows={3} placeholder={t('expense_desc_ph')} />
            </div>
          </div>
        );

      case 'invoice':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               <div className="md:col-span-2 relative z-[100]">
                  <SearchableClientSelect formData={formData} setFormData={setFormData} clients={clients} label={t('choose_client_required')} name="clientId" placeholder={t('search_client_placeholder')} />
                  
                  {formData.clientId && (
                    <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-700/30 rounded-xl border border-slate-200 dark:border-slate-700 relative z-10">
                      {(() => {
                        const selectedClient = clients.find(c => String(c.id) === String(formData.clientId));
                        if (!selectedClient) return null;
                        
                        return (
                          <div className="flex items-start space-x-4">
                            {selectedClient.avatar ? (
                              <img src={selectedClient.avatar} alt={selectedClient.name} className="w-12 h-12 rounded-xl object-cover shadow-sm" />
                            ) : (
                              <div className="w-12 h-12 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center shadow-sm">
                                <User size={20} className="text-indigo-600 dark:text-indigo-400" />
                              </div>
                            )}
                            <div className="flex-1">
                              <h4 className="font-bold text-slate-800 dark:text-white">{selectedClient.name}</h4>
                              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 font-medium">{selectedClient.phone || t('no_phone')} • {selectedClient.email || t('no_email')}</p>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
               </div>
               <Input formData={formData} setFormData={setFormData} label={t('invoice_date')} name="date" type="date" icon={Calendar} required={true} />
               <Input formData={formData} setFormData={setFormData} label={`${t('invoice_total')} (${settings.currency})`} name="amount" type="number" icon={DollarSign} required={true} />
               <div className="md:col-span-2">
                  <Select formData={formData} setFormData={setFormData} label={t("status")} name="status" options={[
                    {value: 'pending', label: t('unpaid_pending')},
                    {value: 'paid', label: t('paid')},
                    {value: 'cancelled', label: t('status_cancelled')}
                  ]} required={true} />
               </div>
            </div>
          </div>
        );

      case 'contract':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               <Input formData={formData} setFormData={setFormData} label={t('contract_title_label')} name="title" required={true} placeholder={t('contract_title_ph')} icon={FileSignature} />
               <Input formData={formData} setFormData={setFormData} label={t('created_date')} name="date" type="date" icon={Calendar} required={true} />
               
               <div className="md:col-span-2 relative z-[100]">
                  <SearchableClientSelect formData={formData} setFormData={setFormData} clients={clients} label={t('choose_client_optional')} name="clientId" placeholder={t('search_client_placeholder')} />
                  <p className="text-xs text-slate-500 mt-1">{t('client_optional_hint')}</p>
               </div>
               
               <div className="md:col-span-2">
                  <Select formData={formData} setFormData={setFormData} label={t("status")} name="status" options={[
                    {value: 'draft', label: t('status_draft_template')},
                    {value: 'active', label: t('status_active_signed')},
                    {value: 'completed', label: t('completed')},
                    {value: 'cancelled', label: t('status_cancelled')}
                  ]} required={true} />
               </div>

               <div className="md:col-span-2">
                  <Textarea formData={formData} setFormData={setFormData} label={t('contract_content_label')} name="content" rows={14} placeholder={t('contract_content_ph')} />
                  <div className="mt-3 p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl border border-indigo-100 dark:border-indigo-800">
                    <p className="text-base font-bold text-indigo-800 dark:text-indigo-300 flex items-center mb-1"><Info size={16} className="mr-1.5"/>{t('content_tip_title')}</p>
                    <p className="text-xs text-slate-600 dark:text-slate-400">{t('content_tip_text')}</p>
                  </div>
               </div>
            </div>
          </div>
        );

      case 'insurance':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select formData={formData} setFormData={setFormData} label={`${t('vehicle')}*`} name="vehicle" required={true} options={[
              {value: '', label: t('choose_vehicle')},
              ...cars.map(c => ({value: `${c.brand} ${c.model}`, label: `${c.brand} ${c.model} (${c.plate})`}))
            ]} icon={Car} />
            <Input formData={formData} setFormData={setFormData} label={t("plate")} name="plate" icon={Tag} />
            <div className="md:col-span-2">
              <Input formData={formData} setFormData={setFormData} label={t('insurance_company')} name="provider" icon={Shield} list="insurance-providers" />
              <datalist id="insurance-providers">
                <option value="Sigal Uniqa Group Austria" />
                <option value="Illyria Insurance" />
                <option value="Sigma Vienna Insurance Group" />
                <option value="Scardian Insurance" />
                <option value="Kosova e Re Insurance" />
                <option value="Elsig Insurance" />
                <option value="Dukagjini Insurance" />
                <option value="Prisig Insurance" />
              </datalist>
            </div>
            <Input formData={formData} setFormData={setFormData} label={t('policy_number')} name="policyNumber" icon={FileText} />
            <Input formData={formData} setFormData={setFormData} label={t('expiry_date')} name="endDate" type="date" icon={Calendar} />
            <Input formData={formData} setFormData={setFormData} label={`${t('cost')} (${settings.currency})`} name="cost" type="number" icon={DollarSign} />
            <Select formData={formData} setFormData={setFormData} label={t("status")} name="status" options={[{value: 'active', label: t('status_active')}, {value: 'expired', label: t('expired')}]} />
          </div>
        );

      case 'maintenance':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select formData={formData} setFormData={setFormData} label={`${t('vehicle')}*`} name="vehicle" required={true} options={[
              {value: '', label: t('choose_vehicle')},
              ...cars.map(c => ({value: `${c.brand} ${c.model}`, label: `${c.brand} ${c.model} (${c.plate})`}))
            ]} icon={Car} />
            <Input formData={formData} setFormData={setFormData} label={t("plate")} name="plate" icon={Tag} />
            <div className="md:col-span-2">
              <Textarea formData={formData} setFormData={setFormData} label={t('defect_service_description')} name="description" />
            </div>
            <Input formData={formData} setFormData={setFormData} label={t('date')} name="date" type="date" icon={Calendar} />
            <Input formData={formData} setFormData={setFormData} label={`${t('cost')} (${settings.currency})`} name="cost" type="number" icon={DollarSign} />
            <div className="md:col-span-2">
              <Select formData={formData} setFormData={setFormData} label={t("status")} name="status" options={[{value: 'scheduled', label: t('scheduled')}, {value: 'in_progress', label: t('in_progress')}, {value: 'completed', label: t('status_completed')}]} />
            </div>
          </div>
        );

      case 'tpl':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Select
                formData={formData}
                setFormData={setFormData}
                label={`${t('vehicle')}*`}
                name="carId"
                required={true}
                options={[
                  { value: '', label: t('select_car') },
                  ...cars.map(c => ({ value: c.id, label: `${c.brand} ${c.model} (${c.plate})` }))
                ]}
                icon={Car}
              />
            </div>
            <Input formData={formData} setFormData={setFormData} label={`${t('tpl_amount')} (${settings.currency})`} name="amount" type="number" icon={DollarSign} required={true} />
            <Input formData={formData} setFormData={setFormData} label={t('renewal_date')} name="renewalDate" type="date" icon={Calendar} required={true} />
            <div className="md:col-span-2">
              <Input formData={formData} setFormData={setFormData} label={t('expiry_date')} name="expiryDate" type="date" icon={Calendar} required={true} />
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-8 text-slate-500">
            {t('preparing')}
          </div>
        );
    }
  };


  const getActionText = (kind) => {
    const map = {
      print: {
        sq: 'Printo',
        en: 'Print',
        de: 'Drucken',
        fr: 'Imprimer',
        it: 'Stampa',
        hr: 'Ispis',
      },
      export: {
        sq: 'Excel',
        en: 'Excel',
        de: 'Excel',
        fr: 'Excel',
        it: 'Excel',
        hr: 'Excel',
      },
    };
    return map[kind]?.[language] || (kind === 'print' ? 'Print' : 'Excel');
  };

  const getSectionFileName = (title) => {
    return String(title || 'export')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'export';
  };

  const normalizeExportValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.map(normalizeExportValue).join(', ');
    if (value instanceof Date) return value.toISOString();
    if (typeof value === 'object') {
      if (value.props?.children) {
        const children = value.props.children;
        if (Array.isArray(children)) {
          return children.map(normalizeExportValue).join(' ').replace(/\s+/g, ' ').trim();
        }
        return normalizeExportValue(children);
      }
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  };

  const escapeCsvCell = (value) => {
    const str = normalizeExportValue(value).replace(/"/g, '""');
    return `"${str}"`;
  };

  const defaultColumnExportValue = (item, col) => {
    if (typeof col.export === 'function') return col.export(item, { bookings, payments, expenses, invoices, contracts, insurances, maintenance, clients, cars });
    const raw = item?.[col.key];

    if (col.key === 'amount' || col.key === 'cost') return Number(raw || 0);
    if (col.key?.toLowerCase().includes('date') || col.key === 'date' || col.key === 'startDate' || col.key === 'endDate') {
      return raw || '';
    }
    return raw ?? '';
  };

  const downloadCsvFile = (title, columns, rows) => {
    const header = columns.map(col => escapeCsvCell(col.label)).join(',');
    const body = rows.map(row =>
      columns.map(col => escapeCsvCell(defaultColumnExportValue(row, col))).join(',')
    );
    const csv = [header, ...body].join('\n');
    const blob = new Blob(["\ufeff" + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${getSectionFileName(title)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const openPrintWindow = (title, subtitle, columns, rows) => {
    const headHtml = columns.map(col => `<th style="padding:10px 12px;border:1px solid #e5e7eb;background:#f8fafc;text-align:left;font-size:12px;">${normalizeExportValue(col.label)}</th>`).join('');
    const bodyHtml = rows.map(row => {
      const cells = columns.map(col => `<td style="padding:10px 12px;border:1px solid #e5e7eb;font-size:12px;">${normalizeExportValue(defaultColumnExportValue(row, col))}</td>`).join('');
      return `<tr>${cells}</tr>`;
    }).join('');

    const w = window.open('', '_blank', 'width=1200,height=800');
    if (!w) return;

    w.document.write(`
      <html>
        <head>
          <title>${normalizeExportValue(title)}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #0f172a; }
            h1 { margin: 0 0 6px; font-size: 28px; }
            p { margin: 0 0 18px; color: #475569; }
            table { width: 100%; border-collapse: collapse; }
            .meta { margin: 0 0 18px; font-size: 12px; color: #64748b; }
          </style>
        </head>
        <body>
          <h1>${normalizeExportValue(title)}</h1>
          <p>${normalizeExportValue(subtitle || '')}</p>
          <div class="meta">Generated: ${new Date().toLocaleString()}</div>
          <table>
            <thead><tr>${headHtml}</tr></thead>
            <tbody>${bodyHtml || `<tr><td colspan="${columns.length}" style="padding:14px;border:1px solid #e5e7eb;">No data</td></tr>`}</tbody>
          </table>
        </body>
      </html>
    `);
    w.document.close();
    w.focus();
    setTimeout(() => {
      w.print();
    }, 250);
  };

  const renderHeaderActions = (title, subtitle, columns, rows) => (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => openPrintWindow(title, subtitle, columns, rows)}
        className="px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2 text-sm font-semibold text-slate-700 text-slate-800 dark:text-slate-200"
      >
        <Printer size={17} />
        <span>{getActionText('print')}</span>
      </button>
      <button
        type="button"
        onClick={() => downloadCsvFile(title, columns, rows)}
        className="px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2 text-sm font-semibold text-slate-700 text-slate-800 dark:text-slate-200"
      >
        <Download size={17} />
        <span>{getActionText('export')}</span>
      </button>
    </div>
  );

  // --- KRYESORE: RENDERIMI I TABELËS DHE APLIKIMI I FILTRAVE ---
  const renderGenericTable = (type, title, subtitle, columns, data, extraActions = null, filterConfig = null) => {
    let filtered = data;

    if (filterConfig) {
      filtered = filtered.filter(item => {
        if (advancedFilters.client) {
          if (item.client) {
             if (item.client !== advancedFilters.client) return false;
          } else if (type === 'payment' && item.bookingId) {
             const b = bookings.find(b => b.id === item.bookingId);
             if (!b || b.client !== advancedFilters.client) return false;
          }
        }
        
        if (advancedFilters.method && item.method !== advancedFilters.method) return false;
        if (advancedFilters.status && item.status !== advancedFilters.status) return false;
        if (advancedFilters.category && item.category !== advancedFilters.category) return false;
        
        if (advancedFilters.search) {
           const searchLower = advancedFilters.search.toLowerCase();
           const hasMatch = Object.values(item).some(val => String(val).toLowerCase().includes(searchLower));
           
           let hasClientMatch = false;
           if (type === 'payment' && item.bookingId) {
              const b = bookings.find(b => b.id === item.bookingId);
              if (b && b.client.toLowerCase().includes(searchLower)) hasClientMatch = true;
           }
           if (!hasMatch && !hasClientMatch) return false;
        }
        
        const itemDateStr = item.date || item.startDate;
        if (itemDateStr) {
            const itemDate = new Date(itemDateStr);
            itemDate.setHours(0,0,0,0);
            
            if (advancedFilters.startDate) {
                const startD = new Date(advancedFilters.startDate);
                startD.setHours(0,0,0,0);
                if (itemDate < startD) return false;
            }
            if (advancedFilters.endDate) {
                const endD = new Date(advancedFilters.endDate);
                endD.setHours(23,59,59,999);
                if (itemDate > endD) return false;
            }
        }
        
        return true;
      });
    }

    if (searchTerm) {
      filtered = filtered.filter(item => Object.values(item).some(val => String(val).toLowerCase().includes(searchTerm.toLowerCase())));
    }

    const pagination = paginateCollection(type, filtered);
    const paginatedItems = pagination.pageItems;

    const accentByType = {
      client: 'bg-violet-500',
      booking: 'bg-indigo-500',
      payment: 'bg-emerald-500',
      expense: 'bg-rose-500',
      invoice: 'bg-blue-500',
      contract: 'bg-amber-500',
      insurance: 'bg-cyan-500',
      maintenance: 'bg-orange-500',
      tpl: 'bg-sky-500'
    };

    const getCardTitle = (item) => {
      if (type === 'payment') return item.bookingId || item.id || title;
      if (type === 'expense') return item.description || item.category || item.id || title;
      if (type === 'invoice') return item.client || item.id || title;
      if (type === 'contract') return item.title || item.client || item.id || title;
      if (type === 'insurance' || type === 'maintenance' || type === 'tpl') return item.vehicle || item.plate || item.id || title;
      if (type === 'client') return item.name || item.id || title;
      return item.name || item.title || item.client || item.vehicle || item.id || title;
    };

    const getCardSubtitle = (item) => {
      if (type === 'client') return [item.email, item.phone].filter(Boolean).join(' • ');
      if (type === 'payment') return item.method ? trPaymentMethod(item.method) : (item.date ? formatDate(item.date) : '');
      if (type === 'expense') return item.vehicle || item.date || '';
      if (type === 'invoice') return item.id || '';
      if (type === 'contract') return item.client || '';
      if (type === 'insurance' || type === 'maintenance' || type === 'tpl') return item.plate || '';
      return item.id || '';
    };

    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <SectionHeader 
          title={title} 
          subtitle={subtitle} 
          onAdd={() => openModal(type, 'add')} 
          searchTerm={searchTerm} 
          setSearchTerm={setSearchTerm} 
          viewMode={viewMode} 
          setViewMode={setViewMode} 
          actions={renderHeaderActions(title, subtitle, columns, filtered)}
          t={t} 
        />

        {filterConfig && (
          <FilterBar config={filterConfig} filters={advancedFilters} setFilters={setAdvancedFilters} t={t} />
        )}

        <div className="md:hidden space-y-5 pb-28">
          {filtered.length === 0 ? (
            <div className="rounded-3xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-8 text-center shadow-sm">
              <Database size={44} className="mx-auto mb-3 text-slate-300 dark:text-slate-600" />
              <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">{t('no_search_results')}</p>
            </div>
          ) : (
            paginatedItems.map(item => (
              <div key={item.id} className="relative overflow-hidden rounded-[24px] border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-md shadow-slate-200/50 dark:shadow-none">
                <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${accentByType[type] || 'bg-indigo-500'}`} />
                <div className="p-4 pl-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="truncate text-[15px] font-extrabold text-slate-900 dark:text-white">{getCardTitle(item)}</h3>
                      {getCardSubtitle(item) ? (
                        <p className="mt-1 truncate text-xs font-medium text-slate-500 dark:text-slate-400">{getCardSubtitle(item)}</p>
                      ) : null}
                    </div>
                    {item.status ? (
                      <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-bold ${getStatusColor(item.status)}`}>
                        {t(item.status)}
                      </span>
                    ) : null}
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3">
                    {columns.slice(0, 4).map(col => (
                      <div key={col.key} className="rounded-2xl border border-slate-200/80 dark:border-slate-700 bg-slate-50/90 dark:bg-slate-900/40 px-3 py-2.5">
                        <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{col.label}</p>
                        <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 break-words">{col.render(item)}</div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {extraActions && extraActions(item)}
                    <button onClick={() => openModal(type, 'edit', item)} className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-3 text-sm font-bold text-indigo-700 dark:border-indigo-800/60 dark:bg-indigo-900/30 dark:text-indigo-300">
                      <Edit3 size={15} />
                      <span>{getActionText('edit')}</span>
                    </button>
                    <button onClick={() => handleDelete(type, item.id, item.name || item.id || item.title)} className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 text-sm font-bold text-red-700 dark:border-red-800/60 dark:bg-red-900/30 dark:text-red-300">
                      <Trash2 size={15} />
                      <span>{getActionText('delete')}</span>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="hidden md:block bg-white dark:bg-slate-800 rounded-3xl shadow-xl shadow-slate-200/40 dark:shadow-none border border-slate-100 dark:border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-100 dark:border-slate-700">
                  {columns.map(col => (
                    <th key={col.key} className="py-4 px-6 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{col.label}</th>
                  ))}
                  <th className="py-4 px-6 text-right text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('actions_col')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length + 1} className="py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <Database size={48} className="text-slate-300 dark:text-slate-600 mb-3" />
                        <p className="text-slate-500 dark:text-slate-400 font-medium">{t('no_search_results')}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedItems.map(item => (
                    <tr key={item.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition-colors group">
                      {columns.map(col => (
                        <td key={col.key} className="py-4 px-6 text-sm text-slate-800 dark:text-slate-200">{col.render(item)}</td>
                      ))}
                      <td className="py-4 px-6">
                        <div className="flex justify-end space-x-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                          {extraActions && extraActions(item)}
                          <button onClick={() => openModal(type, 'edit', item)} className="p-1.5 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"><Edit3 size={16} /></button>
                          <button onClick={() => handleDelete(type, item.id, item.name || item.id || item.title)} className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        {renderPaginationControls(type, pagination)}
      </div>
    );
  };

  const renderCars = () => {
    const filtered = cars.filter(c => (c.brand?.toLowerCase() || '').includes(searchTerm.toLowerCase()) || (c.model?.toLowerCase() || '').includes(searchTerm.toLowerCase()) || (c.plate?.toLowerCase() || '').includes(searchTerm.toLowerCase()));
    const pagination = paginateCollection('car', filtered);
    const paginatedCars = pagination.pageItems;
    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <SectionHeader 
          title={t('cars')} 
          subtitle={t('manage_cars_subtitle')} 
          onAdd={() => openModal('car', 'add')} 
          searchTerm={searchTerm} 
          setSearchTerm={setSearchTerm} 
          viewMode={viewMode} 
          setViewMode={setViewMode} 
          actions={renderHeaderActions(t('cars'), t('manage_cars_subtitle'), [
            { key: 'brand', label: t('brand') },
            { key: 'model', label: t('model') },
            { key: 'plate', label: t('plate') },
            { key: 'status', label: t('status') },
            { key: 'pricePerDay', label: t('price_per_day') }
          ], filtered)}
          t={t} 
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {paginatedCars.map(car => (
            <div key={car.id} className="group bg-white dark:bg-slate-800 rounded-3xl border border-slate-100 dark:border-slate-700 overflow-hidden shadow-xl shadow-slate-200/40 dark:shadow-none hover:-translate-y-1 transition-all duration-300">
              <div className="relative h-44 bg-slate-100 dark:bg-slate-700 overflow-hidden">
                <img src={car.image || 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=500'} alt={car.model} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute bottom-3 left-3">
                  <span className={`px-2.5 py-1 text-xs font-bold rounded-lg border shadow-sm backdrop-blur-md bg-white/90 dark:bg-slate-800/90 ${getStatusColor(car.status)}`}>{t(car.status)}</span>
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-extrabold text-slate-800 dark:text-white text-lg truncate">{car.brand} {car.model}</h3>
                  <div className="flex items-center space-x-1 bg-amber-50 dark:bg-amber-900/30 px-2 py-1 rounded-md">
                    <Star size={14} className="text-amber-500 fill-current" />
                    <span className="text-xs font-bold text-amber-600 dark:text-amber-400">{car.rating || '5.0'}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between mb-4 text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-700/50 p-2.5 rounded-xl">
                  <span>{car.year}</span><span>•</span><span>{car.fuelType}</span><span>•</span><span>{trTransmissionLabel(t, car.transmission)}</span>
                </div>
                <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-700">
                  <div>
                    <span className="text-2xl font-black text-indigo-600 dark:text-indigo-400">{formatCurrency(car.price)}</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">/{t('day')}</span>
                  </div>
                  <div className="flex space-x-2">
                    <button onClick={() => openModal('car', 'view', car)} title={t('view_car')} className="p-2.5 text-slate-700 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-xl transition-colors"><Eye size={16} /></button>
                    <button onClick={() => openModal('tpl', 'add', car)} title={t('add_tpl')} className="p-2.5 text-cyan-600 bg-cyan-50 dark:bg-cyan-900/30 hover:bg-cyan-100 dark:hover:bg-cyan-800/50 rounded-xl transition-colors"><Shield size={16} /></button>
                    <button onClick={() => openModal('car', 'edit', car)} className="p-2.5 text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-800/50 rounded-xl transition-colors"><Edit3 size={16} /></button>
                    <button onClick={() => handleDelete('car', car.id, `${car.brand} ${car.model}`)} className="p-2.5 text-red-600 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-800/50 rounded-xl transition-colors"><Trash2 size={16} /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {renderPaginationControls('car', pagination)}
      </div>
    );
  };

  const renderClients = () => renderGenericTable('client', t('clients'), t('client_details_title'), [
    { 
      key: 'name', 
      label: t('client'),
      export: (c) => c.name || '',
      render: (c) => (<div className="flex items-center space-x-3">{c.avatar ? <img src={c.avatar} alt={c.name} className="w-10 h-10 rounded-xl object-cover" /> : <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center"><User size={18} className="text-indigo-600 dark:text-indigo-400" /></div>}<span className={`font-bold ${c.isBlacklisted ? 'text-red-500 line-through' : ''}`}>{c.name}</span></div>) 
    },
    { 
      key: 'contact', 
      label: t('contacts'),
      export: (c) => [c.email || t('no_email'), c.phone || t('no_phone')].join(' | '),
      render: (c) => (<div><p className="text-sm">{c.email || t('no_email')}</p><p className="text-xs text-slate-500">{c.phone || t('no_phone')}</p></div>) 
    },
    { 
      key: 'stats', 
      label: t('bookings_expenses'),
      export: (c, ctx) => {
        const clientName = String(c.name || '').trim().toLowerCase();
        const clientBookings = (ctx.bookings || []).filter(b => 
          String(b.clientId || '') === String(c.id || '') ||
          String(b.client || '').trim().toLowerCase() === clientName ||
          String(b.clientName || '').trim().toLowerCase() === clientName
        );
        const bookingCount = clientBookings.length || Number(c.rentals || 0);
        const spent = Number(c.totalSpent || 0);
        return `${bookingCount} ${t('bookings_word')} | ${formatCurrency(spent)}`;
      },
      render: (c) => (<span><b className="text-indigo-600">{c.rentals || 0}</b> {t('bookings_word')} | <b className="text-emerald-600 ml-1">{formatCurrency(c.totalSpent || 0)}</b></span>) 
    },
    { 
      key: 'status', 
      label: t('status'),
      export: (c) => c.isBlacklisted ? t('blacklisted') : t(c.status),
      render: (c) => (<span className={`px-2.5 py-1.5 rounded-lg text-xs font-bold border ${c.isBlacklisted ? getStatusColor('blacklist') : getStatusColor(c.status)}`}>{c.isBlacklisted ? t('blacklisted') : t(c.status)}</span>) 
    }
  ], clients, (c) => (
    <button onClick={() => openModal('client', 'view', c)} className="p-1.5 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors" title={t('view_client')}><Eye size={16} /></button>
  ));

  const getBookingPaidAmount = (b) => {
    const directPaid = Number(b?.paid ?? b?.totalPaid ?? 0);
    const paymentSum = payments
      .filter(p => String(p.bookingId || '') === String(b?.id || ''))
      .reduce((sum, p) => sum + Number(p.amount || 0), 0);
    return Number(Math.max(directPaid, paymentSum).toFixed(2));
  };

  const getBookingDepositAmount = (b) => Number(b?.deposit || 0);

  const getBookingCollectedAmount = (b) => {
    const rentalPaid = getBookingPaidAmount(b);
    const depositAmount = getBookingDepositAmount(b);
    return Number((rentalPaid + depositAmount).toFixed(2));
  };

  const getBookingRemainingAmount = (b) => {
    const paid = getBookingPaidAmount(b);
    const total = Number(b?.total ?? b?.totalPrice ?? 0);
    return Number((total - paid).toFixed(2));
  };

  const getBookingPaymentStatus = (b) => {
    const remaining = getBookingRemainingAmount(b);
    if (remaining <= 0.009) return 'paid';
    const paid = getBookingPaidAmount(b);
    if (paid > 0) return 'partial';
    return b?.paymentStatus || 'pending';
  };

  const finishBookingPayment = (booking) => {
    const remaining = getBookingRemainingAmount(booking);
    if (remaining <= 0.009) {
      showToast(language === 'sq' ? 'Ky rezervim është paguar tashmë.' : 'This booking is already fully paid.');
      return;
    }

    const paymentDate = booking.endDate || booking.startDate || new Date().toISOString().slice(0, 10);
    setPayments(prev => [
      {
        id: generateId('PAY'),
        bookingId: booking.id,
        amount: Number(remaining.toFixed(2)),
        method: language === 'sq' ? 'Kesh' : 'Cash',
        date: paymentDate,
        status: 'completed'
      },
      ...prev
    ]);

    setBookings(prev => prev.map(item => item.id === booking.id ? {
      ...item,
      paid: Number((getBookingPaidAmount(booking) + remaining).toFixed(2)),
      totalPaid: Number((getBookingPaidAmount(booking) + remaining).toFixed(2)),
      paymentStatus: 'paid'
    } : item));

    showToast(language === 'sq' ? 'Pagesa u përfundua me sukses.' : 'Payment finished successfully.');
  };

  const getBookingClientDetails = (booking) => clients.find(c => String(c.id) === String(booking?.clientId)) || clients.find(c => (c.name || '').trim().toLowerCase() === String(booking?.client || '').trim().toLowerCase()) || null;
  const getBookingPayments = (booking) => payments.filter(p => String(p.bookingId || '') === String(booking?.id || '')).sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

  const renderBookingViewContent = (booking) => {
    const client = getBookingClientDetails(booking);
    const bookingPayments = getBookingPayments(booking);
    const rentalPaid = getBookingPaidAmount(booking);
    const depositAmount = getBookingDepositAmount(booking);
    const totalCollected = getBookingCollectedAmount(booking);
    const remaining = getBookingRemainingAmount(booking);
    const depositStatus = getDepositStatus(booking);
    const depositStatusText = depositStatusLabel(depositStatus);
    const totalRental = Number(booking.totalPrice || booking.total || 0);
    const days = booking.days ?? booking.totalDays ?? Math.max(1, Math.ceil((new Date(booking.endDate) - new Date(booking.startDate)) / 86400000));
    const Row = ({ label, value, accent = false }) => (
      <div className="flex items-start justify-between gap-4 py-2.5 border-b border-slate-100 dark:border-slate-700 last:border-b-0">
        <span className="font-bold text-slate-700 dark:text-slate-300">{label}</span>
        <span className={`text-right ${accent ? 'font-extrabold text-indigo-600 dark:text-indigo-400' : 'text-slate-900 text-slate-800 dark:text-white'}`}>{value || '-'}</span>
      </div>
    );
    const tabBtn = (tab, label) => (
      <button type="button" onClick={() => setActiveBookingTab(tab)} className={`px-4 py-2.5 rounded-xl font-bold text-sm transition-colors ${activeBookingTab === tab ? 'bg-green-100 text-slate-900 dark:bg-green-900/30 text-slate-800 dark:text-white border border-green-200 dark:border-green-800' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 border border-transparent'}`}>{label}</button>
    );

    const detailsView = (
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h4 className="text-lg font-extrabold text-slate-900 text-slate-800 dark:text-white">{t('details')}</h4>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setModalMode('edit')} className="h-10 w-10 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 flex items-center justify-center"><Edit3 size={18} /></button>
              <button type="button" onClick={() => { setIsModalOpen(false); handleDelete('booking', booking.id, booking.id); }} className="h-10 w-10 rounded-xl bg-red-600 text-white hover:bg-red-700 flex items-center justify-center"><Trash2 size={18} /></button>
            </div>
          </div>
          <Row label={t('client_word')} value={booking.client} />
          <Row label={t('car_word')} value={booking.vehicle} />
          <Row label={t('start_date_label')} value={`${formatDate(booking.startDate)} ${booking.startTime || ''}`.trim()} />
          <Row label={t('end_date_label')} value={`${formatDate(booking.endDate)} ${booking.endTime || ''}`.trim()} />
          <Row label={t('total_days')} value={String(days)} />
          <Row label={t('deposit_amount')} value={formatCurrency(Number(booking.deposit || 0))} />
          <Row label={t('flight_number')} value={booking.flightNumber} />
          <Row label={t('daily_price_label')} value={formatCurrency(Number(booking.dailyPrice || booking.price || 0))} />
          <Row label={t('rental_price_label')} value={formatCurrency(totalRental)} />
          <Row label={uiText({ sq: 'Pagesat e qirasë', en: 'Rental payments', de: 'Mietzahlungen', it: 'Pagamenti noleggio', fr: 'Paiements de location', hr: 'Uplate najma' })} value={formatCurrency(rentalPaid)} />
          <Row label={t('deposit_amount')} value={formatCurrency(depositAmount)} />
          <Row label={uiText({ sq: 'Totali i mbledhur', en: 'Total collected', de: 'Insgesamt eingezogen', it: 'Totale incassato', fr: 'Total encaissé', hr: 'Ukupno naplaćeno' })} value={formatCurrency(totalCollected)} />
          <Row label={uiText({ sq: 'Statusi i depozitës', en: 'Deposit status', de: 'Kautionsstatus', it: 'Stato del deposito', fr: 'Statut du dépôt', hr: 'Status depozita' })} value={depositStatusText} />
          <Row label={uiText({ sq: 'Mbetja e qirasë', en: 'Rental remaining', de: 'Restmiete', it: 'Residuo noleggio', fr: 'Reste du loyer', hr: 'Preostalo za najam' })} value={formatCurrency(Math.max(0, remaining))} accent />
          <div className="mt-5 rounded-xl bg-sky-50 dark:bg-slate-700/40 p-4 text-sm text-slate-800 dark:text-slate-200">
            <div>GPS: {booking.extraGps ? t('yes_word') : t('no_word')}</div>
            <div>Child Seat: {booking.extraChildSeat ? t('yes_word') : t('no_word')}</div>
            <div>Full Insurance: {booking.extraInsurance ? t('yes_word') : t('no_word')}</div>
          </div>
        </div>
        <div className="space-y-6">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
            <h4 className="text-lg font-extrabold mb-4 text-slate-900 text-slate-800 dark:text-white">{t('pickup_dropoff_location')}</h4>
            <Row label={t('dropoff_location')} value={booking.returnLocation} />
            <Row label={t('pickup_location')} value={booking.pickupLocation} />
          </div>
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
            <h4 className="text-lg font-extrabold mb-4 text-slate-900 text-slate-800 dark:text-white">{t('documents')}</h4>
            <div className="rounded-xl bg-sky-100/80 dark:bg-slate-700/40 p-4 text-sm font-semibold text-slate-700 text-slate-800 dark:text-slate-200">{t('no_documents_available')}</div>
          </div>
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
            <h4 className="text-lg font-extrabold mb-4 text-slate-900 text-slate-800 dark:text-white">{t('car_label')}: {booking.vehicle || '-'}</h4>
          </div>
        </div>
      </div>
    );

    const clientView = (
      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
        <Row label={t('name_label')} value={client?.name || booking.client} />
        <Row label={t('phone')} value={client?.phone} />
        <Row label={t('email')} value={client?.email} />
        <Row label={t('address')} value={client?.address} />
        <Row label={t('personal_id')} value={client?.personalId} />
        <Row label={t('license_number')} value={client?.licenseNumber} />
      </div>
    );

    const paymentsView = (
      <div className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5"><div className="text-sm font-bold text-slate-500 mb-2">{uiText({ sq: 'Totali i qirasë', en: 'Rental total', de: 'Mietsumme', it: 'Totale noleggio', fr: 'Total location', hr: 'Ukupno najam' })}</div><div className="text-3xl font-extrabold text-slate-900 dark:text-white">{formatCurrency(totalRental)}</div></div>
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5"><div className="text-sm font-bold text-slate-500 mb-2">{uiText({ sq: 'Pagesat e qirasë', en: 'Rental payments', de: 'Mietzahlungen', it: 'Pagamenti noleggio', fr: 'Paiements de location', hr: 'Uplate najma' })}</div><div className="text-3xl font-extrabold text-emerald-600">{formatCurrency(rentalPaid)}</div></div>
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5"><div className="text-sm font-bold text-slate-500 mb-2">{t('deposit')}</div><div className="text-3xl font-extrabold text-amber-600">{formatCurrency(depositAmount)}</div><div className="mt-2 inline-flex px-3 py-1 rounded-full text-xs font-black bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200">{depositStatusText}</div></div>
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5"><div className="text-sm font-bold text-slate-500 mb-2">{uiText({ sq: 'Totali i mbledhur', en: 'Total collected', de: 'Insgesamt eingezogen', it: 'Totale incassato', fr: 'Total encaissé', hr: 'Ukupno naplaćeno' })}</div><div className="text-3xl font-extrabold text-indigo-600">{formatCurrency(totalCollected)}</div></div>
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5"><div className="text-sm font-bold text-slate-500 mb-2">{uiText({ sq: 'Mbetja e qirasë', en: 'Rental remaining', de: 'Restmiete', it: 'Residuo noleggio', fr: 'Reste du loyer', hr: 'Preostalo za najam' })}</div><div className="text-3xl font-extrabold text-red-600">{formatCurrency(Math.max(0, remaining))}</div></div>
        </div>
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
            <div>
              <h4 className="font-extrabold text-slate-900 dark:text-white">{t('payments')}</h4>
              <p className="text-xs text-slate-500 mt-1">{uiText({ sq: 'Këtu shfaqen vetëm pagesat e qirasë. Depozita paraqitet veçmas.', en: 'Only rental payments are shown here. Deposit is displayed separately.', de: 'Hier werden nur Mietzahlungen angezeigt. Die Kaution wird separat dargestellt.', it: 'Qui vengono mostrati solo i pagamenti del noleggio. Il deposito è mostrato separatamente.', fr: 'Seuls les paiements de location sont affichés ici. Le dépôt est affiché séparément.', hr: 'Ovdje su prikazane samo uplate najma. Depozit je prikazan odvojeno.' })}</p>
            </div>
            <button type="button" onClick={() => openModal('payment', 'add', { bookingId: booking.id, amount: Math.max(0, remaining), date: new Date().toISOString().slice(0,10), status: 'completed' })} className="px-4 py-2 rounded-full bg-black text-white text-sm font-bold">{t('add_payment')}</button>
          </div>
          {!bookingPayments.length ? <div className="p-5 text-sm text-slate-500">{t('no_payments_available')}</div> : (
            <div className="overflow-x-auto"><table className="w-full text-left text-sm"><thead><tr className="bg-slate-50 dark:bg-slate-700/50"><th className="px-5 py-3">{t('id')}</th><th className="px-5 py-3">{t('date')}</th><th className="px-5 py-3">{t('method_col')}</th><th className="px-5 py-3">{t('amount_col')}</th></tr></thead><tbody className="divide-y divide-slate-200 dark:divide-slate-700">{bookingPayments.map(payment => <tr key={payment.id}><td className="px-5 py-4">{payment.id}</td><td className="px-5 py-4">{formatDate(payment.date)}</td><td className="px-5 py-4">{payment.method || '-'}</td><td className="px-5 py-4 font-extrabold text-emerald-600">{formatCurrency(payment.amount || 0)}</td></tr>)}</tbody></table></div>
          )}
        </div>
      </div>
    );

    const driversView = (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5"><Row label={t('main_driver')} value={client?.name || booking.client} /><Row label={t('phone')} value={client?.phone} /><Row label={t('license_number')} value={client?.licenseNumber} /></div>
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5"><Row label={t('additional_driver')} value={booking.additionalDriverName || booking.driverName || t('no_additional_driver')} /><Row label={t('phone')} value={booking.additionalDriverPhone || booking.driverPhone} /><Row label={t('license_number')} value={booking.additionalDriverLicense || booking.driverLicense} /></div>
      </div>
    );

    const commentsView = (
      <div className="space-y-4">
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5"><div className="text-xs font-bold uppercase text-slate-500 mb-2">{t('client_notes')}</div><div className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-wrap">{booking.clientNotes || '-'}</div></div>
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5"><div className="text-xs font-bold uppercase text-slate-500 mb-2">{t('notes_label')}</div><div className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-wrap">{booking.notes || '-'}</div></div>
      </div>
    );

    return (
      <div className="space-y-5">
        <div className="flex flex-wrap gap-2">
          {tabBtn('details', t('details'))}
          {tabBtn('client', t('client_info'))}
          {tabBtn('payments', t('payments'))}
          {tabBtn('drivers', t('drivers_tab'))}
          {tabBtn('comments', t('comments_tab'))}
        </div>
        {activeBookingTab === 'details' && detailsView}
        {activeBookingTab === 'client' && clientView}
        {activeBookingTab === 'payments' && paymentsView}
        {activeBookingTab === 'drivers' && driversView}
        {activeBookingTab === 'comments' && commentsView}
      </div>
    );
  };

  const renderBookingsList = () => {
    const bookingFilterConfig = [
      { name: 'client', label: t('user_label'), type: 'select', options: clients.map(c => ({value: c.name, label: c.name})) },
      { name: 'status', label: t('status'), type: 'select', options: [{value: 'pending', label: t('pending')}, {value: 'active', label: t('active')}, {value: 'completed', label: t('completed')}, {value: 'cancelled', label: t('cancelled')}] },
      { name: 'search', label: t('search'), type: 'text', placeholder: t('search_booking_placeholder') },
      { name: 'dateRange', label: t('date_range'), type: 'dateRange' }
    ];

    let filteredBookings = bookings;

    filteredBookings = filteredBookings.filter(item => {
      if (advancedFilters.client && item.client !== advancedFilters.client) return false;
      if (advancedFilters.status && item.status !== advancedFilters.status) return false;
      if (advancedFilters.search) {
        const searchLower = advancedFilters.search.toLowerCase();
        const hasMatch = Object.values(item).some(val => String(val).toLowerCase().includes(searchLower));
        if (!hasMatch) return false;
      }
      const itemDateStr = item.startDate;
      if (itemDateStr) {
        const itemDate = new Date(itemDateStr);
        itemDate.setHours(0,0,0,0);
        if (advancedFilters.startDate) {
          const startD = new Date(advancedFilters.startDate);
          startD.setHours(0,0,0,0);
          if (itemDate < startD) return false;
        }
        if (advancedFilters.endDate) {
          const endD = new Date(advancedFilters.endDate);
          endD.setHours(23,59,59,999);
          if (itemDate > endD) return false;
        }
      }
      return true;
    });

    if (searchTerm) {
      filteredBookings = filteredBookings.filter(item => Object.values(item).some(val => String(val).toLowerCase().includes(searchTerm.toLowerCase())));
    }

    const pagination = paginateCollection('booking', filteredBookings);
    const paginatedBookings = pagination.pageItems;

    const bookingColumns = [
      { key: 'id', label: 'ID', render: (b) => <span className="font-mono font-bold text-indigo-600">{b.id}</span> },
      { key: 'clientVehicle', label: t('name') + ' (' + t('client_col') + ' - ' + t('vehicle_col') + ')', render: (b) => <span className="font-bold">{b.client} - {b.vehicle}</span> },
      { key: 'startDate', label: t('start_date_label'), render: (b) => `${formatDate(b.startDate)} ${b.startTime || ''}`.trim() },
      { key: 'endDate', label: t('end_date_label'), render: (b) => `${formatDate(b.endDate)} ${b.endTime || ''}`.trim() },
      { key: 'days', label: t('total_days') || 'Total Days', render: (b) => b.days ?? b.totalDays ?? Math.max(1, Math.ceil((new Date(b.endDate) - new Date(b.startDate)) / 86400000)) },
      { key: 'paid', label: t('total_paid'), render: (b) => formatCurrency(getBookingPaidAmount(b)) },
      { key: 'pricePerDay', label: t('daily_price'), render: (b) => formatCurrency(Number(b.price ?? b.dailyPrice ?? 0)) },
      { key: 'total', label: t('total_price'), render: (b) => formatCurrency(Number(b.total ?? b.totalPrice ?? 0)) }
    ];

    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <SectionHeader 
          title={t('bookings')} 
          subtitle={t('manage_bookings_subtitle')} 
          onAdd={() => openModal('booking', 'add')} 
          searchTerm={searchTerm} 
          setSearchTerm={setSearchTerm} 
          viewMode={viewMode} 
          setViewMode={setViewMode} 
          actions={renderHeaderActions(t('bookings'), t('manage_bookings_subtitle'), bookingColumns, filteredBookings)}
          t={t} 
        />

        <FilterBar config={bookingFilterConfig} filters={advancedFilters} setFilters={setAdvancedFilters} t={t} />

        <div className="md:hidden space-y-6 pb-28">
          {filteredBookings.length === 0 ? (
            <div className="rounded-3xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-8 text-center shadow-sm">
              <Database size={44} className="mx-auto mb-3 text-slate-300 dark:text-slate-600" />
              <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">{t('no_search_results')}</p>
            </div>
          ) : (
            paginatedBookings.map((b) => {
              const daysValue = b.days ?? b.totalDays ?? Math.max(1, Math.ceil((new Date(b.endDate) - new Date(b.startDate)) / 86400000));
              return (
                <div key={b.id} className="relative overflow-hidden rounded-[26px] border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-md shadow-slate-200/50 dark:shadow-none">
                  <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-indigo-500" />
                  <div className="p-4 pl-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="truncate text-[15px] font-extrabold text-slate-900 dark:text-white">{b.client || t('client_col')}</h3>
                        <p className="mt-1 truncate text-xs font-medium text-slate-500 dark:text-slate-400">{b.vehicle || t('vehicle_col')} • {b.id}</p>
                      </div>
                      <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-bold ${getStatusColor(b.status || 'pending')}`}>
                        {t(b.status || 'pending')}
                      </span>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700 bg-slate-50/90 dark:bg-slate-900/40 px-3 py-2.5">
                        <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{t('start_date_label')}</p>
                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{`${formatDate(b.startDate)} ${b.startTime || ''}`.trim()}</p>
                      </div>
                      <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700 bg-slate-50/90 dark:bg-slate-900/40 px-3 py-2.5">
                        <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{t('end_date_label')}</p>
                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{`${formatDate(b.endDate)} ${b.endTime || ''}`.trim()}</p>
                      </div>
                      <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700 bg-slate-50/90 dark:bg-slate-900/40 px-3 py-2.5">
                        <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{t('total_days') || 'Total Days'}</p>
                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{daysValue}</p>
                      </div>
                      <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700 bg-slate-50/90 dark:bg-slate-900/40 px-3 py-2.5">
                        <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{t('total_paid')}</p>
                        <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">{formatCurrency(getBookingPaidAmount(b))}</p>
                      </div>
                      <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700 bg-slate-50/90 dark:bg-slate-900/40 px-3 py-2.5">
                        <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{t('daily_price')}</p>
                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{formatCurrency(Number(b.price ?? b.dailyPrice ?? 0))}</p>
                      </div>
                      <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700 bg-slate-50/90 dark:bg-slate-900/40 px-3 py-2.5">
                        <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{t('total_price')}</p>
                        <p className="text-sm font-extrabold text-indigo-600 dark:text-indigo-400">{formatCurrency(Number(b.total ?? b.totalPrice ?? 0))}</p>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <button onClick={() => openModal('booking', 'view', b)} className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white text-sm font-bold text-slate-700 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-200">
                        <Eye size={15} />
                        <span>{getActionText('view')}</span>
                      </button>
                      <button onClick={() => printBookingContract(b)} className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 text-sm font-bold text-blue-700 dark:border-blue-800/60 dark:bg-blue-900/30 dark:text-blue-300">
                        <Printer size={15} />
                        <span>{getActionText('print')}</span>
                      </button>
                      <button onClick={() => openModal('booking', 'edit', b)} className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50 text-sm font-bold text-indigo-700 dark:border-indigo-800/60 dark:bg-indigo-900/30 dark:text-indigo-300">
                        <Edit3 size={15} />
                        <span>{getActionText('edit')}</span>
                      </button>
                      <button onClick={() => handleDelete('booking', b.id, b.client || b.id)} className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 text-sm font-bold text-red-700 dark:border-red-800/60 dark:bg-red-900/30 dark:text-red-300">
                        <Trash2 size={15} />
                        <span>{getActionText('delete')}</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="hidden md:block bg-white dark:bg-slate-800 rounded-3xl shadow-xl shadow-slate-200/40 dark:shadow-none border border-slate-100 dark:border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-100 dark:border-slate-700">
                  {bookingColumns.map(col => (
                    <th key={col.key} className="py-4 px-6 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{col.label}</th>
                  ))}
                  <th className="py-4 px-6 text-right text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('actions_col')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {filteredBookings.length === 0 ? (
                  <tr>
                    <td colSpan={bookingColumns.length + 1} className="py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <Database size={48} className="text-slate-300 dark:text-slate-600 mb-3" />
                        <p className="text-slate-500 dark:text-slate-400 font-medium">{t('no_search_results')}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedBookings.map(item => (
                    <tr key={item.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition-colors group">
                      {bookingColumns.map(col => (
                        <td key={col.key} className="py-4 px-6 text-sm text-slate-800 dark:text-slate-200">{col.render(item)}</td>
                      ))}
                      <td className="py-4 px-6">
                        <div className="flex justify-end space-x-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                          <button onClick={() => openModal('booking', 'view', item)} className="p-1.5 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors" title={t('view')}>
                            <Eye size={16} />
                          </button>
                          <button onClick={() => printBookingContract(item)} className="p-1.5 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors" title={t('print_rental_contract')}>
                            <Printer size={16} />
                          </button>
                          <button onClick={() => openModal('booking', 'edit', item)} className="p-1.5 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"><Edit3 size={16} /></button>
                          <button onClick={() => handleDelete('booking', item.id, item.client || item.id)} className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        {renderPaginationControls('booking', pagination)}
      </div>
    );
  };

  const renderBookingsNotFinished = () => {
    const today = new Date().toISOString().slice(0, 10);
    const data = bookings
      .map(b => ({ ...b, remainingAmount: getBookingRemainingAmount(b) }))
      .filter(b => Math.abs(b.remainingAmount) > 0.009 || ['pending', 'active'].includes(b.paymentStatus || ''));

    const totalUnpaid = data.filter(b => b.remainingAmount > 0).reduce((s, b) => s + b.remainingAmount, 0);
    const overdueAmount = data.filter(b => b.remainingAmount > 0 && b.endDate < today).reduce((s, b) => s + b.remainingAmount, 0);
    const incomingAmount = data.filter(b => b.remainingAmount > 0 && b.endDate >= today).reduce((s, b) => s + b.remainingAmount, 0);
    const filteredData = data.filter(b => {
      const q = (advancedFilters.search || searchTerm || '').toLowerCase().trim();
      if (!q) return true;
      const haystack = [
        b.id,
        b.client,
        b.vehicle,
        b.startDate,
        b.endDate,
        b.paymentStatus,
        b.status,
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
    const pagination = paginateCollection('bookings_not_finished', filteredData);
    const paginatedData = pagination.pageItems;

    const summaryCards = [
      {
        label: t('total_unpaid'),
        value: totalUnpaid,
        count: data.filter(b => b.remainingAmount > 0).length,
        accent: 'from-rose-500 to-red-600',
      },
      {
        label: t('payments_overdue'),
        value: overdueAmount,
        count: data.filter(b => b.remainingAmount > 0 && b.endDate < today).length,
        accent: 'from-amber-500 to-orange-600',
      },
      {
        label: t('payments_incoming'),
        value: incomingAmount,
        count: data.filter(b => b.remainingAmount > 0 && b.endDate >= today).length,
        accent: 'from-emerald-500 to-green-600',
      },
    ];

    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <SectionHeader
          title={t('reservations_with_incomplete_payments')}
          subtitle={t('booking_payments_not_finished')}
          onAdd={null}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          viewMode={viewMode}
          setViewMode={setViewMode}
          t={t}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {summaryCards.map((card, idx) => (
            <div key={idx} className="relative overflow-hidden rounded-[24px] border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-md shadow-slate-200/40 dark:shadow-none">
              <div className={`absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r ${card.accent}`} />
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">{card.label}</p>
                  <p className="mt-3 text-2xl font-black text-slate-900 dark:text-white">{formatCurrency(card.value)}</p>
                  <p className="mt-2 text-sm font-semibold text-slate-500 dark:text-slate-400">{card.count} {t('bookings_word')}</p>
                </div>
                <div className={`h-12 w-12 rounded-2xl bg-gradient-to-br ${card.accent} text-white flex items-center justify-center shadow-lg`}>
                  <Wallet size={22} />
                </div>
              </div>
            </div>
          ))}
        </div>

        <FilterBar config={[{ name: 'search', label: t('search'), type: 'text', placeholder: t('search_booking_placeholder') }]} filters={advancedFilters} setFilters={setAdvancedFilters} t={t} />

        <div className="md:hidden space-y-5 pb-28">
          {filteredData.length === 0 ? (
            <div className="rounded-3xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-8 text-center shadow-sm">
              <Wallet size={44} className="mx-auto mb-3 text-slate-300 dark:text-slate-600" />
              <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">{t('no_search_results')}</p>
            </div>
          ) : (
            paginatedData.map((b) => {
              const remainingPositive = Number(b.remainingAmount || 0) > 0;
              return (
                <div key={b.id} className="relative overflow-hidden rounded-[24px] border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-md shadow-slate-200/50 dark:shadow-none">
                  <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${remainingPositive ? 'bg-rose-500' : 'bg-emerald-500'}`} />
                  <div className="p-4 pl-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="truncate text-[15px] font-extrabold text-slate-900 dark:text-white">{b.client || '-'}</h3>
                        <p className="mt-1 truncate text-xs font-medium text-slate-500 dark:text-slate-400">{b.vehicle || '-'} • {b.id}</p>
                      </div>
                      <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-bold ${remainingPositive ? 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800/60 dark:bg-rose-900/30 dark:text-rose-300' : 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800/60 dark:bg-emerald-900/30 dark:text-emerald-300'}`}>
                        {remainingPositive ? t('unpaid_pending') : t('paid')}
                      </span>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700 bg-slate-50/90 dark:bg-slate-900/40 px-3 py-2.5">
                        <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{t('start_date_label')}</p>
                        <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">{formatDate(b.startDate)} {b.startTime || ''}</div>
                      </div>
                      <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700 bg-slate-50/90 dark:bg-slate-900/40 px-3 py-2.5">
                        <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{t('end_date_label')}</p>
                        <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">{formatDate(b.endDate)} {b.endTime || ''}</div>
                      </div>
                      <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700 bg-slate-50/90 dark:bg-slate-900/40 px-3 py-2.5">
                        <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{t('total_paid')}</p>
                        <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">{formatCurrency(getBookingPaidAmount(b))}</div>
                      </div>
                      <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700 bg-slate-50/90 dark:bg-slate-900/40 px-3 py-2.5">
                        <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{t('remaining_amount')}</p>
                        <div className={`text-sm font-extrabold ${remainingPositive ? 'text-rose-600 dark:text-rose-300' : 'text-emerald-600 dark:text-emerald-300'}`}>{formatCurrency(b.remainingAmount)}</div>
                      </div>
                      <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700 bg-slate-50/90 dark:bg-slate-900/40 px-3 py-2.5">
                        <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{t('daily_price')}</p>
                        <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">{formatCurrency(Number(b.price ?? b.dailyPrice ?? 0))}</div>
                      </div>
                      <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700 bg-slate-50/90 dark:bg-slate-900/40 px-3 py-2.5">
                        <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{t('total_price')}</p>
                        <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">{formatCurrency(Number(b.total ?? b.totalPrice ?? 0))}</div>
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      <button onClick={() => openModal('booking', 'view', b)} className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-bold text-slate-700 dark:border-slate-700 dark:bg-slate-900/30 dark:text-slate-200">
                        <Eye size={15} />
                        <span>{t('view')}</span>
                      </button>
                      <button onClick={() => openModal('booking', 'edit', b)} className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-3 text-sm font-bold text-indigo-700 dark:border-indigo-800/60 dark:bg-indigo-900/30 dark:text-indigo-300">
                        <Edit3 size={15} />
                        <span>{t('edit')}</span>
                      </button>
                      <button onClick={() => finishBookingPayment(b)} className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-3 text-sm font-bold text-white shadow-sm">
                        <CheckCircle size={15} />
                        <span>{t('finish')}</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="hidden md:block bg-white dark:bg-slate-800 rounded-3xl shadow-xl shadow-slate-200/40 dark:shadow-none border border-slate-100 dark:border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-100 dark:border-slate-700">
                  {[
                    'ID',
                    t('name') + ' (' + t('client_col') + ' - ' + t('vehicle_col') + ')',
                    t('start_date_label'),
                    t('end_date_label'),
                    t('total_paid'),
                    t('daily_price'),
                    t('total_price'),
                    t('remaining_amount'),
                    t('actions_col')
                  ].map((label, idx) => <th key={idx} className="py-4 px-6 text-xs font-extrabold text-slate-700 dark:text-slate-200 uppercase tracking-wider">{label}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {filteredData.length === 0 ? (
                  <tr><td colSpan={9} className="py-12 text-center text-slate-600 dark:text-slate-300 font-medium">{t('no_search_results')}</td></tr>
                ) : paginatedData.map(b => (
                  <tr key={b.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/30">
                    <td className="py-4 px-6 text-slate-700 dark:text-slate-200 font-medium">{b.id}</td>
                    <td className="py-4 px-6 text-slate-800 dark:text-slate-100 font-semibold">{b.client} - {b.vehicle}</td>
                    <td className="py-4 px-6 text-slate-700 dark:text-slate-200">{formatDate(b.startDate)} {b.startTime || ''}</td>
                    <td className="py-4 px-6 text-slate-700 dark:text-slate-200">{formatDate(b.endDate)} {b.endTime || ''}</td>
                    <td className="py-4 px-6 text-slate-700 dark:text-slate-200">{formatCurrency(getBookingPaidAmount(b))}</td>
                    <td className="py-4 px-6 text-slate-700 dark:text-slate-200">{formatCurrency(Number(b.price ?? b.dailyPrice ?? 0))}</td>
                    <td className="py-4 px-6 text-slate-700 dark:text-slate-200">{formatCurrency(Number(b.total ?? b.totalPrice ?? 0))}</td>
                    <td className={`py-4 px-6 font-bold ${b.remainingAmount > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{formatCurrency(b.remainingAmount)}</td>
                    <td className="py-4 px-6">
                      <div className="flex justify-end space-x-2">
                        <button onClick={() => openModal('booking', 'view', b)} className="p-2 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"><Eye size={16}/></button>
                        <button onClick={() => finishBookingPayment(b)} className="px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-bold">{t('finish')}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {renderPaginationControls('bookings_not_finished', pagination)}
      </div>
    );
  };

  const renderBookingsWaitlist = () => {
    const data = bookings.filter(b => (b.status || '').toLowerCase() === 'waitlist');
    const pagination = paginateCollection('bookings_waitlist', data);
    const paginatedData = pagination.pageItems;
    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <SectionHeader title={t('booking_waitlist')} subtitle={t('booking_waitlist')} onAdd={null} searchTerm={searchTerm} setSearchTerm={setSearchTerm} viewMode={viewMode} setViewMode={setViewMode} t={t} />
        <FilterBar config={[{ name: 'search', label: t('search'), type: 'text', placeholder: t('search_booking_placeholder') }]} filters={advancedFilters} setFilters={setAdvancedFilters} t={t} />
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl shadow-slate-200/40 dark:shadow-none border border-slate-100 dark:border-slate-700 overflow-hidden p-10 text-center">
          {data.length === 0 ? (
            <div className="py-8">
              <Calendar size={52} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-3xl font-black text-slate-900 text-slate-800 dark:text-white mb-3">{t('no_waitlist_records')}</h3>
              <p className="text-slate-500 font-medium">{t('no_waitlist_subtitle')}</p>
            </div>
          ) : (
            <div className="space-y-4 text-left">
              {paginatedData.map(b => (
                <div key={b.id} className="p-4 rounded-2xl border border-slate-200 dark:border-slate-700 flex items-center justify-between">
                  <div>
                    <div className="font-bold">{b.client} - {b.vehicle}</div>
                    <div className="text-sm text-slate-500">{formatDate(b.startDate)} - {formatDate(b.endDate)}</div>
                  </div>
                  <button onClick={() => openModal('booking','view',b)} className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-bold">{t('view')}</button>
                </div>
              ))}
            </div>
          )}
        </div>
        {renderPaginationControls('bookings_waitlist', pagination)}
      </div>
    );
  };

  const renderBookingsOperations = () => {
    const findBestOperationsMonth = () => {
      if (!bookings.length) return new Date();
      const dated = bookings.map(b => new Date(b.startDate || b.endDate || Date.now())).filter(d => !Number.isNaN(d.getTime())).sort((a, b) => a - b);
      return dated[0] || new Date();
    };
    const currentMonthHasBookings = bookings.some(b => {
      const d = new Date(b.startDate || b.endDate || Date.now());
      return !Number.isNaN(d.getTime()) && d.getFullYear() === bookingOpsDate.getFullYear() && d.getMonth() === bookingOpsDate.getMonth();
    });
    const opsDate = currentMonthHasBookings ? bookingOpsDate : findBestOperationsMonth();
    const monthStart = new Date(opsDate.getFullYear(), opsDate.getMonth(), 1);
    const monthEnd = new Date(opsDate.getFullYear(), opsDate.getMonth() + 1, 0);
    const startGrid = new Date(monthStart);
    startGrid.setDate(monthStart.getDate() - monthStart.getDay());
    const endGrid = new Date(monthEnd);
    endGrid.setDate(monthEnd.getDate() + (6 - monthEnd.getDay()));
    const days = [];
    const cur = new Date(startGrid);
    while (cur <= endGrid) {
      days.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
    const weekdayKeys = ['weekday_sun', 'weekday_mon', 'weekday_tue', 'weekday_wed', 'weekday_thu', 'weekday_fri', 'weekday_sat'];
    const monthLabel = `${t('month_name_' + opsDate.getMonth())} ${opsDate.getFullYear()}`;
    const normalizeDateKey = (value) => {
      if (!value) return '';
      const parsed = new Date(String(value).replace(' ', 'T'));
      if (Number.isNaN(parsed.getTime())) return String(value).slice(0, 10);
      const y = parsed.getFullYear();
      const m = String(parsed.getMonth() + 1).padStart(2, '0');
      const d = String(parsed.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };
    const dateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const getBookingClient = (b) => b.client || clients.find(c => String(c.id) === String(b.clientId))?.name || '-';
    const getBookingVehicle = (b) => b.vehicle || cars.find(c => String(c.id) === String(b.carId))?.name || '-';
    const pickups = bookings.filter(b => normalizeDateKey(b.startDate || b.pickupDate || b.dateFrom));
    const returns = bookings.filter(b => normalizeDateKey(b.endDate || b.returnDate || b.dateTo));

    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4 text-sm font-bold flex-wrap text-slate-800 dark:text-slate-100">
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-blue-500 inline-block"></span>{t('pickup_delivery_to_client')}</div>
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-red-600 inline-block"></span>{t('pickup_return_from_client')}</div>
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-amber-500 inline-block"></span>{t('reserved')}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setBookingOpsDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))} className="px-4 py-2 rounded-lg bg-slate-700 text-white"><ChevronLeft size={16} /></button>
            <button onClick={() => setBookingOpsDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))} className="px-4 py-2 rounded-lg bg-slate-700 text-white"><ChevronRight size={16} /></button>
            <button onClick={() => setBookingOpsDate(new Date())} className="px-4 py-2 rounded-lg bg-slate-500 text-white font-bold">{t('today')}</button>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <h2 className="text-4xl font-light text-slate-800 dark:text-white">{monthLabel}</h2>
          <button className="px-4 py-2 rounded-lg bg-slate-900 text-white font-bold">{t('month_view')}</button>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-100 dark:border-slate-700 overflow-hidden shadow-xl shadow-slate-200/30">
          <div className="grid grid-cols-7 border-b border-slate-200 dark:border-slate-700">
            {weekdayKeys.map(k => <div key={k} className="p-4 text-center font-bold text-slate-700 dark:text-slate-200">{t(k)}</div>)}
          </div>
          {weeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7 border-b last:border-b-0 border-slate-200 dark:border-slate-700">
              {week.map((day, di) => {
                const key = dateKey(day);
                const pickupItems = pickups.filter(b => normalizeDateKey(b.startDate || b.pickupDate || b.dateFrom) === key);
                const returnItems = returns.filter(b => normalizeDateKey(b.endDate || b.returnDate || b.dateTo) === key);
                const reservedItems = bookings.filter(b => {
                  const startKey = normalizeDateKey(b.startDate || b.pickupDate || b.dateFrom);
                  const endKey = normalizeDateKey(b.endDate || b.returnDate || b.dateTo);
                  return startKey && endKey && key > startKey && key < endKey;
                });
                const inMonth = day.getMonth() === opsDate.getMonth();
                return (
                  <div key={di} className={`min-h-[120px] p-2 border-r last:border-r-0 border-slate-200 dark:border-slate-700 ${inMonth ? '' : 'bg-slate-50/70 dark:bg-slate-900/20 text-slate-400'}`}>
                    <div className="text-right text-sm mb-2 font-semibold text-slate-700 dark:text-slate-200">{day.getDate()}</div>
                    <div className="space-y-1">
                      {pickupItems.map(b => <button type="button" onClick={() => openModal('booking','view',b)} key={'p' + b.id} className="w-full text-left text-[11px] px-2 py-1 rounded bg-blue-500 text-white font-semibold truncate hover:brightness-110 transition">{(b.startTime || '09:00')} {getBookingClient(b)} - {getBookingVehicle(b)}</button>)}
                      {reservedItems.map(b => <button type="button" onClick={() => openModal('booking','view',b)} key={'m' + b.id + key} className="w-full text-left text-[11px] px-2 py-1 rounded bg-amber-500 text-white font-semibold truncate hover:brightness-110 transition">{getBookingClient(b)} - {getBookingVehicle(b)}</button>)}
                      {returnItems.map(b => <button type="button" onClick={() => openModal('booking','view',b)} key={'r' + b.id} className="w-full text-left text-[11px] px-2 py-1 rounded bg-red-600 text-white font-semibold truncate hover:brightness-110 transition">{(b.endTime || '14:30')} {getBookingClient(b)} - {getBookingVehicle(b)}</button>)}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderBookings = () => renderBookingsList();

  const renderBookingsPayments = () => renderPayments();
  const renderPayments = () => {

    const trPaymentMethod = (v) => {
      if (!v) return '';
      const map = {
        'Kesh': t('cash'),
        'Kartelë Bankare': t('bank_card'),
        'Transfer Bankar': t('bank_transfer'),
        'PayPal': t('paypal'),
        'Tjera': t('other')
      };
      return map[v] || v;
    };
    const paymentFilterConfig = [
      { name: 'client', label: t('user_label'), type: 'select', options: clients.map(c => ({ value: c.name, label: c.name })) },
      { name: 'method', label: t('payment_method_label'), type: 'select', options: [
        { value: 'Kesh', label: t('cash') },
        { value: 'Kartelë Bankare', label: t('bank_card') },
        { value: 'Transfer Bankar', label: t('bank_transfer') },
        { value: 'PayPal', label: t('paypal') },
        { value: 'Tjera', label: t('other') },
      ] },
      { name: 'search', label: t('search'), type: 'text', placeholder: t('search_payment_placeholder') },
      { name: 'dateRange', label: t('date_range'), type: 'dateRange' }
    ];

    return renderGenericTable('payment', t('payments'), t('payments_subtitle'), [
      { key: 'id', label: 'ID', render: (p) => <span className="font-mono font-bold text-slate-500">{p.id}</span> },
      { key: 'bookingId', label: t('booking_col'), render: (p) => p.bookingId },
      { key: 'amount', label: t('amount_col'), render: (p) => <span className="font-black text-emerald-600 text-lg">{formatCurrency(p.amount)}</span> },
      { key: 'method', label: t('method_col'), render: (p) => <span className="px-2.5 py-1 bg-slate-100 dark:bg-slate-700 rounded-lg text-xs font-bold">{trPaymentMethod(p.method)}</span> },
      { key: 'date', label: t('date_col'), render: (p) => formatDate(p.date) }
    ], payments, null, paymentFilterConfig);
  }

  const renderExpenses = () => {
    const expenseFilterConfig = [
      { name: 'category', label: t('category'), type: 'select', options: ['Karburant', 'Servis dhe Mirëmbajtje', 'Pjesë Këmbimi', 'Sigurim', 'Paga', 'Qira e Zyrës', 'Tjera'].map(c => ({value: c, label: t(c === 'Karburant' ? 'expense_cat_fuel' : c === 'Servis dhe Mirëmbajtje' ? 'expense_cat_service' : c === 'Pjesë Këmbimi' ? 'expense_cat_parts' : c === 'Sigurim' ? 'expense_cat_insurance' : c === 'Paga' ? 'expense_cat_salary' : c === 'Qira e Zyrës' ? 'expense_cat_office_rent' : 'expense_cat_other')})) },
      { name: 'search', label: t('search'), type: 'text', placeholder: t('search_expense_placeholder') },
      { name: 'dateRange', label: t('date_range'), type: 'dateRange' }
    ];

    return renderGenericTable('expense', t('expenses'), t('expenses_subtitle'), [
      { key: 'category', label: 'Kategoria', render: (e) => <span className="px-2.5 py-1 bg-slate-100 dark:bg-slate-700 rounded-lg text-xs font-bold border dark:border-slate-600">{e.category}</span> },
      { key: 'description', label: t('description'), render: (e) => (<div><p className="text-sm font-medium">{e.description}</p><p className="text-xs text-slate-500">{e.vehicle}</p></div>) },
      { key: 'amount', label: t('amount_col'), render: (e) => <span className="font-black text-red-600 text-lg">-{formatCurrency(e.amount)}</span> },
      { key: 'date', label: t('date_col'), render: (e) => formatDate(e.date) }
    ], expenses, null, expenseFilterConfig);
  }

  const renderInvoices = () => {
    const invoiceFilterConfig = [
      { name: 'client', label: t('user_label'), type: 'select', options: clients.map(c => ({value: c.name, label: c.name})) },
      { name: 'status', label: t('status'), type: 'select', options: [{value: 'pending', label: t('pending')}, {value: 'paid', label: t('paid')}, {value: 'cancelled', label: t('status_cancelled')}] },
      { name: 'search', label: t('search'), type: 'text', placeholder: t('search') },
      { name: 'dateRange', label: t('date_range'), type: 'dateRange' }
    ];

    const invoiceActions = (i) => (
      <button onClick={() => printInvoice(i)} className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors" title={language === 'sq' ? 'Printo Faturën' : language === 'de' ? 'Rechnung drucken' : language === 'it' ? 'Stampa fattura' : language === 'fr' ? 'Imprimer la facture' : language === 'hr' ? 'Ispiši račun' : 'Print invoice'}>
        <Printer size={16} />
      </button>
    );

    return renderGenericTable('invoice', t('invoices'), t('invoices_subtitle'), [
      { key: 'id', label: t('invoice_number_col'), render: (i) => <span className="font-mono font-bold text-indigo-600">{i.id}</span> },
      { key: 'client', label: t('client_col'), render: (i) => <span className="font-bold">{i.client}</span> },
      { key: 'date', label: t('date_col'), render: (i) => formatDate(i.date) },
      { key: 'amount', label: t('amount_col'), render: (i) => <span className="font-black text-lg">{formatCurrency(i.amount)}</span> },
      { key: 'status', label: t('status'), render: (i) => (<span className={`px-2.5 py-1 rounded-lg text-xs font-bold border ${getStatusColor(i.status)}`}>{t(i.status)}</span>) }
    ], invoices, invoiceActions, invoiceFilterConfig);
  };

  const renderContracts = () => {
    const contractFilterConfig = [
      { name: 'client', label: t('client_col'), type: 'select', options: [{value: 'Template i Përgjithshëm', label: t('template_general')}, ...clients.map(c => ({value: c.name, label: c.name}))] },
      { name: 'status', label: t('status'), type: 'select', options: [{value: 'draft', label: 'Draft'}, {value: 'active', label: 'Aktive'}, {value: 'completed', label: 'E Përfunduar'}, {value: 'cancelled', label: t('status_cancelled')}] },
      { name: 'search', label: t('search'), type: 'text', placeholder: t('search_contract') },
      { name: 'dateRange', label: t('date_range'), type: 'dateRange' }
    ];

    const contractActions = (c) => (
      <button onClick={() => printManualContract(c)} className="p-1.5 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors" title={t('print_contract')}>
        <Printer size={16} />
      </button>
    );
    
    const GENERAL_TEMPLATE_VARIANTS = new Set([
      'Template i Përgjithshëm','General template','Allgemeine Vorlage','Template generale','Modèle général','Opći predložak'
    ]);
    const STANDARD_CONTRACT_TITLE_VARIANTS = new Set([
      'Kontratë Qiraje (Template Standard)',
      'Rental Contract (Standard Template)',
      'Mietvertrag (Standardvorlage)',
      'Contratto di noleggio (Template standard)',
      'Contrat de location (Modèle standard)',
      'Ugovor o najmu (Standardni predložak)'
    ]);

    const trContractClient = (v) => {
      if (!v) return '';
      return GENERAL_TEMPLATE_VARIANTS.has(v) ? t('template_general') : v;
    };
    const trContractTitle = (v) => {
      if (!v) return '';
      return STANDARD_CONTRACT_TITLE_VARIANTS.has(v) ? t('rental_contract_standard_template') : v;
    };


    return renderGenericTable('contract', t('contracts'), t('contracts_subtitle'), [
      { key: 'id', label: t('id_number'), render: (c) => <span className="font-mono font-bold text-slate-500">{c.id}</span> },
      { key: 'title', label: t('title_col'), render: (c) => <span className="font-bold text-slate-800 dark:text-white">{trContractTitle(c.title)}</span> },
      { key: 'client', label: t('client_usage'), render: (c) => <span className={c.client === 'Template i Përgjithshëm' ? 'text-indigo-500 font-medium text-xs border border-indigo-200 bg-indigo-50 px-2 py-1 rounded' : 'font-medium'}>{trContractClient(c.client)}</span> },
      { key: 'date', label: t('date'), render: (c) => formatDate(c.date) },
      { key: 'status', label: t('status'), render: (c) => (<span className={`px-2.5 py-1 rounded-lg text-xs font-bold border ${getStatusColor(c.status)}`}>{t(c.status)}</span>) }
    ], contracts, contractActions, contractFilterConfig);
  };

  const renderInsurance = () => renderGenericTable('insurance', t('insurance'), t('insurance_subtitle'), [
    { key: 'vehicle', label: t('vehicle'), render: (i) => (<div><span className="font-bold block">{i.vehicle}</span><span className="text-xs text-slate-500 font-mono">{i.plate}</span></div>) },
    { key: 'provider', label: 'Kompania', render: (i) => i.provider },
    { key: 'endDate', label: 'Data Skadimit', render: (i) => {
        const isExpired = new Date(i.endDate) < new Date();
        return <span className={`font-bold ${isExpired ? 'text-red-600' : 'text-amber-600'}`}>{formatDate(i.endDate)}</span>
    } },
    { key: 'cost', label: 'Kostoja', render: (i) => <span className="font-bold">{formatCurrency(i.cost)}</span> },
    { key: 'status', label: t('status'), render: (i) => {
      const isExpired = new Date(i.endDate) < new Date();
      return <span className={`px-2.5 py-1 rounded-lg text-xs font-bold border ${isExpired ? getStatusColor('expired') : getStatusColor(i.status)}`}>{isExpired ? t('expired') : t(i.status)}</span>
    }}
  ], insurances);

  const renderMaintenance = () => renderGenericTable('maintenance', t('maintenance'), t('services_faults'), [
    { key: 'vehicle', label: t('vehicle'), render: (m) => (<div><span className="font-bold block">{m.vehicle}</span><span className="text-xs text-slate-500 font-mono">{m.plate}</span></div>) },
    { key: 'description', label: t('description'), render: (m) => m.description },
    { key: 'date', label: t('date'), render: (m) => formatDate(m.date) },
    { key: 'cost', label: t('cost'), render: (m) => <span className="text-red-500 font-bold">{formatCurrency(m.cost)}</span> },
    { key: 'status', label: t('status'), render: (m) => (<span className={`px-2.5 py-1 rounded-lg text-xs font-bold border ${getStatusColor(m.status)}`}>{t(m.status)}</span>) }
  ], maintenance);

  const renderTpl = () => renderGenericTable('tpl', t('car_tab_tpl'), t('add_tpl'), [
    { key: 'vehicle', label: t('vehicle'), render: (r) => (<div><span className="font-bold block">{r.vehicle}</span><span className="text-xs text-slate-500 font-mono">{r.plate}</span></div>) },
    { key: 'amount', label: t('tpl_amount'), render: (r) => <span className="font-bold text-cyan-600">{formatCurrency(r.amount || 0)}</span> },
    { key: 'renewalDate', label: t('renewal_date'), render: (r) => formatDate(r.renewalDate) },
    { key: 'expiryDate', label: t('expiry_date'), render: (r) => formatDate(r.expiryDate) }
  ], tplRecords);

  const renderReports = () => {
    const isCompletedPayment = (payment) => {
      const status = String(payment?.status || '').trim().toLowerCase();
      return ['completed', 'paid', 'success', 'successful', 'done'].includes(status);
    };

    const parseSafeDate = (value) => {
      if (!value) return null;
      const raw = String(value).trim();
      const parsed = new Date(raw);
      if (!Number.isNaN(parsed.getTime())) return parsed;

      const normalized = raw.replace(/\./g, '-').replace(/\//g, '-');
      const parts = normalized.split('-');
      if (parts.length === 3) {
        const [a, b, c] = parts;
        let yyyy, mm, dd;

        if (a.length === 4) {
          yyyy = a; mm = b; dd = c;
        } else if (c.length === 4) {
          dd = a; mm = b; yyyy = c;
        }

        if (yyyy && mm && dd) {
          const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
          if (!Number.isNaN(d.getTime())) return d;
        }
      }

      return null;
    };

    const months = ['Jan', 'Shk', 'Mar', 'Pri', 'Maj', 'Qer', 'Krr', 'Gus', 'Sht', 'Tet', 'Nën', 'Dhj'];
    const monthLabel = (date) => `${months[date.getMonth()]} ${date.getFullYear().toString().slice(-2)}`;

    const createMonthBucket = (baseDate) => {
      const result = {};
      for (let i = 5; i >= 0; i--) {
        const d = new Date(baseDate.getFullYear(), baseDate.getMonth() - i, 1);
        const key = monthLabel(d);
        result[key] = { name: key, income: 0, expense: 0, sortKey: d.getTime() };
      }
      return result;
    };

    const totalRevenue = payments
      .filter(isCompletedPayment)
      .reduce((acc, curr) => acc + Number(curr.amount || 0), 0);
    const totalExpenses = expenses.reduce((acc, curr) => acc + Number(curr.amount || 0), 0);
    const netProfit = totalRevenue - totalExpenses;

    const today = new Date();
    const totalClients = clients.length;
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    const monthlyExpenses = expenses
      .filter(e => {
        const d = parseSafeDate(e.date);
        return d && d.getMonth() === currentMonth && d.getFullYear() === currentYear;
      })
      .reduce((acc, curr) => acc + Number(curr.amount || 0), 0);

    const allValidDates = [
      ...payments.filter(isCompletedPayment).map(p => parseSafeDate(p.date)).filter(Boolean),
      ...expenses.map(e => parseSafeDate(e.date)).filter(Boolean),
    ];

    const latestDataDate = allValidDates.length
      ? new Date(Math.max(...allValidDates.map(d => d.getTime())))
      : today;

    let monthlyData = createMonthBucket(today);

    payments
      .filter(p => isCompletedPayment(p) && p.date)
      .forEach(p => {
        const d = parseSafeDate(p.date);
        if (!d) return;
        const key = monthLabel(d);
        if (monthlyData[key]) monthlyData[key].income += Number(p.amount || 0);
      });

    expenses
      .filter(e => e.date)
      .forEach(e => {
        const d = parseSafeDate(e.date);
        if (!d) return;
        const key = monthLabel(d);
        if (monthlyData[key]) monthlyData[key].expense += Number(e.amount || 0);
      });

    let chartData = Object.values(monthlyData).sort((a, b) => a.sortKey - b.sortKey);
    const hasVisibleData = chartData.some(d => Number(d.income) > 0 || Number(d.expense) > 0);

    if (!hasVisibleData && allValidDates.length) {
      monthlyData = createMonthBucket(latestDataDate);

      payments
        .filter(p => isCompletedPayment(p) && p.date)
        .forEach(p => {
          const d = parseSafeDate(p.date);
          if (!d) return;
          const key = monthLabel(d);
          if (monthlyData[key]) monthlyData[key].income += Number(p.amount || 0);
        });

      expenses
        .filter(e => e.date)
        .forEach(e => {
          const d = parseSafeDate(e.date);
          if (!d) return;
          const key = monthLabel(d);
          if (monthlyData[key]) monthlyData[key].expense += Number(e.amount || 0);
        });

      chartData = Object.values(monthlyData).sort((a, b) => a.sortKey - b.sortKey);
    }

    const maxVal = Math.max(
      100,
      ...chartData.map(d => Math.max(Number(d.income || 0), Number(d.expense || 0)))
    );

    const reportExportRows = [
      { name: 'TOTAL', income: totalRevenue, expense: totalExpenses },
      ...chartData
    ];

    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <SectionHeader 
          title={t('reports')} 
          subtitle={t('reports_financial_summary')} 
          searchTerm={searchTerm} 
          setSearchTerm={setSearchTerm} 
          viewMode={viewMode} 
          setViewMode={setViewMode} 
          actions={renderHeaderActions(t('reports'), t('reports_financial_summary'), [
            { key: 'name', label: 'Month' },
            { key: 'income', label: t('income') },
            { key: 'expense', label: t('expenses_lbl') }
          ], reportExportRows)}
          t={t} 
        />

        

        {/* --- RAPORTET E AVANCUARA (Nga Data - Deri më Datë) --- */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-xl border border-slate-100 dark:border-slate-700">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <div>
              <h3 className="text-xl font-extrabold text-slate-800 dark:text-white">{t('advanced_report')}</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{t('advanced_report_sub')}</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex flex-col">
                <label className="text-xs font-extrabold text-slate-600 dark:text-slate-300 mb-1">{t('from_date')}</label>
                <input
                  type="date"
                  value={reportRange.from || ''}
                  onChange={(e) => setReportRange({ ...reportRange, from: e.target.value })}
                  className="px-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-xl text-sm font-semibold outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700 text-slate-800 dark:text-slate-200 [&::-webkit-calendar-picker-indicator]:opacity-50"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs font-extrabold text-slate-600 dark:text-slate-300 mb-1">{t('to_date')}</label>
                <input
                  type="date"
                  value={reportRange.to || ''}
                  onChange={(e) => setReportRange({ ...reportRange, to: e.target.value })}
                  className="px-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-xl text-sm font-semibold outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700 text-slate-800 dark:text-slate-200 [&::-webkit-calendar-picker-indicator]:opacity-50"
                />
              </div>
            </div>
          </div>

          {(() => {
            const from = reportRange.from ? new Date(reportRange.from + 'T00:00:00') : null;
            const to = reportRange.to ? new Date(reportRange.to + 'T23:59:59') : null;

            const inRange = (dStr) => {
              if (!from || !to) return false;
              const d = new Date(dStr);
              return d >= from && d <= to;
            };

            // Të ardhurat: Pagesa të përfunduara në periudhë
            const income = payments
              .filter(p => p.status === 'completed')
              .filter(p => inRange(p.date))
              .reduce((a, p) => a + Number(p.amount || 0), 0);

            // Shpenzimet: Shpenzime në periudhë
            const exp = expenses
              .filter(e => inRange(e.date))
              .reduce((a, e) => a + Number(e.amount || 0), 0);

            // Fiksimi: Totali i rezervimeve (si vlerë e kontraktuar) në periudhë
            const fixed = bookings
              .filter(b => inRange(b.startDate) || inRange(b.endDate))
              .reduce((a, b) => a + Number(b.totalPrice || b.total || 0), 0);

            const net = income - exp;

            return (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
                <div className="p-5 rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40">
                  <p className="text-xs font-extrabold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Fiksimi</p>
                  <p className="text-2xl font-black text-slate-800 dark:text-white mt-1">{formatCurrency(fixed)}</p>
                </div>
                <div className="p-5 rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40">
                  <p className="text-xs font-extrabold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('expenses_lbl')}</p>
                  <p className="text-2xl font-black text-slate-800 dark:text-white mt-1">{formatCurrency(exp)}</p>
                </div>
                <div className="p-5 rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40">
                  <p className="text-xs font-extrabold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('revenue')}</p>
                  <p className="text-2xl font-black text-slate-800 dark:text-white mt-1">{formatCurrency(income)}</p>
                </div>
                <div className="p-5 rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40">
                  <p className="text-xs font-extrabold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('net_profit')}</p>
                  <p className={`text-2xl font-black mt-1 ${net >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>{formatCurrency(net)}</p>
                </div>
              </div>
            );
          })()}
        </div>

<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-xl border border-slate-100 dark:border-slate-700 relative overflow-hidden">
            <div className="absolute -right-6 -top-6 bg-emerald-500/10 w-32 h-32 rounded-full blur-2xl"></div>
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-2xl flex items-center justify-center">
                <TrendingUp size={24} />
              </div>
              <h3 className="text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-xs">{t('income')}</h3>
            </div>
            <p className="text-4xl font-black text-slate-800 dark:text-white">{formatCurrency(totalRevenue)}</p>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-xl border border-slate-100 dark:border-slate-700 relative overflow-hidden">
            <div className="absolute -right-6 -top-6 bg-red-500/10 w-32 h-32 rounded-full blur-2xl"></div>
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-2xl flex items-center justify-center">
                <Activity size={24} />
              </div>
              <h3 className="text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-xs">{t('expenses_lbl')}</h3>
            </div>
            <p className="text-4xl font-black text-slate-800 dark:text-white">{formatCurrency(totalExpenses)}</p>
          </div>

          <div className="bg-gradient-to-br from-indigo-600 to-blue-700 rounded-3xl p-6 shadow-xl relative overflow-hidden text-white">
            <div className="absolute -right-6 -top-6 bg-white/10 w-32 h-32 rounded-full blur-2xl"></div>
            <div className="flex items-center space-x-4 mb-4 relative z-10">
              <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                <Wallet size={24} />
              </div>
              <h3 className="text-indigo-100 font-bold uppercase tracking-wider text-xs">{t('net_profit')}</h3>
            </div>
            <p className="text-4xl font-black relative z-10">{formatCurrency(netProfit)}</p>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 md:p-8 shadow-xl border border-slate-100 dark:border-slate-700 mt-6 overflow-hidden">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 border-b border-slate-100 dark:border-slate-700 pb-4">
            <div>
              <h3 className="font-extrabold text-lg flex items-center text-slate-800 dark:text-white">
                <BarChart3 size={20} className="mr-2 text-indigo-500" />
                {t('performance_last_6_months')}
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{t('reports_financial_summary')}</p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-2 text-sm font-bold text-slate-600 dark:text-slate-300">
                <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
                {t('revenue')}
              </span>
              <span className="inline-flex items-center gap-2 text-sm font-bold text-slate-600 dark:text-slate-300">
                <span className="w-3 h-3 rounded-full bg-rose-500"></span>
                {t('expenses_lbl')}
              </span>
            </div>
          </div>

          <div className="relative h-[320px]">
            <div className="absolute inset-0 pointer-events-none">
              {[0, 25, 50, 75, 100].map((line) => (
                <div
                  key={line}
                  className="absolute left-0 right-0 border-t border-dashed border-slate-200 dark:border-slate-700"
                  style={{ bottom: `${line}%` }}
                />
              ))}
            </div>

            <div className="relative h-full flex items-end justify-between gap-3 px-1 md:px-4">
              {chartData.map((data, idx) => {
                const incomeHeight = data.income > 0 ? `${Math.max((data.income / maxVal) * 100, 7)}%` : '0%';
                const expenseHeight = data.expense > 0 ? `${Math.max((data.expense / maxVal) * 100, 7)}%` : '0%';

                return (
                  <div key={idx} className="group flex-1 h-full flex flex-col justify-end items-center">
                    <div className="relative w-full h-[250px] flex items-end justify-center gap-2">
                      <div className="relative w-[42%] max-w-[40px]">
                        <div
                          className="w-full rounded-t-2xl bg-gradient-to-t from-emerald-600 to-emerald-400 shadow-lg shadow-emerald-500/20 transition-all duration-300 group-hover:brightness-110"
                          style={{ height: incomeHeight }}
                        />
                        <div className="opacity-0 group-hover:opacity-100 transition absolute -top-11 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[11px] font-bold px-2.5 py-1.5 rounded-xl whitespace-nowrap shadow-xl">
                          + {formatCurrency(data.income)}
                        </div>
                      </div>

                      <div className="relative w-[42%] max-w-[40px]">
                        <div
                          className="w-full rounded-t-2xl bg-gradient-to-t from-rose-600 to-rose-400 shadow-lg shadow-rose-500/20 transition-all duration-300 group-hover:brightness-110"
                          style={{ height: expenseHeight }}
                        />
                        <div className="opacity-0 group-hover:opacity-100 transition absolute -top-11 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[11px] font-bold px-2.5 py-1.5 rounded-xl whitespace-nowrap shadow-xl">
                          - {formatCurrency(data.expense)}
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 text-center">
                      <div className="text-xs md:text-sm font-black text-slate-700 dark:text-slate-200">{data.name}</div>
                      <div className={`text-[11px] font-bold mt-1 ${Number(data.income - data.expense) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                        {formatCurrency(data.income - data.expense)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div className="group relative bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-xl shadow-slate-200/50 dark:shadow-none border border-slate-100 dark:border-slate-700 overflow-hidden hover:-translate-y-1 transition-transform">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-violet-500/10 rounded-full blur-2xl group-hover:bg-violet-500/20 transition-colors"></div>
            <div className="flex justify-between items-start mb-4">
              <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-fuchsia-600 rounded-2xl flex items-center justify-center shadow-lg shadow-violet-500/30 text-white">
                <Users size={28} />
              </div>
              <span className="px-2.5 py-1 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-xs font-bold rounded-lg">{t('badge_total')}</span>
            </div>
            <h3 className="text-slate-500 dark:text-slate-400 font-medium text-sm">{t('total_clients')}</h3>
            <p className="text-4xl font-black text-slate-800 dark:text-white mt-1">{totalClients}</p>
          </div>

          <div className="group relative bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-xl shadow-slate-200/50 dark:shadow-none border border-slate-100 dark:border-slate-700 overflow-hidden hover:-translate-y-1 transition-transform">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-rose-500/10 rounded-full blur-2xl group-hover:bg-rose-500/20 transition-colors"></div>
            <div className="flex justify-between items-start mb-4">
              <div className="w-14 h-14 bg-gradient-to-br from-rose-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg shadow-rose-500/30 text-white">
                <Receipt size={28} />
              </div>
              <span className="px-2.5 py-1 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-xs font-bold rounded-lg">{t('badge_monthly')}</span>
            </div>
            <h3 className="text-slate-500 dark:text-slate-400 font-medium text-sm">{t('monthly_expenses')}</h3>
            <p className="text-4xl font-black text-slate-800 dark:text-white mt-1">{formatCurrency(monthlyExpenses)}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl border border-slate-100 dark:border-slate-700 p-6">
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-100 dark:border-slate-700">
              <h3 className="font-extrabold text-lg flex items-center text-slate-800 dark:text-white"><Receipt size={20} className="mr-2 text-red-500"/> {t('recent_expenses')}</h3>
              <button onClick={() => setActiveMenu('expenses')} className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline">{t('see_all')}</button>
            </div>
            <div className="space-y-3">
              {expenses.length === 0 ? <p className="text-sm text-slate-500">{t('no_expenses')}</p> : 
                expenses.slice(0, 5).map(e => (
                <div key={e.id} className="flex justify-between items-center p-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-xl transition-colors">
                  <div>
                    <p className="font-bold text-sm text-slate-800 dark:text-white">{e.category}</p>
                    <p className="text-xs text-slate-500">{formatDate(e.date)}</p>
                  </div>
                  <span className="font-bold text-red-500">-{formatCurrency(e.amount)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl border border-slate-100 dark:border-slate-700 p-6">
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-100 dark:border-slate-700">
              <h3 className="font-extrabold text-lg flex items-center text-slate-800 dark:text-white"><FileText size={20} className="mr-2 text-indigo-500"/> {t('recent_invoices')}</h3>
              <button onClick={() => setActiveMenu('invoices')} className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline">{t('see_all')}</button>
            </div>
            <div className="space-y-3">
              {invoices.length === 0 ? <p className="text-sm text-slate-500">{t('no_invoices')}</p> : 
                invoices.slice(0, 5).map(i => (
                <div key={i.id} className="flex justify-between items-center p-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-xl transition-colors">
                  <div>
                    <p className="font-bold text-sm text-slate-800 dark:text-white">{i.client}</p>
                    <p className="text-xs text-slate-500">{formatDate(i.date)}</p>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-emerald-600 block">{formatCurrency(i.amount)}</span>
                    <span className={`text-[10px] font-bold uppercase ${i.status === 'paid' ? 'text-emerald-500' : 'text-amber-500'}`}>{t(i.status)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- SEKSIONI I PROFILIT ---
  const renderProfile = () => {
    const tabs = [
      { id: 'detajet', label: t('details') },
      { id: 'njoftimet', label: t('notification_preferences') },
      { id: 'reminders', label: t('reminders') },
      { id: 'siguria', label: t('security') },
      { id: 'calendar', label: t('google_calendar') }
    ];

    const toggleNotificationPref = (key) => {
      setProfileNotificationPrefs({...profileNotificationPrefs, [key]: !profileNotificationPrefs[key]});
    };

    const handlePasswordUpdate = (e) => {
      e.preventDefault();
      if (passwords.current !== adminPassword) {
        showToast(t('password_incorrect'), 'error');
        return;
      }
      if (passwords.new !== passwords.confirm) {
        showToast(t('passwords_not_match'), 'error');
        return;
      }
      if (passwords.new.length < 6) {
        showToast(t('password_min_length'), 'error');
        return;
      }
      setAdminPassword(passwords.new);
      showToast(t('password_updated_success'), 'success');
      setPasswords({ current: '', new: '', confirm: '' });
    };

    return (
      <div className="max-w-5xl animate-in fade-in duration-500">
        <SectionHeader 
          title={t('profile_title')} 
          subtitle={t('profile_subtitle')}
        />

        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden mt-6">
          {/* Tabs Navigation */}
          <div className="flex border-b border-slate-200 dark:border-slate-700 overflow-x-auto custom-scrollbar">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveProfileTab(tab.id)}
                className={`whitespace-nowrap px-6 py-4 text-base font-bold transition-all border-b-2 ${
                  activeProfileTab === tab.id 
                    ? 'border-indigo-600 text-indigo-700 bg-indigo-50 dark:bg-indigo-900/20 dark:text-indigo-400 dark:border-indigo-500' 
                    : 'border-transparent text-slate-600 hover:text-indigo-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-700/50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-6 md:p-8">
            {activeProfileTab === 'detajet' && (
              <div className="space-y-6 animate-in slide-in-from-bottom-2 duration-300">
                <div className="flex justify-between items-center bg-indigo-700 dark:bg-slate-900 px-6 py-4 rounded-t-xl text-white">
                  {!isEditingProfile ? (
                    <button onClick={() => { setEditProfileForm(user); setIsEditingProfile(true); }} className="flex items-center space-x-2 bg-white text-indigo-800 hover:bg-slate-100 px-4 py-2 rounded-lg text-base font-bold transition shadow-sm">
                      <Edit3 size={16} />
                      <span>{t('update')}</span>
                    </button>
                  ) : (
                    <div className="flex space-x-3">
                      <button onClick={() => setIsEditingProfile(false)} className="flex items-center space-x-2 bg-slate-500 hover:bg-slate-600 text-white px-4 py-2 rounded-lg text-base font-bold transition shadow-sm">
                        <span>Anulo</span>
                      </button>
                      <button onClick={() => { setUser(editProfileForm); setIsEditingProfile(false); showToast(t('details_saved_success')); }} className="flex items-center space-x-2 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-base font-bold transition shadow-sm">
                        <Check size={16} />
                        <span>{t('save_changes')}</span>
                      </button>
                    </div>
                  )}
                </div>
                <div className="border border-t-0 border-slate-200 dark:border-slate-700 rounded-b-xl overflow-hidden -mt-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 border-b border-slate-200 dark:border-slate-700">
                    <div className="bg-slate-50 dark:bg-slate-800/50 p-4 font-bold text-slate-700 dark:text-slate-300 text-sm flex items-center">Name:</div>
                    <div className="p-4 md:col-span-2 text-slate-800 dark:text-white text-sm bg-white dark:bg-slate-900">
                      {isEditingProfile ? (
                        <input type="text" value={editProfileForm.name || ''} onChange={e => setEditProfileForm({...editProfileForm, name: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg dark:bg-slate-800 dark:border-slate-600 focus:ring-2 focus:ring-indigo-500 outline-none transition" />
                      ) : user.name}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 border-b border-slate-200 dark:border-slate-700">
                    <div className="bg-slate-50 dark:bg-slate-800/50 p-4 font-bold text-slate-700 dark:text-slate-300 text-sm flex items-center">Username:</div>
                    <div className="p-4 md:col-span-2 text-slate-800 dark:text-white text-sm bg-white dark:bg-slate-900">
                      {isEditingProfile ? (
                        <input type="text" value={editProfileForm.username || ''} onChange={e => setEditProfileForm({...editProfileForm, username: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg dark:bg-slate-800 dark:border-slate-600 focus:ring-2 focus:ring-indigo-500 outline-none transition" />
                      ) : user.username}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 border-b border-slate-200 dark:border-slate-700">
                    <div className="bg-slate-50 dark:bg-slate-800/50 p-4 font-bold text-slate-700 dark:text-slate-300 text-sm flex items-center">Email:</div>
                    <div className="p-4 md:col-span-2 text-slate-800 dark:text-white text-sm bg-white dark:bg-slate-900">
                      {isEditingProfile ? (
                        <input type="email" value={editProfileForm.email || ''} onChange={e => setEditProfileForm({...editProfileForm, email: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg dark:bg-slate-800 dark:border-slate-600 focus:ring-2 focus:ring-indigo-500 outline-none transition" />
                      ) : user.email}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3">
                    <div className="bg-slate-50 dark:bg-slate-800/50 p-4 font-bold text-slate-700 dark:text-slate-300 text-sm flex items-center">Phone Number:</div>
                    <div className="p-4 md:col-span-2 text-slate-800 dark:text-white text-sm bg-white dark:bg-slate-900">
                      {isEditingProfile ? (
                        <input type="text" value={editProfileForm.phone || ''} onChange={e => setEditProfileForm({...editProfileForm, phone: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg dark:bg-slate-800 dark:border-slate-600 focus:ring-2 focus:ring-indigo-500 outline-none transition" />
                      ) : user.phone}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeProfileTab === 'njoftimet' && (
              <div className="space-y-6 animate-in slide-in-from-bottom-2 duration-300">
                <div className="divide-y divide-slate-100 dark:divide-slate-700">
                  {[
                    { key: 'makinaShtua', label: t('car_added') },
                    { key: 'makinaPerditesua', label: t('car_updated') },
                    { key: 'makinaArkivua', label: t('car_archived') },
                    { key: 'makinaFshi', label: t('car_deleted') },
                    { key: 'klientiPerditesua', label: t('client_updated') },
                    { key: 'shpenzimiShtua', label: t('expense_added') },
                    { key: 'shpenzimiPerditesua', label: t('expense_updated') },
                    { key: 'shpenzimiFshi', label: t('expense_deleted') },
                    { key: 'pagesaShtua', label: t('payment_added') },
                    { key: 'pagesaPerditesua', label: t('payment_updated') },
                    { key: 'pagesaFshi', label: t('payment_deleted') },
                    { key: 'rezervimiArkivua', label: t('booking_archived') },
                    { key: 'rezervimiFshi', label: t('booking_deleted') },
                    { key: 'rezervimiShtua', label: t('booking_added') },
                    { key: 'rezervimiPerditesua', label: t('booking_updated') }
                  ].map((item) => (
                    <div key={item.key} className="flex justify-between items-center py-4">
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{item.label}</span>
                      <SwitchToggle checked={profileNotificationPrefs[item.key]} onChange={() => toggleNotificationPref(item.key)} />
                    </div>
                  ))}
                </div>
                <div className="pt-4">
                  <button onClick={() => showToast('Preferencat u ruajtën!')} className="px-6 py-2.5 bg-[#005a87] hover:bg-[#00476b] text-white font-bold rounded-lg transition-colors shadow-sm">{t('save')}</button>
                </div>
              </div>
            )}

            {activeProfileTab === 'reminders' && (
              <div className="space-y-8 animate-in slide-in-from-bottom-2 duration-300">
                <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-slate-100 dark:border-slate-700 pb-6 gap-4">
                  <div>
                    <h4 className="font-bold text-slate-800 dark:text-white text-base mb-1">{t('daily_summary')}</h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{t('daily_summary_desc')}</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <SwitchToggle checked={profileReminders.permDitore} onChange={(v) => setProfileReminders({...profileReminders, permDitore: v})} />
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-slate-600 dark:text-slate-300">{t('time')}:</span>
                      <div className="relative">
                        <TimeSelect value={profileReminders.permDitoreKoha} onChange={(v) => setProfileReminders({ ...profileReminders, permDitoreKoha: v })} className="w-full max-w-[220px]" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-slate-100 dark:border-slate-700 pb-6 gap-4">
                  <div>
                    <h4 className="font-bold text-slate-800 dark:text-white text-base mb-1">{t('car_return_notification')}</h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{t('car_return_desc')}</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <SwitchToggle checked={profileReminders.kthimMakine} onChange={(v) => setProfileReminders({...profileReminders, kthimMakine: v})} />
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-slate-600 dark:text-slate-300">{t('minutes_before')}:</span>
                      <input type="number" value={profileReminders.kthimMakineMin} onChange={(e) => setProfileReminders({...profileReminders, kthimMakineMin: e.target.value})} className="w-20 border border-slate-300 dark:border-slate-600 rounded-md px-3 py-1.5 text-sm outline-none focus:border-indigo-500 bg-white dark:bg-slate-900 text-slate-800 dark:text-white text-center" />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-slate-100 dark:border-slate-700 pb-6 gap-4">
                  <div>
                    <h4 className="font-bold text-slate-800 dark:text-white text-base mb-1">{t('car_pickup_notification')}</h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{t('car_pickup_desc')}</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <SwitchToggle checked={profileReminders.marrjeMakine} onChange={(v) => setProfileReminders({...profileReminders, marrjeMakine: v})} />
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-slate-600 dark:text-slate-300">{t('minutes_before')}:</span>
                      <input type="number" value={profileReminders.marrjeMakineMin} onChange={(e) => setProfileReminders({...profileReminders, marrjeMakineMin: e.target.value})} className="w-20 border border-slate-300 dark:border-slate-600 rounded-md px-3 py-1.5 text-sm outline-none focus:border-indigo-500 bg-white dark:bg-slate-900 text-slate-800 dark:text-white text-center" />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-slate-100 dark:border-slate-700 pb-6 gap-4">
                  <div>
                    <h4 className="font-bold text-slate-800 dark:text-white text-base mb-1">{t('car_registration_reminder')}</h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{t('car_registration_desc')}</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <SwitchToggle checked={profileReminders.kujteseRegjistrim} onChange={(v) => setProfileReminders({...profileReminders, kujteseRegjistrim: v})} />
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-slate-600 dark:text-slate-300">{t('days_before')}:</span>
                      <input type="number" value={profileReminders.kujteseRegjistrimDite} onChange={(e) => setProfileReminders({...profileReminders, kujteseRegjistrimDite: e.target.value})} className="w-20 border border-slate-300 dark:border-slate-600 rounded-md px-3 py-1.5 text-sm outline-none focus:border-indigo-500 bg-white dark:bg-slate-900 text-slate-800 dark:text-white text-center" />
                    </div>
                  </div>
                </div>

                <div className="pt-2">
                  <button onClick={() => showToast('Preferencat u ruajtën!')} className="px-6 py-2.5 bg-[#005a87] hover:bg-[#00476b] text-white font-bold rounded-lg transition-colors shadow-sm">
                    {t('save_preferences')}
                  </button>
                </div>
              </div>
            )}

            {activeProfileTab === 'siguria' && (
              <form onSubmit={handlePasswordUpdate} className="space-y-6 max-w-2xl animate-in slide-in-from-bottom-2 duration-300">
                <div>
                  <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-2">{t('current_password')}</label>
                  <div className="relative">
                    <input 
                      type={showPassword.current ? "text" : "password"} 
                      placeholder={t('enter_current_password')} 
                      required
                      value={passwords.current}
                      onChange={(e) => setPasswords({...passwords, current: e.target.value})}
                      className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 transition-all" 
                    />
                    <button type="button" onClick={() => setShowPassword({...showPassword, current: !showPassword.current})} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                      {showPassword.current ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-2">{t('new_password')}</label>
                  <div className="relative">
                    <input 
                      type={showPassword.new ? "text" : "password"} 
                      placeholder={t('enter_new_password')} 
                      required
                      value={passwords.new}
                      onChange={(e) => setPasswords({...passwords, new: e.target.value})}
                      className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 transition-all" 
                    />
                    <button type="button" onClick={() => setShowPassword({...showPassword, new: !showPassword.new})} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                      {showPassword.new ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-2">{t('confirm_new_password')}</label>
                  <div className="relative">
                    <input 
                      type={showPassword.confirm ? "text" : "password"} 
                      placeholder={t('confirm_password')} 
                      required
                      value={passwords.confirm}
                      onChange={(e) => setPasswords({...passwords, confirm: e.target.value})}
                      className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 transition-all" 
                    />
                    <button type="button" onClick={() => setShowPassword({...showPassword, confirm: !showPassword.confirm})} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                      {showPassword.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="pt-2">
                  <button type="submit" className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-colors shadow-sm">
                    {t('update_password')}
                  </button>
                </div>
              </form>
            )}

            {activeProfileTab === 'calendar' && (
              <div className="space-y-4 animate-in slide-in-from-bottom-2 duration-300">
                {profileNotificationPrefs.googleSync ? (
                  <div className="flex items-center space-x-2 text-slate-800 dark:text-white">
                    <CheckCircle size={18} className="text-emerald-500" />
                    <span className="text-sm font-medium">Lidhur me Google Calendar me sukses</span>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center space-x-2 text-slate-800 dark:text-white">
                      <XCircle size={18} className="text-red-500" />
                      <span className="text-sm font-medium">{t('google_calendar_not_connected')}</span>
                    </div>
                    <div className="pt-4">
                      <button onClick={handleGoogleLogin} className="px-6 py-2.5 bg-black dark:bg-white text-white dark:text-black font-bold rounded-full transition-transform hover:scale-105 shadow-md">
                        {t('connect_google_calendar')}
                      </button>
                    </div>
                  </>
                )}
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                  {t('google_calendar_desc')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSettings = () => (
    <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-500">
      <SectionHeader 
        title={t('settings')} 
        subtitle={t('system_config')} 
        searchTerm={searchTerm} 
        setSearchTerm={setSearchTerm} 
        viewMode={viewMode} 
        setViewMode={setViewMode} 
        t={t} 
      />

      <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl shadow-slate-200/40 dark:shadow-none border border-slate-100 dark:border-slate-700 p-8">
        <h3 className="text-xl font-extrabold text-slate-800 dark:text-white mb-6 flex items-center border-b border-slate-100 dark:border-slate-700 pb-4">
          <Briefcase size={22} className="mr-3 text-indigo-500" />
          {t('company_data')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-2">{t('company_name')}</label>
            <input type="text" value={settings.companyName} onChange={(e) => setSettings({...settings, companyName: e.target.value})} className="w-full px-4 py-3 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 transition-all hover:border-indigo-300" />
          </div>
          <div>
            <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-2">{t('business_number')}</label>
            <input type="text" value={settings.businessNumber} onChange={(e) => setSettings({...settings, businessNumber: e.target.value})} className="w-full px-4 py-3 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 transition-all hover:border-indigo-300" />
          </div>
          <div>
            <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-2">{t('email')}</label>
            <input type="email" value={settings.email} onChange={(e) => setSettings({...settings, email: e.target.value})} className="w-full px-4 py-3 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 transition-all hover:border-indigo-300" />
          </div>
          <div>
            <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-2">{t('phone')}</label>
            <input type="text" value={settings.phone} onChange={(e) => setSettings({...settings, phone: e.target.value})} className="w-full px-4 py-3 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 transition-all hover:border-indigo-300" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-2">{t('address')}</label>
            <input type="text" value={settings.address} onChange={(e) => setSettings({...settings, address: e.target.value})} className="w-full px-4 py-3 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 transition-all hover:border-indigo-300" />
          </div>
          <div>
            <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-2">{t('currency')}</label>
            <div className="relative">
              <select value={settings.currency} onChange={(e) => setSettings({...settings, currency: e.target.value})} className="w-full px-4 py-3 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 appearance-none cursor-pointer transition-all hover:border-indigo-300">
                <option value="€">{t('euro')}</option>
                <option value="$">USD ($)</option>
                <option value="Lek">Lek</option>
              </select>
              <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
            </div>
          </div>
        </div>
        <div className="mt-8 flex justify-end">
          <button onClick={() => showToast(t('settings_saved'))} className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-extrabold rounded-xl hover:shadow-lg hover:shadow-indigo-500/30 transition-all hover:scale-[1.02]">
            {t('save_changes')}
          </button>
        </div>
      </div>

      <div className="bg-gradient-to-br from-indigo-50/80 to-blue-50/80 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-3xl shadow-xl border border-indigo-100 dark:border-indigo-800/50 p-8 border-t-4 border-t-indigo-500 relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
          <Database size={120} />
        </div>
        <div className="flex items-center space-x-3 mb-6 border-b border-indigo-100/50 dark:border-indigo-800/50 pb-4 relative z-10">
          <Database className="text-indigo-600 dark:text-indigo-400" size={26} />
          <h3 className="text-xl font-extrabold text-indigo-900 dark:text-indigo-200">{t('database_management')}</h3>
        </div>
        <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-8 leading-relaxed max-w-2xl relative z-10">
          {t('db_help')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 relative z-10">
          <button onClick={exportDatabase} className="group flex flex-col items-center justify-center p-6 border-2 border-dashed border-indigo-300 dark:border-indigo-700 bg-white/50 dark:bg-slate-800/50 rounded-2xl hover:bg-indigo-50 dark:hover:bg-indigo-900/40 hover:border-indigo-500 transition-all backdrop-blur-sm">
            <Download size={32} className="text-indigo-500 dark:text-indigo-400 mb-3 transition-transform group-hover:-translate-y-1" />
            <span className="font-bold text-indigo-900 text-slate-800 dark:text-white">{t('download_database')}</span>
            <span className="text-xs font-medium text-indigo-600/70 dark:text-indigo-300/70 mt-1">{t('save_as_json')}</span>
          </button>
          
          <button onClick={() => fileInputRef.current.click()} className="group flex flex-col items-center justify-center p-6 border-2 border-dashed border-slate-300 dark:border-slate-700 bg-white/50 dark:bg-slate-800/50 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800 hover:border-slate-500 transition-all backdrop-blur-sm">
            <Upload size={32} className="text-slate-500 dark:text-slate-400 mb-3 transition-transform group-hover:-translate-y-1" />
            <span className="font-bold text-slate-800 dark:text-white">{t('upload_database')}</span>
            <span className="text-xs font-medium text-slate-500 mt-1">{t('restore_from_pc')}</span>
          </button>

          <button onClick={handleResetProgram} className="group md:col-span-2 flex flex-col items-center justify-center p-6 border-2 border-dashed border-red-300 dark:border-red-800 bg-red-50/70 dark:bg-red-950/20 rounded-2xl hover:bg-red-100 dark:hover:bg-red-950/30 hover:border-red-500 transition-all backdrop-blur-sm">
            <AlertTriangle size={32} className="text-red-500 dark:text-red-400 mb-3 transition-transform group-hover:-translate-y-1" />
            <span className="font-bold text-red-700 dark:text-red-300">{t('reset_program')}</span>
            <span className="text-xs font-medium text-red-600/80 dark:text-red-300/80 mt-1 text-center max-w-xl">{t('reset_program_desc')}</span>
          </button>
        </div>
      </div>
    </div>
  );

  const globalSmartNotifications = (() => {
    const today = getTodayDateOnly();
    const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);

    const returnsToday = bookings.filter(b => {
      const end = getBookingDate(b, 'endDate');
      return end && end.getTime() === today.getTime();
    }).map(b => ({ type: 'return', title: uiText({sq:'Kthim sot',en:'Return today',de:'Rückgabe heute',it:'Rientro oggi',fr:'Retour aujourd’hui',hr:'Povrat danas'}), text: `${b.client} • ${b.vehicle}` }));

    const upcomingTomorrow = bookings.filter(b => {
      const start = getBookingDate(b, 'startDate');
      return start && start.getTime() === tomorrow.getTime();
    }).map(b => ({ type: 'booking', title: uiText({sq:'Rezervim nesër',en:'Booking tomorrow',de:'Buchung morgen',it:'Prenotazione domani',fr:'Réservation demain',hr:'Rezervacija sutra'}), text: `${b.client} • ${b.vehicle}` }));

    const expiringInsurance = insurances.filter(i => {
      const end = parseDateOnly(i.endDate);
      if (!end) return false;
      const diff = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
      return diff >= 0 && diff <= 7;
    }).map(i => ({ type: 'insurance', title: uiText({sq:'Sigurim në skadim',en:'Insurance expiring',de:'Versicherung läuft ab',it:'Assicurazione in scadenza',fr:'Assurance expirante',hr:'Osiguranje istječe'}), text: `${i.vehicle} • ${i.endDate}` }));

    const upcomingService = maintenance.filter(m => {
      const d = parseDateOnly(m.date);
      if (!d) return false;
      const diff = Math.ceil((d - today) / (1000 * 60 * 60 * 24));
      return diff >= 0 && diff <= 7;
    }).map(m => ({ type: 'service', title: uiText({sq:'Servis së shpejti',en:'Upcoming service',de:'Bevorstehender Service',it:'Manutenzione imminente',fr:'Entretien à venir',hr:'Nadolazeći servis'}), text: `${m.vehicle} • ${m.date}` }));

    return [...returnsToday, ...upcomingTomorrow, ...expiringInsurance, ...upcomingService];
  })();

  const renderFeatureCard = (icon, title, value, colorClass = 'from-indigo-500 to-blue-600', subtitle = '') => {
    const Icon = icon;
    return (
      <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-xl border border-slate-100 dark:border-slate-700">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500 dark:text-slate-400 font-bold">{title}</p>
            <p className="text-3xl font-black text-slate-800 dark:text-white mt-1">{value}</p>
            {subtitle ? <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">{subtitle}</p> : null}
          </div>
          <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${colorClass} flex items-center justify-center text-white shadow-lg`}>
            <Icon size={28} />
          </div>
        </div>
      </div>
    );
  };

  const renderBookingCalendar = () => {
    const days = Array.from({ length: 14 }, (_, i) => {
      const d = getTodayDateOnly();
      d.setDate(d.getDate() + i);
      return d;
    });
    const filteredCars = cars.filter(car => {
      const q = searchTerm.toLowerCase();
      return !q || getCarDisplayName(car).toLowerCase().includes(q) || String(car.plate || '').toLowerCase().includes(q);
    });
    const pagination = paginateCollection('booking_calendar', filteredCars);
    const paginatedCars = pagination.pageItems;

    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <SectionHeader
          title={uiText({sq:'Kalendari i rezervimeve',en:'Booking Calendar',de:'Buchungskalender',it:'Calendario prenotazioni',fr:'Calendrier des réservations',hr:'Kalendar rezervacija'})}
          subtitle={uiText({sq:'Pamje vizuale e veturave, rezervimeve dhe serviseve për 14 ditët e ardhshme.',en:'Visual view of vehicles, bookings and maintenance for the next 14 days.',de:'Visuelle Ansicht von Fahrzeugen, Buchungen und Wartungen für die nächsten 14 Tage.',it:'Vista visiva di veicoli, prenotazioni e manutenzioni per i prossimi 14 giorni.',fr:'Vue visuelle des véhicules, réservations et maintenances pour les 14 prochains jours.',hr:'Vizualni prikaz vozila, rezervacija i servisa za sljedećih 14 dana.'})}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          viewMode={viewMode}
          setViewMode={setViewMode}
          actions={<div className="text-sm text-slate-500 dark:text-slate-400 font-semibold">{uiText({sq:'E kuqe = rezervuar • Jeshile = e lirë • Portokalli = servis',en:'Red = reserved • Green = available • Orange = maintenance',de:'Rot = reserviert • Grün = verfügbar • Orange = Wartung',it:'Rosso = prenotato • Verde = disponibile • Arancione = manutenzione',fr:'Rouge = réservé • Vert = disponible • Orange = maintenance',hr:'Crveno = rezervirano • Zeleno = dostupno • Narančasto = servis'})}</div>}
          t={t}
        />

        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl border border-slate-100 dark:border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-[1100px] w-full">
              <thead className="bg-slate-50 dark:bg-slate-900/40">
                <tr>
                  <th className="px-4 py-4 text-left text-sm font-black text-slate-600 dark:text-slate-300">{uiText({sq:'Vetura',en:'Vehicle',de:'Fahrzeug',it:'Veicolo',fr:'Véhicule',hr:'Vozilo'})}</th>
                  {days.map((day, idx) => (
                    <th key={idx} className="px-3 py-4 text-center text-sm font-black text-slate-600 dark:text-slate-300">
                      <div>{day.toLocaleDateString(locale, { day: '2-digit', month: 'short' })}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedCars.map(car => {
                  const carBookings = getBookingsForCar(car);
                  return (
                    <tr key={car.id} className="border-t border-slate-100 dark:border-slate-700">
                      <td className="px-4 py-4 font-bold text-slate-800 dark:text-white whitespace-nowrap">{getCarDisplayName(car)}</td>
                      {days.map((day, idx) => {
                        const booking = carBookings.find(b => isBookingActiveOnDate(b, day));
                        const isMaintenance = (car.status || '').toLowerCase() === 'maintenance';
                        const classes = isMaintenance
  ? 'bg-orange-200 text-orange-900 border border-orange-500 font-bold'
  : booking
    ? 'bg-red-500 text-white border border-red-700 font-bold shadow'
    : 'bg-emerald-200 text-emerald-900 border border-emerald-500 font-semibold';
                        return (
                          <td key={idx} className="px-2 py-3">
                            <div className={`rounded-xl px-2 py-2 text-center text-xs font-extrabold ${classes}`}>
                              {isMaintenance ? uiText({sq:'Servis',en:'Maintenance',de:'Wartung',it:'Manutenzione',fr:'Maintenance',hr:'Servis'}) : booking ? booking.client.split(' ')[0] : uiText({sq:'Lirë',en:'Free',de:'Frei',it:'Libera',fr:'Libre',hr:'Slobodno'})}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        {renderPaginationControls('booking_calendar', pagination)}

        <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-xl border border-slate-100 dark:border-slate-700">
          <h3 className="text-xl font-black text-slate-800 dark:text-white mb-4">{uiText({sq:'Lëviz rezervimet shpejt',en:'Move bookings quickly',de:'Buchungen schnell verschieben',it:'Sposta rapidamente le prenotazioni',fr:'Déplacer rapidement les réservations',hr:'Brzo pomakni rezervacije'})}</h3>
          <div className="space-y-3">
            {bookings.slice(0, 6).map(booking => (
              <div key={booking.id} className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-4 rounded-2xl border border-slate-200 dark:border-slate-700">
                <div>
                  <p className="font-bold text-slate-800 dark:text-white">{booking.client} • {booking.vehicle}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{booking.startDate} → {booking.endDate}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => shiftBookingDays(booking.id, -1)} className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-700 text-slate-800 dark:text-slate-200 font-bold">-1 {uiText({sq:'ditë',en:'day',de:'Tag',it:'giorno',fr:'jour',hr:'dan'})}</button>
                  <button onClick={() => shiftBookingDays(booking.id, 1)} className="px-4 py-2 rounded-xl bg-indigo-600 text-white font-bold">+1 {uiText({sq:'ditë',en:'day',de:'Tag',it:'giorno',fr:'jour',hr:'dan'})}</button>
                  <button onClick={() => openModal('booking', 'edit', booking)} className="px-4 py-2 rounded-xl bg-emerald-600 text-white font-bold">{uiText({sq:'Ndrysho',en:'Edit',de:'Bearbeiten',it:'Modifica',fr:'Modifier',hr:'Uredi'})}</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderVehicleStatus = () => {
    const data = cars
      .map(car => ({ ...car, liveStatus: getVehicleStatusComputed(car) }))
      .filter(car => {
        const q = searchTerm.toLowerCase();
        return !q || getCarDisplayName(car).toLowerCase().includes(q) || String(car.plate || '').toLowerCase().includes(q) || String(car.liveStatus || '').toLowerCase().includes(q);
      });
    const counts = data.reduce((acc, car) => {
      acc[car.liveStatus] = (acc[car.liveStatus] || 0) + 1;
      return acc;
    }, {});
    const pagination = paginateCollection('vehicle_status', data);
    const paginatedData = pagination.pageItems;
    const badgeClass = (status) => ({
      available: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
      rented: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
      reserved: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
      maintenance: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'
    }[status] || 'bg-slate-100 text-slate-700');

    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <SectionHeader title={uiText({sq:'Statusi i veturave',en:'Vehicle Status',de:'Fahrzeugstatus',it:'Stato veicoli',fr:'Statut des véhicules',hr:'Status vozila'})}
          subtitle={uiText({sq:'Status live për secilën veturë bazuar në rezervime dhe servis.',en:'Live status for each vehicle based on bookings and maintenance.',de:'Live-Status jedes Fahrzeugs basierend auf Buchungen und Wartung.',it:'Stato live per ogni veicolo in base a prenotazioni e manutenzione.',fr:'Statut en direct de chaque véhicule selon les réservations et la maintenance.',hr:'Status uživo za svako vozilo na temelju rezervacija i servisa.'})}
          searchTerm={searchTerm} setSearchTerm={setSearchTerm} viewMode={viewMode} setViewMode={setViewMode} t={t} />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {renderFeatureCard(Car, vehicleStatusLabel('available'), counts.available || 0, 'from-emerald-500 to-green-600')}
          {renderFeatureCard(Activity, vehicleStatusLabel('rented'), counts.rented || 0, 'from-indigo-500 to-blue-600')}
          {renderFeatureCard(Clock, vehicleStatusLabel('reserved'), counts.reserved || 0, 'from-amber-500 to-orange-600')}
          {renderFeatureCard(Wrench, vehicleStatusLabel('maintenance'), counts.maintenance || 0, 'from-rose-500 to-red-600')}
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl border border-slate-100 dark:border-slate-700 overflow-hidden">
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {paginatedData.map(car => (
              <div key={car.id} className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-5">
                <div>
                  <p className="font-black text-slate-800 dark:text-white">{getCarDisplayName(car)}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{car.plate} • {car.year}</p>
                </div>
                <span className={`px-4 py-2 rounded-full text-sm font-black ${badgeClass(car.liveStatus)}`}>{vehicleStatusLabel(car.liveStatus)}</span>
              </div>
            ))}
          </div>
        </div>
        {renderPaginationControls('vehicle_status', pagination)}
      </div>
    );
  };

  const renderContractsAutomation = () => {
    const filteredBookings = bookings.filter(booking => {
      const q = searchTerm.toLowerCase();
      return !q || String(booking.client || '').toLowerCase().includes(q) || String(booking.vehicle || '').toLowerCase().includes(q) || String(booking.id || '').toLowerCase().includes(q);
    });
    const pagination = paginateCollection('contracts_auto', filteredBookings);
    const paginatedBookings = pagination.pageItems;
    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <SectionHeader title={uiText({sq:'Gjenero Kontrata',en:'Generate Contracts',de:'Verträge generieren',it:'Genera contratti',fr:'Générer des contrats',hr:'Generiraj ugovore'})}
          subtitle={uiText({sq:'Krijo kontratë automatike nga rezervimi.',en:'Create an automatic contract from a booking.',de:'Automatischen Vertrag aus einer Buchung erstellen.',it:'Crea un contratto automatico da una prenotazione.',fr:'Créer un contrat automatique à partir d’une réservation.',hr:'Kreiraj automatski ugovor iz rezervacije.'})}
          searchTerm={searchTerm} setSearchTerm={setSearchTerm} viewMode={viewMode} setViewMode={setViewMode} t={t} />
        <div className="space-y-4">
          {paginatedBookings.map(booking => (
            <div key={booking.id} className="bg-white dark:bg-slate-800 rounded-3xl p-5 shadow-xl border border-slate-100 dark:border-slate-700 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <p className="font-black text-slate-800 dark:text-white">{booking.client} • {booking.vehicle}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">{booking.startDate} → {booking.endDate} • {formatCurrency(booking.total || 0)}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => createContractFromBooking(booking)} className="px-4 py-2.5 rounded-xl bg-indigo-600 text-white font-bold">{uiText({sq:'Gjenero Kontratën',en:'Generate Contract',de:'Vertrag generieren',it:'Genera contratto',fr:'Générer le contrat',hr:'Generiraj ugovor'})}</button>
                <button onClick={() => setActiveMenu('contracts')} className="px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-700 text-slate-800 dark:text-slate-200 font-bold">{uiText({sq:'Shiko kontratat',en:'View contracts',de:'Verträge ansehen',it:'Vedi contratti',fr:'Voir les contrats',hr:'Pogledaj ugovore'})}</button>
              </div>
            </div>
          ))}
        </div>
        {renderPaginationControls('contracts_auto', pagination)}
      </div>
    );
  };

  const renderDeposits = () => {
    const filteredBookings = bookings.filter(booking => {
      const q = searchTerm.toLowerCase();
      return !q || String(booking.client || '').toLowerCase().includes(q) || String(booking.vehicle || '').toLowerCase().includes(q) || String(booking.id || '').toLowerCase().includes(q);
    });
    const pagination = paginateCollection('deposits', filteredBookings);
    const paginatedBookings = pagination.pageItems;
    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <SectionHeader title={uiText({sq:'Depozitat',en:'Deposits',de:'Kautionen',it:'Depositi',fr:'Dépôts',hr:'Depoziti'})}
          subtitle={uiText({sq:'Menaxho statusin e depozitës për çdo rezervim.',en:'Manage deposit status for every booking.',de:'Kautionsstatus für jede Buchung verwalten.',it:'Gestisci lo stato del deposito per ogni prenotazione.',fr:'Gérer le statut du dépôt pour chaque réservation.',hr:'Upravljaj statusom depozita za svaku rezervaciju.'})}
          searchTerm={searchTerm} setSearchTerm={setSearchTerm} viewMode={viewMode} setViewMode={setViewMode} t={t} />
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl border border-slate-100 dark:border-slate-700 overflow-hidden">
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {paginatedBookings.map(booking => (
              <div key={booking.id} className="p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div>
                  <p className="font-black text-slate-800 dark:text-white">{booking.client}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{booking.vehicle} • {booking.id}</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="px-4 py-2 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 text-slate-800 dark:text-slate-200 font-bold">{formatCurrency(booking.deposit || 200)}</span>
                  <span className={`px-4 py-2 rounded-full text-sm font-black ${getDepositStatus(booking) === 'returned' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'}`}>{depositStatusLabel(getDepositStatus(booking))}</span>
                  <button onClick={() => toggleDepositReturned(booking.id)} className="px-4 py-2.5 rounded-xl bg-indigo-600 text-white font-bold">{getDepositStatus(booking) === 'returned' ? uiText({sq:'Shëno si e mbajtur',en:'Mark as held',de:'Als einbehalten markieren',it:'Segna come trattenuto',fr:'Marquer comme conservé',hr:'Označi kao zadržan'}) : uiText({sq:'Shëno si e kthyer',en:'Mark as returned',de:'Als zurückgegeben markieren',it:'Segna come restituito',fr:'Marquer comme retourné',hr:'Označi kao vraćen'})}</button>
                </div>
              </div>
            ))}
          </div>
        </div>
        {renderPaginationControls('deposits', pagination)}
      </div>
    );
  };

  const renderAdvancedAnalytics = () => {
    const totalBookingValueByCar = cars.map(car => {
      const carName = getCarDisplayName(car);
      const carBookings = bookings.filter(b => b.vehicle === carName);
      const revenue = carBookings.reduce((sum, b) => sum + Number(b.total || b.totalPrice || 0), 0);
      const daysBooked = carBookings.reduce((sum, b) => {
        const start = getBookingDate(b, 'startDate');
        const end = getBookingDate(b, 'endDate');
        if (!start || !end) return sum;
        return sum + Math.max(1, Math.round((end - start)/(1000*60*60*24)) + 1);
      }, 0);
      return { carName, revenue, bookingsCount: carBookings.length, occupancy: Math.min(100, Math.round((daysBooked / 30) * 100)) };
    }).sort((a,b) => b.revenue - a.revenue);

    const filteredRows = totalBookingValueByCar.filter(row => {
      const q = searchTerm.toLowerCase();
      return !q || String(row.carName || '').toLowerCase().includes(q);
    });
    const pagination = paginateCollection('analytics_plus', filteredRows);
    const paginatedRows = pagination.pageItems;

    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <SectionHeader title={uiText({sq:'Analitika e avancuar',en:'Advanced Analytics',de:'Erweiterte Analysen',it:'Analisi avanzate',fr:'Analyses avancées',hr:'Napredna analitika'})}
          subtitle={uiText({sq:'Top veturat, fitimi për veturë dhe norma e zënies.',en:'Top vehicles, profit per car and occupancy rate.',de:'Top-Fahrzeuge, Gewinn pro Fahrzeug und Auslastung.',it:'Top veicoli, profitto per veicolo e tasso di occupazione.',fr:'Meilleurs véhicules, profit par véhicule et taux d’occupation.',hr:'Najbolja vozila, profit po vozilu i stopa popunjenosti.'})}
          searchTerm={searchTerm} setSearchTerm={setSearchTerm} viewMode={viewMode} setViewMode={setViewMode} t={t} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {renderFeatureCard(Award, uiText({sq:'Top veturat',en:'Top Cars',de:'Top-Fahrzeuge',it:'Top auto',fr:'Top voitures',hr:'Top vozila'}), totalBookingValueByCar[0]?.carName || '-', 'from-indigo-500 to-violet-600')}
          {renderFeatureCard(DollarSign, uiText({sq:'Fitimi më i lartë',en:'Highest Profit',de:'Höchster Gewinn',it:'Profitto più alto',fr:'Profit le plus élevé',hr:'Najveći profit'}), formatCurrency(totalBookingValueByCar[0]?.revenue || 0), 'from-emerald-500 to-green-600')}
          {renderFeatureCard(Target, uiText({sq:'Norma mesatare e zënies',en:'Average Occupancy',de:'Durchschnittliche Auslastung',it:'Occupazione media',fr:'Occupation moyenne',hr:'Prosječna popunjenost'}), `${Math.round((totalBookingValueByCar.reduce((a,c)=>a+c.occupancy,0)/(totalBookingValueByCar.length || 1)) || 0)}%`, 'from-amber-500 to-orange-600')}
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl border border-slate-100 dark:border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-900/40">
                <tr>
                  <th className="px-4 py-4 text-left text-sm font-black text-slate-700 dark:text-slate-200">Car</th>
                  <th className="px-4 py-4 text-left text-sm font-black text-slate-700 dark:text-slate-200">Revenue</th>
                  <th className="px-4 py-4 text-left text-sm font-black text-slate-700 dark:text-slate-200">Bookings</th>
                  <th className="px-4 py-4 text-left text-sm font-black text-slate-700 dark:text-slate-200">Occupancy</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((row, idx) => (
                  <tr key={idx} className="border-t border-slate-100 dark:border-slate-700">
                    <td className="px-4 py-4 font-bold text-slate-800 dark:text-white">{row.carName}</td>
                    <td className="px-4 py-4 text-base font-bold text-emerald-600">{formatCurrency(row.revenue)}</td>
                    <td className="px-4 py-4 text-base">{row.bookingsCount}</td>
                    <td className="px-4 py-4 text-slate-700 dark:text-slate-200">
                      <div className="w-full max-w-[220px] bg-slate-100 dark:bg-slate-700 rounded-full h-3 overflow-hidden">
                        <div className="bg-indigo-600 h-3 rounded-full" style={{ width: `${row.occupancy}%` }}></div>
                      </div>
                      <span className="text-sm font-bold text-slate-600 dark:text-slate-300 mt-2 inline-block">{row.occupancy}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {renderPaginationControls('analytics_plus', pagination)}
      </div>
    );
  };

  const renderSmartNotifications = () => {
    const filteredNotifications = globalSmartNotifications.filter(n => {
      const q = searchTerm.toLowerCase();
      return !q || String(n.title || '').toLowerCase().includes(q) || String(n.text || '').toLowerCase().includes(q);
    });
    const pagination = paginateCollection('smart_notifications', filteredNotifications);
    const paginatedNotifications = pagination.pageItems;
    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <SectionHeader title={uiText({sq:'Njoftime inteligjente',en:'Smart Notifications',de:'Intelligente Benachrichtigungen',it:'Notifiche intelligenti',fr:'Notifications intelligentes',hr:'Pametne obavijesti'})}
          subtitle={uiText({sq:'Kthime sot, rezervime nesër, sigurime dhe servise që afrohen.',en:'Returns today, bookings tomorrow, expiring insurances and upcoming services.',de:'Rückgaben heute, Buchungen morgen, ablaufende Versicherungen und bevorstehende Services.',it:'Rientri oggi, prenotazioni domani, assicurazioni in scadenza e manutenzioni imminenti.',fr:'Retours aujourd’hui, réservations demain, assurances expirantes et entretiens à venir.',hr:'Povrati danas, rezervacije sutra, osiguranja koja istječu i nadolazeći servisi.'})}
          searchTerm={searchTerm} setSearchTerm={setSearchTerm} viewMode={viewMode} setViewMode={setViewMode} t={t} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {paginatedNotifications.map((n, idx) => (
            <div key={idx} className="bg-white dark:bg-slate-800 rounded-3xl p-5 shadow-xl border border-slate-100 dark:border-slate-700">
              <p className="text-sm font-black text-indigo-600 dark:text-indigo-400">{n.title}</p>
              <p className="text-lg font-bold text-slate-800 dark:text-white mt-1">{n.text}</p>
            </div>
          ))}
          {filteredNotifications.length === 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-xl border border-slate-100 dark:border-slate-700 text-slate-500 dark:text-slate-400 font-bold">
              {uiText({sq:'Nuk ka njoftime të reja',en:'No new notifications',de:'Keine neuen Benachrichtigungen',it:'Nessuna nuova notifica',fr:'Aucune nouvelle notification',hr:'Nema novih obavijesti'})}
            </div>
          )}
        </div>
        {renderPaginationControls('smart_notifications', pagination)}
      </div>
    );
  };

  const renderInvoiceAutomation = () => {
    const filteredBookings = bookings.filter(booking => {
      const q = searchTerm.toLowerCase();
      return !q || String(booking.client || '').toLowerCase().includes(q) || String(booking.vehicle || '').toLowerCase().includes(q) || String(booking.id || '').toLowerCase().includes(q);
    });
    const pagination = paginateCollection('invoice_auto', filteredBookings);
    const paginatedBookings = pagination.pageItems;
    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <SectionHeader title={uiText({sq:'Gjenero Fatura',en:'Generate Invoices',de:'Rechnungen generieren',it:'Genera fatture',fr:'Générer des factures',hr:'Generiraj fakture'})}
          subtitle={uiText({sq:'Krijo faturë automatike nga rezervimi.',en:'Create an automatic invoice from a booking.',de:'Automatische Rechnung aus einer Buchung erstellen.',it:'Crea una fattura automatica da una prenotazione.',fr:'Créer une facture automatique à partir d’une réservation.',hr:'Kreiraj automatsku fakturu iz rezervacije.'})}
          searchTerm={searchTerm} setSearchTerm={setSearchTerm} viewMode={viewMode} setViewMode={setViewMode} t={t} />
        <div className="space-y-4">
          {paginatedBookings.map(booking => (
            <div key={booking.id} className="bg-white dark:bg-slate-800 rounded-3xl p-5 shadow-xl border border-slate-100 dark:border-slate-700 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <p className="font-black text-slate-800 dark:text-white">{booking.client} • {booking.vehicle}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">{booking.id} • {formatCurrency(booking.total || 0)}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => createInvoiceFromBooking(booking)} className="px-4 py-2.5 rounded-xl bg-emerald-600 text-white font-bold">{uiText({sq:'Gjenero Faturën',en:'Generate Invoice',de:'Rechnung generieren',it:'Genera fattura',fr:'Générer la facture',hr:'Generiraj fakturu'})}</button>
                <button onClick={() => setActiveMenu('invoices')} className="px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-700 text-slate-800 dark:text-slate-200 font-bold">{uiText({sq:'Shiko faturat',en:'View invoices',de:'Rechnungen ansehen',it:'Vedi fatture',fr:'Voir les factures',hr:'Pogledaj fakture'})}</button>
              </div>
            </div>
          ))}
        </div>
        {renderPaginationControls('invoice_auto', pagination)}
      </div>
    );
  };

  const renderMultiUserSystem = () => {
    const viewerMode = isViewerAccess();

    return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <SectionHeader
        title={uiText({sq:'Menaxhimi i përdoruesve',en:'Multi-user System',de:'Mehrbenutzersystem',it:'Sistema multiutente',fr:'Système multi-utilisateur',hr:'Sustav s više korisnika'})}
        subtitle={uiText({sq:'Krijo përdorues me username, password, rol dhe qasje reale në sistem.',en:'Create users with username, password, role and real system access.',de:'Benutzer mit Benutzername, Passwort, Rolle und echtem Systemzugriff erstellen.',it:'Crea utenti con username, password, ruolo e accesso reale al sistema.',fr:'Créez des utilisateurs avec nom d’utilisateur, mot de passe, rôle et accès réel au système.',hr:'Kreirajte korisnike s korisničkim imenom, lozinkom, ulogom i stvarnim pristupom sustavu.'})}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        viewMode={viewMode}
        setViewMode={setViewMode}
        t={t}
      />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {!viewerMode && (
        <div className="xl:col-span-1 bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-xl border border-slate-100 dark:border-slate-700">
          <h3 className="text-xl font-black text-slate-800 dark:text-white mb-5">{uiText({sq:'Krijo përdorues të ri',en:'Create new user',de:'Neuen Benutzer erstellen',it:'Crea nuovo utente',fr:'Créer un nouvel utilisateur',hr:'Kreiraj novog korisnika'})}</h3>
          <div className="space-y-4">
            <input value={systemUserForm.name} onChange={(e) => setSystemUserForm(prev => ({ ...prev, name: e.target.value }))} placeholder={uiText({sq:'Emri i plotë',en:'Full name',de:'Vollständiger Name',it:'Nome completo',fr:'Nom complet',hr:'Puno ime'})} className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-white" />
            <input value={systemUserForm.username} onChange={(e) => setSystemUserForm(prev => ({ ...prev, username: e.target.value }))} placeholder="Username" className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-white" />
            <input value={systemUserForm.email} onChange={(e) => setSystemUserForm(prev => ({ ...prev, email: e.target.value }))} placeholder="Email" className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-white" />
            <input type="password" value={systemUserForm.password} onChange={(e) => setSystemUserForm(prev => ({ ...prev, password: e.target.value }))} placeholder={uiText({sq:'Password',en:'Password',de:'Passwort',it:'Password',fr:'Mot de passe',hr:'Lozinka'})} className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-white" />
            <select value={systemUserForm.role} onChange={(e) => {
              const role = e.target.value;
              setSystemUserForm(prev => ({ ...prev, role, access: getDefaultAccessForRole(role) }));
            }} className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-white">
              <option value="Admin">Admin</option>
              <option value="Staff">Staff</option>
              <option value="Manager">Manager</option>
              <option value="Viewer">Viewer</option>
            </select>
            <div className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/70 text-slate-700 dark:text-slate-200 font-semibold">
              {getSystemUserAccessLabel(systemUserForm.access)}
            </div>
            <button onClick={handleCreateSystemUser} className="w-full px-5 py-3 rounded-xl bg-indigo-600 text-white font-bold">
              {uiText({sq:'Krijo përdorues',en:'Create user',de:'Benutzer erstellen',it:'Crea utente',fr:'Créer utilisateur',hr:'Kreiraj korisnika'})}
            </button>
          </div>
        </div>
        )}

        <div className={`${viewerMode ? 'xl:col-span-3' : 'xl:col-span-2'} bg-white dark:bg-slate-800 rounded-3xl shadow-xl border border-slate-100 dark:border-slate-700 overflow-hidden`}>
          {viewerMode && (
            <div className="px-5 py-4 border-b border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 text-amber-800 dark:text-amber-200 text-sm font-semibold">
              {uiText({
                sq:'Ky llogari është Viewer. Password-at dhe menaxhimi i përdoruesve janë të fshehura.',
                en:'This account is Viewer. User passwords and user management actions are hidden.',
                de:'Dieses Konto ist ein Viewer. Benutzerpasswörter und Verwaltungsaktionen sind ausgeblendet.',
                it:'Questo account è Viewer. Le password e le azioni di gestione utenti sono nascoste.',
                fr:'Ce compte est Viewer. Les mots de passe et les actions de gestion des utilisateurs sont masqués.',
                hr:'Ovaj račun je Viewer. Lozinke i radnje upravljanja korisnicima su skrivene.'
              })}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-900/40">
                <tr>
                  <th className="px-4 py-4 text-left text-sm font-black text-slate-700 dark:text-slate-200">{uiText({sq:'Emri',en:'Name',de:'Name',it:'Nome',fr:'Nom',hr:'Ime'})}</th>
                  <th className="px-4 py-4 text-left text-sm font-black text-slate-700 dark:text-slate-200">Username</th>
                  <th className="px-4 py-4 text-left text-sm font-black text-slate-700 dark:text-slate-200">Email</th>
                  {!viewerMode && (
                    <th className="px-4 py-4 text-left text-sm font-black text-slate-700 dark:text-slate-200">{uiText({sq:'Password',en:'Password',de:'Passwort',it:'Password',fr:'Mot de passe',hr:'Lozinka'})}</th>
                  )}
                  <th className="px-4 py-4 text-left text-sm font-black text-slate-700 dark:text-slate-200">{uiText({sq:'Roli',en:'Role',de:'Rolle',it:'Ruolo',fr:'Rôle',hr:'Uloga'})}</th>
                  <th className="px-4 py-4 text-left text-sm font-black text-slate-700 dark:text-slate-200">{uiText({sq:'Qasja',en:'Access',de:'Zugriff',it:'Accesso',fr:'Accès',hr:'Pristup'})}</th>
                  <th className="px-4 py-4 text-left text-sm font-black text-slate-700 dark:text-slate-200">{uiText({sq:'Statusi',en:'Status',de:'Status',it:'Stato',fr:'Statut',hr:'Status'})}</th>
                </tr>
              </thead>
              <tbody>
                {systemUsers.map((userRow) => (
                  <tr key={userRow.id} className="border-t border-slate-100 dark:border-slate-700">
                    <td className="px-4 py-4 font-bold text-slate-800 dark:text-white">{userRow.name}</td>
                    <td className="px-4 py-4 text-slate-700 dark:text-slate-200">{userRow.username}</td>
                    <td className="px-4 py-4 text-slate-700 dark:text-slate-200">{userRow.email}</td>
                    {!viewerMode && (
                      <td className="px-4 py-4 font-mono text-slate-700 dark:text-slate-200">{userRow.password}</td>
                    )}
                    <td className="px-4 py-4 text-slate-700 dark:text-slate-200">
                      {viewerMode ? (
                        <span className="font-semibold">{userRow.role}</span>
                      ) : (
                        <select value={userRow.role} onChange={async (e) => {
                          const role = e.target.value;
                          const updatedUser = { ...userRow, role, access: getDefaultAccessForRole(role) };
                          try {
                            await persistItemToFirestore('systemUser', updatedUser);
                            upsertCollectionState('systemUser', updatedUser, 'edit');
                            if (userRow.id === user?.id) {
                              setUser(prev => ({ ...prev, role: updatedUser.role, access: updatedUser.access }));
                            }
                          } catch (error) {
                            console.error('Failed to update system user role:', error);
                            showToast(uiText({sq:'Përditësimi dështoi.',en:'Update failed.',de:'Aktualisierung fehlgeschlagen.',it:'Aggiornamento non riuscito.',fr:'Échec de la mise à jour.',hr:'Ažuriranje nije uspjelo.'}), 'error');
                          }
                        }} className="px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
                          <option value="Admin">Admin</option>
                          <option value="Staff">Staff</option>
                          <option value="Manager">Manager</option>
                          <option value="Viewer">Viewer</option>
                        </select>
                      )}
                    </td>
                    <td className="px-4 py-4 text-slate-700 dark:text-slate-200 font-semibold">
                      {getSystemUserAccessLabel(userRow.access || getDefaultAccessForRole(userRow.role))}
                    </td>
                    <td className="px-4 py-4 text-slate-700 dark:text-slate-200">
                      {viewerMode ? (
                        <span className={`inline-flex px-4 py-2 rounded-full text-sm font-black ${userRow.status === 'active' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-slate-100 text-slate-700 dark:bg-slate-700 text-slate-800 dark:text-slate-200'}`}>{userRow.status}</span>
                      ) : (
                        <div className="flex gap-2 flex-wrap">
                          <button onClick={async () => {
                            const updatedUser = { ...userRow, status: userRow.status === 'active' ? 'disabled' : 'active' };
                            try {
                              await persistItemToFirestore('systemUser', updatedUser);
                              upsertCollectionState('systemUser', updatedUser, 'edit');
                            } catch (error) {
                              console.error('Failed to update system user status:', error);
                              showToast(uiText({sq:'Përditësimi dështoi.',en:'Update failed.',de:'Aktualisierung fehlgeschlagen.',it:'Aggiornamento non riuscito.',fr:'Échec de la mise à jour.',hr:'Ažuriranje nije uspjelo.'}), 'error');
                            }
                          }} className={`px-4 py-2 rounded-full text-sm font-black ${userRow.status === 'active' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-slate-100 text-slate-700 dark:bg-slate-700 text-slate-800 dark:text-slate-200'}`}>{userRow.status}</button>
                          <button onClick={async () => {
                            try {
                              await deleteItemFromFirestore('systemUser', userRow.id);
                              removeCollectionState('systemUser', userRow.id);
                              if (userRow.id === user?.id) {
                                setUser(prev => ({ ...prev, name: userRow.name, username: userRow.username, role: userRow.role, access: userRow.access, email: userRow.email }));
                              }
                              showToast(uiText({sq:'Përdoruesi u fshi',en:'User deleted',de:'Benutzer gelöscht',it:'Utente eliminato',fr:'Utilisateur supprimé',hr:'Korisnik obrisan'}), 'success');
                            } catch (error) {
                              console.error('Failed to delete system user:', error);
                              showToast(uiText({sq:'Fshirja dështoi.',en:'Delete failed.',de:'Löschen fehlgeschlagen.',it:'Eliminazione non riuscita.',fr:'La suppression a échoué.',hr:'Brisanje nije uspjelo.'}), 'error');
                            }
                          }} className="px-4 py-2 rounded-full text-sm font-black bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
                            {uiText({sq:'Fshij',en:'Delete',de:'Löschen',it:'Elimina',fr:'Supprimer',hr:'Obriši'})}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
  };

  const renderContent = () => {
    switch(activeMenu) {
      case 'dashboard': return renderDashboard();
      case 'cars': return renderCars();
      case 'clients': return renderClients();
      case 'bookings':
      case 'bookings_list': return renderBookingsList();
      case 'bookings_payments':
      case 'payments': return renderBookingsPayments();
      case 'bookings_not_finished': return renderBookingsNotFinished();
      case 'bookings_waitlist': return renderBookingsWaitlist();
      case 'bookings_operations': return renderBookingsOperations();
      case 'tpl': return renderTpl();
      case 'expenses': return renderExpenses();
      case 'invoices': return renderInvoices();
      case 'contracts': return renderContracts(); 
      case 'booking_calendar': return renderBookingCalendar();
      case 'vehicle_status': return renderVehicleStatus();
      case 'contracts_auto': return renderContractsAutomation();
      case 'deposits': return renderDeposits();
      case 'analytics_plus': return renderAdvancedAnalytics();
      case 'smart_notifications': return renderSmartNotifications();
      case 'invoice_auto': return renderInvoiceAutomation();
      case 'multi_user': return renderMultiUserSystem();
      case 'reports': return renderReports();
      case 'insurance': return renderInsurance();
      case 'maintenance': return renderMaintenance();
      case 'settings': return renderSettings();
      case 'profile': return renderProfile();
      default: return <div className="text-center py-12 text-slate-500 font-medium">Po përgatitet...</div>;
    }
  };


  if (!isAuthenticated) {
    return (
      <div className={`min-h-screen relative overflow-hidden transition-colors duration-500 ${
        isDarkMode
          ? 'dark bg-gradient-to-br from-slate-950 via-[#071226] to-slate-900'
          : 'bg-gradient-to-br from-slate-100 via-white to-indigo-50'
      }`}>
        <style>{notificationAnimationStyles}</style>

        <div className="absolute inset-0 pointer-events-none">
          <div className={`absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full blur-3xl ${
            isDarkMode ? 'bg-indigo-500/20' : 'bg-indigo-300/30'
          }`}></div>
          <div className={`absolute bottom-[-120px] right-[-80px] w-[420px] h-[420px] rounded-full blur-3xl ${
            isDarkMode ? 'bg-violet-500/20' : 'bg-violet-300/25'
          }`}></div>
          <div className={`absolute top-[25%] right-[18%] w-[240px] h-[240px] rounded-full blur-3xl ${
            isDarkMode ? 'bg-blue-400/15' : 'bg-sky-200/30'
          }`}></div>
          <div className={`absolute bottom-[12%] left-[22%] w-[220px] h-[220px] rounded-full blur-3xl ${
            isDarkMode ? 'bg-fuchsia-500/10' : 'bg-fuchsia-200/20'
          }`}></div>
        </div>

        <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-10">
          <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-10 items-center">

            <div className="hidden lg:block">
              <div className="max-w-xl">
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full shadow-sm text-sm font-semibold mb-6 ${
                  isDarkMode
                    ? 'bg-slate-800/90 border border-slate-700 text-slate-200'
                    : 'bg-white/80 border border-white/70 text-slate-700'
                }`}>
                  <Sparkles size={15} className="text-indigo-500" />
                  {uiText({
                    sq: 'Menaxhim modern për Rent A Car',
                    en: 'Modern management for Rent A Car',
                    de: 'Modernes Management für Rent A Car',
                    it: 'Gestione moderna per Rent A Car',
                    fr: 'Gestion moderne pour Rent A Car',
                    hr: 'Moderno upravljanje za Rent A Car'
                  })}
                </div>

                <h1 className={`text-5xl leading-tight font-black mb-5 ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}>
                  {uiText({
                    sq: 'Menaxho rezervimet, veturat dhe klientët në një panel modern Rent A Car.',
                    en: 'Manage bookings, vehicles and clients in one modern Rent A Car panel.',
                    de: 'Verwalte Buchungen, Fahrzeuge und Kunden in einem modernen Rent A Car Panel.',
                    it: 'Gestisci prenotazioni, veicoli e clienti in un unico pannello moderno Rent A Car.',
                    fr: 'Gérez réservations, véhicules et clients dans un panneau moderne Rent A Car.',
                    hr: 'Upravljaj rezervacijama, vozilima i klijentima u modernom Rent A Car panelu.'
                  })}
                </h1>

                <p className={`text-lg leading-8 mb-8 ${
                  isDarkMode ? 'text-slate-300' : 'text-slate-600'
                }`}>
                  {uiText({
                    sq: 'Optimizoni operacionet tuaja të përditshme me një sistem të fuqishëm dhe intuitiv. Ky aplikacion qendërzon çdo proces të biznesit tuaj: gjurmoni detajet e veturave, menaxhoni me lehtësi rezervimet e klientëve, procesoni pagesat dhe monitoroni performancën e stafit në kohë reale. Zgjidhja ideale për të rritur produktivitetin dhe për të kursyer kohë.',
                    en: 'Optimize your daily operations with a powerful and intuitive system. This application centralizes every process of your business: track vehicle details, easily manage customer bookings, process payments, and monitor staff performance in real time. The ideal solution to increase productivity and save time.',
                    de: 'Optimieren Sie Ihre täglichen Abläufe mit einem leistungsstarken und intuitiven System. Diese Anwendung zentralisiert jeden Prozess Ihres Unternehmens: Verfolgen Sie Fahrzeugdetails, verwalten Sie Kundenbuchungen ganz einfach, wickeln Sie Zahlungen ab und überwachen Sie die Mitarbeiterleistung in Echtzeit. Die ideale Lösung, um die Produktivität zu steigern und Zeit zu sparen.',
                    it: 'Ottimizza le tue operazioni quotidiane con un sistema potente e intuitivo. Questa applicazione centralizza ogni processo della tua attività: tieni traccia dei dettagli dei veicoli, gestisci facilmente le prenotazioni dei clienti, elabora i pagamenti e monitora le prestazioni del personale in tempo reale. La soluzione ideale per aumentare la produttività e risparmiare tempo.',
                    fr: 'Optimisez vos opérations quotidiennes avec un système puissant et intuitif. Cette application centralise chaque processus de votre entreprise : suivez les détails des véhicules, gérez facilement les réservations des clients, traitez les paiements et surveillez les performances du personnel en temps réel. La solution idéale pour augmenter la productivité et gagner du temps.',
                    hr: 'Optimizirajte svoje svakodnevne operacije pomoću snažnog i intuitivnog sustava. Ova aplikacija centralizira svaki proces vašeg poslovanja: pratite detalje o vozilima, jednostavno upravljajte rezervacijama kupaca, obrađujte plaćanja i pratite učinak osoblja u stvarnom vremenu. Idealno rješenje za povećanje produktivnosti i uštedu vremena.'
                })}
                </p>

                <div className="grid grid-cols-3 gap-4">
                  <div className={`rounded-3xl shadow-sm p-5 border ${
                    isDarkMode
                      ? 'bg-slate-800/90 border-slate-700'
                      : 'bg-white/85 border-white/70'
                  }`}>
                    <ShieldCheck className="text-indigo-500 mb-4" size={22} />
                    <h3 className={`font-black mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {uiText({ sq: 'Hyrje e sigurt', en: 'Secure login', de: 'Sicherer Login', it: 'Accesso sicuro', fr: 'Connexion sécurisée', hr: 'Sigurna prijava' })}
                    </h3>
                    <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      {uiText({ sq: 'Përdorues, role dhe log aktiviteti.', en: 'Users, roles and activity logs.', de: 'Benutzer, Rollen und Aktivitätsprotokolle.', it: 'Utenti, ruoli e log attività.', fr: 'Utilisateurs, rôles et journaux.', hr: 'Korisnici, uloge i evidencija aktivnosti.' })}
                    </p>
                  </div>

                  <div className={`rounded-3xl shadow-sm p-5 border ${
                    isDarkMode
                      ? 'bg-slate-800/90 border-slate-700'
                      : 'bg-white/85 border-white/70'
                  }`}>
                    <Globe className="text-indigo-500 mb-4" size={22} />
                    <h3 className={`font-black mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {uiText({ sq: 'Shumë gjuhë', en: 'Multi language', de: 'Mehrsprachig', it: 'Multilingua', fr: 'Multi-langue', hr: 'Više jezika' })}
                    </h3>
                    <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      {uiText({ sq: 'Ndrysho gjuhën menjëherë.', en: 'Switch language instantly.', de: 'Sprache sofort wechseln.', it: 'Cambia lingua subito.', fr: 'Changez de langue instantanément.', hr: 'Odmah promijeni jezik.' })}
                    </p>
                  </div>

                  <div className={`rounded-3xl shadow-sm p-5 border ${
                    isDarkMode
                      ? 'bg-slate-800/90 border-slate-700'
                      : 'bg-white/85 border-white/70'
                  }`}>
                    <Database className="text-indigo-500 mb-4" size={22} />
                    <h3 className={`font-black mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {uiText({ sq: 'Backup automatik', en: 'Auto backup', de: 'Automatisches Backup', it: 'Backup automatico', fr: 'Sauvegarde auto', hr: 'Automatski backup' })}
                    </h3>
                    <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      {uiText({ sq: 'Të dhënat ruhen çdo ditë.', en: 'Data is saved daily.', de: 'Daten werden täglich gesichert.', it: 'I dati si salvano ogni giorno.', fr: 'Les données sont sauvegardées chaque jour.', hr: 'Podaci se čuvaju svaki dan.' })}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <div className={`w-full max-w-md rounded-[32px] backdrop-blur-xl p-8 border shadow-[0_20px_70px_rgba(15,23,42,0.18)] ${
                isDarkMode
                  ? 'bg-slate-900/85 border-slate-700'
                  : 'bg-white/92 border-white'
              }`}>
                <div className="flex flex-col items-center mb-8">
                  <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-lg shadow-indigo-300/50 mb-5">
                    <Car className="text-white" size={36} />
                  </div>
                  <h1 className={`text-4xl font-black tracking-tight ${
                    isDarkMode ? 'text-white' : 'text-slate-900'
                  }`}>
                    AutoRent
                  </h1>
                  <p className={`mt-2 text-sm font-extrabold tracking-[0.28em] uppercase text-center ${
                    isDarkMode ? 'text-indigo-300' : 'text-indigo-600'
                  }`}>
                    {uiText({
                      sq: 'Sistemi i Menaxhimit',
                      en: 'Management System',
                      de: 'Managementsystem',
                      it: 'Sistema di Gestione',
                      fr: 'Système de Gestion',
                      hr: 'Sustav Upravljanja'
                    })}
                  </p>
                </div>

                <form onSubmit={handleLogin} className="space-y-5">
                  <div>
                    <label className={`block text-sm font-bold mb-2 ${
                      isDarkMode ? 'text-slate-200' : 'text-slate-700'
                    }`}>
                      {uiText({
                        sq: 'Username ose Email',
                        en: 'Username or Email',
                        de: 'Benutzername oder E-Mail',
                        it: 'Username o Email',
                        fr: 'Nom d’utilisateur ou Email',
                        hr: 'Korisničko ime ili Email'
                      })}
                    </label>
                    <div className="relative">
                      <Users className={`absolute left-4 top-1/2 -translate-y-1/2 ${
                        isDarkMode ? 'text-slate-500' : 'text-slate-400'
                      }`} size={18} />
                      <input
                        type="text"
                        value={loginIdentifier}
                        onChange={(e) => {
                          setLoginIdentifier(e.target.value);
                          setLoginError('');
                        }}
                        placeholder={uiText({
                          sq: 'Shënoni username ose email...',
                          en: 'Enter username or email...',
                          de: 'Benutzername oder E-Mail eingeben...',
                          it: 'Inserisci username o email...',
                          fr: 'Entrez le nom d’utilisateur ou l’email...',
                          hr: 'Unesite korisničko ime ili email...'
                        })}
                        className={`w-full pl-12 pr-4 py-4 rounded-2xl border outline-none transition ${
                          isDarkMode
                            ? 'bg-slate-800 border-slate-700 text-white placeholder:text-slate-400'
                            : 'bg-white text-slate-900 placeholder:text-slate-400'
                        } ${
                          loginError
                            ? 'border-red-400 focus:ring-2 focus:ring-red-400'
                            : 'border-slate-200 focus:ring-2 focus:ring-indigo-500 hover:border-indigo-300'
                        }`}
                        autoFocus
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-bold mb-2 ${
                      isDarkMode ? 'text-slate-200' : 'text-slate-700'
                    }`}>
                      {uiText({
                        sq: 'Fjalëkalimi i hyrjes',
                        en: 'Login password',
                        de: 'Anmeldepasswort',
                        it: 'Password di accesso',
                        fr: 'Mot de passe',
                        hr: 'Lozinka za prijavu'
                      })}
                    </label>
                    <div className="relative">
                      <Lock className={`absolute left-4 top-1/2 -translate-y-1/2 ${
                        isDarkMode ? 'text-slate-500' : 'text-slate-400'
                      }`} size={18} />
                      <input
                        type="password"
                        value={loginPassword}
                        onChange={(e) => {
                          setLoginPassword(e.target.value);
                          setLoginError('');
                        }}
                        placeholder={uiText({
                          sq: 'Shënoni fjalëkalimin...',
                          en: 'Enter password...',
                          de: 'Passwort eingeben...',
                          it: 'Inserisci la password...',
                          fr: 'Entrez le mot de passe...',
                          hr: 'Unesite lozinku...'
                        })}
                        className={`w-full pl-12 pr-4 py-4 rounded-2xl border outline-none transition ${
                          isDarkMode
                            ? 'bg-slate-800 border-slate-700 text-white placeholder:text-slate-400'
                            : 'bg-white text-slate-900 placeholder:text-slate-400'
                        } ${
                          loginError
                            ? 'border-red-400 focus:ring-2 focus:ring-red-400'
                            : 'border-slate-200 focus:ring-2 focus:ring-indigo-500 hover:border-indigo-300'
                        }`}
                      />
                    </div>

                    {loginError && (
                      <p className="text-red-500 text-xs font-bold mt-2 flex items-center">
                        <XCircle size={12} className="mr-1" />
                        {loginError}
                      </p>
                    )}

                  </div>

                  <button
                    type="submit"
                    className="w-full py-4 rounded-2xl bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-extrabold shadow-lg shadow-indigo-300/20 hover:shadow-xl transition flex items-center justify-center group"
                  >
                    <span>
                      {uiText({
                        sq: 'Hyr në Panel',
                        en: 'Login to Panel',
                        de: 'Zum Panel anmelden',
                        it: 'Accedi al pannello',
                        fr: 'Se connecter au panneau',
                        hr: 'Prijava u panel'
                      })}
                    </span>
                    <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" />
                  </button>
                </form>

                <div className={`mt-6 rounded-2xl p-4 border ${
                  isDarkMode
                    ? 'bg-slate-800/90 border-slate-700'
                    : 'bg-indigo-50 border-indigo-100'
                }`}>
                  <div className="flex items-start gap-3">
                    <ShieldCheck className="text-indigo-500 mt-0.5" size={18} />
                    <div>
                      <p className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {uiText({
                          sq: 'Qasje e sigurt me role',
                          en: 'Secure access with roles',
                          de: 'Sicherer Zugriff mit Rollen',
                          it: 'Accesso sicuro con ruoli',
                          fr: 'Accès sécurisé avec rôles',
                          hr: 'Siguran pristup s ulogama'
                        })}
                      </p>
                      <p className={`text-xs mt-1 leading-5 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {uiText({
                          sq: 'Admin, Staff dhe Viewer mund të hyjnë sipas lejeve që u cakton nga paneli.',
                          en: 'Admin, Staff and Viewer can sign in based on the permissions you assign from the panel.',
                          de: 'Admin, Staff und Viewer melden sich je nach zugewiesenen Berechtigungen an.',
                          it: 'Admin, Staff e Viewer accedono in base ai permessi assegnati dal pannello.',
                          fr: 'Admin, Staff et Viewer se connectent selon les autorisations attribuées.',
                          hr: 'Admin, Staff i Viewer prijavljuju se prema dodijeljenim dozvolama.'
                        })}
                      </p>
                    </div>
                  </div>
                </div>

                <div className={`mt-8 pt-5 border-t text-xs ${
                  isDarkMode ? 'border-slate-700 text-slate-400' : 'border-slate-200 text-slate-500'
                }`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowLanguageMenu(v => !v)}
                        className={`flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-xl border shadow-sm transition-all ${
                          isDarkMode
                            ? 'border-slate-700 bg-slate-800/90 text-slate-200 hover:border-indigo-500'
                            : 'border-slate-200 bg-white text-slate-700 hover:border-indigo-300'
                        }`}
                        title={t('language')}
                      >
                        <span className={`flex items-center justify-center h-7 w-9 rounded-lg overflow-hidden border ${
                          isDarkMode
                            ? 'bg-slate-900 border-slate-700'
                            : 'bg-slate-50 border-slate-200'
                        }`}>
                          {currentLanguageOption.flagSrc ? (
                            <>
                              <img
                                src={currentLanguageOption.flagSrc}
                                alt={currentLanguageOption.label}
                                className="h-4 w-6 object-contain"
                                onError={(e) => { e.currentTarget.style.display = 'none'; const next = e.currentTarget.nextElementSibling; if (next) next.style.display = 'inline-flex'; }}
                              />
                              <span className="text-base leading-none items-center justify-center" style={{ display: 'none' }}>{currentLanguageOption.flag}</span>
                            </>
                          ) : (
                            <span className="text-base leading-none inline-flex items-center justify-center">{currentLanguageOption.flag}</span>
                          )}
                        </span>
                        <span className="text-xs font-semibold">{currentLanguageOption.label}</span>
                        <ChevronDown size={14} className={`transition-transform ${showLanguageMenu ? 'rotate-180' : ''}`} />
                      </button>

                      {showLanguageMenu && (
                        <div className={`absolute left-0 bottom-full mb-2 w-60 rounded-2xl border backdrop-blur shadow-2xl overflow-hidden z-50 ${
                          isDarkMode
                            ? 'border-slate-700 bg-slate-900/95'
                            : 'border-slate-200 bg-white/95'
                        }`}>
                          <div className={`px-4 pt-4 pb-2 border-b ${
                            isDarkMode ? 'border-slate-800' : 'border-slate-100'
                          }`}>
                            <div className={`flex items-center gap-2 ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                              <Globe size={16} className="text-indigo-500" />
                              <span className="text-sm font-bold">{t('language')}</span>
                            </div>
                          </div>
                          <div className="p-2">
                            {LANGUAGE_OPTIONS.map(item => (
                              <button
                                key={item.code}
                                type="button"
                                onClick={() => changeLanguage(item.code)}
                                className={`w-full flex items-center justify-between px-3 py-3 rounded-2xl text-sm font-semibold transition-all ${
                                  language === item.code
                                    ? 'bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-indigo-900/30 dark:to-violet-900/20 text-indigo-700 dark:text-indigo-300 ring-1 ring-indigo-200 dark:ring-indigo-700'
                                    : 'text-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'
                                }`}
                              >
                                <span className="flex items-center gap-3">
                                  <span className="flex items-center justify-center h-9 w-12 rounded-xl bg-slate-100 dark:bg-slate-800 overflow-hidden border border-slate-200 dark:border-slate-700">
                                    {item.flagSrc ? (
                                      <>
                                        <img
                                          src={item.flagSrc}
                                          alt={item.label}
                                          className="h-5 w-7 object-contain"
                                          onError={(e) => { e.currentTarget.style.display = 'none'; const next = e.currentTarget.nextElementSibling; if (next) next.style.display = 'inline-flex'; }}
                                        />
                                        <span className="text-lg items-center justify-center" style={{ display: 'none' }}>{item.flag}</span>
                                      </>
                                    ) : (
                                      <span className="text-lg inline-flex items-center justify-center">{item.flag}</span>
                                    )}
                                  </span>
                                  <span className="flex flex-col items-start leading-tight">
                                    <span>{item.label}</span>
                                    <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">{item.code}</span>
                                  </span>
                                </span>
                                {language === item.code && <CheckCircle size={16} className="text-indigo-500" />}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => setIsDarkMode(!isDarkMode)}
                      className={`transition flex items-center gap-1 font-semibold ${
                        isDarkMode ? 'hover:text-white text-slate-200' : 'hover:text-indigo-600 text-slate-600'
                      }`}
                    >
                      {isDarkMode ? <Sun size={14} /> : <Moon size={14} />}
                      {uiText({
                        sq: 'Tema',
                        en: 'Theme',
                        de: 'Thema',
                        it: 'Tema',
                        fr: 'Thème',
                        hr: 'Tema'
                      })}
                    </button>
                  </div>

                  <div className={`mt-3 text-center ${
                    isDarkMode ? 'text-slate-500' : 'text-slate-500'
                  }`}>
                    © {new Date().getFullYear()} {uiText({
                      sq: 'Të gjitha të drejtat e rezervuara.',
                      en: 'All rights reserved.',
                      de: 'Alle Rechte vorbehalten.',
                      it: 'Tutti i diritti riservati.',
                      fr: 'Tous droits réservés.',
                      hr: 'Sva prava pridržana.'
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <I18nContext.Provider value={{ language, locale, t }}>
    <div className={`min-h-screen ${isDarkMode ? 'dark bg-slate-900' : 'bg-slate-50'} flex font-sans text-[15px] selection:bg-indigo-200 selection:text-indigo-900 transition-colors duration-300`}><style>{notificationAnimationStyles}</style>
      {toast && (
        <div className="fixed top-5 right-5 z-[120] animate-in slide-in-from-top-5 fade-in duration-300">
          <div className={`relative overflow-hidden w-[min(92vw,420px)] rounded-3xl border shadow-2xl backdrop-blur-xl ${
            toast.type === 'error' ? 'bg-white/95 dark:bg-slate-900/95 border-red-200/80 dark:border-red-800/70' :
            toast.type === 'warning' ? 'bg-white/95 dark:bg-slate-900/95 border-amber-200/80 dark:border-amber-800/70' :
            toast.type === 'info' ? 'bg-white/95 dark:bg-slate-900/95 border-blue-200/80 dark:border-blue-800/70' :
            'bg-white/95 dark:bg-slate-900/95 border-emerald-200/80 dark:border-emerald-800/70'
          }`}>
            <div className={`absolute left-0 top-0 h-full w-1.5 ${
              toast.type === 'error' ? 'bg-red-500' :
              toast.type === 'warning' ? 'bg-amber-500' :
              toast.type === 'info' ? 'bg-blue-500' :
              'bg-emerald-500'
            }`} />
            <div className="p-5 pl-6">
              <div className="flex items-start gap-4">
                <div className={`mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl shadow-sm ${
                  toast.type === 'error' ? 'bg-red-50 dark:bg-red-500/15 text-red-500' :
                  toast.type === 'warning' ? 'bg-amber-50 dark:bg-amber-500/15 text-amber-500' :
                  toast.type === 'info' ? 'bg-blue-50 dark:bg-blue-500/15 text-blue-500' :
                  'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-500'
                }`}>
                  {toast.type === 'error' && <XCircle size={22} />}
                  {toast.type === 'warning' && <AlertTriangle size={22} />}
                  {toast.type === 'success' && <CheckCircle size={22} />}
                  {toast.type === 'info' && <Info size={22} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">{t('notification')}</p>
                      <p className="mt-1 text-[15px] font-bold leading-6 text-slate-800 dark:text-white">{toast.message}</p>
                    </div>
                    <button onClick={closeToast} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200 transition-colors">
                      <X size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="h-1 w-full bg-slate-100 dark:bg-slate-800">
              <div
                key={toast.id}
                className={`h-full origin-left animate-[shrink_var(--toast-duration)_linear_forwards] ${
                  toast.type === 'error' ? 'bg-red-500' :
                  toast.type === 'warning' ? 'bg-amber-500' :
                  toast.type === 'info' ? 'bg-blue-500' :
                  'bg-emerald-500'
                }`}
                style={{ '--toast-duration': `${toast.duration || 3200}ms` }}
              />
            </div>
          </div>
        </div>
      )}

      <input type="file" accept=".json" ref={fileInputRef} onChange={importDatabase} className="hidden" />

      {confirmDialog.isOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-md animate-in fade-in duration-200">
          <div className="w-full max-w-lg overflow-hidden rounded-[30px] border border-white/70 bg-white/95 shadow-[0_30px_80px_rgba(15,23,42,0.35)] dark:border-slate-700/80 dark:bg-slate-900/95 animate-in zoom-in-95 duration-300">
            <div className="relative px-8 pt-8 pb-6">
              <div className="absolute right-5 top-5">
                <button
                  onClick={() => setConfirmDialog((prev) => ({ ...prev, isOpen: false }))}
                  className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200 transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
              <div className={`mb-5 flex h-20 w-20 items-center justify-center rounded-[24px] shadow-lg ${
                confirmDialog.variant === 'danger'
                  ? 'bg-gradient-to-br from-red-500 to-rose-600 text-white shadow-red-500/30'
                  : 'bg-gradient-to-br from-indigo-500 to-blue-600 text-white shadow-indigo-500/30'
              }`}>
                <AlertTriangle size={34} />
              </div>
              <h3 className="text-2xl font-black tracking-tight text-slate-900 text-slate-800 dark:text-white">{confirmDialog.title || t('notification')}</h3>
              <p className="mt-3 text-[15px] leading-7 text-slate-600 dark:text-slate-300">{confirmDialog.message}</p>
            </div>
            <div className="flex flex-col-reverse gap-3 border-t border-slate-100 bg-slate-50/80 px-8 py-5 dark:border-slate-800 dark:bg-slate-950/40 sm:flex-row sm:justify-end">
              <button
                onClick={() => setConfirmDialog((prev) => ({ ...prev, isOpen: false }))}
                className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 hover:bg-slate-100 transition-colors dark:border-slate-700 dark:bg-slate-900 text-slate-800 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                {confirmDialog.cancelText || t('cancel')}
              </button>
              <button
                onClick={confirmDialog.onConfirm}
                className={`rounded-2xl px-5 py-3 text-sm font-bold text-white shadow-lg transition-all hover:scale-[1.02] ${
                  confirmDialog.variant === 'danger'
                    ? 'bg-gradient-to-r from-red-500 to-rose-600 hover:shadow-red-500/25'
                    : 'bg-gradient-to-r from-indigo-500 to-blue-600 hover:shadow-indigo-500/25'
                }`}
              >
                {confirmDialog.confirmText || t('confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-950/55 backdrop-blur-sm lg:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      <div className={`${isSidebarCollapsed ? 'lg:w-20' : 'lg:w-[18.5rem]'} w-[18rem] max-w-[88vw] ${isDarkMode ? 'bg-[linear-gradient(180deg,#0f172a_0%,#111827_100%)]' : 'bg-[linear-gradient(180deg,#0b1736_0%,#0f172a_100%)]'} fixed inset-y-0 left-0 overflow-y-auto custom-scrollbar z-50 border-r border-white/10 shadow-2xl transition-all duration-300 ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="p-4 sm:p-5 lg:p-6">
          <div className="flex items-center justify-between mb-5 lg:mb-8">
            <div className={`flex items-center ${isSidebarCollapsed ? 'justify-center w-full' : 'space-x-3.5'} min-w-0`}>
              <div className="w-11 h-11 lg:w-12 lg:h-12 bg-gradient-to-br from-indigo-500 via-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-[0_14px_30px_rgba(59,130,246,0.35)] flex-shrink-0 relative overflow-hidden ring-1 ring-white/20">
                <div className="absolute inset-0 bg-white/20 w-full h-full transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                <Car className="text-white relative z-10" size={26} />
              </div>
              {!isSidebarCollapsed && (
                <div>
                  <h1 className="text-[1.15rem] lg:text-[1.45rem] font-black text-white tracking-tight leading-none truncate">{settings.companyName}</h1>
                  <p className="text-indigo-300/90 text-[10px] font-extrabold tracking-[0.22em] uppercase mt-1.5">Admin Panel</p>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsMobileSidebarOpen(false)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-slate-300 hover:bg-white/10 hover:text-white transition-colors lg:hidden"
                aria-label="Close menu"
              >
                <X size={18} />
              </button>
              <button onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)} className="text-slate-400 hover:text-white hidden lg:block transition-colors">
                {isSidebarCollapsed ? <Maximize2 size={18} /> : <Minimize2 size={18} />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            {!isSidebarCollapsed && <p className="text-[10px] font-black text-slate-500/95 uppercase tracking-[0.18em] mb-2.5 mt-1 px-3.5 lg:px-4">{t('menu_main_section')}</p>}
            <MenuItem icon={LayoutDashboard} label={t('dashboard')} id="dashboard" activeMenu={activeMenu} setActiveMenu={setActiveMenu} setSearchTerm={setSearchTerm} setAdvancedFilters={setAdvancedFilters} isSidebarCollapsed={isSidebarCollapsed} />
            <div className="space-y-2">
              <MenuItem icon={Calendar} label={t('bookings')} rightIcon={ChevronDown} id="bookings" badge={bookings.filter(b => b.status === 'pending').length} activeMenu={activeMenu} setActiveMenu={setActiveMenu} setSearchTerm={setSearchTerm} setAdvancedFilters={setAdvancedFilters} isSidebarCollapsed={isSidebarCollapsed} />
              {!isSidebarCollapsed && ['bookings','bookings_list','payments','bookings_not_finished','bookings_waitlist','bookings_operations','booking_calendar','deposits','invoice_auto'].includes(activeMenu) && (
                <div className="ml-4 lg:ml-5 pl-3.5 border-l border-white/10 space-y-1.5 relative before:absolute before:left-0 before:top-1 before:bottom-1 before:w-px before:bg-gradient-to-b before:from-white/0 before:via-white/15 before:to-white/0">
                  <button onClick={() => { setActiveMenu('bookings'); setSearchTerm(''); setAdvancedFilters({ client: '', method: '', status: '', category: '', startDate: '', endDate: '', search: '' }); }} className={`w-full text-left px-3 py-2.5 rounded-lg text-sm lg:text-[15px] transition-colors ${['bookings','bookings_list'].includes(activeMenu) ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800/60'}`}>{t('booking_list')}</button>
                  <button onClick={() => { setActiveMenu('bookings_not_finished'); setSearchTerm(''); setAdvancedFilters({ client: '', method: '', status: '', category: '', startDate: '', endDate: '', search: '' }); }} className={`group w-full text-left px-3.5 py-2.5 rounded-xl text-[13.5px] lg:text-[14.5px] font-medium transition-all duration-200 border ${activeMenu === 'bookings_not_finished' ? 'bg-white/10 text-white border-white/10 shadow-sm' : 'text-slate-400 border-transparent hover:text-white hover:bg-white/5 hover:border-white/10'}`}>{t('booking_payments_not_finished')}</button>
                  <button onClick={() => { setActiveMenu('bookings_waitlist'); setSearchTerm(''); setAdvancedFilters({ client: '', method: '', status: '', category: '', startDate: '', endDate: '', search: '' }); }} className={`group w-full text-left px-3.5 py-2.5 rounded-xl text-[13.5px] lg:text-[14.5px] font-medium transition-all duration-200 border ${activeMenu === 'bookings_waitlist' ? 'bg-white/10 text-white border-white/10 shadow-sm' : 'text-slate-400 border-transparent hover:text-white hover:bg-white/5 hover:border-white/10'}`}>{t('booking_waitlist')}</button>
                  <button onClick={() => { setActiveMenu('bookings_operations'); setSearchTerm(''); setAdvancedFilters({ client: '', method: '', status: '', category: '', startDate: '', endDate: '', search: '' }); }} className={`group w-full text-left px-3.5 py-2.5 rounded-xl text-[13.5px] lg:text-[14.5px] font-medium transition-all duration-200 border ${activeMenu === 'bookings_operations' ? 'bg-white/10 text-white border-white/10 shadow-sm' : 'text-slate-400 border-transparent hover:text-white hover:bg-white/5 hover:border-white/10'}`}>{t('booking_operations')}</button>
                  <button onClick={() => { setActiveMenu('payments'); setSearchTerm(''); setAdvancedFilters({ client: '', method: '', status: '', category: '', startDate: '', endDate: '', search: '' }); }} className={`group w-full text-left px-3.5 py-2.5 rounded-xl text-[13.5px] lg:text-[14.5px] font-medium transition-all duration-200 border ${activeMenu === 'payments' ? 'bg-white/10 text-white border-white/10 shadow-sm' : 'text-slate-400 border-transparent hover:text-white hover:bg-white/5 hover:border-white/10'}`}>{t('payments')}</button>
                  <button onClick={() => { setActiveMenu('booking_calendar'); setSearchTerm(''); setAdvancedFilters({ client: '', method: '', status: '', category: '', startDate: '', endDate: '', search: '' }); }} className={`group w-full text-left px-3.5 py-2.5 rounded-xl text-[13.5px] lg:text-[14.5px] font-medium transition-all duration-200 border ${activeMenu === 'booking_calendar' ? 'bg-white/10 text-white border-white/10 shadow-sm' : 'text-slate-400 border-transparent hover:text-white hover:bg-white/5 hover:border-white/10'}`}>{uiText({sq:'Kalendari i rezervimeve', en:'Booking Calendar', de:'Buchungskalender', it:'Calendario prenotazioni', fr:'Calendrier des réservations', hr:'Kalendar rezervacija'})}</button>
                  <button onClick={() => { setActiveMenu('deposits'); setSearchTerm(''); setAdvancedFilters({ client: '', method: '', status: '', category: '', startDate: '', endDate: '', search: '' }); }} className={`group w-full text-left px-3.5 py-2.5 rounded-xl text-[13.5px] lg:text-[14.5px] font-medium transition-all duration-200 border ${activeMenu === 'deposits' ? 'bg-white/10 text-white border-white/10 shadow-sm' : 'text-slate-400 border-transparent hover:text-white hover:bg-white/5 hover:border-white/10'}`}>{uiText({sq:'Depozitat', en:'Deposits', de:'Kautionen', it:'Depositi', fr:'Dépôts', hr:'Depoziti'})}</button>
                  <button onClick={() => { setActiveMenu('invoice_auto'); setSearchTerm(''); setAdvancedFilters({ client: '', method: '', status: '', category: '', startDate: '', endDate: '', search: '' }); }} className={`group w-full text-left px-3.5 py-2.5 rounded-xl text-[13.5px] lg:text-[14.5px] font-medium transition-all duration-200 border ${activeMenu === 'invoice_auto' ? 'bg-white/10 text-white border-white/10 shadow-sm' : 'text-slate-400 border-transparent hover:text-white hover:bg-white/5 hover:border-white/10'}`}>{uiText({sq:'Gjenero Faturë', en:'Generate Invoice', de:'Rechnung generieren', it:'Genera fattura', fr:'Générer la facture', hr:'Generiraj fakturu'})}</button>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <MenuItem icon={Car} label={t('cars')} rightIcon={ChevronDown} id="cars" activeMenu={activeMenu} setActiveMenu={setActiveMenu} setSearchTerm={setSearchTerm} setAdvancedFilters={setAdvancedFilters} isSidebarCollapsed={isSidebarCollapsed} />
              {!isSidebarCollapsed && ['cars','tpl','vehicle_status','insurance','maintenance'].includes(activeMenu) && (
                <div className="ml-4 lg:ml-5 pl-3.5 border-l border-white/10 space-y-1.5 relative before:absolute before:left-0 before:top-1 before:bottom-1 before:w-px before:bg-gradient-to-b before:from-white/0 before:via-white/15 before:to-white/0">
                  <button onClick={() => { setActiveMenu('cars'); setSearchTerm(''); setAdvancedFilters({ client: '', method: '', status: '', category: '', startDate: '', endDate: '', search: '' }); }} className={`group w-full text-left px-3.5 py-2.5 rounded-xl text-[13.5px] lg:text-[14.5px] font-medium transition-all duration-200 border ${activeMenu === 'cars' ? 'bg-white/10 text-white border-white/10 shadow-sm' : 'text-slate-400 border-transparent hover:text-white hover:bg-white/5 hover:border-white/10'}`}>{t('cars')}</button>
                  <button onClick={() => { setActiveMenu('vehicle_status'); setSearchTerm(''); setAdvancedFilters({ client: '', method: '', status: '', category: '', startDate: '', endDate: '', search: '' }); }} className={`group w-full text-left px-3.5 py-2.5 rounded-xl text-[13.5px] lg:text-[14.5px] font-medium transition-all duration-200 border ${activeMenu === 'vehicle_status' ? 'bg-white/10 text-white border-white/10 shadow-sm' : 'text-slate-400 border-transparent hover:text-white hover:bg-white/5 hover:border-white/10'}`}>{uiText({sq:'Statusi i veturave', en:'Vehicle Status', de:'Fahrzeugstatus', it:'Stato veicoli', fr:'Statut des véhicules', hr:'Status vozila'})}</button>
                  <button onClick={() => { setActiveMenu('tpl'); setSearchTerm(''); setAdvancedFilters({ client: '', method: '', status: '', category: '', startDate: '', endDate: '', search: '' }); }} className={`group w-full text-left px-3.5 py-2.5 rounded-xl text-[13.5px] lg:text-[14.5px] font-medium transition-all duration-200 border ${activeMenu === 'tpl' ? 'bg-white/10 text-white border-white/10 shadow-sm' : 'text-slate-400 border-transparent hover:text-white hover:bg-white/5 hover:border-white/10'}`}>{t('car_tab_tpl')}</button>
                  <button onClick={() => { setActiveMenu('insurance'); setSearchTerm(''); setAdvancedFilters({ client: '', method: '', status: '', category: '', startDate: '', endDate: '', search: '' }); }} className={`group w-full text-left px-3.5 py-2.5 rounded-xl text-[13.5px] lg:text-[14.5px] font-medium transition-all duration-200 border ${activeMenu === 'insurance' ? 'bg-white/10 text-white border-white/10 shadow-sm' : 'text-slate-400 border-transparent hover:text-white hover:bg-white/5 hover:border-white/10'}`}>{t('insurance')}</button>
                  <button onClick={() => { setActiveMenu('maintenance'); setSearchTerm(''); setAdvancedFilters({ client: '', method: '', status: '', category: '', startDate: '', endDate: '', search: '' }); }} className={`group w-full text-left px-3.5 py-2.5 rounded-xl text-[13.5px] lg:text-[14.5px] font-medium transition-all duration-200 border ${activeMenu === 'maintenance' ? 'bg-white/10 text-white border-white/10 shadow-sm' : 'text-slate-400 border-transparent hover:text-white hover:bg-white/5 hover:border-white/10'}`}>{t('maintenance')}</button>
                </div>
              )}
            </div>
            <MenuItem icon={Users} label={t('clients')} id="clients" activeMenu={activeMenu} setActiveMenu={setActiveMenu} setSearchTerm={setSearchTerm} setAdvancedFilters={setAdvancedFilters} isSidebarCollapsed={isSidebarCollapsed} />
            
            {!isSidebarCollapsed && <p className="text-[10px] font-black text-slate-500/95 uppercase tracking-[0.18em] mb-2.5 mt-5 px-3.5 lg:px-4">{t('menu_finance_analytics_section')}</p>}
            <MenuItem icon={Receipt} label={t('expenses')} id="expenses" activeMenu={activeMenu} setActiveMenu={setActiveMenu} setSearchTerm={setSearchTerm} setAdvancedFilters={setAdvancedFilters} isSidebarCollapsed={isSidebarCollapsed} />
            <MenuItem icon={FileText} label={t('invoices')} id="invoices" activeMenu={activeMenu} setActiveMenu={setActiveMenu} setSearchTerm={setSearchTerm} setAdvancedFilters={setAdvancedFilters} isSidebarCollapsed={isSidebarCollapsed} />
            <MenuItem icon={BarChart3} label={t('reports')} rightIcon={ChevronDown} id="reports" activeMenu={activeMenu} setActiveMenu={setActiveMenu} setSearchTerm={setSearchTerm} setAdvancedFilters={setAdvancedFilters} isSidebarCollapsed={isSidebarCollapsed} />
            {!isSidebarCollapsed && ['reports','analytics_plus','smart_notifications'].includes(activeMenu) && (
              <div className="ml-4 lg:ml-5 pl-3.5 border-l border-white/10 space-y-1.5 relative before:absolute before:left-0 before:top-1 before:bottom-1 before:w-px before:bg-gradient-to-b before:from-white/0 before:via-white/15 before:to-white/0">
                <button onClick={() => { setActiveMenu('reports'); setSearchTerm(''); setAdvancedFilters({ client: '', method: '', status: '', category: '', startDate: '', endDate: '', search: '' }); }} className={`group w-full text-left px-3.5 py-2.5 rounded-xl text-[13.5px] lg:text-[14.5px] font-medium transition-all duration-200 border ${activeMenu === 'reports' ? 'bg-white/10 text-white border-white/10 shadow-sm' : 'text-slate-400 border-transparent hover:text-white hover:bg-white/5 hover:border-white/10'}`}>{t('reports')}</button>
                <button onClick={() => { setActiveMenu('analytics_plus'); setSearchTerm(''); setAdvancedFilters({ client: '', method: '', status: '', category: '', startDate: '', endDate: '', search: '' }); }} className={`group w-full text-left px-3.5 py-2.5 rounded-xl text-[13.5px] lg:text-[14.5px] font-medium transition-all duration-200 border ${activeMenu === 'analytics_plus' ? 'bg-white/10 text-white border-white/10 shadow-sm' : 'text-slate-400 border-transparent hover:text-white hover:bg-white/5 hover:border-white/10'}`}>{uiText({sq:'Analitika e avancuar', en:'Advanced Analytics', de:'Erweiterte Analysen', it:'Analisi avanzate', fr:'Analyses avancées', hr:'Napredna analitika'})}</button>
                <button onClick={() => { setActiveMenu('smart_notifications'); setSearchTerm(''); setAdvancedFilters({ client: '', method: '', status: '', category: '', startDate: '', endDate: '', search: '' }); }} className={`group w-full text-left px-3.5 py-2.5 rounded-xl text-[13.5px] lg:text-[14.5px] font-medium transition-all duration-200 border ${activeMenu === 'smart_notifications' ? 'bg-white/10 text-white border-white/10 shadow-sm' : 'text-slate-400 border-transparent hover:text-white hover:bg-white/5 hover:border-white/10'}`}>{uiText({sq:'Njoftime inteligjente', en:'Smart Notifications', de:'Intelligente Benachrichtigungen', it:'Notifiche intelligenti', fr:'Notifications intelligentes', hr:'Pametne obavijesti'})}</button>
              </div>
            )}

            {!isSidebarCollapsed && <p className="text-[10px] font-black text-slate-500/95 uppercase tracking-[0.18em] mb-2.5 mt-5 px-3.5 lg:px-4">{t('menu_operations_section')}</p>}
            <MenuItem icon={ScrollText} label={t('contracts')} rightIcon={ChevronDown} id="contracts" activeMenu={activeMenu} setActiveMenu={setActiveMenu} setSearchTerm={setSearchTerm} setAdvancedFilters={setAdvancedFilters} isSidebarCollapsed={isSidebarCollapsed} />
            {!isSidebarCollapsed && ['contracts','contracts_auto'].includes(activeMenu) && (
              <div className="ml-4 lg:ml-5 pl-3.5 border-l border-white/10 space-y-1.5 relative before:absolute before:left-0 before:top-1 before:bottom-1 before:w-px before:bg-gradient-to-b before:from-white/0 before:via-white/15 before:to-white/0">
                <button onClick={() => { setActiveMenu('contracts'); setSearchTerm(''); setAdvancedFilters({ client: '', method: '', status: '', category: '', startDate: '', endDate: '', search: '' }); }} className={`group w-full text-left px-3.5 py-2.5 rounded-xl text-[13.5px] lg:text-[14.5px] font-medium transition-all duration-200 border ${activeMenu === 'contracts' ? 'bg-white/10 text-white border-white/10 shadow-sm' : 'text-slate-400 border-transparent hover:text-white hover:bg-white/5 hover:border-white/10'}`}>{t('contracts')}</button>
                <button onClick={() => { setActiveMenu('contracts_auto'); setSearchTerm(''); setAdvancedFilters({ client: '', method: '', status: '', category: '', startDate: '', endDate: '', search: '' }); }} className={`group w-full text-left px-3.5 py-2.5 rounded-xl text-[13.5px] lg:text-[14.5px] font-medium transition-all duration-200 border ${activeMenu === 'contracts_auto' ? 'bg-white/10 text-white border-white/10 shadow-sm' : 'text-slate-400 border-transparent hover:text-white hover:bg-white/5 hover:border-white/10'}`}>{uiText({sq:'Gjenero Kontratë', en:'Generate Contract', de:'Vertrag generieren', it:'Genera contratto', fr:'Générer le contrat', hr:'Generiraj ugovor'})}</button>
              </div>
            )}

            {!isSidebarCollapsed && <p className="text-[10px] font-black text-slate-500/95 uppercase tracking-[0.18em] mb-2.5 mt-5 px-3.5 lg:px-4">{t('menu_system_section')}</p>}
            <MenuItem icon={Settings} label={t('settings')} rightIcon={ChevronDown} id="settings" activeMenu={activeMenu} setActiveMenu={setActiveMenu} setSearchTerm={setSearchTerm} setAdvancedFilters={setAdvancedFilters} isSidebarCollapsed={isSidebarCollapsed} />
            {!isSidebarCollapsed && ['settings','multi_user','profile'].includes(activeMenu) && (
              <div className="ml-4 lg:ml-5 pl-3.5 border-l border-white/10 space-y-1.5 relative before:absolute before:left-0 before:top-1 before:bottom-1 before:w-px before:bg-gradient-to-b before:from-white/0 before:via-white/15 before:to-white/0">
                <button onClick={() => { setActiveMenu('settings'); setSearchTerm(''); setAdvancedFilters({ client: '', method: '', status: '', category: '', startDate: '', endDate: '', search: '' }); }} className={`group w-full text-left px-3.5 py-2.5 rounded-xl text-[13.5px] lg:text-[14.5px] font-medium transition-all duration-200 border ${activeMenu === 'settings' ? 'bg-white/10 text-white border-white/10 shadow-sm' : 'text-slate-400 border-transparent hover:text-white hover:bg-white/5 hover:border-white/10'}`}>{t('settings')}</button>
                <button onClick={() => { setActiveMenu('multi_user'); setSearchTerm(''); setAdvancedFilters({ client: '', method: '', status: '', category: '', startDate: '', endDate: '', search: '' }); }} className={`group w-full text-left px-3.5 py-2.5 rounded-xl text-[13.5px] lg:text-[14.5px] font-medium transition-all duration-200 border ${activeMenu === 'multi_user' ? 'bg-white/10 text-white border-white/10 shadow-sm' : 'text-slate-400 border-transparent hover:text-white hover:bg-white/5 hover:border-white/10'}`}>{uiText({sq:'Menaxhimi i përdoruesve', en:'Multi-user System', de:'Mehrbenutzersystem', it:'Sistema multiutente', fr:'Système multi-utilisateur', hr:'Sustav s više korisnika'})}</button>
                <button onClick={() => { setActiveMenu('profile'); setSearchTerm(''); setAdvancedFilters({ client: '', method: '', status: '', category: '', startDate: '', endDate: '', search: '' }); }} className={`group w-full text-left px-3.5 py-2.5 rounded-xl text-[13.5px] lg:text-[14.5px] font-medium transition-all duration-200 border ${activeMenu === 'profile' ? 'bg-white/10 text-white border-white/10 shadow-sm' : 'text-slate-400 border-transparent hover:text-white hover:bg-white/5 hover:border-white/10'}`}>{t('profile')}</button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={`${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-72'} flex-1 flex flex-col min-h-screen transition-all duration-300 w-full`}>
        
       
<div className={`sticky top-0 z-30 ${isDarkMode ? 'bg-slate-800/90 border-slate-700' : 'bg-white/90 border-slate-200'} backdrop-blur-xl border-b px-3 sm:px-5 lg:px-8 py-3 lg:py-3.5 shadow-sm`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={() => setIsMobileSidebarOpen(true)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm transition-colors hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 lg:hidden"
                aria-label="Open menu"
              >
                <Menu size={20} />
              </button>
              <h1 className="truncate text-lg sm:text-xl lg:text-2xl font-extrabold text-slate-800 dark:text-white capitalize">{{
              dashboard: t('dashboard'),
              bookings: t('bookings'),
              bookings_list: t('bookings'),
              bookings_payments: 'Payments',
              bookings_not_finished: t('booking_payments_not_finished'),
              bookings_waitlist: t('booking_waitlist'),
              bookings_operations: t('booking_operations'),
              tpl: t('car_tab_tpl'),
              booking_calendar: uiText({sq:'Kalendari i rezervimeve',en:'Booking Calendar',de:'Buchungskalender',it:'Calendario prenotazioni',fr:'Calendrier des réservations',hr:'Kalendar rezervacija'}),
              vehicle_status: uiText({sq:'Statusi i veturave',en:'Vehicle Status',de:'Fahrzeugstatus',it:'Stato veicoli',fr:'Statut des véhicules',hr:'Status vozila'}),
              contracts_auto: uiText({sq:'Gjenero Kontratë',en:'Generate Contract',de:'Vertrag generieren',it:'Genera contratto',fr:'Générer le contrat',hr:'Generiraj ugovor'}),
              deposits: uiText({sq:'Depozitat',en:'Deposits',de:'Kautionen',it:'Depositi',fr:'Dépôts',hr:'Depoziti'}),
              analytics_plus: uiText({sq:'Analitika e avancuar',en:'Advanced Analytics',de:'Erweiterte Analysen',it:'Analisi avanzate',fr:'Analyses avancées',hr:'Napredna analitika'}),
              smart_notifications: uiText({sq:'Njoftime inteligjente',en:'Smart Notifications',de:'Intelligente Benachrichtigungen',it:'Notifiche intelligenti',fr:'Notifications intelligentes',hr:'Pametne obavijesti'}),
              invoice_auto: uiText({sq:'Gjenero Faturë',en:'Generate Invoice',de:'Rechnung generieren',it:'Genera fattura',fr:'Générer la facture',hr:'Generiraj fakturu'}),
              multi_user: uiText({sq:'Menaxhimi i përdoruesve',en:'Multi-user System',de:'Mehrbenutzersystem',it:'Sistema multiutente',fr:'Système multi-utilisateur',hr:'Sustav s više korisnika'})
            }[activeMenu] || t(activeMenu)}</h1>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3 lg:gap-4 flex-shrink-0 overflow-visible">
              
              {/* --- ALERTS / BELL ICON LART --- */}
              <div className="relative overflow-visible shrink-0">
                <button 
                  onClick={() => setShowNotificationsPanel(!showNotificationsPanel)} 
                  className="relative flex h-11 w-11 shrink-0 items-center justify-center overflow-visible rounded-2xl text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <Bell size={20} className="shrink-0" />
                  {globalAlerts.length > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-3.5 w-3.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-red-500 ring-2 ring-white dark:ring-slate-800"></span>
                    </span>
                  )}
                </button>
                
                {showNotificationsPanel && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowNotificationsPanel(false)}></div>
                    <div className="fixed left-3 right-3 top-24 w-auto max-w-[calc(100vw-1.5rem)] sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 sm:max-w-none bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-100 dark:border-slate-700 z-50 overflow-hidden animate-in slide-in-from-top-2">
                      <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50">
                        <h3 className="font-bold text-slate-800 dark:text-white">Njoftimet</h3>
                        <span className="text-xs font-bold bg-indigo-100 text-indigo-600 dark:bg-indigo-900 dark:text-indigo-400 px-2 py-0.5 rounded-md">{globalAlerts.length} të reja</span>
                      </div>
                      <div className="max-h-80 overflow-y-auto custom-scrollbar p-2">
                        {globalAlerts.length === 0 ? (
                          <div className="p-4 text-center text-slate-500 dark:text-slate-400 text-sm py-8">
                            Nuk ka asnjë njoftim për momentin.
                          </div>
                        ) : (
                          globalAlerts.map(alert => {
                            const AlertIcon = alert.icon;
                            return (
                              <div key={alert.id} className="p-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-xl flex items-start space-x-3 transition-colors cursor-pointer border-b border-slate-50 dark:border-slate-700/50 last:border-0" onClick={() => setShowNotificationsPanel(false)}>
                                <div className={`p-2 rounded-lg mt-0.5 flex-shrink-0 ${
                                  alert.type === 'danger' ? 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400' :
                                  alert.type === 'warning' ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400' :
                                  'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400'
                                }`}>
                                  <AlertIcon size={16} />
                                </div>
                                <div>
                                  <p className={`font-bold text-sm ${alert.type === 'danger' ? 'text-red-600 dark:text-red-400' : 'text-slate-800 dark:text-slate-200'}`}>{alert.title}</p>
                                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{alert.message}</p>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                      <div className="p-3 text-center border-t border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                        <button onClick={() => { setActiveMenu('dashboard'); setShowNotificationsPanel(false); }} className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline">
                          Shko tek Paneli (Dashboard)
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <button onClick={() => setIsDarkMode(!isDarkMode)} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl p-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors overflow-visible">
                  {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
                </button>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowLanguageMenu(v => !v)}
                    className="flex items-center gap-2 sm:gap-3 pl-2 pr-2 sm:pr-3 py-2 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-900/90 shadow-sm hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-500 transition-all"
                    title={t('language')}
                  >
                    <span className="flex items-center justify-center h-8 w-11 rounded-xl bg-slate-100 dark:bg-slate-800 overflow-hidden border border-slate-200 dark:border-slate-700">
                      {currentLanguageOption.flagSrc ? (
                        <>
                          <img
                            src={currentLanguageOption.flagSrc}
                            alt={currentLanguageOption.label}
                            className="h-5 w-7 object-contain"
                            onError={(e) => { e.currentTarget.style.display = 'none'; const next = e.currentTarget.nextElementSibling; if (next) next.style.display = 'inline-flex'; }}
                          />
                          <span className="text-lg leading-none items-center justify-center" style={{ display: 'none' }}>{currentLanguageOption.flag}</span>
                        </>
                      ) : (
                        <span className="text-lg leading-none inline-flex items-center justify-center">{currentLanguageOption.flag}</span>
                      )}
                    </span>
                    <span className="hidden sm:flex flex-col items-start leading-tight">
                      <span className="text-[10px] uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">{t('language')}</span>
                      <span className="text-sm font-bold text-slate-700 text-slate-800 dark:text-slate-200">{currentLanguageOption.label}</span>
                    </span>
                    <ChevronDown size={16} className={`text-slate-400 transition-transform ${showLanguageMenu ? 'rotate-180' : ''}`} />
                  </button>
                  {showLanguageMenu && (
                    <div className="absolute right-0 mt-3 w-64 rounded-3xl border border-slate-200 dark:border-slate-700 bg-white/95 dark:bg-slate-900/95 backdrop-blur shadow-2xl overflow-hidden z-50">
                      <div className="px-4 pt-4 pb-2 border-b border-slate-100 dark:border-slate-800">
                        <div className="flex items-center gap-2 text-slate-700 text-slate-800 dark:text-slate-200">
                          <Globe size={16} className="text-indigo-500" />
                          <span className="text-sm font-bold">{t('language')}</span>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Choose the interface language</p>
                      </div>
                      <div className="p-2">
                        {LANGUAGE_OPTIONS.map(item => (
                          <button
                            key={item.code}
                            type="button"
                            onClick={() => changeLanguage(item.code)}
                            className={`w-full flex items-center justify-between px-3 py-3 rounded-2xl text-sm font-semibold transition-all ${language === item.code ? 'bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-indigo-900/30 dark:to-violet-900/20 text-indigo-700 dark:text-indigo-300 ring-1 ring-indigo-200 dark:ring-indigo-700' : 'text-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                          >
                            <span className="flex items-center gap-3">
                              <span className="flex items-center justify-center h-9 w-12 rounded-xl bg-slate-100 dark:bg-slate-800 overflow-hidden border border-slate-200 dark:border-slate-700">
                                {item.flagSrc ? (
                                  <>
                                    <img
                                      src={item.flagSrc}
                                      alt={item.label}
                                      className="h-5 w-7 object-contain"
                                      onError={(e) => { e.currentTarget.style.display = 'none'; const next = e.currentTarget.nextElementSibling; if (next) next.style.display = 'inline-flex'; }}
                                    />
                                    <span className="text-lg items-center justify-center" style={{ display: 'none' }}>{item.flag}</span>
                                  </>
                                ) : (
                                  <span className="text-lg inline-flex items-center justify-center">{item.flag}</span>
                                )}
                              </span>
                              <span className="flex flex-col items-start leading-tight">
                                <span>{item.label}</span>
                                <span className="text-[11px] uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">{item.code}</span>
                              </span>
                            </span>
                            {language === item.code ? (
                              <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-indigo-600 text-white">
                                <Check size={14} />
                              </span>
                            ) : (
                              <span className="h-6 w-6 rounded-full border border-slate-300 dark:border-slate-600" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="hidden sm:block h-8 w-px bg-slate-200 dark:bg-slate-700"></div>

              <div className="flex items-center space-x-2 sm:space-x-3 cursor-pointer group" onClick={() => setActiveMenu('profile')}>
                <div className="hidden sm:flex flex-col items-end">
                  <p className="text-base font-bold text-slate-800 dark:text-white leading-none group-hover:text-indigo-600 transition-colors">{user.name}</p>
                  <p className="text-[10px] font-black text-indigo-500 uppercase mt-1">{user.role}</p>
                </div>
                <img src={user.avatar} alt="Admin" className="w-10 h-10 rounded-xl object-cover border-2 border-white dark:border-slate-800 shadow-sm group-hover:border-indigo-500 transition-colors" />
                
                <button onClick={(e) => { e.stopPropagation(); handleLogout(); }} title="Dil nga sistemi" className="ml-1 p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-xl transition-all">
                  <LogOut size={20} />
                </button>
              </div>
            </div>
          </div>

          <GlobalBannerBar alerts={bannerAlerts} onDismiss={dismissBanner} />
        </div>

        <div className="flex-1 p-3 sm:p-5 lg:p-8 xl:p-10 pb-24 lg:pb-10 max-w-[1600px] w-full mx-auto">
          {renderContent()}
        </div>

        <div className={`py-4 px-4 sm:px-8 text-center text-xs sm:text-sm font-medium mb-16 lg:mb-0 ${isDarkMode ? 'text-slate-500 border-slate-800' : 'text-slate-400 border-slate-200'} border-t`}>
          © {new Date().getFullYear()} {settings.companyName}. {language === 'sq' ? 'Të gjitha të drejtat e rezervuara.' : 'All rights reserved.'}
        </div>
      </div>

      <div className={`fixed bottom-0 left-0 right-0 z-40 border-t lg:hidden ${isDarkMode ? 'border-slate-700 bg-slate-900/96' : 'border-slate-200 bg-white/96'} backdrop-blur-xl shadow-[0_-8px_30px_rgba(15,23,42,0.08)]`}>
        <div className="grid grid-cols-5 gap-1 px-2 py-2">
          {[
            { id: 'dashboard', icon: LayoutDashboard, label: t('dashboard') },
            { id: 'bookings', icon: Calendar, label: t('bookings') },
            { id: 'cars', icon: Car, label: t('cars') },
            { id: 'expenses', icon: Receipt, label: t('expenses') },
            { id: 'settings', icon: Settings, label: t('settings') },
          ].map((item) => {
            const Icon = item.icon;
            const isActive = activeMenu === item.id || (item.id === 'bookings' && ['bookings_list','payments','bookings_not_finished','bookings_waitlist','bookings_operations','booking_calendar','deposits','invoice_auto'].includes(activeMenu)) || (item.id === 'cars' && ['tpl','vehicle_status','insurance','maintenance'].includes(activeMenu));
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  setActiveMenu(item.id);
                  setSearchTerm('');
                  setAdvancedFilters({ client: '', method: '', status: '', category: '', startDate: '', endDate: '', search: '' });
                }}
                className={`flex flex-col items-center justify-center rounded-2xl px-1 py-2 transition-all ${isActive ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-slate-800' : 'text-slate-500 dark:text-slate-400'}`}
              >
                <Icon size={18} />
                <span className="mt-1 text-[10px] font-semibold leading-none">{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <UpdateStatusModal
        state={updateState}
        isDarkMode={isDarkMode}
        onInstallNow={handleInstallUpdateNow}
        onLater={handleLaterUpdate}
        formatBytes={formatUpdateBytes}
      />

      {isModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4">
          <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm animate-in fade-in duration-300" onClick={() => setIsModalOpen(false)} />
          <div className={`relative w-full ${modalType === 'booking' && modalMode === 'view' ? 'max-w-[95vw]' : ['booking', 'client', 'car', 'invoice', 'contract'].includes(modalType) ? 'max-w-[98vw] sm:max-w-4xl' : 'max-w-[96vw] sm:max-w-2xl'} overflow-hidden rounded-3xl border border-white/20 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl shadow-2xl animate-in zoom-in-95 slide-in-from-bottom-5 duration-300`}>
            {(() => {
              const headerInfo = getModalHeaderInfo();
              return (
                <div className="relative px-4 sm:px-6 lg:px-8 py-4 sm:py-5 lg:py-6 border-b border-slate-200/60 dark:border-slate-700/60">
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/10 via-transparent to-blue-600/10 pointer-events-none" />
                  <div className="relative flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className={`h-11 w-11 sm:h-12 sm:w-12 lg:h-14 lg:w-14 rounded-2xl ${headerInfo.color} text-white flex items-center justify-center shadow-lg shadow-indigo-500/20`}>
                        <headerInfo.icon size={28} />
                      </div>
                      <div className="pt-1">
                        <h3 className="text-lg sm:text-xl lg:text-2xl font-extrabold text-slate-900 text-slate-800 dark:text-white tracking-tight">{headerInfo.title}</h3>
                        <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mt-1">{modalMode === 'view' ? (modalType === 'car' ? t('car_details_title') : modalType === 'booking' ? 'Client Details' : t('client_details_title')) : t('fill_fields')}</p>
                      </div>
                    </div>
                    <button type="button" onClick={() => setIsModalOpen(false)} className="h-10 w-10 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-500 hover:text-red-600 hover:border-red-200 hover:bg-red-50 dark:hover:bg-red-900/20 transition flex items-center justify-center">
                      <X size={20} />
                    </button>
                  </div>
                </div>
              );
            })()}

            <form onSubmit={handleSave}>
              <div className={`px-4 sm:px-6 lg:px-8 py-4 sm:py-5 lg:py-6 ${modalType === 'booking' && modalMode === 'view' ? 'max-h-[80vh]' : 'max-h-[65vh]'} overflow-y-auto custom-scrollbar`}>
                {renderModalContent()}
              </div>
              <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-5 border-t border-slate-200/60 dark:border-slate-700/60 bg-slate-50/50 dark:bg-slate-800/50 backdrop-blur flex justify-end gap-3">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-6 py-2.5 rounded-xl font-bold text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm">
                  {t('cancel')}
                </button>
                {modalMode !== 'view' && (
                  <button type="submit" className="px-8 py-2.5 rounded-xl font-extrabold text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:shadow-lg hover:shadow-indigo-500/30 transition-all transform hover:scale-[1.02]">
                    {t('save')}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </I18nContext.Provider>
  );
};

const notificationAnimationStyles = `
@keyframes shrink {
  from { transform: scaleX(1); }
  to { transform: scaleX(0); }
}
`;


const UpdateStatusModal = ({ state, isDarkMode, onInstallNow, onLater, formatBytes }) => {
  if (!state?.visible || !['checking', 'downloading', 'ready'].includes(state.stage)) return null;

  const isReady = state.stage === 'ready';
  const isChecking = state.stage === 'checking';
  const percent = Math.max(0, Math.min(100, Number(state.percent || 0)));

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-950/50 backdrop-blur-md" />
      <div className={`relative w-full max-w-md overflow-hidden rounded-[28px] border shadow-2xl ${
        isDarkMode ? 'border-slate-700 bg-slate-900 text-white' : 'border-white/70 bg-white text-slate-900'
      }`}>
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_top_right,rgba(99,102,241,0.20),transparent_35%),radial-gradient(circle_at_bottom_left,rgba(59,130,246,0.18),transparent_30%)]" />
        <div className="relative p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`flex h-14 w-14 items-center justify-center rounded-2xl shadow-lg ${
                isReady ? 'bg-gradient-to-br from-emerald-500 to-teal-500' : 'bg-gradient-to-br from-indigo-500 to-blue-600'
              } text-white`}>
                {isReady ? <CheckCircle size={28} /> : <Download size={26} />}
              </div>
              <div>
                <div className={`text-xs font-black uppercase tracking-[0.22em] ${isDarkMode ? 'text-indigo-300' : 'text-indigo-600'}`}>
                  {isChecking ? 'Auto Update' : isReady ? 'Update gati' : 'Po shkarkohet'}
                </div>
                <h3 className="mt-1 text-2xl font-black tracking-tight">
                  {isChecking ? 'Po kontrollojmë versionin e ri' : isReady ? 'Instalo përditësimin' : `Versioni ${state.version || ''}`}
                </h3>
              </div>
            </div>
            <button
              type="button"
              onClick={onLater}
              className={`flex h-10 w-10 items-center justify-center rounded-xl border transition ${
                isDarkMode
                  ? 'border-slate-700 bg-slate-800 text-slate-300 hover:border-slate-600 hover:bg-slate-700'
                  : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <X size={18} />
            </button>
          </div>

          <div className={`mt-6 rounded-2xl border p-4 ${
            isDarkMode ? 'border-slate-800 bg-slate-950/60' : 'border-slate-100 bg-slate-50/90'
          }`}>
            <p className={`text-sm leading-6 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              {state.message || (isReady
                ? 'Programi do të rifillohet për ta përfunduar instalimin.'
                : 'Përditësimi po shkarkohet në sfond dhe do të instalohet sapo të jetë gati.')}
            </p>

            {!isChecking && (
              <div className="mt-4">
                <div className="mb-2 flex items-center justify-between text-xs font-bold uppercase tracking-wide">
                  <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>
                    {isReady ? 'Statusi' : 'Progresi'}
                  </span>
                  <span className={isReady ? 'text-emerald-500' : 'text-indigo-500'}>
                    {isReady ? '100%' : `${Math.round(percent)}%`}
                  </span>
                </div>
                <div className={`h-3 overflow-hidden rounded-full ${isDarkMode ? 'bg-slate-800' : 'bg-slate-200'}`}>
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${
                      isReady ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 'bg-gradient-to-r from-indigo-500 via-blue-500 to-cyan-400'
                    }`}
                    style={{ width: `${isReady ? 100 : percent}%` }}
                  />
                </div>

                {!isReady && (
                  <div className={`mt-3 flex items-center justify-between text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    <span>{formatBytes(state.transferred)} / {formatBytes(state.total)}</span>
                    <span>{formatBytes(state.bytesPerSecond)}/s</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onLater}
              className={`rounded-xl px-4 py-2.5 font-bold transition ${
                isDarkMode
                  ? 'border border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700'
                  : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              {isReady ? 'Më vonë' : 'Fshihe'}
            </button>
            {isReady ? (
              <button
                type="button"
                onClick={onInstallNow}
                className="rounded-xl bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-500 px-5 py-2.5 font-extrabold text-white shadow-lg shadow-indigo-500/30 transition hover:scale-[1.02]"
              >
                Instalo tani
              </button>
            ) : (
              <div className={`rounded-xl px-4 py-2.5 text-sm font-bold ${isDarkMode ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                Në sfond...
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


export default App;